import React from 'react';
import Archive from './Archive';
import ConfirmModal from './components/ConfirmModal';
import { arrayMove } from '@dnd-kit/sortable';
import { pdf } from '@react-pdf/renderer';
import { pdfjs } from 'react-pdf';
import RemnantBookPDF from './RemnantBookPDF';
import RemnantCoverPDF, { getCoverDimensions } from './RemnantCoverPDF';
import { uploadData, downloadData, list, remove, getUrl } from 'aws-amplify/storage';
import { generateClient } from 'aws-amplify/api';
import { post } from 'aws-amplify/api';
import { getCurrentUser, signOut as amplifySignOut, fetchAuthSession } from 'aws-amplify/auth';
import { isAuthExpiry } from './utils/authExpiry';
import { Hub } from 'aws-amplify/utils';
import { fetchMFAPreference, setUpTOTP, verifyTOTPSetup, updateMFAPreference } from 'aws-amplify/auth';
import { createLegacyEntry, deleteLegacyEntry, updateLegacyEntry } from './graphql/mutations';
import { listLegacyEntries } from './graphql/queries';
// userDataByUserId, createUserData, updateUserData — moved to hooks/useUserData
import { createShareableMedia, shareableMediaByUserId, deleteShareableMedia } from './shareableMediaGraphql';
import QRCode from 'qrcode';
import { Book, Heart, Gift, PencilSimple, FileText, Clock, Shield, Users, Sparkle, Gear, ChatCircle, Microphone, Feather, BookOpenText, Image as ImageIcon, List as MenuIcon, CreditCard } from '@phosphor-icons/react';
import { OCCASION_TYPES, CELEBRATION_PROMPTS, calculateDeadlines, createCelebrationBookData } from './celebrationPrompts';
import LandingPage from './LandingPageV2';
import { trackEvent, setAnalyticsUser } from './utils/analytics';
import AuthPage from './AuthPage';
import ContributePage from './ContributePage';
import './styles/app.css';
// Pip poses (illustrator vector, bundled — not in public/). Celebrating + holding-book
// drive the completion loop. (Poised lives in StRecordPage's "writing your story" screen.)
import pipCelebrating from './assets/pip/pip-celebrating.svg';
import pipHoldingBook from './assets/pip/pip-holding-book.svg';
import { MEMORY_PROMPTS, ST_PROMPTS, AI_Q_CATEGORIES } from './constants/prompts';
import { DEFAULT_PACK_ID, PROMPT_PACKS } from './constants/promptPacks';
import { ESTATE_SECTIONS, IMPORTANT_CONTACTS } from './constants/estateConfig';
import { AI_API, ADMIN_USER_ID, ORDER_API_BASE, STRIPE_PK, MEMORY_CARE_ENABLED } from './constants/config';
import MemoryCareComingSoon from './components/MemoryCareComingSoon';
import GiftSheet from './components/GiftSheet';
import { callAi, callAiResumable, newJobKey, logInterviewStart, logReachedGeneration, logGenerationFailed } from './utils/aiClient';
import { runPolishPair } from './utils/polishRace'; // F-334: complete or fail, no substitutes
import { GENERATION_FAILED_MESSAGE, GENERATION_TOO_THIN_MESSAGE } from './constants/generationMessages'; // F-334 word-gated copy
import { cleanContributionContent, promptToChapterTitle } from './utils/contentHelpers';
import { prepareStoryDisplayText } from './utils/storyText'; // F-207: shared display text-prep + punctuation repair
import { cleanPagesForSave, buildUpdatedBookPages, LULU_MIN_PAGES, stBookEstimate, clearTombstones } from './utils/bookHelpers';
import { runStoryDelete } from './utils/storyDeleteFlow';
import { isImageFile, normalizeImageForBook, checkPhotoQuality } from './utils/imageHelpers';
import { POLISH_SYSTEM_PROMPT, getFlickPolishPrompt, getWholePolishPrompt, getWholeShapePlanPrompt, resolvePolishRouting, shouldRunShapePlan } from './utils/aiPrompts';
import { gateStoryOutput, stripDuplicateTitle, correctiveRegenDirective } from './utils/outputGate'; // F-116 output gate (extends PIPE-LANG-1): language on title+body, self-talk screen, dup-title clean
import { gateBorrowedQuestions, borrowedQuestionRegenDirective } from './utils/borrowedQuestionGate'; // borrowed-question gate: Pip-question + yes/no staple, sibling of F-116, failGeneration not Flick/raw
import { buildFidelityJudgePrompt, parseFidelityVerdict, fidelityGateDecision, fidelityRegenDirective, FIDELITY_JUDGE_SYSTEM } from './utils/fidelityJudge'; // F-145 STORY-FIDELITY-1: live details-in-vs-out gate (dropped threads + invented facts)
import { interviewHasSubstance } from './utils/interviewDraft'; // F-118b: an unsent draft is substance — no destroy without a warn
// TB-104 Stage 2a: adaptive follow-up interview engine
import {
  classifyAnswer,
  selectMove,
  selectDepthMove, // TB-104 keep-going
  buildFollowUpPrompt,
  buildWrapTurnPrompt, // TB-104 rollout Stage A
  validateFollowUpStructure, // TB-104 rollout Stage A
  stripEchoFragment, // PIP-ECHO-FRAGMENT-1 (F-266): deterministic salvage, no regen
  stripInventedHabitReaction, // TOPIC-DOOR-HONOR-1: drop "you think about that often" the teller never said
  applyWarmOwnWordsLine, // NEW (warm own-words line, 2026-10-09): shared with the voice guard
  isColdStartRampOpen, // TOPIC-DOOR-HONOR-1: ramp closes when a real move has been asked
  stripVocative, // VOCATIVE-GUARD-1 (F-319): deterministic salvage for a name spoken AT the teller
  sanitizeDashes, // TB-104 generous pursuit: belt-and-suspenders em-dash removal
  buildClassificationPrompt,
  parseClassification,
  buildAssemblerPrompt,
  buildQAPairs, // F-216: pairing seam + PLEASANTRY-NOT-MATERIAL-1 (moved out of this file)
  stripInterviewFrame, // INTERVIEW-FRAME-1: stew residue off both polished outputs
  needsAssembly,
  routeToTier,
  getEmptyReInvitation,
  hasHardMemorySignal, // TB-104 Stage 3
  hasDeathLossSignal, // PIPE-GRIEF-1: narrow death-of-a-person fork trigger
  hasCrisisSignal, // grief mode: crisis (988) routing + stronger-model switch
  getPackConfig, // TB-104 Stage 4
  findSalientThread, // TB-104 thread-salience
  isSubstantiveForCounter, // F-177: counter-only substantive gate (meters followUpCount on real answers)
  getAuthorVantage, // relationship-aware vantage
  detectFollowUpViolations, // TB-IQ Part 3: structural regeneration guard
  chooseGeneration, // PIPE-OUT-1 + F-334: the Whole Tale or nothing
  // isUsableGeneration was imported for the old Flick-substitute branch, which F-334 removed.
  // The predicate still runs inside chooseGeneration and inside checkSaveArtifact (saveGuards.js).
  // F-23/F-24 wrap engine
  buildOpenerLine, shouldOfferWrap, isWrapAcceptance, countSubstantiveTurns,
  conversationHasHeavyContent, shouldFireCrisisGate, isCrisisRedirect, CRISIS_GATE_LINE,
  shouldFireSoftCrisisAck, THIRD_PERSON_CRISIS_SOFT_LINE, // F-111 item 0 (F-130): soft crisis class
  shouldFireViolenceGrounding, impliesOngoingDanger, THIRD_PARTY_VIOLENCE_GROUND_LINE, VIOLENCE_ONGOING_SAFETY_LINE, // F-174: third-party violence grounding
  reconstructOfferState, // F-40 (C): the ONE shared offer-state reconstruction (also used by voice adapter)
  // F-62 (Front Door wiring): cold-start ramp. coldStart is PASSED from this caller's seedless-entry
  // state (stSelectedPrompt has no text); warmupCount is DERIVED via the ONE shared helper. Inert until
  // the doorway provides a seedless entry — every topic-picked flow has prompt text, so coldStart=false.
  countWarmupMoves, WARMUP_REST_LINE, COLD_START_OPENER,
  SILENCE_STOP_LINE, // F-111 DEFECT 3 (F-137): silence-policy rest line (founder-approved)
  stripToPendingQuestion, getWarmupDoorLine, // F-156: question-only clarification repeat + interim door lines (by ramp position)
} from './utils/interviewEngine';
import { isProcessedStoryOverride, checkSaveArtifact } from './utils/saveGuards'; // PIPE-SAVE-1 hotfix guard (see saveGuards.js)
import { persistDraft, persistPreGenerationDraft, updateDraftWithStory, removeDraft, markLandingPlayed, saveDraftEdits, fetchActiveDraft, draftToProcessed, resumeTargetFor, deriveTellerFirstLine, buildResumeThread } from './utils/storyDraft'; // REVEAL-002 R1 server-side draft + R3 veil line + F-402 resume thread + RESUME-LANDS-ON-A-RENDERED-SURFACE-1 target
import { coverPhotoStateFromBook, resolveCoverPhotoToSave } from './utils/coverState'; // F-87 COVER-PHOTO-1
import ReorderableChapterList from './components/ReorderableChapterList';
import ChapterSearchList from './components/ChapterSearchList';
import BookSpreadPreview from './components/BookSpreadPreview';
import useUserData from './hooks/useUserData';
import AppContext from './contexts/AppContext';
import BookBuilderPage, { GiftBooksTab } from './pages/BookBuilderPage';
import RecipientsPage from './pages/RecipientsPage';
import TimeCapsulePage from './pages/TimeCapsulePage';
import EstatePlanningPage from './pages/EstatePlanningPage';
import JournalPage from './pages/JournalPage';
import AccountPage from './pages/AccountPage';
import MemoryInterviewPage from './pages/MemoryInterviewPage';
import AdminPage from './pages/AdminPage';
import KeepsakeView from './components/KeepsakeView';
import BookReader from './components/BookReader';
import OrphanRecoveryModal from './components/OrphanRecoveryModal';
import StWritePage from './pages/StWritePage';
import StRecordPage from './pages/StRecordPage';
import OwnStoryHome from './pages/OwnStoryHome';

/* Buffer polyfill for @react-pdf/renderer browser compatibility */
if (typeof window !== 'undefined' && !window.Buffer) {
  window.Buffer = {
    from: (data, encoding) => {
      if (typeof data === 'string') {
        if (encoding === 'base64') {
          const binary = atob(data);
          const bytes = new Uint8Array(binary.length);
          for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
          return bytes;
        }
        return new TextEncoder().encode(data);
      }
      if (ArrayBuffer.isView(data)) return new Uint8Array(data.buffer, data.byteOffset, data.byteLength);
      return new Uint8Array(data);
    },
    isBuffer: () => false,
    alloc: (size) => new Uint8Array(size),
    allocUnsafe: (size) => new Uint8Array(size),
    concat: (list, totalLength) => {
      if (!totalLength) totalLength = list.reduce((acc, buf) => acc + buf.length, 0);
      const result = new Uint8Array(totalLength);
      let offset = 0;
      for (const buf of list) { result.set(buf instanceof Uint8Array ? buf : new Uint8Array(buf), offset); offset += buf.length; }
      return result;
    },
    byteLength: (str, encoding) => {
      if (encoding === 'base64') return Math.ceil(str.length * 3 / 4);
      return new TextEncoder().encode(str).length;
    },
  };
}

const client = generateClient();

// TB-IQ Part 3: structural regeneration guard (typed path). Appended to the follow-up
// system prompt on the single regeneration pass when the first attempt trips a HARD
// structural violation (fragment question, leading template, compound, no question).
const FOLLOWUP_REGEN_NUDGE = `Your previous reply was malformed and was rejected. Rewrite it now, obeying EVERY rule:
- Line 1: exactly one short, warm reaction sentence. Never tell them how they felt (no "that must have felt...", no "you must have been...").
- Never repeat their own "I / me / my" words back at them (F-145 ECHO-TRANSPOSE). Speak TO them in second person: their "he does love me" becomes "he does love you"; their "me crying" becomes "you were crying"; their "I was scared" becomes "you were scared".
- Line 3: exactly ONE complete question that BEGINS with a question word (what, where, when, who, why, which, how) OR an auxiliary (did, do, was, were, is, are, had) OR "tell me / describe / walk me through". No sentence fragments, no second question.
- NEVER address the storyteller by name (VOCATIVE-GUARD-1). No "..., Name." and no "Name, ...". You do not know their name, and a name you heard in their answer belongs to someone they are telling you about. You may still refer to that person in the third person.`;

/**
 * Generates a typed follow-up question through the structural guard: calls /follow_up,
 * applies existing hygiene (validateFollowUpStructure + sanitizeDashes), then runs
 * detectFollowUpViolations. On a HARD violation it regenerates ONCE with a nudge and
 * keeps the retry only if strictly cleaner. The guard never blocks or worsens a turn --
 * any failure falls through to the original text. Mirrors the voice adapter's guard.
 * @param {function} callAi -- the component's AI caller (passed in; it is a closure)
 */
// Grief / crisis interview turns run on the stronger model (validated in the grief stress
// harness; Haiku leaked self-blame). Ordinary turns fall back to the Lambda default (Haiku).
// Matches production's Whole-tale Sonnet. The remnantAI Lambda honors this per-request override.
const GRIEF_FOLLOWUP_MODEL = 'claude-sonnet-5';

export async function guardedFollowUp(callAi, { systemPrompt, userContent, roughDraft = '', conversation = [], move = '' }) {
  const post = (t) => validateFollowUpStructure(sanitizeDashes((t || '').trim()));
  const lastUser = [...conversation].reverse().find(t => t.role === 'user');
  const lastUserAnswer = lastUser ? lastUser.content : '';
  // Detect grief/crisis the same way buildFollowUpPrompt does, so the model matches the
  // companion prompt it already built for this turn. PIPE-GRIEF-1: fork is gated on the narrow
  // death-of-a-person signal, not the broad hard-memory signal (kept in sync with the engine).
  const griefMode = conversation.some(t => t.role === 'user' && hasDeathLossSignal(t.content)) || hasCrisisSignal(lastUserAnswer);
  // NEW (warm own-words line, 2026-10-09): one shared line on the reply that ships. No extra model call.
  const ship = (text) => applyWarmOwnWordsLine(text, lastUserAnswer, { move, isGrief: griefMode, conversation });
  const model = griefMode ? GRIEF_FOLLOWUP_MODEL : undefined;
  const fuData = await callAi({ action: 'follow_up', model, messages: [{ role: 'user', content: userContent }], roughDraft, systemPrompt });
  let out = post(fuData.text);
  try {
    // F-67: isGrief exempts the reaction-brevity soft-flag so a quiet grief acknowledgment is never
    // measured as "too long" (matches the companion prompt built for this same turn).
    let v = detectFollowUpViolations(out, { lastUserAnswer, move, isGrief: griefMode });
    // PIP-ECHO-FRAGMENT-1 (F-266): DETERMINISTIC STRIP, no regeneration. Precision on the trailing tape
    // was ~100%, so the strip is near-always strictly improving; and a regen on ~13% of turns would eat
    // the voice lane's dead-air margin (p95 2.853s against an 8s cascade timeout). Applied BEFORE the
    // regen branch so an echo never spends a model call. Every OTHER hard code keeps the existing
    // keep-only-if-cleaner regeneration, untouched.
    if (v.hard.includes('INVENTED_HABIT')) {
      out = stripInventedHabitReaction(out, lastUserAnswer);
      // INVENTED-STRIP-RECHECK (sim 2026-10-09 T14): the strip remedies INVENTED_HABIT only, so it must not
      // return early and mask another hard code (a dry feel-ask shipped with no regen). Re-check what
      // survives with the SAME detector; nothing left -> return the stripped reply as before; anything
      // left falls through to the existing salvage/regen below, and the strip stands unless a retry wins.
      v = detectFollowUpViolations(out, { lastUserAnswer, move, isGrief: griefMode });
      v = { ...v, hard: v.hard.filter((c) => c !== 'INVENTED_HABIT') };
      if (!v.hard.length) return ship(out);
    }
    if (v.hard.includes('ECHO_FRAGMENT')) {
      out = stripEchoFragment(out, lastUserAnswer);
      // VOCATIVE-GUARD-1 (F-319): the echo strip drops the reaction LINE, which may or may not have
      // been where the vocative sat. Run the vocative salvage on what survives so the two guards
      // cannot pass a violation between them. No-op when clean or unsafe to strip.
      out = stripVocative(out, { verifiedTellerName: null });
      return ship(out);
    }
    // VOCATIVE-GUARD-1 (F-319): DETERMINISTIC STRIP, mirroring the F-266 shape and placed BEFORE the
    // regen branch so a strippable vocative never spends a model call. Only the TRAILING slot raises
    // this hard code (LEADING/MID are soft — see VOCATIVE_SLOT_HARD), and `stripVocative` refuses any
    // strip that would not be well formed, returning the input unchanged. A refusal falls through to
    // the existing keep-only-if-cleaner regeneration below, which is the intended path for it.
    if (v.hard.includes('VOCATIVE_NAME')) {
      const stripped = stripVocative(out, { verifiedTellerName: null });
      if (stripped && stripped !== out) {
        out = stripped;
        v = detectFollowUpViolations(out, { lastUserAnswer, move, isGrief: griefMode });
        if (!v.hard.length) return ship(out);
      }
    }
    if (v.hard.length) {
      const retry = await callAi({ action: 'follow_up', model, messages: [{ role: 'user', content: userContent }], roughDraft, systemPrompt: `${systemPrompt}\n\n${FOLLOWUP_REGEN_NUDGE}` });
      let retryOut = post(retry.text);
      let v2 = detectFollowUpViolations(retryOut, { lastUserAnswer, move, isGrief: griefMode });
      // INVENTED-STRIP-RECHECK: the retry gets the same deterministic strip, so an invented lead-in is
      // never shown via the retry and the cleaner-than comparison is like for like with the stripped original.
      if (v2.hard.includes('INVENTED_HABIT')) {
        retryOut = stripInventedHabitReaction(retryOut, lastUserAnswer);
        v2 = detectFollowUpViolations(retryOut, { lastUserAnswer, move, isGrief: griefMode });
      }
      if (retryOut && v2.hard.length < v.hard.length) out = retryOut;
    }
  } catch (e) {
    // Guard must never break the interview -- keep the original output on any error.
  }
  return ship(out);
}

// ── TB-075 QW-1: module-scope signed URL cache ──────────────────────────────
// Stops the photo-resolve effect from generating a fresh signed URL on every
// book load. Without this, each `getUrl()` call rotates X-Amz-Date/Signature
// query params, the browser sees a different URL string, and re-downloads
// every photo regardless of HTTP cache state.
//
// Cleared on sign-out (handleSignOut) so User A's URLs don't bleed into
// User B's session on a shared device.
const SIGNED_URL_TTL_S = 3600;
const SIGNED_URL_REFRESH_BUFFER_MS = 120_000; // 2-minute safety window
const signedUrlCache = new Map(); // filename -> { url, expiresAt }

function _cacheKey(pathOrFilename) {
  return (pathOrFilename || '').split('/').pop();
}

function getCachedSignedUrl(pathOrFilename) {
  const key = _cacheKey(pathOrFilename);
  if (!key) return null;
  const entry = signedUrlCache.get(key);
  if (!entry) return null;
  if (Date.now() > entry.expiresAt - SIGNED_URL_REFRESH_BUFFER_MS) {
    signedUrlCache.delete(key);
    return null;
  }
  return entry.url;
}

function setCachedSignedUrl(pathOrFilename, url, expiresInSeconds = SIGNED_URL_TTL_S) {
  const key = _cacheKey(pathOrFilename);
  if (!key || !url) return;
  signedUrlCache.set(key, {
    url,
    expiresAt: Date.now() + expiresInSeconds * 1000,
  });
}

export function clearSignedUrlCache() {
  signedUrlCache.clear();
}

// Configure pdf.js worker for react-pdf viewer
pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;
// Constants imported from ./constants/config, ./constants/prompts, ./constants/estateConfig

// CSS extracted to styles/app.css
// (font imports, layout, components, responsive, storyteller, keepsake, etc.)

// TB-096 / TB-043: clear app-scoped localStorage (drafts, AI quota, mode prefs, intent)
// on sign-out OR session-expiry, preserving permanent non-sensitive flags. Cognito's own
// storage (CognitoIdentityServiceProvider.*) is cleared by amplifySignOut.
function clearAppScopedLocalStorage() {
  const PRESERVE_PREFIXES = ['remnant_welcomed_'];
  const PRESERVE_KEYS = new Set(['remnant_version', 'talebound_beta_dismissed']);
  try {
    const keysToRemove = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;
      if (!(key.startsWith('remnant_') || key.startsWith('talebound_'))) continue;
      if (PRESERVE_KEYS.has(key)) continue;
      if (PRESERVE_PREFIXES.some(p => key.startsWith(p))) continue;
      keysToRemove.push(key);
    }
    keysToRemove.forEach(key => localStorage.removeItem(key));
  } catch (e) { console.warn('[Auth] localStorage cleanup failed:', e); }
  try { localStorage.removeItem('tb_intent'); } catch (e) {}
}

function VaultApp({ signOut, user }) {
  // 'memory' is gated behind MEMORY_CARE_ENABLED (F-12) — it stays in CUT_TABS while the
  // flag is false so any direct/cached route to the memory tab redirects to 'book'.
  const CUT_TABS = ['capsule', 'recipients', 'estate', 'upgrade', 'giftbooks', ...(MEMORY_CARE_ENABLED ? [] : ['memory'])];
  const [activeTab, setActiveTabRaw] = React.useState(() => {
    try {
      // Activation-email deep link: ?start (taleboundbook.com/?start) drops the user
      // straight at the write doorway ('talk'), overriding whatever tab they last left
      // off on in localStorage. This is the one-tap "come write your first story" path.
      const params = new URLSearchParams(window.location.search);
      if (params.has('start')) return 'talk';
      const saved = localStorage.getItem('remnant_activeTab');
      // TB-082: redirect cached cut-tab state to book
      if (saved && !CUT_TABS.includes(saved)) return saved;
      // Core-redesign: the app opens on Pip's Talk home (the storyteller's doorway).
      return 'talk';
    } catch(e) { return 'talk'; }
  });
  const setActiveTab = (tab, opts = {}) => {
    // Unsaved-work guard: warn before leaving the review screen with a story that has
    // not been saved to the book yet, so a just-made chapter is never lost to a nav tap.
    // PIPE-SAVE-1 F-7: this is the IN-APP twin of the beforeunload guard. It must ALSO respect the
    // saved state — an auto-saved (clean) reveal must NOT prompt "you haven't saved this yet" on a
    // nav tap (the exact bug: chip says "Saved" while this fired). Warn only when unsaved OR dirty.
    if (activeTab === 'st-record' && stInterviewStep === 'review' && tab !== 'st-record'
        && (stProcessed?.content || '').trim()
        && (!stProcessed?.saved || stProcessed?.dirty)) {
      if (typeof window !== 'undefined' && !window.confirm("You haven't saved this story to your book yet. Leave without saving it?")) return;
    }
    // F-103: extend the in-app nav guard to the PRE-generation conversation step. A bottom-nav tab tap
    // mid-interview was unguarded (turns silently lost — Person C's class of loss on the in-app path). Warn
    // when the live conversation carries unwritten substance (>=5 words, mirroring ivHasSubstance + the
    // beforeunload guard). Native confirm here, the SAME mechanism as the review-step guard above; the styled
    // universal dialog covers the in-screen Cancel/Back exits. Excludes the reopen ('Tell Pip more') case,
    // whose original chapter is already saved and is handled by the abandonment block below.
    if (activeTab === 'st-record' && stInterviewStep === 'conversation' && tab !== 'st-record' && !stReopenStoryId
        && interviewHasSubstance({ draft: stInterviewInput, conversation: interviewConversation })) { // F-118b: an unsent draft counts too
      if (typeof window !== 'undefined' && !window.confirm('Your interview will be lost unless Pip writes your story. Leave without keeping it?')) return;
    }
    // REVEAL-001 C5: leaving a reopened "Tell Pip more" session before it finishes = abandoned. The
    // reopened conversation runs at step 'conversation' (the review-state guard above never fires for
    // it, and it must not — the original chapter is still safely saved, so there is nothing to warn
    // about; only the in-progress additions are lost). Fire once here, the single in-app nav chokepoint.
    // Guard 2 (Fable F-41): fire ONLY at step 'conversation'. During 'generating' the user is waiting on
    // a write-up they DID request — that is not abandonment, and nulling the marker mid-generation would
    // misroute the in-flight tail into the create path (a DUPLICATE). The generation tail routes on an
    // id captured at start, so leaving it set here is safe; the tail clears it on completion.
    if (stReopenStoryId && activeTab === 'st-record' && tab !== 'st-record' && stInterviewStep === 'conversation') {
      try { trackEvent('interview_abandoned', { context: 'tell_pip_more', story_id: stReopenStoryId }); } catch (e) { /* telemetry best-effort */ }
      setStReopenStoryId(null);
    }
    // TB-082: redirect cut tabs to book — handles cached localStorage state
    if (CUT_TABS.includes(tab)) tab = 'book';
    // 'st-book' means "the book the user is working on" (keep it open); plain
    // 'book' means the My Books list (clear the active book). Both render the
    // same 'book' tab, differentiated by activeBookId — so capture the intent
    // BEFORE remapping. Returning into a book (e.g. after adding a chapter) must
    // NOT boot the user back to the list.
    const keepBook = tab === 'st-book';
    if (tab === 'st-book') tab = 'book';
    setActiveTabRaw(tab);
    if (tab === 'book') {
      // Save current book before navigating — unless caller already saved with
      // fresh data (skipSave). saveActiveBook reads from React state which can
      // be stale within the same synchronous call as a setState; using its
      // result to "save" actually clobbers a just-completed save.
      if (activeBookId && !opts.skipSave) saveActiveBook();
      // Only clear the active book for true "My Books" navigation. The list is
      // where users discover "Start a New Book" / gift book CTAs (session 70);
      // but returning into the book they're building must keep it open.
      if (!keepBook) setActiveBookId(null);
      // Do not reset book step if an order confirmation is showing
      if (stOrderStep !== 'success') {
        setStBookStep(0);
        setStBookPreviewIdx(0);
        setStBookOpen(false);
        if (!keepBook) setShowGiftCreate(false);
      }
      window.scrollTo(0, 0);
    }
    // Reset polish original content when navigating away from write tab
    if (tab !== 'st-write') {
      setStOriginalContent(null);
    }
    try { localStorage.setItem('remnant_activeTab', tab); } catch(e) {}
  };

  // TB-082: appMode / isStoryteller / switchMode removed. Story Manager is the single layout for all users.

  // ── Beta / Admin config ──
  // PASTE YOUR COGNITO USER ID HERE (F12 → Console → getCurrentUser())
  const isAdmin = (user?.userId || user?.username) === ADMIN_USER_ID;

  // ── Feedback state ──
  const [showFeedback, setShowFeedback] = React.useState(false);

  // Activation-email deep links: ?feedback opens the feedback modal; ?start is handled by
  // the activeTab initializer above. Both params are stripped from the URL afterward so a
  // refresh does not re-trigger them. Runs once on mount.
  React.useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const hadStart = params.has('start');
      const hadFeedback = params.has('feedback');
      if (hadFeedback) setShowFeedback(true);
      if (hadStart || hadFeedback) {
        params.delete('start');
        params.delete('feedback');
        const qs = params.toString();
        window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : ''));
      }
    } catch (e) { /* deep-link parsing is best-effort */ }
  }, []);

  const [feedbackRating, setFeedbackRating] = React.useState(0);
  const [feedbackComment, setFeedbackComment] = React.useState('');
  const [feedbackSubmitting, setFeedbackSubmitting] = React.useState(false);
  const [feedbackSuccess, setFeedbackSuccess] = React.useState(false);
  const [showContact, setShowContact] = React.useState(false);
  // F-12: Memory Care "coming soon" gate — the shared modal opened by gated doorways.
  const [showMemoryCareComingSoon, setShowMemoryCareComingSoon] = React.useState(false);
  // F-13: in-app "warm path" gift sheet (reuses BuyForm embedded).
  const [showGiftSheet, setShowGiftSheet] = React.useState(false);
  const [contactMessage, setContactMessage] = React.useState('');
  const [contactSubmitting, setContactSubmitting] = React.useState(false);
  const [contactSuccess, setContactSuccess] = React.useState(false);

  // Admin dashboard state
  const [adminFeedback, setAdminFeedback] = React.useState([]);
  const [adminLoading, setAdminLoading] = React.useState(false);
  const [adminFilter, setAdminFilter] = React.useState('all'); // all | unreviewed | reviewed

  // Show welcome modal once per user — only mark as seen when user completes or dismisses
  const WELCOME_KEY = `remnant_welcomed_${user?.userId || user?.username || 'user'}`;
  const [showWelcome, setShowWelcome] = React.useState(() => {
    try {
      return !localStorage.getItem(WELCOME_KEY);
    } catch { return false; }
  });
  const dismissWelcome = () => {
    try { localStorage.setItem(WELCOME_KEY, '1'); } catch {}
    setShowWelcome(false);
  };
  // First-run: instead of a separate welcome modal, land new users on the book-type
  // chooser (the books screen with no book yet). Picking a type frames Pip's questions.
  React.useEffect(() => {
    if (showWelcome) {
      // First-run: land EVERY new user on the warm chooser so they pick their own
      // path (write my own story vs celebrate someone). We do not auto-route based
      // on which ad/CTA they came from.
      setActiveTab('book');
      dismissWelcome();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showWelcome]);

  // ── Toast Notifications ──
  const [toast, setToast] = React.useState(null);
  const toastTimer = React.useRef(null);
  const showToast = (message, type = 'info') => {
    if (toastTimer.current) clearTimeout(toastTimer.current);
    setToast({ message, type });
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  };

  const [files, setFiles] = React.useState([]);
  const [fileUrls, setFileUrls] = React.useState({});
  const [loadingFiles, setLoadingFiles] = React.useState(true);

  const [stories, setStories] = React.useState([]);
  const [loadingStories, setLoadingStories] = React.useState(true);
  // Orphan detection: story records that exist in LegacyEntries but are not
  // linked in any book's pages array (can happen after a multi-device clobber).
  // Surface this in a future recovery UI — do not auto-repair here.
  const [orphanedStoryIds, setOrphanedStoryIds] = React.useState([]);
  const [recordings, setRecordings] = React.useState([]);
  const [loadingRecordings, setLoadingRecordings] = React.useState(true);
  const [playingMedia, setPlayingMedia] = React.useState(null); // { url, mediaType }
  const [storyTitle, setStoryTitle] = React.useState('');
  const [storyContent, setStoryContent] = React.useState('');
  const [savingStory, setSavingStory] = React.useState(false);
  const [editingId, setEditingId] = React.useState(null);
  const [editTitle, setEditTitle] = React.useState('');
  const [editContent, setEditContent] = React.useState('');
  const [savingEdit, setSavingEdit] = React.useState(false);
  const [viewingStory, setViewingStory] = React.useState(null);
  const [quickCapture, setQuickCapture] = React.useState('');
  const [quickCaptureExpanded, setQuickCaptureExpanded] = React.useState(false);
  const [savingQuickCapture, setSavingQuickCapture] = React.useState(false);

  const [aiPrompt, setAiPrompt] = React.useState('');
  const [aiDraft, setAiDraft] = React.useState('');
  const [aiMessages, setAiMessages] = React.useState([]);
  const [aiResponse, setAiResponse] = React.useState('');
  const [aiLoading, setAiLoading] = React.useState(false);
  const [aiTitle, setAiTitle] = React.useState('');

  // User identity
  const userId = user?.userId || user?.username || 'user';
  const displayName = user?.attributes?.name || user?.signInDetails?.loginId?.split('@')[0] || 'Your Archive';

  // ── UserData hook — all DynamoDB-persisted user data ──
  const {
    userDataId, userDataLoaded,
    recipients, setRecipients, shares, setShares,
    capsules, setCapsules, books, setBooks, booksRef,
    activeBookId, setActiveBookId, collections, setCollections,
    journalEntries, setJournalEntries,
    memoryCareSubjects, setMemoryCareSubjects,
    accountSettings, setAccountSettings, accountName, setAccountName,
    legacyMode, setLegacyMode, legacySubjectName, setLegacySubjectName,
    notifyCheckin, setNotifyCheckin, notifyRelease, setNotifyRelease,
    notifyCapsule, setNotifyCapsule, releaseMethod, setReleaseMethod,
    checkinFrequency, setCheckinFrequency,
    estateChecks, setEstateChecks,
    estateNotes, setEstateNotes,
    estateContacts, setEstateContacts,
    isPremium, setIsPremium,
    isKeeper, subjectName,
    persistUserData, saveRecipients, saveShares, saveCapsules,
    saveCollections, saveAccountSettings, saveJournalEntries, saveMemoryCareSubjects,
    saveEstateChecks, saveEstateNotes, saveEstateContacts,
    persistBooks, saveBookData, confirmBookPagePersisted, loadUserData,
    removeStoryPageEverywhere, removeStoryPageFromBook,
  } = useUserData(userId, displayName);

  // REVEAL-002 R3 / B1 — the personal-address name for the landing beat. Resolution order: profile
  // display name → given name from auth attributes → NO name (dropping it is dignified). The Cognito
  // username / email fragment (the login handle) is NEVER shown — "leavearemnant, here's your story"
  // is a bug. Rejects any candidate equal to the handle or containing '@'.
  const beatName = (() => {
    const handle = String(user?.signInDetails?.loginId || user?.username || '').split('@')[0].trim().toLowerCase();
    for (const c of [accountName, user?.attributes?.name, user?.attributes?.given_name]) {
      const v = String(c || '').trim();
      if (v && !v.includes('@') && v.toLowerCase() !== handle) return v.split(/\s+/)[0];
    }
    return null;
  })();

  const [newRecipient, setNewRecipient] = React.useState({ name: '', email: '', relationship: '', accessLevel: 'full' });
  const [savingRecipient, setSavingRecipient] = React.useState(false);
  const [shareModalItem, setShareModalItem] = React.useState(null);
  const [showShareModal, setShowShareModal] = React.useState(false);

  // Confirmation modal state — replaces window.confirm() calls
  const [confirmRemoveRecipientId, setConfirmRemoveRecipientId] = React.useState(null);
  const [confirmDeleteStory, setConfirmDeleteStory] = React.useState(null); // { id, msg }
  const [confirmDeleteCapsuleId, setConfirmDeleteCapsuleId] = React.useState(null);

  // Multi-Book System — UI state (data synced via useUserData hook)
  const [showGiftCreate, setShowGiftCreate] = React.useState(false);
  const [hideGiftCta, setHideGiftCta] = React.useState(false);
  const [betaDismissed, setBetaDismissed] = React.useState(() => localStorage.getItem('talebound_beta_dismissed') === '1');
  // Gift book create wizard state
  const [cbCreateStep, setCbCreateStep] = React.useState(0);
  const [cbOccasion, setCbOccasion] = React.useState(null);
  const [cbRecipientName, setCbRecipientName] = React.useState('');
  const [cbDeliveryDate, setCbDeliveryDate] = React.useState('');
  const [cbCustomOccasion, setCbCustomOccasion] = React.useState('');
  const [cbAuthorRelationship, setCbAuthorRelationship] = React.useState('');
  // Invite contributor state (used in step 7 for gift books)
  const [expandedContributor, setExpandedContributor] = React.useState(null);
  const [editingBook, setEditingBook] = React.useState(false);
  const [bookReaderOpen, setBookReaderOpen] = React.useState(false);
  const [bookReaderChapter, setBookReaderChapter] = React.useState(0);
  const [bookReaderSource, setBookReaderSource] = React.useState(null);

  // Derived multi-book values
  const activeBook = books.find(b => b.id === activeBookId);
  // bookType:'GIFT' denotes the Celebration book (stories about a recipient contributed by others),
  // NOT the gift coupon. The gift coupon is a purchase/redemption mechanic; it has no interview wiring.
  const isGiftBook = activeBook?.bookType === 'GIFT';

  // ── Storyteller Mode: Recording ──
  const [stSelectedPrompt, setStSelectedPrompt] = React.useState(null);
  // Core-redesign: how the storyteller chose to tell their story from the Talk home
  // ('voice' | 'text' | null). The topic picker reads this to route straight into
  // the right interview after a topic is chosen.
  const [stStartMode, setStStartMode] = React.useState(null);
  // Completion loop: after a chapter saves, Pip celebrates and points to "tell another"
  // or "see my book". null = hidden; { title, chapterCount } = show.
  const [stStoryCelebration, setStStoryCelebration] = React.useState(null);
  const [stShowPromptPicker, setStShowPromptPicker] = React.useState(false);
  // Which prompt pack the storyteller is working from. Persisted so a returning
  // writer resumes their chosen vibe. Defaults to the spec's starter pack.
  const [activePackId, setActivePackId] = React.useState(() => {
    try { return localStorage.getItem('tb_activePack') || DEFAULT_PACK_ID; } catch (e) { return DEFAULT_PACK_ID; }
  });
  React.useEffect(() => {
    try { localStorage.setItem('tb_activePack', activePackId || ''); } catch (e) {}
  }, [activePackId]);
  // "Create your story" should always open the pack chooser first, even for returning
  // writers (who would otherwise skip straight into their last pack). StTalkHome sets
  // this true; StRecordPage's pick step consumes it to force the pack picker.
  const [forcePackPicker, setForcePackPicker] = React.useState(false);
  // F-80: the doorway's "Start talking" sets this so StRecordPage enters the SEEDLESS voice interview
  // (null prompt + easy-start + coldStart) instead of the pack picker. Consumed + cleared by an effect
  // in StRecordPage (mirrors the forcePackPicker handshake).
  const [seedlessStart, setSeedlessStart] = React.useState(false);
  // Path home → existing pack questions. StRecordPage consumes this once.
  const [pathOpenPackId, setPathOpenPackId] = React.useState(null);
  const [stPostRecordStep, setStPostRecordStep] = React.useState(null); // null | 'processing' | 'review'
  const [stAutoTranscribeTriggered, setStAutoTranscribeTriggered] = React.useState(false);
  const [stProcessed, setStProcessed] = React.useState({ title: '', content: '' });
  // TB-118: save-in-flight guard. Ref = synchronous re-entry block (state updates lag taps);
  // state = UI (disable button, "Saving..." label). saveIdRef makes rapid repeat saves target
  // the same entry id, so a race that slips past the ref cannot insert a duplicate row.
  const [stSaving, setStSaving] = React.useState(false);
  const stSavingRef = React.useRef(false);
  const stSaveIdRef = React.useRef(null);

  // SAVE-IDENTITY-1 (F-236): mint the identity of ONE generation's output. Every place that puts NEW
  // story content on stProcessed stamps a fresh id; a real save records the id it saved. reviewState
  // honours `savedStoryId` only when the two match, so a story that inherited a previous chapter's
  // savedStoryId through a stale merge can never read as saved. See docs/INVARIANTS.md SAVE-IDENTITY-1.
  const mintGenerationId = () => ((typeof crypto !== 'undefined' && crypto.randomUUID)
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`);
  const [stOriginalContent, setStOriginalContent] = React.useState(null);

  // ── Storyteller Mode: Book Builder ──
  // Lulu specs: 8.5"×11" hardcover casewrap, full-color, 80# coated white, matte finish
  // SKU: 0850X1100FCSTDCW080CW444MXX
  // Interior PDF: single-page layout, 8.75"×11.25" with 0.125" bleed
  // Cover PDF: one-piece spread (back + spine + front), spine from lookup table
  const [stBookStep, setStBookStep] = React.useState(0); // 0=overview, 1=edit-page, 2=details, 3=preview
  const [stBookPages, setStBookPages] = React.useState([]); // [{storyId, photoUrl?, photoPath?, caption?}]
  const removedPagesRef = React.useRef({}); // storyId → page data, so re-adding restores photos
  const [stBookTitle, setStBookTitle] = React.useState('');
  const [stBookDedication, setStBookDedication] = React.useState('');
  const [stBookSubtitle, setStBookSubtitle] = React.useState('');
  const [stBookAuthor, setStBookAuthor] = React.useState('');
  const [stBookCover, setStBookCover] = React.useState('classic');
  const [stBookCoverDesign, setStBookCoverDesign] = React.useState('mustardplum'); // Plum Blossom = default new-book cover (DECISION cover-default-plum-blossom-2026-07-10)
  const [stBookCoverPhotoPath, setStBookCoverPhotoPath] = React.useState('');
  const [stBookCoverPhotoUrl, setStBookCoverPhotoUrl] = React.useState('');
  const [stBookCoverPhotoBase64, setStBookCoverPhotoBase64] = React.useState('');
  const [stBookCoverPhotoY, setStBookCoverPhotoY] = React.useState(50); // 0-100, vertical position for cover photo crop
  const [stBookCoverPhotoOrientation, setStBookCoverPhotoOrientation] = React.useState('landscape'); // 'landscape' | 'portrait' — TB-083 dual-orientation
  const [stBookCoverPhotoTone, setStBookCoverPhotoTone] = React.useState('color'); // 'color' | 'sepia' | 'bw' — floral wrap photo mat treatment (default natural color)
  const [stBookEditIdx, setStBookEditIdx] = React.useState(null); // index of page being edited
  const [stBookPhotoUploading, setStBookPhotoUploading] = React.useState(false);
  const stBookPhotoRef = React.useRef(null);
  const [stBookPreviewIdx, setStBookPreviewIdx] = React.useState(0); // for flip-through preview
  const [stBookOpen, setStBookOpen] = React.useState(false); // closed cover vs open interior
  const [stChapterSearch, setStChapterSearch] = React.useState("");

  // ── Chapter Media Recording (QR code feature) ──
  const [chapterMediaModal, setChapterMediaModal] = React.useState(null); // { pageIdx, storyId }
  const [chapterMediaType, setChapterMediaType] = React.useState('audio'); // 'audio' | 'video'
  const [chapterRecording, setChapterRecording] = React.useState(false);
  const [chapterMediaBlob, setChapterMediaBlob] = React.useState(null);
  const [chapterMediaUrl, setChapterMediaUrl] = React.useState('');
  const [chapterMediaSaving, setChapterMediaSaving] = React.useState(false);
  const chapterRecorderRef = React.useRef(null);
  const chapterChunksRef = React.useRef([]);
  const chapterStreamRef = React.useRef(null);
  const chapterLiveVideoRef = React.useRef(null);
  const [chapterQrCodes, setChapterQrCodes] = React.useState({}); // { [storyId]: dataUrl }

  // TB-103: call the Lambda transcode_recording action after a WebM upload.
  // Returns the transcoded s3Key (MP4/M4A) on success, or the original key
  // on failure so the caller can fall back gracefully.
  const transcodeRecording = async (s3Key, mediaType) => {
    try {
      const result = await callAi({ action: 'transcode_recording', s3Key, mediaType });
      if (result && result.s3Key) return result.s3Key;
      return s3Key; // unexpected shape — fall back
    } catch (err) {
      console.warn('[TB-103] transcode_recording failed, falling back to WebM:', err.message);
      return s3Key; // non-fatal fallback
    }
  };

  // TB-103 (defensive): pick a Safari-compatible mimeType when available so
  // iOS Safari can record natively without transcoding. Falls back to the
  // WebM default on Chrome/Android where MP4 recording is not supported.
  // Guard the global: some browser contexts (older WebKit, embedded webviews)
  // don't expose MediaRecorder. Referencing it bare here would crash the whole
  // app on render, so fall back to WebM when it's absent. (Recording paths below
  // already guard with window.MediaRecorder.)
  const preferredAudioMime = (typeof window !== 'undefined' && window.MediaRecorder && MediaRecorder.isTypeSupported('audio/mp4'))
    ? 'audio/mp4'
    : 'audio/webm';
  const preferredVideoMime = (typeof window !== 'undefined' && window.MediaRecorder && MediaRecorder.isTypeSupported('video/mp4'))
    ? 'video/mp4'
    : 'video/webm;codecs=vp9,opus';
  const preferredAudioContentType = preferredAudioMime === 'audio/mp4' ? 'audio/mp4' : 'audio/webm';
  const preferredVideoContentType = preferredVideoMime === 'video/mp4' ? 'video/mp4' : 'video/webm';
  const preferredAudioExt = preferredAudioMime === 'audio/mp4' ? 'm4a' : 'webm';
  const preferredVideoExt = preferredVideoMime === 'video/mp4' ? 'mp4' : 'webm';

  const startChapterRecording = async (type) => {
    try {
      const constraints = type === 'video' ? { video: true, audio: true } : { audio: true };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      chapterStreamRef.current = stream;
      if (type === 'video' && chapterLiveVideoRef.current) {
        chapterLiveVideoRef.current.srcObject = stream;
        chapterLiveVideoRef.current.play();
      }
      chapterChunksRef.current = [];
      const mimeType = type === 'video' ? preferredVideoMime : preferredAudioMime;
      const recorder = new MediaRecorder(stream, { mimeType, audioBitsPerSecond: 48000 });
      recorder.ondataavailable = (e) => { if (e.data.size > 0) chapterChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const contentType = type === 'video' ? preferredVideoContentType : preferredAudioContentType;
        const blob = new Blob(chapterChunksRef.current, { type: contentType });
        setChapterMediaBlob(blob);
        setChapterMediaUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };
      chapterRecorderRef.current = recorder;
      recorder.start(100);
      setChapterRecording(true);
    } catch (err) {
      showToast(`Could not access ${type === 'video' ? 'camera' : 'microphone'}. Please allow access.`, 'error');
    }
  };

  const stopChapterRecording = () => {
    if (chapterRecorderRef.current && chapterRecording) {
      chapterRecorderRef.current.stop();
      setChapterRecording(false);
    }
  };

  const resetChapterMedia = () => {
    setChapterMediaBlob(null);
    setChapterMediaUrl('');
    setChapterRecording(false);
    if (chapterStreamRef.current) {
      chapterStreamRef.current.getTracks().forEach(t => t.stop());
      chapterStreamRef.current = null;
    }
  };

  const saveChapterMedia = async () => {
    if (!chapterMediaBlob || !chapterMediaModal) return;
    setChapterMediaSaving(true);
    const { pageIdx, storyId } = chapterMediaModal;
    const page = stBookPages[pageIdx];
    const story = stories.find(s => s.id === storyId);
    const title = story?.title || 'Chapter';
    const mediaType = chapterMediaType === 'video' ? 'VIDEO' : 'AUDIO';

    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const identityId = session.identityId;

      // Upload media to S3
      // TB-103: use the preferred ext/contentType (MP4/M4A on Safari, WebM on Chrome).
      const ext = chapterMediaType === 'video' ? preferredVideoExt : preferredAudioExt;
      const safeName = title.replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '_');
      const fileName = `chapter_${safeName}_${Date.now()}.${ext}`;
      const folder = chapterMediaType === 'video' ? 'videos' : 'audio';
      let s3Key = `private/${identityId}/${folder}/${fileName}`;

      await uploadData({
        path: s3Key,
        data: chapterMediaBlob,
        options: { contentType: chapterMediaBlob.type, metadata: { title } }
      });

      // TB-103: transcode WebM to MP4/M4A so QR playback works on iPhone.
      // Falls back to the original WebM key on transcode failure (non-fatal).
      if (chapterMediaBlob.type.includes('webm')) {
        s3Key = await transcodeRecording(s3Key, chapterMediaType.toUpperCase());
      }

      // Generate short shareId
      const shareId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 10);

      // Get photo S3 key if the chapter has a photo
      const photoS3Key = page?.photoPath || null;

      // Create ShareableMedia record
      await client.graphql({
        query: createShareableMedia,
        variables: {
          input: {
            shareId,
            storyId,
            userId,
            mediaType,
            s3Key,
            title,
            storytellerName: accountName || user?.attributes?.name || 'Someone who loves you',
            photoS3Key,
            sourceType: 'DEDICATED_MESSAGE',
            createdAt: new Date().toISOString(),
          }
        }
      });

      // Update the page with shareId and qrEnabled
      const updatedPages = [...stBookPages];
      updatedPages[pageIdx] = { ...updatedPages[pageIdx], shareId, qrEnabled: true, mediaType };
      setStBookPages(updatedPages);

      // Generate QR code
      const qrDataUrl = await QRCode.toDataURL(`https://taleboundbook.com/s/${shareId}`, {
        width: 200, margin: 1, color: { dark: '#0f172a', light: '#ffffff' }
      });
      setChapterQrCodes(prev => ({ ...prev, [storyId]: qrDataUrl }));

      resetChapterMedia();
      setChapterMediaModal(null);
      showToast('Recording saved! A QR code will appear in your printed book.', 'success');
    } catch (err) {
      console.error('Failed to save chapter media:', err);
      showToast('Something went wrong saving your recording. Please try again.', 'error');
    } finally {
      setChapterMediaSaving(false);
    }
  };

  // ── Demo helper: attach a real, scannable "blank video" QR to every chapter ──
  // Records a short branded placeholder clip once, uploads it, then creates a real
  // ShareableMedia record per chapter so each QR resolves to a real Talebound
  // playback page (chapter title + photo + the placeholder video) instead of a
  // "not found" error. Intended for building the marketing demo book. Triggered
  // from the console: window.taleboundSetupBlankQR()
  const blankQrBusyRef = React.useRef(false);
  const [blankQrBusy, setBlankQrBusy] = React.useState(false);
  const makeBlankVideoBlob = () => new Promise((resolve, reject) => {
    try {
      const canvas = document.createElement('canvas');
      canvas.width = 540; canvas.height = 960;
      const ctx = canvas.getContext('2d');
      const draw = () => {
        ctx.fillStyle = '#faf8f5';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.fillStyle = '#c8bda8';
        ctx.textAlign = 'center';
        ctx.font = '600 44px Georgia, serif';
        ctx.fillText('Talebound', canvas.width / 2, canvas.height / 2 - 10);
        ctx.fillStyle = 'rgba(60,70,83,0.55)';
        ctx.font = 'italic 22px Georgia, serif';
        ctx.fillText('A recorded message', canvas.width / 2, canvas.height / 2 + 34);
      };
      draw();
      const stream = canvas.captureStream(8);
      const types = ['video/webm;codecs=vp9', 'video/webm;codecs=vp8', 'video/webm'];
      const mimeType = (window.MediaRecorder && types.find(t => MediaRecorder.isTypeSupported(t))) || '';
      if (!window.MediaRecorder) { reject(new Error('MediaRecorder not supported')); return; }
      const mr = new MediaRecorder(stream, mimeType ? { mimeType } : undefined);
      const chunks = [];
      mr.ondataavailable = e => { if (e.data && e.data.size) chunks.push(e.data); };
      mr.onerror = reject;
      mr.onstop = () => { stream.getTracks().forEach(t => t.stop()); resolve(new Blob(chunks, { type: 'video/webm' })); };
      mr.start();
      let n = 0;
      const iv = setInterval(() => { draw(); if (++n >= 8) { clearInterval(iv); mr.stop(); } }, 200);
    } catch (e) { reject(e); }
  });
  const setupBlankQrForAllChapters = async () => {
    if (blankQrBusyRef.current) return;
    if (!stBookPages.length) { showToast('Open a book with chapters first.', 'error'); return; }

    blankQrBusyRef.current = true;
    setBlankQrBusy(true);
    showToast('Setting up QR codes... this takes a moment.', 'info');
    try {
      // Clear any old direct-URL demo setting — QRs now resolve to a Talebound
      // playback page (taleboundbook.com/s/...), never an external site.
      try { localStorage.removeItem('tb_demo_qr_url'); } catch (e) {}

      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const identityId = session.identityId;

      // Use the branded Talebound logo clip as the placeholder recording. Fall
      // back to a generated blank clip if the asset can't be fetched.
      let blob;
      try {
        const resp = await fetch('/talebound-logo.webm');
        if (!resp.ok) throw new Error(`logo video fetch ${resp.status}`);
        blob = await resp.blob();
      } catch (e) {
        console.warn('Logo video unavailable, using generated placeholder:', e.message);
        blob = await makeBlankVideoBlob();
      }
      const s3Key = `private/${identityId}/videos/demo_message_${Date.now()}.webm`;
      await uploadData({ path: s3Key, data: blob, options: { contentType: 'video/webm' } }).result;

      const updatedPages = [...stBookPages];
      const newQrs = {};
      let added = 0;
      // Reprocess EVERY chapter (no skip) so chapters that already had a QR from
      // an earlier run get re-pointed at the new video. Each gets a fresh shareId
      // / record; the old record is simply orphaned. (Demo tool.)
      for (let i = 0; i < updatedPages.length; i++) {
        const page = updatedPages[i];
        const story = stories.find(s => s.id === page.storyId);
        const title = story?.title || 'Chapter';
        const shareId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 10);
        await client.graphql({
          query: createShareableMedia,
          variables: { input: {
            shareId, storyId: page.storyId, userId, mediaType: 'VIDEO', s3Key, title,
            storytellerName: accountName || user?.attributes?.name || 'Talebound',
            photoS3Key: page.photoPath || null, sourceType: 'DEDICATED_MESSAGE',
            createdAt: new Date().toISOString(),
          } }
        });
        updatedPages[i] = { ...page, shareId, qrEnabled: true, mediaType: 'VIDEO' };
        // QR resolves to the Talebound playback page (branded, plays the video)
        newQrs[page.storyId] = await QRCode.toDataURL(`https://taleboundbook.com/s/${shareId}`, {
          width: 200, margin: 1, color: { dark: '#0f172a', light: '#ffffff' }
        });
        added++;
      }
      setStBookPages(updatedPages); // auto-save persists shareId/qrEnabled
      setChapterQrCodes(prev => ({ ...prev, ...newQrs }));
      showToast(added ? `QR codes set on ${added} chapter${added !== 1 ? 's' : ''} — they now play the logo video.` : 'No chapters to set.', 'success');
    } catch (err) {
      console.error('QR setup failed:', err);
      showToast('Could not set up QR codes. See console for details.', 'error');
    } finally {
      blankQrBusyRef.current = false;
      setBlankQrBusy(false);
    }
  };
  React.useEffect(() => { window.taleboundSetupBlankQR = setupBlankQrForAllChapters; });
  // Owner-only gate for the demo "add QR codes" button (keeps it out of the real product UI)
  const isBookToolsOwner = (user?.signInDetails?.loginId || user?.attributes?.email || '').toLowerCase() === 'alextruesdale@gmail.com';

  const linkInterviewRecording = async (pageIdx, storyId) => {
    const story = stories.find(s => s.id === storyId);
    if (!story) return;

    // Extract audio path from story content: [audio:private/xxx/audio/file.webm]
    const audioMatch = story.content?.match(/\[audio:([^\]]+)\]/);
    if (!audioMatch) { showToast('No interview recording found for this chapter.', 'error'); return; }

    const s3Key = audioMatch[1];
    const page = stBookPages[pageIdx];
    const title = story.title || 'Chapter';

    setChapterMediaSaving(true);
    try {
      // TB-103: transcode existing WebM interview recording to M4A before
      // linking so QR playback works on iPhone.
      const qrS3Key = s3Key.match(/\.webm$/i)
        ? await transcodeRecording(s3Key, 'AUDIO')
        : s3Key;

      const shareId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 10);
      const photoS3Key = page?.photoPath || null;

      await client.graphql({
        query: createShareableMedia,
        variables: {
          input: {
            shareId,
            storyId,
            userId,
            mediaType: 'AUDIO',
            s3Key: qrS3Key,
            title,
            storytellerName: accountName || user?.attributes?.name || 'Someone who loves you',
            photoS3Key,
            sourceType: 'INTERVIEW_RECORDING',
            createdAt: new Date().toISOString(),
          }
        }
      });

      const updatedPages = [...stBookPages];
      updatedPages[pageIdx] = { ...updatedPages[pageIdx], shareId, qrEnabled: true, mediaType: 'AUDIO' };
      setStBookPages(updatedPages);

      const qrDataUrl = await QRCode.toDataURL(`https://taleboundbook.com/s/${shareId}`, {
        width: 200, margin: 1, color: { dark: '#0f172a', light: '#ffffff' }
      });
      setChapterQrCodes(prev => ({ ...prev, [storyId]: qrDataUrl }));

      setChapterMediaModal(null);
      showToast('Interview recording linked! QR code added to chapter.', 'success');
    } catch (err) {
      console.error('Failed to link interview recording:', err);
      showToast('Something went wrong linking your recording. Please try again.', 'error');
    } finally {
      setChapterMediaSaving(false);
    }
  };

  const removeChapterMedia = async (pageIdx, storyId) => {
    const page = stBookPages[pageIdx];
    if (!page?.shareId) return;

    try {
      // Find and delete the ShareableMedia record
      const result = await client.graphql({
        query: shareableMediaByUserId,
        variables: { userId, limit: 100 }
      });
      const match = result.data?.shareableMediaByUserId?.items?.find(m => m.shareId === page.shareId);
      if (match) {
        await client.graphql({ query: deleteShareableMedia, variables: { input: { id: match.id } } });
      }
    } catch (err) { console.error('Failed to delete ShareableMedia:', err); }

    const updatedPages = [...stBookPages];
    updatedPages[pageIdx] = { ...updatedPages[pageIdx], shareId: undefined, qrEnabled: undefined, mediaType: undefined };
    setStBookPages(updatedPages);
    setChapterQrCodes(prev => { const next = { ...prev }; delete next[storyId]; return next; });
    showToast('Recording removed from chapter.', 'info');
  };

  // ── Post-save QR prompt state ──
  const [qrPrompt, setQrPrompt] = React.useState(null); // { storyId, s3Key, title }

  // Auto-link a recording (audio or video) to a book page as a QR code (called after save)
  const autoLinkRecordingToQr = async (storyId, s3Key, title, updatedPages, mediaType = 'AUDIO', explicitBookId = null) => {
    const pageIdx = updatedPages.findIndex(p => p.storyId === storyId);
    if (pageIdx < 0) return;
    try {
      // TB-103: transcode WebM to MP4/M4A before creating the ShareableMedia
      // record so the QR-linked playback URL serves an iPhone-playable file.
      // Falls back to the original key on failure — non-fatal, degrades to
      // the existing WebM (silent on iOS) rather than blocking the save.
      let qrS3Key = s3Key;
      if (s3Key && s3Key.match(/\.webm$/i)) {
        qrS3Key = await transcodeRecording(s3Key, mediaType);
      }

      const shareId = crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2, 10);
      const photoS3Key = updatedPages[pageIdx]?.photoPath || null;
      await client.graphql({
        query: createShareableMedia,
        variables: {
          input: {
            shareId, storyId, userId,
            mediaType, s3Key: qrS3Key, title,
            storytellerName: accountName || user?.attributes?.name || 'Someone who loves you',
            photoS3Key, sourceType: mediaType === 'VIDEO' ? 'VIDEO_RECORDING' : 'VOICE_RECORDING',
            createdAt: new Date().toISOString(),
          }
        }
      });
      const newPages = [...updatedPages];
      newPages[pageIdx] = { ...newPages[pageIdx], shareId, qrEnabled: true, mediaType };
      setStBookPages(newPages);
      const bookData = { title: stBookTitle, subtitle: stBookSubtitle, author: stBookAuthor, dedication: stBookDedication, coverStyle: stBookCover, coverDesign: stBookCoverDesign, coverPhotoPath: stBookCoverPhotoPath, coverPhotoY: stBookCoverPhotoY, coverPhotoOrientation: stBookCoverPhotoOrientation, coverPhotoTone: stBookCoverPhotoTone, pages: cleanPagesForSave(newPages) };
      await saveBookData(bookData, explicitBookId);
      const qrDataUrl = await QRCode.toDataURL(`https://taleboundbook.com/s/${shareId}`, {
        width: 200, margin: 1, color: { dark: '#0f172a', light: '#ffffff' }
      });
      setChapterQrCodes(prev => ({ ...prev, [storyId]: qrDataUrl }));
      showToast(`${mediaType === 'VIDEO' ? 'Video' : 'Voice'} recording linked! QR code added to your printed book.`, 'success');
    } catch (err) {
      console.error('Failed to auto-link recording to QR:', err);
    }
  };

  // Generate QR codes for chapters that already have shareIds (on load)
  React.useEffect(() => {
    const pagesWithShare = stBookPages.filter(p => p.shareId && !chapterQrCodes[p.storyId]);
    if (!pagesWithShare.length) return;
    (async () => {
      const newQrs = {};
      for (const page of pagesWithShare) {
        try {
          newQrs[page.storyId] = await QRCode.toDataURL(`https://taleboundbook.com/s/${page.shareId}`, {
            width: 200, margin: 1, color: { dark: '#0f172a', light: '#ffffff' }
          });
        } catch (e) { console.error('QR generation error:', e); }
      }
      if (Object.keys(newQrs).length) setChapterQrCodes(prev => ({ ...prev, ...newQrs }));
    })();
  }, [stBookPages.map(p => p.shareId).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Compressed preview photos — smaller base64 for PDF preview rendering (avoids memory issues on older devices)
  // Full-quality originals stay in stBookPages for the actual print order
  const [stPreviewPhotos, setStPreviewPhotos] = React.useState({}); // { [storyId]: compressedBase64 }
  React.useEffect(() => {
    let cancelled = false;
    const compressPhoto = (base64) => new Promise((resolve) => {
      if (!base64 || !base64.startsWith('data:')) return resolve(base64);
      const img = new window.Image();
      img.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const maxDim = 1200;
          let w = img.naturalWidth, h = img.naturalHeight;
          if (w <= maxDim && h <= maxDim) return resolve(base64); // already small enough
          const scale = maxDim / Math.max(w, h);
          w = Math.round(w * scale); h = Math.round(h * scale);
          canvas.width = w; canvas.height = h;
          canvas.getContext('2d').drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL('image/jpeg', 0.85));
        } catch (e) { resolve(base64); }
      };
      img.onerror = () => resolve(base64);
      img.src = base64;
    });
    (async () => {
      const result = {};
      for (const page of stBookPages) {
        if (page.photoBase64 && page.storyId) {
          result[page.storyId] = await compressPhoto(page.photoBase64);
        }
      }
      if (!cancelled) setStPreviewPhotos(result);
    })();
    return () => { cancelled = true; };
  }, [stBookPages.map(p => `${p.storyId}:${p.photoBase64 ? p.photoBase64.length : 0}`).join(',')]); // eslint-disable-line react-hooks/exhaustive-deps

  // Bonus page state
  const [stMemoryMode, setStMemoryMode] = React.useState(null); // null, 'prompt', 'photo'
  const [stMemoryTitle, setStMemoryTitle] = React.useState('');
  const [stMemoryContent, setStMemoryContent] = React.useState('');
  const [stMemoryPhotoPath, setStMemoryPhotoPath] = React.useState('');
  const [stMemoryPhotoPreview, setStMemoryPhotoPreview] = React.useState('');
  const [stMemoryPhotoBase64, setStMemoryPhotoBase64] = React.useState(null);

  // Order flow state
  const [stOrderStep, setStOrderStep] = React.useState(null); // null, 'address', 'upsell', 'address_checkout', 'processing', 'success', 'error'
  const [stOrderAddress, setStOrderAddress] = React.useState({ name: '', street1: '', street2: '', city: '', stateCode: '', zip: '', countryCode: 'US', phone: '' });
  const [stOrderId, setStOrderId] = React.useState(null);
  const [stOrderError, setStOrderError] = React.useState('');
  const [stOrderLoading, setStOrderLoading] = React.useState(false);
  // Guard: the checkout/pay screen hides its action button while "loading". A stale
  // loading/error flag carried in (e.g. via browser back/forward after a failed attempt)
  // could leave the screen with a spinner and no way to proceed. Clear both on ENTRY to
  // the pay step so the Pay button always shows. Keyed on stOrderStep only, so a real
  // Pay click (which sets loading true without changing the step) is not interrupted.
  React.useEffect(() => {
    if (stOrderStep === 'address_checkout') { setStOrderLoading(false); setStOrderError(''); }
  }, [stOrderStep]);

  // Extra copies upsell state (celebration books)
  const [cbExtraCopies, setCbExtraCopies] = React.useState(0);
  const [cbExtraAddresses, setCbExtraAddresses] = React.useState([]); // [{name, street1, street2, city, stateCode, zip, countryCode, phone}]
  const [cbUseSameAddress, setCbUseSameAddress] = React.useState(true);

  // Invite contributor state
  const [stInviteQuestion, setStInviteQuestion] = React.useState('');
  const [stInviteMessage, setStInviteMessage] = React.useState('');
  const [stInviteLink, setStInviteLink] = React.useState('');
  const [stInviteLoading, setStInviteLoading] = React.useState(false);
  const [stInviteCopied, setStInviteCopied] = React.useState(false);
  const [stInviteName, setStInviteName] = React.useState('');
  const [stInviteRelationship, setStInviteRelationship] = React.useState('');
  const [stInvites, setStInvites] = React.useState([]); // list of sent invites
  const [stOrderStatus, setStOrderStatus] = React.useState(null);
  const [stCheckingStatus, setStCheckingStatus] = React.useState(false);

  // ── Google Places Autocomplete ──
  const streetInputRef = React.useRef(null);
  const autocompleteRef = React.useRef(null);

  React.useEffect(() => {
    // Load Google Maps JS API once
    if (!window.google?.maps?.places && !document.getElementById('google-places-script')) {
      const script = document.createElement('script');
      script.id = 'google-places-script';
      script.src = 'https://maps.googleapis.com/maps/api/js?key=AIzaSyArcQTnTfpiv9BWcmiokZj7c0req4tNH24&libraries=places';
      script.async = true;
      document.head.appendChild(script);
    }
  }, []);

  // Attach autocomplete when the street input appears
  React.useEffect(() => {
    if (!streetInputRef.current || autocompleteRef.current) return;
    const tryAttach = () => {
      if (!window.google?.maps?.places) return;
      const ac = new window.google.maps.places.Autocomplete(streetInputRef.current, {
        componentRestrictions: { country: 'us' },
        types: ['address'],
        fields: ['address_components', 'formatted_address'],
      });
      ac.addListener('place_changed', () => {
        const place = ac.getPlace();
        if (!place.address_components) return;
        let street_number = '', route = '', city = '', state = '', zip = '', apt = '';
        for (const comp of place.address_components) {
          const t = comp.types[0];
          if (t === 'street_number') street_number = comp.long_name;
          if (t === 'route') route = comp.short_name;
          if (t === 'subpremise') apt = comp.long_name;
          if (t === 'locality' || t === 'sublocality_level_1') city = comp.long_name;
          if (t === 'administrative_area_level_1') state = comp.short_name;
          if (t === 'postal_code') zip = comp.long_name;
        }
        setStOrderAddress(p => ({
          ...p,
          street1: `${street_number} ${route}`.trim(),
          street2: apt || p.street2,
          city: city || p.city,
          stateCode: state || p.stateCode,
          zip: zip || p.zip,
        }));
      });
      autocompleteRef.current = ac;
    };
    // If Google is already loaded, attach immediately; otherwise poll briefly
    if (window.google?.maps?.places) tryAttach();
    else { const iv = setInterval(() => { if (window.google?.maps?.places) { tryAttach(); clearInterval(iv); } }, 200); setTimeout(() => clearInterval(iv), 10000); }
  });

  const stBookSaveTimer = React.useRef(null);
  const [newCollectionName, setNewCollectionName] = React.useState('');
  const [addToMenuStory, setAddToMenuStory] = React.useState(null);

  // persistUserData, saveCollections, persistBooks, saveBookData — moved to hooks/useUserData

  // Save current builder state back to the active book
  const saveActiveBook = () => {
    if (!activeBookId) return;
    // F-87 COVER-PHOTO-1 (belt): never auto-save a coverPhotoPath that belongs to a DIFFERENT book (a bleed
    // from the shared cover-photo state). Falls back to THIS book's own record path; a real upload's fresh
    // unique path passes through. Complements the per-book reset (applyCoverPhotoFromBook).
    const coverPhotoPath = resolveCoverPhotoToSave(activeBookId, stBookCoverPhotoPath, booksRef.current);
    const bookData = { title: stBookTitle, subtitle: stBookSubtitle, author: stBookAuthor, dedication: stBookDedication, coverStyle: stBookCover, coverDesign: stBookCoverDesign, coverPhotoPath, coverPhotoY: stBookCoverPhotoY, coverPhotoOrientation: stBookCoverPhotoOrientation, coverPhotoTone: stBookCoverPhotoTone, pages: cleanPagesForSave(stBookPages) };
    // F-252: the debounced auto-save is the PRIMARY persistence path for a page add, and it calls
    // persistBooks directly rather than going through saveBookData — so the tombstone clear has to
    // be applied here too, or a re-added story would be stripped straight back out by the merge.
    const updated = booksRef.current.map(b => b.id === activeBookId ? clearTombstones({ ...b, ...bookData }, bookData.pages) : b);
    persistBooks(updated);
  };

  // Flush pending book save when the user switches tabs or closes the page.
  // The 1500ms auto-save debounce can fire AFTER pagehide on mobile Safari, so
  // we flush synchronously here to guarantee the write is dispatched before the
  // browser suspends the page. Listening to both 'visibilitychange' and 'pagehide'
  // covers Chrome (fires visibilitychange) and Safari (fires pagehide first).
  React.useEffect(() => {
    const flush = () => {
      if (activeBookId && stBookPages.length > 0) {
        if (stBookSaveTimer.current) {
          clearTimeout(stBookSaveTimer.current);
          stBookSaveTimer.current = null;
        }
        saveActiveBook();
      }
    };
    const onVisibilityChange = () => { if (document.visibilityState === 'hidden') flush(); };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', flush);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', flush);
    };
  }, [activeBookId, stBookPages]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-save cover edits while customizing (stBookStep 2). setActiveTab only saves
  // when navigating INTO the book, so leaving the cover screen for Talk/Account would
  // otherwise silently drop title/photo/layout edits. Debounced to avoid thrashing;
  // the effect re-runs on every field change so it always persists the latest values.
  React.useEffect(() => {
    if (stBookStep !== 2 || !activeBookId) return;
    const t = setTimeout(() => { saveActiveBook(); }, 700);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stBookStep, activeBookId, stBookTitle, stBookSubtitle, stBookAuthor, stBookDedication, stBookCover, stBookCoverDesign, stBookCoverPhotoPath, stBookCoverPhotoY, stBookCoverPhotoOrientation, stBookCoverPhotoTone]);

  // Enter a book — load its data into builder state
  // F-87 COVER-PHOTO-1: reset ALL cover-photo builder state from a book UNCONDITIONALLY (incl. null). The
  // single reset used by every book-load path so one book's cover photo can never bleed into another's
  // render (the shared stBookCoverPhoto* state) or auto-save onto its record.
  const applyCoverPhotoFromBook = (book) => {
    const cp = coverPhotoStateFromBook(book);
    const pathChanged = cp.coverPhotoPath !== stBookCoverPhotoPath;
    setStBookCoverPhotoPath(cp.coverPhotoPath);
    if (pathChanged) { setStBookCoverPhotoUrl(''); setStBookCoverPhotoBase64(''); } // drop the cached image
    setStBookCoverPhotoY(cp.coverPhotoY);
    setStBookCoverPhotoOrientation(cp.coverPhotoOrientation);
    setStBookCoverPhotoTone(cp.coverPhotoTone);
  };

  const enterBook = (bookId) => {
    // Save current book first
    if (activeBookId) saveActiveBook();
    const book = (booksRef.current || books).find(b => b.id === bookId);
    if (!book) return;
    // Remember which book was last opened so "My Book" reopens it (not the oldest).
    try { localStorage.setItem('remnant_lastBookId', bookId); } catch (e) {}
    setActiveBookId(bookId);
    setStBookPages(book.pages || []);
    setStBookTitle(book.title || '');
    setStBookSubtitle(book.subtitle || '');
    setStBookAuthor(book.author || '');
    setStBookDedication(book.dedication || '');
    setStBookCover(book.coverStyle || 'classic');
    setStBookCoverDesign(book.coverDesign || 'charcoal');
    applyCoverPhotoFromBook(book);
    setStBookStep(0);
    setStBookOpen(false);
    window.scrollTo(0, 0);
  };

  // Exit to book list
  const exitToBookList = () => {
    if (activeBookId) saveActiveBook();
    setActiveBookId(null);
    setStBookStep(0);
    setStBookOpen(false);
    window.scrollTo(0, 0);
  };

  // "My Book" nav: open the storyteller's book directly instead of a books LIST.
  // The list is no longer a landing page — it only matters for switching books
  // (the header switcher) or when you have none yet. Mirrors the smart 'write' routing.
  const openMyBook = () => {
    setShowGiftCreate(false);
    setStBookStep(0);
    const list = booksRef.current || books || [];
    if (activeBookId) {
      setActiveTab('st-book');          // keep the current book open (detail view)
    } else if (list.length > 0) {
      // Reopen the book you last worked on; fall back to the first if unknown.
      let lastId = null;
      try { lastId = localStorage.getItem('remnant_lastBookId'); } catch (e) {}
      const target = (lastId && list.find(b => b.id === lastId)) ? lastId : list[0].id;
      enterBook(target);
      setActiveTab('st-book');
    } else {
      setActiveTab('book');             // no books yet — show the list (create options)
    }
  };

  // Delete a book
  const deleteBook = (bookId) => {
    const updated = booksRef.current.filter(b => b.id !== bookId);
    persistBooks(updated);
    if (activeBookId === bookId) {
      setActiveBookId(null);
      setStBookPages([]);
      setStBookTitle(''); setStBookSubtitle(''); setStBookAuthor(''); setStBookDedication('');
      setStBookCover('classic'); setStBookCoverDesign('mustardplum'); setStBookCoverPhotoPath(null);
      setStBookCoverPhotoUrl(''); setStBookCoverPhotoBase64(''); setStBookCoverPhotoY(50); setStBookCoverPhotoTone('color');
      setStBookStep(0); setStBookOpen(false);
    }
    showToast('Book removed. Your stories are still safe and can be added to a new book anytime.', 'info');
  };

  // Create a new personal book
  const createPersonalBook = () => {
    const newBook = {
      id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Date.now().toString(36),
      bookType: 'PERSONAL',
      title: 'My Stories',
      subtitle: '',
      author: '',
      dedication: '',
      coverStyle: 'framed', // P3-TIDY (F-87): mustardplum is a full-wrap design → born framed, never the
      coverDesign: 'mustardplum', // classic-wrap mismatch the editor has to heal (F-76). Stops minting broken rows.
      coverPhotoPath: null,
      pages: [],
      createdAt: new Date().toISOString(),
    };
    const updated = [...booksRef.current, newBook];
    persistBooks(updated);
    enterBook(newBook.id);
    showToast("Your book is ready. Let's tell your first story!", 'success');
  };

  // Ensure at least one personal book exists before saving a story — returns the activeBookId (creating one if needed)
  const ensureBookExists = () => {
    if (activeBookId) return activeBookId;
    const newBook = {
      id: crypto.randomUUID ? crypto.randomUUID() : Math.random().toString(36).substring(2) + Date.now().toString(36),
      bookType: 'PERSONAL',
      title: 'My Stories',
      subtitle: '',
      author: '',
      dedication: '',
      coverStyle: 'framed', // P3-TIDY (F-87): mustardplum is a full-wrap design → born framed, never the
      coverDesign: 'mustardplum', // classic-wrap mismatch the editor has to heal (F-76). Stops minting broken rows.
      coverPhotoPath: null,
      pages: [],
      createdAt: new Date().toISOString(),
    };
    const updated = [...booksRef.current, newBook];
    persistBooks(updated);
    enterBook(newBook.id);
    return newBook.id;
  };

  // Sync contributed stories into gift book chapters
  const syncGiftBookChapters = (currentStories, currentBooks) => {
    if (!currentBooks || !currentStories || currentStories.length === 0) return;
    const giftBooks = currentBooks.filter(b => b.bookType === 'GIFT');
    if (giftBooks.length === 0) return;
    let changed = false;
    const updated = currentBooks.map(b => {
      if (b.bookType !== 'GIFT') return b;
      const existingStoryIds = new Set((b.pages || []).map(p => p.storyId));
      const newPages = [...(b.pages || [])];
      const updatedContributors = [...(b.contributors || [])];
      currentStories.forEach(story => {
        if (existingStoryIds.has(story.id)) return;
        if (story.type !== 'celebration') return;
        const cbMatch = story.content?.match(/CelebrationBook:\s*([^\n]+)/);
        if (!cbMatch || cbMatch[1].trim() !== b.id) return;
        const nameMatch = story.content.match(/\[Contributed by ([^\]]+)\]/);
        const contributorName = nameMatch ? nameMatch[1].trim() : 'Anonymous';
        // Carry photoPath from the story record so the page can be re-resolved
        // on load (cleanPagesForSave strips photoUrl/photoBase64 but keeps photoPath).
        // Without this, celebration-book photos vanish after the first save/reload.
        const photoPath = story.photoPath || null;
        const layout = photoPath ? 'photo-spread' : 'classic';
        newPages.push({ storyId: story.id, contributorName, layout, photoPath, addedAt: new Date().toISOString() });
        const contribIdx = updatedContributors.findIndex(c => c.name && contributorName.toLowerCase().includes(c.name.toLowerCase()));
        if (contribIdx >= 0 && updatedContributors[contribIdx].status !== 'CONTRIBUTED') {
          updatedContributors[contribIdx] = { ...updatedContributors[contribIdx], status: 'CONTRIBUTED' };
        }
        changed = true;
      });
      if (newPages.length !== (b.pages || []).length) return { ...b, pages: newPages, contributors: updatedContributors };
      return b;
    });
    if (changed) persistBooks(updated);
  };

  // Auto-save book whenever pages, title, dedication, or cover change
  React.useEffect(() => {
    if (!activeBookId) return;
    if (!stBookPages.length && !stBookTitle && !stBookDedication) return;
    if (stBookSaveTimer.current) clearTimeout(stBookSaveTimer.current);
    setStBookSaveStatus('');
    stBookSaveTimer.current = setTimeout(async () => {
      setStBookSaveStatus('saving');
      try {
        saveActiveBook();
        setStBookSaveStatus('saved');
        setTimeout(() => setStBookSaveStatus(''), 2000);
      } catch (e) { console.error('Auto-save error:', e); setStBookSaveStatus(''); }
    }, 1500);
    return () => { if (stBookSaveTimer.current) clearTimeout(stBookSaveTimer.current); };
  }, [stBookPages, stBookTitle, stBookSubtitle, stBookAuthor, stBookDedication, stBookCover, stBookCoverDesign, stBookCoverPhotoPath, stBookCoverPhotoY, stBookCoverPhotoOrientation, stBookCoverPhotoTone]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve S3 photo URLs on load — blob/expired URLs die on page reload
  // ALWAYS re-resolve from photoPath since signed URLs expire
  const photoResolveRef = React.useRef(false);
  const [photosResolving, setPhotosResolving] = React.useState(false);
  // Track which pages need resolving so the effect re-runs when new photos are added
  const photosNeedingResolve = stBookPages.filter(p => p.photoPath && (!p.photoUrl || !p.photoBase64)).map(p => p.photoPath).join(',');
  React.useEffect(() => {
    if (!photosNeedingResolve) return;
    // Skip if we just resolved (prevent auto-save → re-resolve loop)
    if (photoResolveRef.current) return;
    setPhotosResolving(true);
    let cancelled = false;
    const resolve = async () => {
      // Photo resolve
      let needsUpdate = false;
      const updated = await Promise.all(stBookPages.map(async (page) => {
        if (!page.photoPath || (page.photoUrl && page.photoBase64)) return page;
        try {
          // If we already have a photoUrl but just need base64, skip the URL fetch
          let newUrl = page.photoUrl;
          let correctedPath = page.photoPath;
          if (!newUrl) {
            // TB-075 QW-1: try the in-memory signed URL cache first so repeat
            // book loads don't generate a fresh signed URL per photo on every
            // mount. Cache key is filename (stable across path-format variants).
            const cached = getCachedSignedUrl(page.photoPath);
            if (cached) {
              newUrl = cached;
            } else {
              // Extract just the filename from whatever path format was stored
              const fileName = page.photoPath.split('/').pop();
              // Use path function so Amplify injects the correct identityId prefix
              const pathFn = ({ identityId }) => `private/${identityId}/photos/${fileName}`;
              const result = await getUrl({ path: pathFn, options: { expiresIn: SIGNED_URL_TTL_S } });
              if (!result?.url) return page;
              newUrl = result.url.toString();
              // Extract the correct full path from the URL for future direct use
              try {
                const urlObj = new URL(newUrl);
                const pathMatch = urlObj.pathname.match(/(private\/[^?]+)/);
                if (pathMatch) correctedPath = decodeURIComponent(pathMatch[1]);
              } catch (_) {}
              // Cache by the corrected canonical path so future lookups hit
              setCachedSignedUrl(correctedPath, newUrl);
            }
          }
          // Convert to base64 data URI for PDF renderer (avoids CORS)
          needsUpdate = true;
          let photoBase64 = null;
          try {
            const imgResp = await fetch(newUrl);
            const blob = await imgResp.blob();
            photoBase64 = await new Promise((res, rej) => {
              const reader = new FileReader();
              reader.onload = () => res(reader.result);
              reader.onerror = rej;
              reader.readAsDataURL(blob);
            });
            // base64 cached
          } catch (b64Err) {
            console.warn('Base64 failed for:', page.photoPath);
          }
          return { ...page, photoUrl: newUrl, photoPath: correctedPath, photoBase64 };
        } catch (err) {
          console.warn('Photo resolve failed:', page.photoPath);
        }
        return page;
      }));
      if (!cancelled && needsUpdate) {
        photoResolveRef.current = true;
        // Use functional update to merge resolved URLs with current state
        // (avoids overwriting flags like photoEnhanced set by other effects)
        setStBookPages(current => current.map((p, i) => {
          const resolved = updated[i];
          if (!resolved || resolved === stBookPages[i]) return p; // no change from resolver
          // Merge resolved fields (photoUrl, photoPath, photoBase64) onto current page
          return { ...p, photoUrl: resolved.photoUrl, photoPath: resolved.photoPath, photoBase64: resolved.photoBase64 };
        }));
        setPhotosResolving(false);
        // Reset flag after auto-save cycle completes
        setTimeout(() => { photoResolveRef.current = false; }, 3000);
      } else if (!cancelled) {
        setPhotosResolving(false);
      }
    };
    resolve();
    return () => { cancelled = true; };
  }, [photosNeedingResolve]); // eslint-disable-line react-hooks/exhaustive-deps

  // Resolve cover photo — download via Amplify SDK (bypasses CORS), convert to base64 once
  React.useEffect(() => {
    if (!stBookCoverPhotoPath) return;
    if (stBookCoverPhotoBase64) return; // already have it
    let cancelled = false;
    const resolve = async () => {
      try {
        const fileName = stBookCoverPhotoPath.split('/').pop();
        const pathFn = ({ identityId }) => `private/${identityId}/photos/${fileName}`;
        const result = await downloadData({ path: pathFn }).result;
        const blob = await result.body.blob();
        const b64 = await new Promise((res, rej) => {
          const reader = new FileReader();
          reader.onload = () => res(reader.result);
          reader.onerror = rej;
          reader.readAsDataURL(blob);
        });
        if (!cancelled) {
          setStBookCoverPhotoBase64(b64);
          setStBookCoverPhotoUrl(b64);
        }
      } catch (err) {
        console.warn('[Cover Photo] Download failed:', err);
      }
    };
    resolve();
    return () => { cancelled = true; };
  }, [stBookCoverPhotoPath]); // eslint-disable-line react-hooks/exhaustive-deps

  // For PDF generation — returns cached base64, or downloads fresh if needed
  const resolveCoverPhotoBase64 = React.useCallback(async () => {
    if (stBookCoverPhotoBase64) return stBookCoverPhotoBase64;
    if (!stBookCoverPhotoPath) return null;
    try {
      const fileName = stBookCoverPhotoPath.split('/').pop();
      const pathFn = ({ identityId }) => `private/${identityId}/photos/${fileName}`;
      const result = await downloadData({ path: pathFn }).result;
      const blob = await result.body.blob();
      const b64 = await new Promise((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result);
        reader.onerror = rej;
        reader.readAsDataURL(blob);
      });
      setStBookCoverPhotoBase64(b64);
      setStBookCoverPhotoUrl(b64);
      return b64;
    } catch (e) {
      console.warn('[Cover Photo] Base64 failed:', e);
      return null;
    }
  }, [stBookCoverPhotoPath, stBookCoverPhotoBase64]);

  // Honor a pending "start a celebration book" intent set by the public
  // /celebration CTA (/?intent=celebration, captured at the auth gate below).
  // Gated entirely on tb_intent, so only users who arrived from that CTA are
  // affected. The short delay lets the activeBook-restore mount effect settle
  // before the celebration/gift wizard opens.
  React.useEffect(() => {
    let pending = null;
    try { pending = localStorage.getItem('tb_intent'); } catch (e) { /* no storage */ }
    // Only the explicit public /celebration entry skips the chooser (a hard
    // declaration). Any other intent flag is cleared and ignored — everyone else
    // picks their path on the warm chooser.
    if (pending !== 'celebration') {
      if (pending) { try { localStorage.removeItem('tb_intent'); } catch (e) {} }
      return;
    }
    try { localStorage.removeItem('tb_intent'); } catch (e) {}
    const t = setTimeout(() => {
      setActiveTabRaw('book');
      setShowGiftCreate(true);
    }, 450);
    return () => clearTimeout(t);
  }, []);

  // Handle return from Stripe Checkout
  const orderSuccessRef = React.useRef(null);
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const orderResult = params.get('order');
    const orderId = params.get('orderId');
    if (orderResult === 'success' && orderId) {
      orderSuccessRef.current = orderId;
      // Clean URL immediately
      window.history.replaceState({}, '', window.location.pathname);
      // Apply after a brief delay so other mount effects don't override
      setTimeout(async () => {
        setStOrderId(orderSuccessRef.current);
        setStOrderStep('success');
        setStBookStep(6);
        setActiveTabRaw('book');
        // Fetch order details (address is lost on page reload from Stripe redirect)
        try {
          let orderStatusToken = null;
          try {
            const { fetchAuthSession } = await import('aws-amplify/auth');
            const session = await fetchAuthSession();
            orderStatusToken = session.tokens?.idToken?.toString() || null;
          } catch (sessionErr) {
            console.warn('[Auth] Could not retrieve session for order status:', sessionErr);
          }
          const resp = await fetch(`${ORDER_API_BASE}/order/status/${orderSuccessRef.current}`, {
            headers: orderStatusToken ? { 'Authorization': `Bearer ${orderStatusToken}` } : {},
          });
          const data = await resp.json();
          if (data.shippingAddress) {
            setStOrderAddress({
              name: data.shippingAddress.name || '',
              street1: data.shippingAddress.street1 || '',
              street2: data.shippingAddress.street2 || '',
              city: data.shippingAddress.city || '',
              stateCode: data.shippingAddress.stateCode || data.shippingAddress.state_code || '',
              zip: data.shippingAddress.zip || data.shippingAddress.postcode || '',
              countryCode: data.shippingAddress.countryCode || 'US',
              phone: data.shippingAddress.phone || data.shippingAddress.phone_number || '',
            });
          }
          if (data.bookTitle) setStBookTitle(data.bookTitle);
          setStOrderStatus(data);
          // GA4 book purchase (client-side). Dedupe by orderId against refresh.
          const purchaseKey = `book_purchase_tracked_${orderSuccessRef.current}`;
          let alreadySent = false;
          try { alreadySent = !!sessionStorage.getItem(purchaseKey); } catch (e) { /* private mode */ }
          if (!alreadySent) {
            const value = typeof data.amount === 'number' ? data.amount / 100 : 89.99;
            trackEvent('purchase', {
              transaction_id: orderSuccessRef.current,
              currency: 'USD',
              value,
              items: [{ item_id: 'talebound_book', item_name: data.bookTitle || 'Talebound Keepsake Book', item_category: 'book', price: value, quantity: 1 }],
            });
            try { sessionStorage.setItem(purchaseKey, '1'); } catch (e) { /* private mode */ }
          }
        } catch (err) { console.error('Failed to fetch order details:', err); }
      }, 500);
    } else if (orderResult === 'cancelled') {
      showToast('Order cancelled. No charge was made.');
      window.history.replaceState({}, '', window.location.pathname);
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const addStoryToBook = (storyId) => {
    const book = activeBook || {};
    const ids = book.storyIds || [];
    if (ids.includes(storyId)) return;
    saveBookData({ storyIds: [...ids, storyId] });
  };
  const removeStoryFromBook = (storyId) => {
    const book = activeBook || {};
    const ids = book.storyIds || [];
    saveBookData({ storyIds: ids.filter(id => id !== storyId) });
  };
  const moveStoryInBook = (storyId, direction) => {
    const book = activeBook || {};
    const ids = [...(book.storyIds || [])];
    const idx = ids.indexOf(storyId);
    if (idx < 0) return;
    const newIdx = direction === 'up' ? idx - 1 : idx + 1;
    if (newIdx < 0 || newIdx >= ids.length) return;
    [ids[idx], ids[newIdx]] = [ids[newIdx], ids[idx]];
    saveBookData({ storyIds: ids });
  };
  const addStoryToCollection = (storyId, collectionId) => {
    const updated = collections.map(c => {
      if (c.id === collectionId && !c.storyIds.includes(storyId)) {
        return { ...c, storyIds: [...c.storyIds, storyId] };
      }
      return c;
    });
    saveCollections(updated);
  };
  const removeStoryFromCollection = (storyId, collectionId) => {
    const updated = collections.map(c => {
      if (c.id === collectionId) return { ...c, storyIds: c.storyIds.filter(id => id !== storyId) };
      return c;
    });
    saveCollections(updated);
  };
  const createCollection = (name) => {
    if (!name.trim()) return;
    const id = 'col_' + Date.now();
    saveCollections([...collections, { id, name: name.trim(), storyIds: [] }]);
    setNewCollectionName('');
  };
  const deleteCollection = (id) => {
    saveCollections(collections.filter(c => c.id !== id));
  };

  const getBookStories = () => {
    const book = activeBook || {};
    if (book.pages && Array.isArray(book.pages)) {
      return book.pages.map(p => stories.find(s => s.id === p.storyId)).filter(Boolean);
    }
    return (book.storyIds || []).map(id => stories.find(s => s.id === id)).filter(Boolean);
  };
  const getCollectionStories = (collectionId) => {
    const col = collections.find(c => c.id === collectionId);
    if (!col) return [];
    return col.storyIds.map(id => stories.find(s => s.id === id)).filter(Boolean);
  };

  // saveRecipients, saveShares — moved to hooks/useUserData

  const handleAddRecipient = () => {
    if (!newRecipient.name.trim() || !newRecipient.email.trim()) { showToast('Please enter a name and email.', 'error'); return; }
    if (recipients.length >= 3) { showToast('You can add up to 3 recipients.', 'error'); return; }
    const emailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(newRecipient.email);
    if (!emailValid) { showToast('Please enter a valid email address.', 'error'); return; }
    setSavingRecipient(true);
    const updated = [...recipients, { ...newRecipient, id: Date.now().toString(), addedAt: new Date().toISOString() }];
    saveRecipients(updated);
    setNewRecipient({ name: '', email: '', relationship: '', accessLevel: 'full' });
    setSavingRecipient(false);
  };

  const handleRemoveRecipient = (id) => {
    setConfirmRemoveRecipientId(id);
  };

  const handleSetAccessLevel = (id, level) => {
    saveRecipients(recipients.map(r => r.id === id ? { ...r, accessLevel: level } : r));
  };

  const openShareModal = (item) => {
    setShareModalItem(item);
    setShowShareModal(true);
  };

  const getItemShares = (itemId) => shares[itemId] || {};

  const toggleShare = (itemId, recipientId, timing) => {
    const current = shares[itemId] || {};
    const key = `${recipientId}`;
    const existing = current[key];
    const updated = { ...shares };
    if (!updated[itemId]) updated[itemId] = {};
    if (existing && existing.timing === timing) {
      // toggle off
      delete updated[itemId][key];
    } else {
      updated[itemId][key] = { timing, sharedAt: new Date().toISOString() };
    }
    saveShares(updated);
  };

  const getSharedCount = (itemId) => Object.keys(shares[itemId] || {}).length;

  React.useEffect(() => {
    refreshFileList();
    refreshStories();
    refreshRecordings();
    loadUserData((firstBook) => {
      // Set builder UI state from the first loaded book
      if (firstBook.pages && Array.isArray(firstBook.pages)) {
        setStBookPages(firstBook.pages.map(p => p.photoPath ? { ...p, photoUrl: null } : p));
      }
      if (firstBook.title) setStBookTitle(firstBook.title);
      if (firstBook.dedication) setStBookDedication(firstBook.dedication);
      if (firstBook.coverStyle) setStBookCover(firstBook.coverStyle);
      if (firstBook.coverDesign) setStBookCoverDesign(firstBook.coverDesign);
      if (firstBook.subtitle) setStBookSubtitle(firstBook.subtitle);
      if (firstBook.author) setStBookAuthor(firstBook.author);
      // F-87: reset the cover-photo state UNCONDITIONALLY from the restored book (incl. null). The old
      // guarded sets (`if (firstBook.coverPhotoPath) ...`) left a previously-loaded book's photo in the
      // shared state, so a photo-less book both rendered and auto-saved a sibling's photo (the bleed).
      applyCoverPhotoFromBook(firstBook);
    }).catch(err => {
      // TB-096: a dead session surfaces here as a load failure — route to re-auth instead
      // of rendering an empty (looks-like-data-loss) account.
      if (isAuthExpiry(err)) signOut('auth_expiry');
    });
  }, []);

  // Persist the last-open book id so a page refresh returns to the same book
  // (not the My Books list). '' = user is on the list. Declared after the mount
  // effect above so loadUserData reads the prior value before this overwrites it.
  React.useEffect(() => {
    try { localStorage.setItem('tb_activeBookId', activeBookId || ''); } catch (e) {}
  }, [activeBookId]);

  // Sync contributed stories into gift books when both are loaded
  const giftSyncedRef = React.useRef(false);
  React.useEffect(() => {
    if (stories.length > 0 && books.some(b => b.bookType === 'GIFT') && !giftSyncedRef.current) {
      giftSyncedRef.current = true;
      syncGiftBookChapters(stories, books);
    }
  }, [stories, books]); // eslint-disable-line react-hooks/exhaustive-deps

  // Backfill photoPath onto celebration/gift book pages that were attached before
  // the Lambda fix that persists photoPath. These pages exist in UserData already
  // (via attachContributionToGiftBook) but have no photoPath set. The story record
  // now carries photoPath (written by the Lambda updateLegacyEntry call), so read
  // it from the stories array and patch the book page in-place.
  // Runs once per session after both stories and books are fully loaded.
  const giftPhotoBackfillRef = React.useRef(false);
  React.useEffect(() => {
    if (giftPhotoBackfillRef.current) return;
    if (!stories.length || !books.some(b => b.bookType === 'GIFT')) return;
    // Mark done immediately so concurrent re-renders don't double-apply
    giftPhotoBackfillRef.current = true;
    let changed = false;
    const updated = books.map(b => {
      if (b.bookType !== 'GIFT' || !b.pages?.length) return b;
      const patchedPages = b.pages.map(p => {
        if (p.photoPath) return p; // already has it
        const story = stories.find(s => s.id === p.storyId);
        if (!story?.photoPath) return p;
        changed = true;
        return { ...p, photoPath: story.photoPath, layout: p.layout === 'classic' ? 'story-page' : p.layout };
      });
      return changed ? { ...b, pages: patchedPages } : b;
    });
    if (changed) persistBooks(updated);
  }, [stories, books]); // eslint-disable-line react-hooks/exhaustive-deps

  // REVEAL-002 R1 — resume-on-entry (note 4). On app open, if the user has an active server-side draft
  // (a finished-but-unsaved story that survived a crash / device switch) and is NOT already mid-session,
  // land them back on the amber State 0 to finish the decision. Runs BEFORE orphan detection is allowed
  // to surface (draftResumeChecked gates it), and a resumed draft SUPPRESSES the orphan modal for this
  // app-open only (orphanSuppressedRef) — the two never fight. Discard clears the suppression (below).
  const orphanSuppressedRef = React.useRef(false);
  const draftResumeRef = React.useRef(false);
  const [draftResumeChecked, setDraftResumeChecked] = React.useState(false);
  React.useEffect(() => {
    if (draftResumeRef.current) return;
    if (!userDataLoaded || !user) return;
    draftResumeRef.current = true;
    (async () => {
      try {
        const alreadyEngaged = stInterviewMode || !!(stProcessed && stProcessed.content);
        if (!alreadyEngaged) {
          const draft = await fetchActiveDraft(client);
          const target = resumeTargetFor(draft);
          if (draft && target) {
            const resumedProcessed = draftToProcessed(draft);
            // RESUME-LANDS-ON-A-RENDERED-SURFACE-1 — the navigation happens ONCE, for BOTH branches,
            // before either sets its own state. It used to live inside the words-only branch only, so
            // a finished story resumed into review state that nothing rendered (BACKLOG
            // RESUME-INVISIBLE-WHEN-TAB-NOT-RECORD, filed F-402 S6, measured on 7 real customers
            // 2026-08-12). The tab now comes from `resumeTargetFor`, which is unit-tested for both
            // cases — App.js does not load in Jest, and that is how the asymmetry survived.
            setActiveTab(target.tab);
            // F-383 — a WORDS-ONLY draft means generation never completed. Resume the INTERVIEW at the
            // point where they can finish and retry, NEVER the review screen: that draft has no story,
            // so 'review' would render blank and the teller would experience exactly the loss this
            // feature exists to prevent. A draft WITH story content keeps today's behaviour unchanged.
            if (resumedProcessed?.wordsOnly) {
              // F-402 — TWO states, and the split is the whole point.
              //   DISPLAY (`stInterviewMessages`) gets the restored turns PLUS Pip's founder-gated
              //   resume notice as the final turn (§10, verbatim, `RESUME_NOTICE_LINE`).
              //   ENGINE (`interviewConversation`) gets the restored turns and NOTHING ELSE.
              // Same split the F-7 chapter-update path uses in the opposite direction (:4765).
              //
              // ⚠️ THE ENGINE RESTORE IS WHAT MAKES THE STRING TRUE. Before F-402 this branch set the
              // display thread only, leaving `interviewConversation` at []. A teller who resumed and
              // answered one more question would have had their story generated from that ONE answer:
              // `sendStInterviewMsg` reads `interviewConversation` (:4081) and the pre-generation
              // draft write serializes `effectiveConversation` (:5104), so every pre-resume turn was
              // dropped from BOTH. Shipping "I still have everything you told me" on top of that would
              // have been the F-401 inversion exactly — reassuring the teller at the moment it was false.
              setStInterviewMessages(buildResumeThread(resumedProcessed.transcript));
              setInterviewConversation(resumedProcessed.transcript);
              setStInterviewMode(true);
              // F-402, ALSO FOUND BY THE WALK: the step is `'conversation'`, not `'interview'`.
              // `'interview'` was never a step this app has — as shipped at F-383 that string appeared
              // EXACTLY ONCE in the entire codebase, on this line, and `StRecordPage` has no branch for
              // it, so the resume fell through to the default COMPOSE screen and the restored thread was
              // never drawn. `'conversation'` is the unified turn-loop surface (`StRecordPage.js:2138`)
              // that all eight other call sites use. No unit test could have caught this: App.js does not
              // load in Jest, and a string that matches nothing fails silently by rendering something else.
              setStInterviewStep(target.step);
              // F-402, FOUND BY THE WALK, NOT BY READING THE CODE: `StRecordPage` renders only when
              // `activeTab === 'st-record'`, and `activeTab` rehydrates from localStorage (:298).
              // Without that navigation the resume set every piece of interview state correctly and
              // the teller still landed on their book home, seeing NOTHING — the restored thread and
              // Pip's notice existed only in state. Worst exactly where the feature is aimed: a DEVICE
              // SWITCH (named in the resume comment above) carries no localStorage, so it defaults to
              // 'talk' and the resume is invisible by construction. Same destination the REVEAL dev
              // fixture uses; the three nav guards in `setActiveTab` all require `tab !== 'st-record'`,
              // so none can fire on the way IN.
              // THE CALL ITSELF NOW LIVES ABOVE, ahead of the branch, because the STORY-BEARING branch
              // needed it just as much and did not have it — that is the whole of this fix.
              setStProcessed({ ...resumedProcessed, resumed: true });
              orphanSuppressedRef.current = true;
              try { trackEvent('interview_resumed_from_draft', { turns: resumedProcessed.transcript.length }); } catch (e) { /* telemetry */ }
              return;   // the `finally` below still runs and sets draftResumeChecked
            }
            setStProcessed({ ...resumedProcessed, resumed: true }); // resumed → ribbon "still here"
            setStInterviewMode(true);
            setStInterviewStep(target.step);
            orphanSuppressedRef.current = true; // note 4: draft resume wins this app-open (no modal fight)
            try { trackEvent('story_ready_resumed', {}); } catch (e) { /* telemetry */ }
          }
        }
      } catch (e) { console.warn('[REVEAL-002] resume check failed:', e?.message); }
      finally { setDraftResumeChecked(true); }
    })();
  }, [userDataLoaded, user]); // eslint-disable-line react-hooks/exhaustive-deps

  // Orphan-story detection — runs once per session after both UserData and stories are loaded.
  // Finds LegacyEntry records of type 'story' or 'memory_interview' whose id does not appear
  // in any book's pages[].storyId. These are candidates for recovery after a multi-device clobber.
  // Does NOT auto-repair (which book to add them to is a UI decision); surfaces count + ids only.
  const orphanDetectRef = React.useRef(false);
  React.useEffect(() => {
    if (orphanDetectRef.current) return;
    // REVEAL-002 note 4: wait for the draft-resume decision, and stand down for this app-open if a draft
    // was resumed (its State-0 owns the screen). If the user discards the draft, suppression lapses and
    // the modal is free to do its job on the NEXT open.
    if (!draftResumeChecked || orphanSuppressedRef.current) return;
    if (!userDataLoaded || stories.length === 0) return;
    orphanDetectRef.current = true;

    const linkedStoryIds = new Set(
      books.flatMap(b => (b.pages || []).map(p => p.storyId).filter(Boolean))
    );
    const orphans = stories
      .filter(s => (s.type === 'story' || s.type === 'memory_interview') && !linkedStoryIds.has(s.id))
      .map(s => s.id);

    if (orphans.length > 0) {
      console.warn(`[OrphanReconcile] Found ${orphans.length} orphaned story/interview entries not linked in any book:`, orphans);
      setOrphanedStoryIds(orphans);
    } else {
      console.log('[OrphanReconcile] No orphaned stories detected.');
    }
  }, [userDataLoaded, stories, books, draftResumeChecked]); // eslint-disable-line react-hooks/exhaustive-deps

  // --- Orphan recovery UI ---
  // Stories the user has explicitly dismissed from the recovery prompt (so we don't re-nag).
  const dismissedOrphanRef = React.useRef(null);
  if (dismissedOrphanRef.current === null) {
    try { dismissedOrphanRef.current = new Set(JSON.parse(localStorage.getItem('remnant_dismissedOrphans') || '[]')); }
    catch { dismissedOrphanRef.current = new Set(); }
  }

  // Only surface RECENT orphans (clobber victims), not old intentionally-loose drafts.
  const ORPHAN_RECENT_DAYS = 30;
  const orphanStories = React.useMemo(() => {
    if (!orphanedStoryIds.length) return [];
    const cutoff = Date.now() - ORPHAN_RECENT_DAYS * 24 * 60 * 60 * 1000;
    return orphanedStoryIds
      .filter(id => !dismissedOrphanRef.current.has(id))
      .map(id => stories.find(s => s.id === id))
      .filter(Boolean)
      .filter(s => {
        if (!s.createdAt) return true;
        const t = new Date(s.createdAt).getTime();
        return isNaN(t) || t >= cutoff;
      });
  }, [orphanedStoryIds, stories]);

  // Re-link an orphaned story to a chosen book by adding a page (same shape gift-sync uses).
  const recoverOrphanToBook = (storyId, bookId) => {
    const story = stories.find(s => s.id === storyId);
    if (!story || !bookId) return;
    const photoPath = story.photoPath || null;
    const buildPage = () => ({
      storyId, photoPath, layout: photoPath ? 'photo-spread' : 'classic',
      caption: '', photoSize: 'large', addedAt: new Date().toISOString(),
    });
    const currentBooks = booksRef.current || books;
    let didAdd = false;
    const updated = currentBooks.map(b => {
      if (b.id !== bookId) return b;
      const existing = new Set((b.pages || []).map(p => p.storyId));
      if (existing.has(storyId)) return b; // already linked
      didAdd = true;
      return { ...b, pages: [...(b.pages || []), buildPage()] };
    });
    if (didAdd) {
      persistBooks(updated); // merge-on-write keeps the new page even if another device also saved
      // If this is the book currently open in the builder, show the chapter immediately.
      if (bookId === activeBookId) {
        setStBookPages(prev => prev.some(p => p.storyId === storyId) ? prev : [...prev, buildPage()]);
        // The recovered page has only photoPath (no photoUrl/base64 yet). Clear the
        // resolve guard so the photo-resolve effect re-runs and generates the base64
        // the open-book PDF preview needs (thumbnail/edit use photoUrl and already show).
        if (photoPath) photoResolveRef.current = false;
      }
    }
    setOrphanedStoryIds(prev => prev.filter(id => id !== storyId));
  };

  const dismissOrphanPrompt = () => {
    try {
      const set = dismissedOrphanRef.current || new Set();
      orphanStories.forEach(s => set.add(s.id));
      dismissedOrphanRef.current = set;
      localStorage.setItem('remnant_dismissedOrphans', JSON.stringify([...set]));
    } catch {}
    setOrphanedStoryIds([]);
  };

  // Removed-stays-removed: a story the user deliberately removed must never resurface in the
  // orphan-recovery modal, even in a partial-persist edge. Record its id in the same persisted
  // exclusion set the modal already honors, and drop it from the live orphan list.
  const markStoryRemoved = (id) => {
    if (!id) return;
    try {
      const set = dismissedOrphanRef.current || new Set();
      set.add(id);
      dismissedOrphanRef.current = set;
      localStorage.setItem('remnant_dismissedOrphans', JSON.stringify([...set]));
    } catch {}
    setOrphanedStoryIds(prev => prev.filter(sid => sid !== id));
  };

  const refreshFileList = async () => {
    setLoadingFiles(true);
    try {
      const result = await list({ path: ({ identityId }) => `private/${identityId}/`, options: { accessLevel: 'guest' } });
      const newFiles = result.items.filter(f => !f.path.endsWith('/'));
      const imageFiles = newFiles.filter(f => /\.(jpg|jpeg|png|gif)$/i.test(f.path.split('/').pop()));
      // TB-075 QW-5: parallelize getUrl calls so vault thumbnail load is 1×RTT, not N×RTT.
      // TB-075 QW-1: check the signed URL cache first so repeat vault loads avoid the network.
      const urlEntries = await Promise.all(
        imageFiles.map(async (file) => {
          const cached = getCachedSignedUrl(file.path);
          if (cached) return [file.path, cached];
          try {
            const urlResult = await getUrl({ path: file.path, options: { expiresIn: SIGNED_URL_TTL_S } });
            const urlStr = urlResult.url?.toString();
            if (!urlStr) return null;
            setCachedSignedUrl(file.path, urlStr);
            return [file.path, urlStr];
          } catch (err) { console.error('Thumbnail error:', err); return null; }
        })
      );
      const urls = Object.fromEntries(urlEntries.filter(Boolean));
      setFileUrls(urls); setFiles(newFiles);
    } catch (err) {
      console.error('List files error:', err);
      if (isAuthExpiry(err)) { signOut('auth_expiry'); return; }
    }
    finally { setLoadingFiles(false); }
  };

  // loadUserData — moved to hooks/useUserData

  const refreshStories = async () => {
    setLoadingStories(true);
    try {
      // TB-042: filter by userId server-side and bump limit. The unfiltered
      // limit:100 query was a table-wide scan with owner-auth applied AFTER
      // the scan window — users whose entries fell outside the first 100
      // scanned items had chapters silently disappear from the UI even
      // though the data was correctly persisted.
      const result = await client.graphql({
        query: listLegacyEntries,
        variables: { filter: { userId: { eq: user?.userId || user?.username } }, limit: 500 },
      });
      const items = result.data.listLegacyEntries.items;
      items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setStories(items);
      // Sync is handled by the useEffect watching stories + books
    } catch (err) {
      console.error('Load stories error:', err);
      if (isAuthExpiry(err)) { signOut('auth_expiry'); return; }
    }
    finally { setLoadingStories(false); }
  };

  const refreshRecordings = async () => {
    setLoadingRecordings(true);
    try {
      const result = await client.graphql({ query: shareableMediaByUserId, variables: { userId, limit: 200 } });
      const items = result.data?.shareableMediaByUserId?.items || [];
      items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
      setRecordings(items);
    } catch (err) {
      console.error('Load recordings error:', err);
      if (isAuthExpiry(err)) { signOut('auth_expiry'); return; }
    }
    finally { setLoadingRecordings(false); }
  };

  const handleDeleteRecording = async (id, s3Key) => {
    try {
      // Delete S3 file
      try { await remove({ path: s3Key }); } catch (err) { console.error('S3 delete error:', err); }
      // Delete ShareableMedia record
      await client.graphql({ query: deleteShareableMedia, variables: { input: { id } } });
      // Unlink from any book chapter that references this recording
      const rec = recordings.find(r => r.id === id);
      if (rec) {
        const pageIdx = stBookPages.findIndex(p => p.shareId === rec.shareId);
        if (pageIdx >= 0) {
          const updatedPages = [...stBookPages];
          updatedPages[pageIdx] = { ...updatedPages[pageIdx], shareId: undefined, qrEnabled: undefined, mediaType: undefined };
          setStBookPages(updatedPages);
          setChapterQrCodes(prev => { const next = { ...prev }; delete next[updatedPages[pageIdx].storyId]; return next; });
        }
      }
      await refreshRecordings();
      showToast('Recording deleted.', 'success');
    } catch (err) { console.error('Delete recording error:', err); showToast('Failed to delete recording.', 'error'); }
  };

  const handleUnlinkRecording = async (id) => {
    try {
      const rec = recordings.find(r => r.id === id);
      await client.graphql({ query: deleteShareableMedia, variables: { input: { id } } });
      // Unlink from any book chapter
      if (rec) {
        const pageIdx = stBookPages.findIndex(p => p.shareId === rec.shareId);
        if (pageIdx >= 0) {
          const updatedPages = [...stBookPages];
          updatedPages[pageIdx] = { ...updatedPages[pageIdx], shareId: undefined, qrEnabled: undefined, mediaType: undefined };
          setStBookPages(updatedPages);
          setChapterQrCodes(prev => { const next = { ...prev }; delete next[updatedPages[pageIdx].storyId]; return next; });
        }
      }
      await refreshRecordings();
      showToast('Public link removed. File kept in storage.', 'success');
    } catch (err) { console.error('Unlink recording error:', err); showToast('Failed to remove link.', 'error'); }
  };

  const handlePlayRecording = async (s3Key, mediaType) => {
    try {
      const result = await getUrl({ path: s3Key });
      setPlayingMedia({ url: result.url.toString(), mediaType: mediaType || 'AUDIO' });
    } catch (err) { console.error('Play recording error:', err); showToast('Failed to load recording.', 'error'); }
  };

  const handleQuickCapture = async () => {
    if (!quickCapture.trim()) return;
    setSavingQuickCapture(true);
    const now = new Date();
    const title = `Quick Note — ${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })} ${now.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
    try {
      await client.graphql({ query: createLegacyEntry, variables: { input: { userId: user.userId || user.username, title, content: quickCapture.trim(), type: 'story', createdAt: now.toISOString() } } });
      setQuickCapture(''); setQuickCaptureExpanded(false); await refreshStories();
    } catch (err) { showToast('Something went wrong saving your work. Please try again — your work is not lost.', 'error'); }
    finally { setSavingQuickCapture(false); }
  };

  const handleSaveStory = async () => {
    if (!storyTitle.trim() || !storyContent.trim()) { showToast('Please enter a title and content.', 'error'); return; }
    setSavingStory(true);
    try {
      await client.graphql({ query: createLegacyEntry, variables: { input: { userId: user.userId || user.username, title: storyTitle.trim(), content: storyContent.trim(), type: 'story', createdAt: new Date().toISOString() } } });
      setStoryTitle(''); setStoryContent(''); await refreshStories();
    } catch (err) { showToast('Something went wrong saving your work. Please try again — your work is not lost.', 'error'); }
    finally { setSavingStory(false); }
  };

  const handleEditStory = (story) => { setEditingId(story.id); setEditTitle(story.title); setEditContent(story.content); };
  const handleCancelEdit = () => { setEditingId(null); setEditTitle(''); setEditContent(''); };

  const handleUpdateStory = async (id) => {
    if (!editTitle.trim() || !editContent.trim()) { showToast('Title and content cannot be empty.', 'error'); return; }
    setSavingEdit(true);
    try {
      await client.graphql({ query: updateLegacyEntry, variables: { input: { id, title: editTitle.trim(), content: editContent.trim() } } });
      setStOriginalContent(null);
      setEditingId(null); await refreshStories();
    } catch (err) { showToast('Something went wrong saving your changes. Please try again.', 'error'); }
    finally { setSavingEdit(false); }
  };

  const handleDeleteStory = async (id) => {
    // Check for linked recordings
    const linkedRecs = recordings.filter(r => r.storyId === id);
    const msg = linkedRecs.length > 0
      ? `This story has ${linkedRecs.length} linked recording${linkedRecs.length !== 1 ? 's' : ''} with public QR links — those will also be deleted.`
      : null;
    setConfirmDeleteStory({ id, msg });
  };

  // Returns true ONLY if the LegacyEntry was actually deleted (atomic-Remove contract). Callers that
  // also drop the book page + claim "Nothing was kept" gate that on a confirmed delete, so a failed
  // delete can never orphan the story (entry survives, page gone) or make a false claim. `silent`
  // suppresses the generic error toast for callers that show their own truthful message.
  const executeDeleteStory = async (id, { silent = false } = {}) => {
    const linkedRecs = recordings.filter(r => r.storyId === id);
    try {
      // Clean up linked recordings (best-effort; a media-cleanup failure must not block the delete)
      for (const rec of linkedRecs) {
        try { await remove({ path: rec.s3Key }); } catch (err) { console.error('S3 cleanup error:', err); }
        try { await client.graphql({ query: deleteShareableMedia, variables: { input: { id: rec.id } } }); } catch (err) { console.error('ShareableMedia cleanup error:', err); }
      }
      await client.graphql({ query: deleteLegacyEntry, variables: { input: { id } } });
      await refreshStories();
      if (linkedRecs.length > 0) await refreshRecordings();
      return true;
    }
    catch (err) {
      console.error('[executeDeleteStory] delete failed:', err);
      if (!silent) showToast('Something went wrong. Please try again.', 'error');
      return false;
    }
  };

  // PIPE-SAVE-1 (rider 2): one-tap undo for a just-saved story from the completion celebration.
  // This is the reversibility guarantee for an auto-save (a spoken done-signal), especially a
  // false-positive done-signal match: a save the user did not intend must be trivially removable.
  // Removes the auto-added book page AND deletes the entry, then closes the celebration.
  const handleRemoveSavedStory = async (storyId) => {
    if (!storyId) { setStStoryCelebration(null); return; }
    // Atomic Remove (orphan fix): delete the ENTRY first and only proceed on a confirmed delete.
    // A failed delete leaves the entry AND its page intact (still fully saved) — so we never orphan
    // the story or claim "Nothing was kept" when something was.
    const deleted = await executeDeleteStory(storyId, { silent: true });
    if (!deleted) {
      // Stay on the celebration so the user can retry — the story is still fully saved (entry + page
      // intact). Consistent with removeRevealedStory, which also stays put on a failed delete.
      showToast("Couldn't remove it. Nothing changed. Please try again.", 'error');
      return;
    }
    markStoryRemoved(storyId);
    setStBookPages(prev => prev.filter(pg => pg.storyId !== storyId));
    // F-252: was a single-book saveBookData built from `stBookPages`. Two problems: the page
    // survived in any OTHER book holding it, and the write was additively merged server-side so
    // the removal was undone on the next persist. removeStoryPageEverywhere tombstones the story in
    // every book off booksRef (authoritative), so neither can happen.
    removeStoryPageEverywhere(storyId);
    // S4 early-end telemetry (save_story build): a Remove shortly after an auto-save is the
    // real-world early-end signal — watch pipe_save_trigger -> early_end_signal during rollout.
    try { trackEvent('early_end_signal', { via: 'remove', context: 'celebration' }); } catch (e) { /* never break flow */ }
    showToast('Removed. Nothing was kept.');
    setStStoryCelebration(null);
  };

  // PIPE-SAVE-1 F-3: Remove from the SAVED review/reveal (first live-tested tonight). Deletes the
  // just-auto-saved entry + its book page, then exits the review cleanly back to the Talk home.
  const removeRevealedStory = async () => {
    const id = stProcessed?.savedStoryId;
    if (!id) { setStInterviewMode(false); setStInterviewStep('pick'); return; }
    // Atomic Remove (orphan fix): confirm the entry delete BEFORE dropping the page or leaving the
    // reveal. On failure we stay on the saved reveal so the user can retry — nothing was lost.
    const deleted = await executeDeleteStory(id, { silent: true });
    if (!deleted) {
      showToast("Couldn't remove it. Nothing changed. Please try again.", 'error');
      return; // stay on the reveal in its saved state
    }
    markStoryRemoved(id);
    setStBookPages(prev => prev.filter(pg => pg.storyId !== id));
    // F-252: see handleRemoveSavedStory — every book, and tombstoned so the merge cannot undo it.
    removeStoryPageEverywhere(id);
    // S4 early-end telemetry (save_story build): a Remove shortly after an auto-save is the
    // real-world early-end signal — watch pipe_save_trigger -> early_end_signal during rollout.
    try { trackEvent('early_end_signal', { via: 'remove', context: 'reveal' }); } catch (e) { /* never break flow */ }
    showToast('Removed. Nothing was kept.');
    setStInterviewStep('pick'); setStInterviewMessages([]); setStProcessed({ title: '', content: '' }); setStSelectedPrompt(null);
    setStInterviewMode(false);
    setActiveTab('talk');
  };

  const callAI = async (action, extra = {}) => {
    setAiLoading(true);
    try {
      const data = await callAi({ action, messages: aiMessages, ...extra });
      return data.text;
    } catch (err) {
      showToast('The writing assistant hit a snag. Please try again in a moment.', 'error');
      console.error('AI call error:', err);
      return null;
    } finally {
      setAiLoading(false);
    }
  };

  const handleGetPrompt = async () => {
    const text = await callAI('get_prompt');
    if (text) { setAiPrompt(text); setAiDraft(''); setAiMessages([]); setAiResponse(''); setAiTitle(''); }
  };

  // ── Feedback functions ──
  const submitFeedback = async () => {
    if (feedbackRating === 0) { showToast('Please select a star rating.', 'error'); return; }
    setFeedbackSubmitting(true);
    try {
      await callAi({
        action: 'submit',
        userId: user?.userId || user?.username || 'unknown',
        displayName: displayName || 'Unknown',
        rating: feedbackRating,
        currentTab: activeTab,
        comment: feedbackComment.trim(),
      }, { path: '/feedback' });
      setFeedbackSuccess(true);
      setTimeout(() => { setShowFeedback(false); setFeedbackRating(0); setFeedbackComment(''); setFeedbackSuccess(false); }, 1800);
    } catch (err) {
      console.error('Feedback error:', err);
      showToast('Failed to submit feedback. Please try again.', 'error');
    } finally { setFeedbackSubmitting(false); }
  };

  // Contact / "Message us" — reuses the /feedback endpoint with type:'contact' so support@
  // gets the note with the user's email as reply-to (Alex can reply directly).
  const submitContact = async () => {
    const msg = contactMessage.trim();
    if (msg.length < 2) { showToast('Please write a short message first.', 'error'); return; }
    setContactSubmitting(true);
    try {
      await callAi({
        action: 'submit',
        type: 'contact',
        userId: user?.userId || user?.username || 'unknown',
        displayName: displayName || 'Unknown',
        email: user?.signInDetails?.loginId || user?.attributes?.email || '',
        currentTab: activeTab,
        comment: msg,
      }, { path: '/feedback' });
      setContactSuccess(true);
      setTimeout(() => { setShowContact(false); setContactMessage(''); setContactSuccess(false); }, 2200);
    } catch (err) {
      console.error('Contact error:', err);
      showToast('Could not send your message. Please try again.', 'error');
    } finally { setContactSubmitting(false); }
  };

  // F-12: Memory Care interest sensor. Reuses the SAME /feedback→hello@ contact path
  // (type:'contact' so it lands as a note Alex can reply to), plus a GA4 event. Returns
  // true on success so the button flips to its once-per-user "thank you" state. No new
  // infrastructure (ask-rule: reuse existing plumbing).
  const submitMemoryCareInterest = async () => {
    const email = user?.signInDetails?.loginId || user?.attributes?.email || '';
    try { trackEvent('memory_care_interest', { source: 'coming_soon' }); } catch (e) { /* telemetry best-effort */ }
    try {
      await callAi({
        action: 'submit',
        type: 'contact',
        userId: user?.userId || user?.username || 'unknown',
        displayName: displayName || 'Unknown',
        email,
        currentTab: 'memory-care-interest',
        comment: `Memory Care interest — ${email || 'no email'} — ${new Date().toISOString()}`,
      }, { path: '/feedback' });
      return true;
    } catch (err) {
      console.error('Memory Care interest send error:', err);
      return false;
    }
  };

  const loadAdminFeedback = async () => {
    setAdminLoading(true);
    try {
      const data = await callAi({ action: 'list' }, { path: '/feedback' });
      setAdminFeedback(data.feedback || []);
    } catch (err) { console.error('Load feedback error:', err); }
    finally { setAdminLoading(false); }
  };

  const markFeedbackReviewed = async (id) => {
    try {
      await callAi({ action: 'mark_reviewed', id }, { path: '/feedback' });
      setAdminFeedback(prev => prev.map(f => f.id === id ? { ...f, reviewed: true } : f));
    } catch (err) { console.error('Mark reviewed error:', err); }
  };

  // Load admin feedback when switching to that tab
  React.useEffect(() => {
    if (activeTab === 'admin' && isAdmin) { loadAdminFeedback(); }
  }, [activeTab]);

  const handleFollowUp = async () => {
    if (!aiDraft.trim()) { showToast('Write something first!', 'error'); return; }
    const newMessages = [...aiMessages, { role: 'user', content: aiDraft }];
    setAiMessages(newMessages);
    const text = await callAI('follow_up', { messages: newMessages });
    if (text) { setAiResponse(text); setAiMessages([...newMessages, { role: 'assistant', content: text }]); }
  };

  const handleExpandDraft = async () => {
    if (!aiDraft.trim()) { showToast('Write something first!', 'error'); return; }
    const text = await callAI('expand', { roughDraft: aiDraft });
    if (text) setAiResponse(text);
  };

  const handleSaveAIStory = async () => {
    const contentToSave = aiResponse || aiDraft;
    if (!aiTitle.trim() || !contentToSave.trim()) { showToast('Please add a title and content to save.', 'error'); return; }
    setSavingStory(true);
    const fullContent = aiPrompt
      ? `Question: ${aiPrompt}\n\n${contentToSave.trim()}`
      : contentToSave.trim();
    try {
      await client.graphql({ query: createLegacyEntry, variables: { input: { userId: user.userId || user.username, title: aiTitle.trim(), content: fullContent, type: 'story', createdAt: new Date().toISOString() } } });
      setAiPrompt(''); setAiDraft(''); setAiMessages([]); setAiResponse(''); setAiTitle('');
      await refreshStories();
      showToast('Story saved to your archive!', 'success');
    } catch (err) { showToast('Something went wrong saving your work. Please try again — your work is not lost.', 'error'); }
    finally { setSavingStory(false); }
  };

  // Estate Planning UI state — estateChecks, estateNotes, estateContacts come from useUserData (DynamoDB-persisted)
  const [savingEstate, setSavingEstate] = React.useState(false);
  const [executorLetter, setExecutorLetter] = React.useState('');
  const [executorLetterAiDraft, setExecutorLetterAiDraft] = React.useState('');
  const [executorLetterLoading, setExecutorLetterLoading] = React.useState(false);
  const [estateTab, setEstateTab] = React.useState('checklist'); // 'checklist' | 'contacts' | 'executor' | 'summary'

  const toggleCheck = (sectionId, item) => {
    const key = `${sectionId}::${item}`;
    const updated = { ...estateChecks, [key]: !estateChecks[key] };
    saveEstateChecks(updated);
  };

  const completedSections = ESTATE_SECTIONS.filter(s =>
    s.items.some(item => estateChecks[`${s.id}::${item}`])
  ).length;


  const handleExecutorLetterAI = async () => {
    setExecutorLetterLoading(true);
    const context = ESTATE_SECTIONS.map(s => {
      const checked = s.items.filter(item => estateChecks[`${s.id}::${item}`]);
      return checked.length ? `${s.title}: ${checked.join(', ')}` : null;
    }).filter(Boolean).join('\n');
    const contactsList = Object.entries(estateContacts).filter(([,v]) => v.name).map(([k,v]) => {
      const label = IMPORTANT_CONTACTS.find(c => c.id === k)?.label || k;
      return `${label}: ${v.name}${v.phone ? ', ' + v.phone : ''}`;
    }).join('\n');
    
    try {
      const data = await callAi({
        action: 'executor_letter',
        messages: [{ role: 'user', content: `Help me write a personal letter to the person who will handle my affairs after I'm gone (my executor). This is NOT a legal document — it's a human, personal letter that says: here's what exists, here's where to find things, here's what matters to me, and here's what I want you to know.

My name is ${accountName || 'the user'}.

Here's what I've completed in my estate plan:
${context || 'Still working on my checklist.'}

${contactsList ? 'My important contacts:\n' + contactsList : ''}

${executorLetter ? 'Here are my own notes/thoughts to include:\n' + executorLetter : ''}

Please write a warm, clear, organized letter from me to my executor. Include:
1. A personal opening (thank them, acknowledge the weight of what you're asking)
2. Where to find important documents
3. Key contacts to reach out to
4. Any specific wishes I've noted
5. A personal closing

Keep the tone loving but practical. This letter should make their job easier and make them feel supported, not overwhelmed. Write in first person as me.` }],
      });
      setExecutorLetterAiDraft(data.text || 'Unable to generate letter.');
    } catch (err) {
      showToast('The writing assistant hit a snag. Please try again in a moment.', 'error');
    } finally {
      setExecutorLetterLoading(false);
    }
  };


  // renderEstate — extracted to pages/EstatePlanningPage.js

  // ── Subscription tier ──
  // isPremium and setIsPremium come from useUserData — DynamoDB is the source of truth.
  // On every session load, applyAccountSettings in useUserData overwrites the localStorage
  // fast-cache with the real DynamoDB value, so DevTools manipulation does not persist.
  // When Stripe goes live, unlockPremium gets wired to actual billing confirmation.

  const unlockPremium = () => {
    setIsPremium(true);
    // Write to DynamoDB via saveAccountSettings so the premium status is device-agnostic
    saveAccountSettings({ isPremium: true });
    setActiveTab('book');
    showToast('Premium features unlocked — thank you for being part of the beta!', 'success');
  };

  // ── AI Story Edit usage tracking (daily rate limit) ──
  const AI_DAILY_LIMIT = 100; // High ceiling — stops accidents/abuse, real users never hit it
  const [aiPolishCount, setAiPolishCount] = React.useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem('remnant_aiDailyUsage') || '{}');
      const today = new Date().toISOString().slice(0, 10);
      return stored.date === today ? stored.count : 0;
    } catch { return 0; }
  });
  const incrementPolishCount = () => {
    const today = new Date().toISOString().slice(0, 10);
    const newCount = aiPolishCount + 1;
    setAiPolishCount(newCount);
    try { localStorage.setItem('remnant_aiDailyUsage', JSON.stringify({ date: today, count: newCount })); } catch {}
  };
  const canUseAiPolish = aiPolishCount < AI_DAILY_LIMIT;

  // Launch menu (post-TB-082 Phase 2): only My Books + Account in the sidebar.
  // - Gift Books: reachable from "Start a New Book" chooser in My Books + appears
  //   in the My Books grid alongside personal books.
  // - My Stories: cross-book archive deferred for launch; chapters reachable inside each book.
  // - Memory Care: subjects appear as cards in My Books; "Interview a Loved One"
  //   in the Start chooser creates new subjects. MemoryInterviewPage route still
  //   active (`activeTab === 'memory'`), just not in the nav.
  // - Code paths for all three remain; only the front-door nav entries are removed.
  const navItems = [
    { id: 'talk',       icon: <Feather size={16} weight="duotone" />, label: 'New Story' },
    { id: 'book',       icon: <Book size={16} weight="duotone" />,     label: 'My Book' },
    { id: 'account',    icon: <Gear size={16} weight="duotone" />,      label: 'Account' },
    ...(isAdmin ? [{ id: 'admin', icon: <ChatCircle size={16} weight="duotone" />, label: 'Beta Feedback' }] : []),
  ];

  // CAPSULE_TYPES — moved to pages/TimeCapsulePage.js

  // capsules, accountSettings, accountName, legacyMode, legacySubjectName,
  // isKeeper, subjectName, notifications, saveAccountSettings — moved to hooks/useUserData
  const [capsuleType, setCapsuleType] = React.useState(null);
  const [capsuleRecipientId, setCapsuleRecipientId] = React.useState('');
  const [capsuleDelivery, setCapsuleDelivery] = React.useState('later');
  const [capsuleDate, setCapsuleDate] = React.useState('');
  const [capsuleDraft, setCapsuleDraft] = React.useState('');
  const [capsuleAiChat, setCapsuleAiChat] = React.useState([]);
  const [capsuleAiLoading, setCapsuleAiLoading] = React.useState(false);
  const [capsuleTitle, setCapsuleTitle] = React.useState('');
  const [savingCapsule, setSavingCapsule] = React.useState(false);
  const [capsuleAiStarted, setCapsuleAiStarted] = React.useState(false);
  const [capsuleReleaseMethod, setCapsuleReleaseMethod] = React.useState('trusted');

  // ── Account Settings (UI state only — data in useUserData) ──
  const [accountSection, setAccountSection] = React.useState('orders');

  // ── MFA State ──
  const [mfaEnabled, setMfaEnabled] = React.useState(false);
  const [mfaLoading, setMfaLoading] = React.useState(true);
  const [mfaStep, setMfaStep] = React.useState('idle'); // idle | setup | done
  const [mfaSetupUri, setMfaSetupUri] = React.useState('');
  const [mfaVerifyCode, setMfaVerifyCode] = React.useState('');
  const [mfaError, setMfaError] = React.useState('');
  const [savingAccount, setSavingAccount] = React.useState(false);

  const [editingCapsuleId, setEditingCapsuleId] = React.useState(null);
  const [editCapsuleTitle, setEditCapsuleTitle] = React.useState('');
  const [editCapsuleContent, setEditCapsuleContent] = React.useState('');
  const [editCapsuleDelivery, setEditCapsuleDelivery] = React.useState('later');
  const [editCapsuleDate, setEditCapsuleDate] = React.useState('');
  const [editCapsuleRecipientId, setEditCapsuleRecipientId] = React.useState('');
  const [capsuleVideoBlob, setCapsuleVideoBlob] = React.useState(null);
  const [capsuleVideoUrl, setCapsuleVideoUrl] = React.useState('');
  const [capsuleVideoRecording, setCapsuleVideoRecording] = React.useState(false);
  const capsuleMediaRecorderRef = React.useRef(null);
  const capsuleVideoChunksRef = React.useRef([]);
  const capsuleLiveVideoRef = React.useRef(null);

  const startCapsuleRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
      if (capsuleLiveVideoRef.current) { capsuleLiveVideoRef.current.srcObject = stream; capsuleLiveVideoRef.current.play(); }
      capsuleVideoChunksRef.current = [];
      const mr = new MediaRecorder(stream, { mimeType: 'video/webm;codecs=vp9,opus', audioBitsPerSecond: 48000 });
      mr.ondataavailable = e => { if (e.data.size > 0) capsuleVideoChunksRef.current.push(e.data); };
      mr.onstop = () => {
        const blob = new Blob(capsuleVideoChunksRef.current, { type: 'video/webm' });
        setCapsuleVideoBlob(blob);
        setCapsuleVideoUrl(URL.createObjectURL(blob));
        stream.getTracks().forEach(t => t.stop());
      };
      capsuleMediaRecorderRef.current = mr;
      mr.start(100);
      setCapsuleVideoRecording(true);
    } catch (err) { showToast('Could not access camera. Please allow camera access and try again.', 'error'); }
  };

  const stopCapsuleRecording = () => {
    if (capsuleMediaRecorderRef.current && capsuleVideoRecording) {
      capsuleMediaRecorderRef.current.stop();
      setCapsuleVideoRecording(false);
    }
  };

  // saveCapsules — moved to hooks/useUserData


  // ── Memory Care subject upsert helper ──
  const saveMemoryCareSubject = (subjectData) => {
    const updated = memoryCareSubjects.some(s => s.id === subjectData.id)
      ? memoryCareSubjects.map(s => s.id === subjectData.id ? subjectData : s)
      : [...memoryCareSubjects, subjectData];
    saveMemoryCareSubjects(updated);
  };

  // ── Memory Care theme & mention extraction (fire-and-forget after session save) ──
  const extractSessionThemes = async (transcript, subjectId) => {
    try {
      const transcriptText = transcript
        .map(m => `${m.role === 'user' ? 'Caregiver' : 'Question'}: ${m.content}`)
        .join('\n');

      const data = await callAi({
        action: 'extract_themes',
        prompt: `You are analyzing a memory care interview transcript.

Transcript:
${transcriptText}

Task 1 — Themes: Which of these 12 life themes were meaningfully discussed?
childhood | parents | siblings | school | first_job | marriage | children | home | faith | hardship | proudest_moment | what_they_want_remembered

Only include themes genuinely present, not just mentioned in passing.

Task 2 — Mentions: Extract specific names of people, places, and meaningful objects.
Examples: "Aunt Clara", "the farmhouse on Route 9", "Dad's pocket watch"
Keep them short and specific. Maximum 10 items.

Respond ONLY with valid JSON, no explanation:
{
  "themes": ["childhood", "home"],
  "mentions": ["Aunt Clara", "David", "the summer kitchen"]
}`,
      });
      const text = data?.text || '';
      const clean = text.replace(/```json|```/g, '').trim();
      const extracted = JSON.parse(clean);

      const subject = memoryCareSubjects.find(s => s.id === subjectId);
      if (!subject) return;

      const updatedSubject = {
        ...subject,
        capturedThemes: [...new Set([...subject.capturedThemes, ...(extracted.themes || [])])],
        mentions: [...new Set([...subject.mentions, ...(extracted.mentions || [])])].slice(-30),
        lastSessionDate: new Date().toISOString(),
        sessionCount: (subject.sessionCount || 0) + 1,
      };

      saveMemoryCareSubject(updatedSubject);
    } catch (err) {
      console.warn('Theme extraction failed silently:', err);
    }
  };

  const [memoryStep, setMemoryStep] = React.useState('intro'); // intro | setup | interview | review | saved
  const [memoryTab, setMemoryTab] = React.useState('overview'); // 'overview' | 'interview' | 'voice' | 'resources'
  const [memorySessionLength, setMemorySessionLength] = React.useState('full'); // 'short' | 'full'
  const [memoryDementiaMode, setMemoryDementiaMode] = React.useState(false);
  const [memoryDayQuality, setMemoryDayQuality] = React.useState(null); // 'good' | 'hard' | null
  const [memoryTopic, setMemoryTopic] = React.useState(null);
  // ── Memory Care guided flow state ──
  const [mcStep, setMcStep] = React.useState('welcome'); // 'welcome' | 'mood' | 'suggest' | 'breath' | 'session'
  const [mcSubject, setMcSubject] = React.useState(null);
  const [mcMood, setMcMood] = React.useState(null); // 'good' | 'mixed' | 'hard'
  const [mcSessionTopic, setMcSessionTopic] = React.useState(null); // { theme, title, reason, openingQuestion }
  const [mcSuggestion, setMcSuggestion] = React.useState(null);
  const [mcSuggestionLoading, setMcSuggestionLoading] = React.useState(false);
  const [mcTargetBookId, setMcTargetBookId] = React.useState(null);
  const [mcShaping, setMcShaping] = React.useState(false);
  const [mcOriginalTranscript, setMcOriginalTranscript] = React.useState(null);

  const fetchSessionSuggestion = async (subject, mood) => {
    setMcSuggestionLoading(true);
    setMcSuggestion(null);
    try {
      const data = await callAi({
        action: 'session_suggestion',
        prompt: `You are helping a caregiver plan a memory care interview session.

Subject name: ${subject.name}
Subject profile:
- Captured themes so far: ${JSON.stringify(subject.capturedThemes || [])}
- Recent mentions (people, places, things): ${JSON.stringify(subject.mentions || [])}
- Today's mood: ${mood} (good = alert and talkative, mixed = some clarity, hard = tired or confused)

The 12 life themes: childhood | parents | siblings | school | first_job | marriage | children | home | faith | hardship | proudest_moment | what_they_want_remembered

Rules:
- Suggest ONE theme not yet in capturedThemes
- If mood is "hard", choose something gentle and sensory (childhood, home, faith)
- If a mention connects to an uncaptured theme, reference it in the reason
- The opening question must be concrete, sensory, one sentence, no compound questions
- Important: Never use he/she/her/his. Always refer to the person by name: ${subject.name}

Respond ONLY with valid JSON:
{
  "theme": "children",
  "title": "${subject.name}'s Children & Family",
  "reason": "You haven't captured this yet, and ${subject.name} mentioned David last time — that could open something beautiful.",
  "openingQuestion": "Tell me about when your children were small — what do you remember most about those years?"
}`,
      });
      const text = data?.text || '';
      const clean = text.replace(/```json|```/g, '').trim();
      setMcSuggestion(JSON.parse(clean));
    } catch (err) {
      console.warn('Suggestion fetch failed:', err);
      setMcSuggestion(null);
    } finally {
      setMcSuggestionLoading(false);
    }
  };

  const [memorySubjectName, setMemorySubjectName] = React.useState('');
  const [memoryCarerName, setMemoryCarerName] = React.useState('');
  const [memoryMessages, setMemoryMessages] = React.useState([]);
  const [memoryActivePrompt, setMemoryActivePrompt] = React.useState(''); // stores the active system prompt for follow-ups
  const [memoryInput, setMemoryInput] = React.useState('');

  // ── Journal (journalEntries state + saveJournalEntries in useUserData) ──
  const [journalText, setJournalText] = React.useState('');
  const [journalMood, setJournalMood] = React.useState(null);
  const [savingJournal, setSavingJournal] = React.useState(false);
  const [journalRecording, setJournalRecording] = React.useState(false);
  const [journalAudioBlob, setJournalAudioBlob] = React.useState(null);
  const [journalAudioUrl, setJournalAudioUrl] = React.useState('');
  const journalRecorderRef = React.useRef(null);
  const journalChunksRef = React.useRef([]);
  const journalStreamRef = React.useRef(null);
  const [journalRecordingTime, setJournalRecordingTime] = React.useState(0);
  const journalTimerRef = React.useRef(null);
  const [viewingJournalEntry, setViewingJournalEntry] = React.useState(null);
  const [editingJournalEntry, setEditingJournalEntry] = React.useState(null);
  const [editJournalText, setEditJournalText] = React.useState('');
  const [journalAudioPlayUrl, setJournalAudioPlayUrl] = React.useState('');

  // Resolve audio URL when viewing a journal entry with audio
  React.useEffect(() => {
    if (!viewingJournalEntry?.audioPath) { setJournalAudioPlayUrl(''); return; }
    (async () => {
      try {
        const result = await getUrl({ path: ({ identityId }) => `private/${identityId}/audio/${viewingJournalEntry.audioPath}` });
        if (result?.url) setJournalAudioPlayUrl(result.url.toString());
      } catch (err) { console.error('Audio URL error:', err); setJournalAudioPlayUrl(''); }
    })();
  }, [viewingJournalEntry]);

  // JOURNAL_MOODS — moved to pages/JournalPage.js

  // saveJournalEntries — moved to hooks/useUserData

  const startJournalRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      journalStreamRef.current = stream;
      const recorder = new MediaRecorder(stream, { mimeType: 'audio/webm', audioBitsPerSecond: 48000 });
      journalChunksRef.current = [];
      recorder.ondataavailable = (e) => { if (e.data.size > 0) journalChunksRef.current.push(e.data); };
      recorder.onstop = () => {
        const blob = new Blob(journalChunksRef.current, { type: 'audio/webm' });
        setJournalAudioBlob(blob);
        setJournalAudioUrl(URL.createObjectURL(blob));
        if (journalStreamRef.current) { journalStreamRef.current.getTracks().forEach(t => t.stop()); journalStreamRef.current = null; }
      };
      journalRecorderRef.current = recorder;
      recorder.start(250);
      setJournalRecording(true);
      setJournalRecordingTime(0);
      journalTimerRef.current = setInterval(() => setJournalRecordingTime(t => t + 1), 1000);
    } catch (err) {
      console.error('Mic error:', err);
      showToast('Could not access microphone. Please allow mic access and try again.', 'error');
    }
  };

  const stopJournalRecording = () => {
    if (journalRecorderRef.current && journalRecording) {
      journalRecorderRef.current.stop();
      setJournalRecording(false);
      if (journalTimerRef.current) { clearInterval(journalTimerRef.current); journalTimerRef.current = null; }
    }
  };

  const saveJournalAudioToVault = async () => {
    if (!journalAudioBlob) return null;
    try {
      const fileName = `journal_${Date.now()}.webm`;
      await uploadData({
        path: ({ identityId }) => `private/${identityId}/audio/${fileName}`,
        data: journalAudioBlob,
        options: { contentType: 'audio/webm', metadata: { title: 'Journal audio' } }
      });
      return fileName;
    } catch (err) { console.error('Audio upload error:', err); return null; }
  };

  const groupJournalByDate = (entries) => {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const yesterday = new Date(today); yesterday.setDate(today.getDate() - 1);
    const weekAgo = new Date(today); weekAgo.setDate(today.getDate() - 7);
    const monthAgo = new Date(today); monthAgo.setMonth(today.getMonth() - 1);

    const groups = { 'Today': [], 'Yesterday': [], 'This Week': [], 'This Month': [], 'Earlier': [] };
    const sorted = [...entries].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
    sorted.forEach(entry => {
      const d = new Date(entry.createdAt);
      if (d >= today) groups['Today'].push(entry);
      else if (d >= yesterday) groups['Yesterday'].push(entry);
      else if (d >= weekAgo) groups['This Week'].push(entry);
      else if (d >= monthAgo) groups['This Month'].push(entry);
      else groups['Earlier'].push(entry);
    });
    return Object.entries(groups).filter(([, items]) => items.length > 0);
  };

  // ── Interview Voice/Video Recording ──
  const [interviewRecording, setInterviewRecording] = React.useState(false);
  const [interviewAudioBlob, setInterviewAudioBlob] = React.useState(null);
  const [interviewAudioUrl, setInterviewAudioUrl] = React.useState('');
  const [interviewVideoMode, setInterviewVideoMode] = React.useState(false);
  const [interviewVideoBlob, setInterviewVideoBlob] = React.useState(null);
  const [interviewVideoUrl, setInterviewVideoUrl] = React.useState('');
  const interviewRecorderRef = React.useRef(null);
  const interviewChunksRef = React.useRef([]);
  const interviewStreamRef = React.useRef(null);
  const interviewVideoRecorderRef = React.useRef(null);
  const interviewVideoChunksRef = React.useRef([]);
  const interviewLiveVideoRef = React.useRef(null);

  const startInterviewRecording = async () => {
    try {
      if (interviewVideoMode) {
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        interviewStreamRef.current = stream;
        if (interviewLiveVideoRef.current) { interviewLiveVideoRef.current.srcObject = stream; interviewLiveVideoRef.current.play(); }
        // Video recorder
        interviewVideoChunksRef.current = [];
        // TB-103: prefer native video/audio mime on Safari (MP4), fall back to WebM.
        const videoRec = new MediaRecorder(stream, { mimeType: preferredVideoMime, audioBitsPerSecond: 48000 });
        videoRec.ondataavailable = (e) => { if (e.data.size > 0) interviewVideoChunksRef.current.push(e.data); };
        videoRec.onstop = () => {
          const blob = new Blob(interviewVideoChunksRef.current, { type: preferredVideoContentType });
          setInterviewVideoBlob(blob);
          setInterviewVideoUrl(URL.createObjectURL(blob));
        };
        interviewVideoRecorderRef.current = videoRec;
        // Audio-only recorder for transcription
        const audioStream = new MediaStream(stream.getAudioTracks());
        interviewChunksRef.current = [];
        const audioRec = new MediaRecorder(audioStream, { mimeType: preferredAudioMime, audioBitsPerSecond: 48000 });
        audioRec.ondataavailable = (e) => { if (e.data.size > 0) interviewChunksRef.current.push(e.data); };
        audioRec.onstop = () => {
          const blob = new Blob(interviewChunksRef.current, { type: preferredAudioContentType });
          setInterviewAudioBlob(blob);
          setInterviewAudioUrl(URL.createObjectURL(blob));
          stream.getTracks().forEach(t => t.stop());
        };
        interviewRecorderRef.current = audioRec;
        videoRec.start(100);
        audioRec.start(250);
      } else {
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        interviewStreamRef.current = stream;
        const recorder = new MediaRecorder(stream, { mimeType: preferredAudioMime, audioBitsPerSecond: 48000 });
        interviewChunksRef.current = [];
        recorder.ondataavailable = (e) => { if (e.data.size > 0) interviewChunksRef.current.push(e.data); };
        recorder.onstop = () => {
          const blob = new Blob(interviewChunksRef.current, { type: preferredAudioContentType });
          setInterviewAudioBlob(blob);
          setInterviewAudioUrl(URL.createObjectURL(blob));
          stream.getTracks().forEach(t => t.stop());
        };
        interviewRecorderRef.current = recorder;
        recorder.start(250);
      }
      setInterviewRecording(true);
    } catch (err) {
      showToast(interviewVideoMode
        ? 'Could not access camera. Please allow camera access and try again.'
        : 'Could not access microphone. Please allow microphone access.', 'error');
    }
  };

  const stopInterviewRecording = () => {
    if (interviewVideoRecorderRef.current) {
      interviewVideoRecorderRef.current.stop();
      interviewVideoRecorderRef.current = null;
    }
    if (interviewRecorderRef.current && interviewRecording) {
      interviewRecorderRef.current.stop();
      setInterviewRecording(false);
    }
  };

  const saveInterviewMedia = async (title) => {
    const hasVideo = !!interviewVideoBlob;
    const blob = hasVideo ? interviewVideoBlob : interviewAudioBlob;
    if (!blob) return null;
    const safeName = (title || 'Interview').replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '_');
    const timestamp = Date.now();
    // TB-103: use preferred ext so Safari recordings land with the correct extension.
    const ext = hasVideo ? preferredVideoExt : preferredAudioExt;
    const fileName = `${safeName}_${timestamp}.${ext}`;
    try {
      const { fetchAuthSession } = await import('aws-amplify/auth');
      const session = await fetchAuthSession();
      const identityId = session.identityId;
      const folder = hasVideo ? 'video' : 'audio';
      const fullPath = `private/${identityId}/${folder}/${fileName}`;
      await uploadData({
        path: fullPath,
        data: blob,
        options: { contentType: hasVideo ? preferredVideoContentType : preferredAudioContentType, metadata: { title: title || 'Interview Recording' } }
      });
      await refreshAudioFiles();
      return { path: fullPath, isVideo: hasVideo };
    } catch (err) { console.error('Failed to save interview media:', err); return null; }
  };
  const [memoryLoading, setMemoryLoading] = React.useState(false);
  const [memoryStory, setMemoryStory] = React.useState('');
  const [memoryTitle, setMemoryTitle] = React.useState('');
  const [memoryPhotoFile, setMemoryPhotoFile] = React.useState(null);
  const [memoryPhotoPreview, setMemoryPhotoPreview] = React.useState(null);
  const [savingMemory, setSavingMemory] = React.useState(false);

  const startMemoryInterview = async () => {
    if (!memoryTopic || !memorySubjectName.trim()) return;
    logInterviewStart(); // Memory Care caretaker interview is a text engagement
    setMemoryStep('interview');
    setMemoryLoading(true);
    setMemoryMessages([]);
    // Replace [name] placeholder in the prompt with the actual subject name
    let promptWithName = memoryTopic.prompt.replace(/\[name\]/g, memorySubjectName.trim());
    // Add short session instruction if selected
    if (memorySessionLength === 'short' && !memoryDementiaMode) {
      promptWithName += `\n\nIMPORTANT - SHORT SESSION MODE: This is a short session. Ask only 5-7 questions total, then gently wrap up by saying something like "Thank you so much for sharing these memories with me. I think we have a beautiful story here. Would you like to stop here, or is there anything else you'd like to add?" Keep each response brief. The person may have limited energy or attention.`;
    }
    // For dementia mode, use the trigger's own prompt (which includes DEMENTIA_BASE_PROMPT)
    if (memoryDementiaMode) {
      let dayGuidance = '';
      if (memoryDayQuality === 'hard') {
        dayGuidance = `\n\nTODAY IS A HARD DAY. The person is confused, tired, or withdrawn.
- Ask only 3-4 questions MAX before offering to stop
- If they can't answer, say something warm like "That's okay. I love just being here with you."
- After 3 exchanges, offer: "We got some wonderful things. Want to stop here?"
- Even one sentence from them is enough to write a story from.`;
      } else if (memoryDayQuality === 'okay') {
        dayGuidance = `\n\nTODAY IS A MIXED DAY. Some clarity, some confusion.
- Ask 5-6 questions, then check in: "Want to keep going or stop here?"
- If they get confused, simplify or switch topics. Don't push.`;
      } else {
        dayGuidance = `\n\nTODAY IS A GOOD DAY. They seem alert.
- You can ask up to 8-10 questions
- Still keep them simple, but you can go a little deeper on good answers`;
      }

      // Use trigger prompt directly + day quality + name replacement
      promptWithName = memoryTopic.prompt.replace(/\[name\]/g, memorySubjectName.trim()) + dayGuidance + `\n\nThe person's name is ${memorySubjectName.trim()}.`;
    }
    setMemoryActivePrompt(promptWithName); // save for follow-up messages
    // Build the initial user message — in dementia mode, reference the trigger
    const initialMsg = memoryDementiaMode
      ? `[The caregiver just sat down with ${memorySubjectName.trim()}. The topic is: ${memoryTopic.desc}. Ask ${memorySubjectName.trim()} your first question. Talk directly to ${memorySubjectName.trim()} — not to the caregiver. ONE short question only.]`
      : `Begin the interview now. The subject's name is ${memorySubjectName.trim()}.`;
    const displayMsg = memoryDementiaMode
      ? `Sitting with ${memorySubjectName.trim()}. ${memoryTopic.desc}.`
      : `Please begin the interview. The person's name is ${memorySubjectName.trim()}.`;
    try {
      const data = await callAi({ action: 'follow_up', messages: [{ role: 'user', content: initialMsg }], roughDraft: '', systemPrompt: promptWithName });
      const reply = data.text || '';
      setMemoryMessages([
        { role: 'user', content: displayMsg },
        { role: 'assistant', content: reply }
      ]);
    } catch (err) { console.error(err); }
    finally { setMemoryLoading(false); }
  };

  const sendMemoryMessage = async () => {
    if (!memoryInput.trim() || memoryLoading) return;
    const rawInput = memoryInput.trim();
    // In dementia mode, frame the message so AI knows to talk to the person, not the caregiver
    const apiContent = memoryDementiaMode
      ? `[The caregiver reports: ${rawInput}]\n[Now ask ${memorySubjectName.trim()} ONE follow-up question. Talk TO ${memorySubjectName.trim()} directly. Under 20 words total.]`
      : rawInput;
    // Display shows raw caregiver text, API gets framed version
    const displayMsg = { role: 'user', content: rawInput };
    const apiMsg = { role: 'user', content: apiContent };
    const updatedDisplay = [...memoryMessages, displayMsg];
    // Build API messages: replace display messages with API-framed ones where needed
    const updatedApi = [...memoryMessages, apiMsg];
    setMemoryMessages(updatedDisplay);
    setMemoryInput('');
    setMemoryLoading(true);
    const prompt = memoryActivePrompt || memoryTopic.prompt.replace(/\[name\]/g, memorySubjectName.trim());
    try {
      const data = await callAi({ action: 'follow_up', messages: updatedApi, roughDraft: '', systemPrompt: prompt });
      const reply = data.text || '';
      if (reply) {
        setMemoryMessages([...updatedDisplay, { role: 'assistant', content: reply }]);
      } else {
        setMemoryMessages([...updatedDisplay, { role: 'assistant', content: `${memorySubjectName.trim()}, tell me more about that.` }]);
      }
    } catch (err) {
      console.error('Memory Care AI error:', err);
      setMemoryMessages([...updatedDisplay, { role: 'assistant', content: `That's wonderful. ${memorySubjectName.trim()}, what else do you remember?` }]);
    }
    finally { setMemoryLoading(false); }
  };

  const generateMemoryStory = async () => {
    setMemoryLoading(true);
    const transcript = memoryMessages
      .filter(m => m.role === 'user' && !m.content.startsWith('Please begin') && !m.content.startsWith('Sitting with') && !m.content.startsWith('We\'re sitting with'))
      .map((m, i) => `Answer ${i + 1}: ${m.content}`)
      .join('\n');
    
    const caregiverName = memoryCarerName.trim() || accountName || 'the caregiver';

    // Dementia-specific story generation prompt
    const dementiaPrompt = `Turn this Memory Care interview into a short piece for ${memorySubjectName}'s family keepsake book.

UNDERSTAND WHAT YOU'RE READING:
- A caregiver named ${caregiverName} sat with ${memorySubjectName}, who has memory loss, and typed what they said
- ${caregiverName} may have added observations like "she smiled" or "he got quiet" or "she seemed to remember this clearly" — these are precious context
- ${memorySubjectName} may have only shared fragments. That is normal and expected.
- ${memorySubjectName} may have repeated things. That means it matters to them.
- ${memorySubjectName} may have said things that contradict each other or aren't factually accurate. Do NOT correct or reconcile these. Every version is true to them.
- This may be one of the last times ${memorySubjectName} can share these memories.

WRITING THE STORY:
- Write in first person, in ${memorySubjectName}'s voice
- Use their EXACT words wherever possible. If they said "Daddy fixed cars," write "Daddy fixed cars" — never "My father was a mechanic."
- If they only shared 2-3 sentences, the story is 2-3 sentences. A short story from someone with memory loss is not incomplete — it is whole.
- If they shared more on a good day, the story can be longer, up to 250 words
- Connect fragments gently where it makes sense: "Mama made biscuits" and "big kitchen" can become "Mama made biscuits in that big kitchen." But don't force connections that aren't there.
- If they repeated something, you may include it once — but understand that repetition signals deep importance. Let that weight come through.

CAREGIVER OBSERVATIONS:
- ${caregiverName} may have noted things like "she smiled," "he laughed," "she got quiet," "he teared up," or "she seemed really clear today"
- Do NOT weave these into the story itself — the story is only ${memorySubjectName}'s words
- Instead, if there are meaningful observations, add ONE brief line after the story in italics, attributed to the caregiver:
  *A note from ${caregiverName}: She was smiling when she shared this.*
  *A note from ${caregiverName}: He got very quiet after talking about his brother.*
- Only include this if ${caregiverName} actually noted something. Do not invent it.
- If there are no caregiver observations, skip this entirely.

DO NOT:
- Invent ANY details, memories, scenes, or emotions they didn't express
- Expand fragments into full scenes. "Big house" does not become a paragraph about the house. It stays "big house" unless they said more.
- Add literary language, metaphors, or poetic flourishes
- Smooth over confusion or contradiction — if they said two different things, include both or pick the one they seemed most connected to
- Write more than they gave you. Padding dishonors what they shared.
- Use the word "dementia," "memory loss," "Alzheimer's," or any clinical language in the story itself

TITLE:
- Generate a specific, evocative chapter title based on what was actually discussed — not a generic label
- Use their own words: "Daddy Fixed Cars" not "A Father's Legacy"
- Good examples: "The White House on Maple Street", "How Tommy Always Made Her Laugh", "Mama's Biscuits on Sunday Morning"
- Bad examples: "Childhood Memories", "Family Stories", "Mom's Interview"
- The title should feel like a chapter in a published memoir
- Under 8 words
- Line 1

OUTPUT RULES:
- Output ONLY the title on line 1, then the story, then optionally the caregiver note. Nothing else.
- No notes (other than the caregiver line), commentary, explanations, or metadata
- No markdown, headers, or formatting
- This goes directly into a printed book that ${memorySubjectName}'s family will keep forever

The goal: ${memorySubjectName}'s family opens this book and for a moment, they hear ${memorySubjectName} talking to them again.

Interview transcript:
${transcript}`;

    // Regular interview story generation prompt
    const regularPrompt = `Turn this interview into a first-person story in ${memorySubjectName}'s voice for their printed keepsake book.

VOICE RULES — THIS IS CRITICAL:
- Use THEIR actual words and phrases as much as possible — restructure, don't rewrite
- If they said "we were dirt poor," keep "we were dirt poor" — don't change it to "we had very little"
- Match their vocabulary level. If they speak simply, write simply. If they're eloquent, match that.
- Do NOT add emotions, thoughts, or details they didn't express. If they didn't say they felt nervous, don't write "I felt nervous."
- Do NOT add literary flourishes, metaphors, or poetic language unless THEY used those words
- Keep their specific names, places, and details exactly as stated
- It's OK to fix grammar, connect ideas, and add paragraph breaks
- It's OK to reorder sentences so the story flows chronologically
- Aim for 200-400 words. Shorter is better than padded.
- Start with a specific, evocative title on line 1 based on what was actually discussed — not a generic label
- Good examples: "The White House on Maple Street", "How Tommy Always Made Her Laugh", "The Summer She Learned to Drive"
- Bad examples: "Childhood Memories", "Family Stories", "Mom's Interview"
- The title should feel like a chapter in a published memoir

OUTPUT RULES:
- Output ONLY the title on line 1, then the story. Nothing else.
- Do NOT include any notes, commentary, explanations, or metadata
- Do NOT write things like "Note:", "I kept...", "Here's...", etc.
- Do NOT include markdown headers (##), horizontal rules (---), or formatting markers
- The output goes DIRECTLY into a printed book — treat it like final copy

The goal: their family reads this and says "that sounds exactly like them."

Interview transcript:
${transcript}`;

    try {
      const data = await callAi({
        action: 'expand',
        messages: memoryMessages,
        roughDraft: memoryDementiaMode ? dementiaPrompt : regularPrompt,
      });
      const storyText = data.text || '';
      // Extract title from first line if it looks like a title
      const lines = storyText.split('\n').filter(l => l.trim());
      const firstLine = lines[0]?.trim() || '';
      if (firstLine.length < 80 && !firstLine.endsWith('.') && lines.length > 1) {
        setMemoryTitle(firstLine);
        setMemoryStory(lines.slice(1).join('\n').trim());
      } else {
        setMemoryTitle(`${memorySubjectName}'s Story`);
        setMemoryStory(storyText);
      }
      setMemoryStep('review');
    } catch (err) { console.error(err); }
    finally { setMemoryLoading(false); }
  };

  const handleShapeIntoChapter = async () => {
    if (mcShaping) return;
    setMcShaping(true);
    setMcOriginalTranscript(memoryStory);
    try {
      await generateMemoryStory();
    } finally {
      setMcShaping(false);
    }
  };

  const handleRestoreTranscript = () => {
    if (mcOriginalTranscript) {
      setMemoryStory(mcOriginalTranscript);
      setMcOriginalTranscript(null);
    }
  };

  const saveMemoryInterview = async () => {
    if (!memoryTitle.trim() || !memoryStory.trim()) return;
    setSavingMemory(true);
    try {
      // Save audio/video first to get the path
      let mediaPath = null;
      let isVideo = false;
      if (interviewVideoBlob || interviewAudioBlob) {
        const result = await saveInterviewMedia(`Interview — ${memorySubjectName} — ${memoryTopic?.label || 'Interview'}`);
        if (result) { mediaPath = result.path; isVideo = result.isVideo; }
        setInterviewAudioBlob(null); setInterviewAudioUrl('');
        setInterviewVideoBlob(null); setInterviewVideoUrl('');
      }
      let content = `[Memory Interview — ${memoryTopic?.label || 'Interview'}]\n\nInterviewed by: ${memoryCarerName || 'Family member'}\nDate: ${new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}\n\n${memoryStory.trim()}`;
      if (mediaPath) { content += `\n\n[${isVideo ? 'video' : 'audio'}:${mediaPath}]`; }
      const result = await client.graphql({
        query: createLegacyEntry,
        variables: { input: {
          userId: user.userId || user.username,
          title: memoryTitle.trim(),
          content,
          type: 'memory_interview',
          createdAt: new Date().toISOString()
        }}
      });
      const newStoryId = result.data?.createLegacyEntry?.id;
      // Auto-add to book — use mcTargetBookId if set, otherwise active book
      const targetId = mcTargetBookId || activeBookId;
      const targetBook = (booksRef.current || books).find(b => b.id === targetId);
      let savedPages = targetId === activeBookId ? stBookPages : (targetBook?.pages || []);
      if (newStoryId) {
        let photoPath = null, photoUrl = null, photoBase64 = null;
        if (memoryPhotoFile) {
          try {
            const file = memoryPhotoFile;
            const ext = file.name.split('.').pop() || 'jpg';
            const fileName = `book_photo_${Date.now()}.${ext}`;
            const pathBuilder = ({ identityId }) => `private/${identityId}/photos/${fileName}`;
            const uploadResult = await (uploadData({ path: pathBuilder, data: file, options: { contentType: file.type, cacheControl: 'public, max-age=31536000, immutable' } })).result;
            photoPath = uploadResult?.path || null;
            if (photoPath) {
              const urlResult = await getUrl({ path: photoPath });
              photoUrl = urlResult?.url?.toString() || null;
              photoBase64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
            }
          } catch (photoErr) { console.warn('[Photo Upload] Failed during save:', photoErr.message); }
        }
        // Persist photoPath on the story itself
        if (photoPath) {
          try { await client.graphql({ query: updateLegacyEntry, variables: { input: { id: newStoryId, photoPath } } }); } catch (e) { console.warn('Failed to save photoPath on story:', e); }
        }
        const newPage = { storyId: newStoryId, photoUrl, photoPath, photoBase64, caption: '', photoSize: 'large', layout: photoPath ? 'photo-spread' : 'classic' };
        savedPages = [...savedPages, newPage];
        if (targetId === activeBookId) {
          setStBookPages(savedPages);
          const bookData = { title: stBookTitle, subtitle: stBookSubtitle, author: stBookAuthor, dedication: stBookDedication, coverStyle: stBookCover, coverDesign: stBookCoverDesign, coverPhotoPath: stBookCoverPhotoPath, coverPhotoY: stBookCoverPhotoY, coverPhotoOrientation: stBookCoverPhotoOrientation, coverPhotoTone: stBookCoverPhotoTone, pages: cleanPagesForSave(savedPages) };
          saveBookData(bookData);
        } else if (targetBook) {
          const updatedBooks = (booksRef.current || books).map(b => b.id === targetId ? { ...b, pages: cleanPagesForSave(savedPages) } : b);
          persistBooks(updatedBooks);
        }
      }
      setMcTargetBookId(null);
      await refreshStories();
      // Auto-link QR code when media exists (Memory Care recordings are irreplaceable)
      if (mediaPath && newStoryId) {
        await autoLinkRecordingToQr(newStoryId, mediaPath, memoryTitle.trim(), savedPages, isVideo ? 'VIDEO' : 'AUDIO');
      }
      setMemoryStep('saved');
      // Fire-and-forget: extract themes & mentions to enrich subject profile
      const currentSubjectId = memoryCareSubjects.find(s => s.name === memorySubjectName)?.id;
      if (currentSubjectId) {
        extractSessionThemes(memoryMessages, currentSubjectId);
      }
    } catch (err) { showToast('Something went wrong saving your work. Please try again — your work is not lost.', 'error'); }
    finally { setSavingMemory(false); }
  };

  const resetMemoryInterview = () => {
    setMemoryStep('intro');
    setMemoryTopic(null);
    setMemorySubjectName('');
    setMemoryCarerName('');
    setMemoryMessages([]);
    setMemoryInput('');
    setMemoryStory('');
    setMemoryTitle('');
    setMemoryPhotoFile(null);
    setMemoryPhotoPreview(null);
    setInterviewAudioBlob(null);
    setInterviewAudioUrl('');
    setMcTargetBookId(null);
    setMcOriginalTranscript(null);
    if (interviewRecorderRef.current && interviewRecording) { try { interviewRecorderRef.current.stop(); } catch(e) {} }
    setInterviewRecording(false);
  };

  // ── Video Messages ─────────────────────────────────────────────
  const VIDEO_MAX_FILES = 10;
  const VIDEO_MAX_FILE_BYTES = 500 * 1024 * 1024;   // 500MB per file
  const VIDEO_MAX_TOTAL_BYTES = 2 * 1024 * 1024 * 1024; // 2GB total

  const [videoFiles, setVideoFiles] = React.useState([]);
  const [videoLoading, setVideoLoading] = React.useState(false);

  // ── Voice Memos ──
  const [audioFiles, setAudioFiles] = React.useState([]);
  const audioPlayerRef = React.useRef(null);

  // Derived storage stats
  const videoTotalBytes = videoFiles.reduce((sum, f) => sum + (f.size || 0), 0);

  const formatBytes = (bytes) => {
    if (bytes >= 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    if (bytes >= 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(0)} MB`;
    return `${Math.round(bytes / 1024)} KB`;
  };

  const refreshVideoFiles = async () => {
    setVideoLoading(true);
    try {
      const result = await list({ path: ({ identityId }) => `private/${identityId}/videos/` });
      setVideoFiles(result.items.filter(f => !f.path.endsWith('/')));
    } catch (err) { console.error('Video list error:', err); }
    finally { setVideoLoading(false); }
  };

  React.useEffect(() => { refreshVideoFiles(); }, []);







  // ── Voice Memo Handlers ──
  const refreshAudioFiles = async () => {
    try {
      const result = await list({ path: ({ identityId }) => `private/${identityId}/audio/`, options: { listAll: true } });
      setAudioFiles(result.items || []);
    } catch (err) { console.error('Load audio error:', err); }
  };

  React.useEffect(() => { refreshAudioFiles(); }, []);






  // ── Voice/Video Recording for Write My Story ──
  const [storyRecording, setStoryRecording] = React.useState(false);
  const [storyAudioBlob, setStoryAudioBlob] = React.useState(null);
  const [storyAudioUrl, setStoryAudioUrl] = React.useState('');
  const [storyVideoMode, setStoryVideoMode] = React.useState(false);
  const [storyVideoBlob, setStoryVideoBlob] = React.useState(null);
  const [storyVideoUrl, setStoryVideoUrl] = React.useState('');
  const storyRecorderRef = React.useRef(null);
  const storyAudioChunksRef = React.useRef([]);
  const storyAudioStreamRef = React.useRef(null);
  const storyVideoRecorderRef = React.useRef(null);
  const storyVideoChunksRef = React.useRef([]);
  const storyLiveVideoRef = React.useRef(null);
  const storyAutoStopRef = React.useRef(null);

  const startStoryRecording = async () => {
    try {
      if (storyVideoMode) {
        // Video mode: capture video+audio, dual recorders
        const stream = await navigator.mediaDevices.getUserMedia({ video: true, audio: true });
        storyAudioStreamRef.current = stream;
        if (storyLiveVideoRef.current) { storyLiveVideoRef.current.srcObject = stream; storyLiveVideoRef.current.play(); }
        // Video recorder (for S3 storage + QR playback)
        // TB-103: prefer native format (MP4 on Safari) so recordings are playable without transcode.
        storyVideoChunksRef.current = [];
        const videoRec = new MediaRecorder(stream, { mimeType: preferredVideoMime, audioBitsPerSecond: 48000 });
        videoRec.ondataavailable = (e) => { if (e.data.size > 0) storyVideoChunksRef.current.push(e.data); };
        videoRec.onstop = () => {
          const blob = new Blob(storyVideoChunksRef.current, { type: preferredVideoContentType });
          setStoryVideoBlob(blob);
          setStoryVideoUrl(URL.createObjectURL(blob));
        };
        storyVideoRecorderRef.current = videoRec;
        // Audio-only recorder (for Whisper transcription — keeps file small)
        const audioStream = new MediaStream(stream.getAudioTracks());
        storyAudioChunksRef.current = [];
        const audioRec = new MediaRecorder(audioStream, { mimeType: preferredAudioMime, audioBitsPerSecond: 48000 });
        audioRec.ondataavailable = (e) => { if (e.data.size > 0) storyAudioChunksRef.current.push(e.data); };
        audioRec.onstop = () => {
          const blob = new Blob(storyAudioChunksRef.current, { type: preferredAudioContentType });
          setStoryAudioBlob(blob);
          setStoryAudioUrl(URL.createObjectURL(blob));
          stream.getTracks().forEach(t => t.stop());
        };
        storyRecorderRef.current = audioRec;
        videoRec.start(100);
        audioRec.start(250);
      } else {
        // Audio-only mode (existing behavior)
        const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
        storyAudioStreamRef.current = stream;
        storyAudioChunksRef.current = [];
        const recorder = new MediaRecorder(stream, { mimeType: preferredAudioMime, audioBitsPerSecond: 48000 });
        recorder.ondataavailable = (e) => { if (e.data.size > 0) storyAudioChunksRef.current.push(e.data); };
        recorder.onstop = () => {
          const blob = new Blob(storyAudioChunksRef.current, { type: preferredAudioContentType });
          setStoryAudioBlob(blob);
          setStoryAudioUrl(URL.createObjectURL(blob));
          stream.getTracks().forEach(t => t.stop());
        };
        storyRecorderRef.current = recorder;
        recorder.start(250);
      }
      setStoryRecording(true);
      setStAutoTranscribeTriggered(false);
      storyAutoStopRef.current = setTimeout(() => stopStoryRecording(), 20 * 60 * 1000);
    } catch (err) {
      showToast(storyVideoMode
        ? 'Could not access camera. Please allow camera access and try again.'
        : 'Could not access microphone. Please allow microphone access.', 'error');
    }
  };

  const stopStoryRecording = () => {
    if (storyAutoStopRef.current) { clearTimeout(storyAutoStopRef.current); storyAutoStopRef.current = null; }
    if (storyVideoRecorderRef.current) {
      storyVideoRecorderRef.current.stop();
      storyVideoRecorderRef.current = null;
    }
    if (storyRecorderRef.current && storyRecording) {
      storyRecorderRef.current.stop();
      setStoryRecording(false);
    }
  };


  // ── Whisper Transcription ──
  const [transcribing, setTranscribing] = React.useState(false);

  const blobToBase64 = async (blob) => {
    const arrayBuffer = await blob.arrayBuffer();
    const bytes = new Uint8Array(arrayBuffer);
    let binary = '';
    const chunkSize = 8192;
    for (let i = 0; i < bytes.length; i += chunkSize) {
      binary += String.fromCharCode.apply(null, bytes.subarray(i, i + chunkSize));
    }
    return btoa(binary);
  };

  const splitChunksForTranscription = (rawChunks, maxBytes = 3_670_016) => {
    if (!rawChunks || rawChunks.length === 0) return [];
    const headerChunk = rawChunks[0];
    const segments = [];
    let currentSegment = [headerChunk];
    let currentSize = headerChunk.size;
    for (let i = 1; i < rawChunks.length; i++) {
      if (currentSize + rawChunks[i].size > maxBytes && currentSegment.length > 1) {
        segments.push(new Blob(currentSegment, { type: 'audio/webm' }));
        currentSegment = [headerChunk];
        currentSize = headerChunk.size;
      }
      currentSegment.push(rawChunks[i]);
      currentSize += rawChunks[i].size;
    }
    if (currentSegment.length > 0) {
      segments.push(new Blob(currentSegment, { type: 'audio/webm' }));
    }
    return segments;
  };

  const transcribeSingleBlob = async (blob) => {
    const base64 = await blobToBase64(blob);
    const data = await callAi({ action: 'transcribe', audioBase64: base64, filename: 'recording.webm' });
    return (data.text || '').trim();
  };

  const transcribeAudio = async (audioBlob, rawChunks = []) => {
    setTranscribing(true);
    try {
      let segments;
      if (audioBlob.size > 3_670_016 && rawChunks.length > 1) {
        segments = splitChunksForTranscription(rawChunks);
      } else {
        segments = [audioBlob];
      }

      const results = [];
      for (const segment of segments) {
        const text = await transcribeSingleBlob(segment);
        if (text) results.push(text);
      }

      return results.join(' ').trim();
    } catch (err) {
      console.error('Transcription error:', err);
      showToast('We had trouble transcribing your recording. Please try again or try a shorter recording.', 'error');
      return '';
    } finally {
      setTranscribing(false);
    }
  };

  const handleCapsuleAIStart = async () => {
    if (!capsuleType) { showToast('Please choose a message type first.', 'error'); return; }
    setCapsuleAiLoading(true);
    setCapsuleAiStarted(true);
    const systemPrompt = capsuleType.prompt;
    const messages = [{ role: 'user', content: systemPrompt }];
    setCapsuleAiChat(messages);
    try {
      const data = await callAi({ action: 'follow_up', messages, roughDraft: '' });
      const reply = data.text || 'Tell me a little about who this message is for.';
      setCapsuleAiChat([...messages, { role: 'assistant', content: reply }]);
    } catch { setCapsuleAiChat([...messages, { role: 'assistant', content: 'Tell me a little about who this message is for and what you want them to know.' }]); }
    finally { setCapsuleAiLoading(false); }
  };

  const handleCapsuleAIReply = async (userText) => {
    if (!userText.trim()) return;
    const updated = [...capsuleAiChat, { role: 'user', content: userText }];
    setCapsuleAiChat(updated);
    setCapsuleDraft('');
    setCapsuleAiLoading(true);
    try {
      const data = await callAi({ action: 'follow_up', messages: updated, roughDraft: '' });
      setCapsuleAiChat([...updated, { role: 'assistant', content: data.text || '' }]);
    } catch (err) { showToast('The writing assistant hit a snag. Please try again in a moment.', 'error'); }
    finally { setCapsuleAiLoading(false); }
  };

  const handleCapsuleExpand = async () => {
    if (!capsuleDraft.trim() && capsuleAiChat.length === 0) { showToast('Write something or have a conversation with AI first.', 'error'); return; }
    setCapsuleAiLoading(true);
    const context = capsuleAiChat.map(m => `${m.role === 'user' ? 'Me' : 'Assistant'}: ${m.content}`).join('\n\n');
    const roughDraft = capsuleDraft || context;
    try {
      const data = await callAi({ action: 'expand', messages: capsuleAiChat, roughDraft });
      setCapsuleDraft(data.text || '');
    } catch (err) { showToast('The writing assistant hit a snag. Please try again in a moment.', 'error'); }
    finally { setCapsuleAiLoading(false); }
  };

  const handleSaveCapsule = async () => {
    if (!capsuleTitle.trim()) { showToast('Please give this message a title.', 'error'); return; }
    if (!capsuleDraft.trim()) { showToast('Please write your message first.', 'error'); return; }
    const recipient = recipients.find(r => r.id === capsuleRecipientId);
    const recipientName = recipient?.name || 'Unspecified';
    const recipientEmail = recipient?.email || '';

    // Upload video if recorded
    let videoPath = '';
    if (capsuleVideoBlob) {
      try {
        const safeName = `${Date.now()}_capsule_${capsuleTitle.trim().replace(/[^a-zA-Z0-9]/g, '_')}.webm`;
        await uploadData({
          path: ({ identityId }) => `private/${identityId}/videos/${safeName}`,
          data: capsuleVideoBlob,
          options: { contentType: 'video/webm', metadata: { title: capsuleTitle.trim(), type: 'capsule' } }
        });
        videoPath = safeName;
        await refreshVideoFiles();
      } catch (err) { console.error('Video upload failed:', err); }
    }

    const newCapsule = {
      id: Date.now().toString(), title: capsuleTitle, type: capsuleType?.id, typeLabel: capsuleType?.label,
      typeIcon: capsuleType?.icon, content: capsuleDraft, recipientName, recipientEmail,
      delivery: capsuleDelivery, deliveryDate: capsuleDate, createdAt: new Date().toISOString(),
      status: 'sealed', videoPath, releaseMethod: capsuleReleaseMethod,
    };
    saveCapsules([...capsules, newCapsule]);
    setCapsuleType(null); setCapsuleDraft(''); setCapsuleAiChat([]); setCapsuleTitle('');
    setCapsuleDate(''); setCapsuleDelivery('later'); setCapsuleRecipientId('');
    setCapsuleAiStarted(false); setCapsuleVideoBlob(null); setCapsuleVideoUrl('');
    setCapsuleReleaseMethod('trusted');
    showToast('Time capsule message sealed and saved!', 'success');
  };

  const handleDeleteCapsule = (id) => {
    setConfirmDeleteCapsuleId(id);
  };

  const handleEditCapsule = (c) => {
    setEditingCapsuleId(c.id);
    setEditCapsuleTitle(c.title);
    setEditCapsuleContent(c.content);
    setEditCapsuleDelivery(c.delivery || 'later');
    setEditCapsuleDate(c.deliveryDate || '');
    setEditCapsuleRecipientId(c.recipientId || '');
  };

  const handleSaveCapsuleEdit = () => {
    if (!editCapsuleTitle.trim() || !editCapsuleContent.trim()) { showToast('Title and message cannot be empty.', 'error'); return; }
    const recipient = recipients.find(r => r.id === editCapsuleRecipientId);
    const updated = capsules.map(c => c.id === editingCapsuleId ? {
      ...c,
      title: editCapsuleTitle.trim(),
      content: editCapsuleContent.trim(),
      delivery: editCapsuleDelivery,
      deliveryDate: editCapsuleDate,
      recipientId: editCapsuleRecipientId,
      recipientName: recipient?.name || c.recipientName,
      recipientEmail: recipient?.email || c.recipientEmail,
    } : c);
    saveCapsules(updated);
    setEditingCapsuleId(null);
  };

  const handleCancelCapsuleEdit = () => setEditingCapsuleId(null);

  const [capsuleReply, setCapsuleReply] = React.useState('');

  // renderCapsule — extracted to pages/TimeCapsulePage.js

  // Share Modal
  const ShareModal = () => {
    if (!showShareModal || !shareModalItem) return null;
    const itemShares = getItemShares(shareModalItem.id);
    return (
      <div data-journey-modal="share" style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.75)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' }}>
        <div style={{ background: '#fbf8f1', border: '1px solid rgba(15,23,42,0.25)', borderRadius: '14px', padding: '36px', maxWidth: '540px', width: '100%' }}>
          <h3 style={{ fontFamily: "'Lora', serif", color: '#0f172a', letterSpacing: '2px', marginBottom: '8px', fontSize: '1rem', textTransform: 'uppercase' }}>Share This Item</h3>
          <p style={{ color: '#0f172a', fontSize: '1.15rem', marginBottom: '6px' }}>{shareModalItem.title}</p>
          <p style={{ color: '#3c4653', fontSize: '1.05rem', fontStyle: 'italic', marginBottom: '28px' }}>Choose who can see this and when they receive it.</p>

          {recipients.length === 0 ? (
            <p style={{ color: '#0f172a', fontSize: '1.05rem', fontStyle: 'italic', marginBottom: '20px' }}>
              No recipients added yet. Go to My Recipients to add people first.
            </p>
          ) : (
            recipients.map(r => {
              const share = itemShares[r.id];
              return (
                <div key={r.id} style={{ padding: '16px 20px', marginBottom: '12px', background: '#ffffff', border: '1px solid rgba(15,23,42,0.1)', borderRadius: '10px' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                    <div>
                      <div style={{ color: '#0f172a', fontSize: '1.1rem', fontWeight: 500 }}>{r.name}</div>
                      <div style={{ color: '#3c4653', fontSize: '1.05rem' }}>{r.relationship} · {r.email}</div>
                    </div>
                    {(r.accessLevel === 'full' || r.isPrimary) && <span style={{ background: 'rgba(15,23,42,0.6)', color: '#0f172a', border: '1px solid rgba(15,23,42,0.7)', borderRadius: '20px', padding: '3px 12px', fontSize: '1rem', fontFamily: "'Lora', serif", letterSpacing: '1px' }}>FULL ACCESS</span>}
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      onClick={() => toggleShare(shareModalItem.id, r.id, 'now')}
                      style={{ flex: 1, padding: '10px', borderRadius: '7px', cursor: 'pointer', fontSize: '1.05rem', fontFamily: "'Lora', serif", letterSpacing: '1px', transition: 'all 0.2s',
                        background: share?.timing === 'now' ? 'rgba(39,174,96,0.25)' : 'rgba(15,23,42,0.04)',
                        border: share?.timing === 'now' ? '1px solid rgba(39,174,96,0.6)' : '1px solid rgba(15,23,42,0.08)',
                        color: share?.timing === 'now' ? '#217a3e' : '#7a6b5a',
                      }}>
                      {share?.timing === 'now' ? '✓ Shared Now' : ' Share Now'}
                    </button>
                    <button
                      onClick={() => toggleShare(shareModalItem.id, r.id, 'later')}
                      style={{ flex: 1, padding: '10px', borderRadius: '7px', cursor: 'pointer', fontSize: '1.05rem', fontFamily: "'Lora', serif", letterSpacing: '1px', transition: 'all 0.2s',
                        background: share?.timing === 'later' ? 'rgba(15,23,42,0.25)' : 'rgba(15,23,42,0.04)',
                        border: share?.timing === 'later' ? '1px solid rgba(15,23,42,0.25)' : '1px solid rgba(15,23,42,0.08)',
                        color: share?.timing === 'later' ? '#0f172a' : '#7a6b5a',
                      }}>
                      {share?.timing === 'later' ? '✓ After I\'m Gone' : ' After I\'m Gone'}
                    </button>
                  </div>
                </div>
              );
            })
          )}

          <div style={{ display: 'flex', gap: '12px', marginTop: '24px' }}>
            <button className="btn btn-primary" onClick={() => setShowShareModal(false)} style={{ flex: 1 }}>Done</button>
            {recipients.length === 0 && <button className="btn btn-ghost" onClick={() => { setShowShareModal(false); setActiveTab('recipients'); }}>Add Recipients</button>}
          </div>
        </div>
      </div>
    );
  };

  // renderRecipients — extracted to pages/RecipientsPage.js

  // renderVideoMessages — removed (dead code)


  const TOP_10 = [
    "What's a moment from your childhood that you can still feel when you close your eyes?",
    "Who loved you in a way that shaped everything — and how would you describe it to someone who never met them?",
    "What's something you almost did, or almost didn't do, that changed the direction of your entire life?",
    "Is there something you've carried for a long time that you've never told anyone?",
    "What's the hardest thing you've survived — and what did it teach you about who you are?",
    "If your children or grandchildren could only know one story about you, which one would matter most?",
    "What did your parents get right? What do you wish they'd done differently?",
    "Describe the moment you knew you loved someone — not the person, but the exact moment it hit you.",
    "What do you believe about life that took you decades to understand?",
    "What do you most want the people you love to know after you're no longer here to say it?",
  ];

  const renderStoryContent = (content) => {
    // F-207: the display text-prep now lives in ONE shared, exported, testable place
    // (src/utils/storyText.js) and applies the SAME punctuation repair the print path uses. The local
    // that used to be declared here was named `cleanContent`, which SHADOWED the imported repair by the
    // same name — so this path silently did no repair and RemnantBookPDF.commaSpacing.test.js passed
    // while the read view showed "it,I would say". Renamed `displayText` so it can never shadow again.
    const displayText = prepareStoryDisplayText(content);
    if (displayText.startsWith('Question: ')) {
      const splitAt = displayText.indexOf('\n\n');
      const question = displayText.substring('Question: '.length, splitAt);
      const answer = displayText.substring(splitAt + 2);
      return (
        <span>
          <span style={{ display: 'block', color: '#0f172a', fontStyle: 'italic', fontSize: '1.05rem', marginBottom: '12px', lineHeight: 1.6 }}>
            "{question}"
          </span>
          {formatMarkdown(answer)}
        </span>
      );
    }
    return formatMarkdown(displayText);
  };

  const formatMarkdown = (text) => {
    if (!text) return text;
    const lines = text.split('\n');
    const elements = [];
    let key = 0;
    for (const line of lines) {
      if (line.startsWith('# ')) {
        elements.push(<h2 key={key++} style={{ fontFamily: "'Lora', serif", fontSize: '1.4rem', color: '#0f172a', margin: '20px 0 10px', fontWeight: 400 }}>{line.slice(2)}</h2>);
      } else if (line.startsWith('## ')) {
        elements.push(<h3 key={key++} style={{ fontFamily: "'Lora', serif", fontSize: '1.2rem', color: '#0f172a', margin: '16px 0 8px', fontWeight: 400 }}>{line.slice(3)}</h3>);
      } else if (line.startsWith('### ')) {
        elements.push(<h4 key={key++} style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', color: '#0f172a', margin: '14px 0 6px', fontWeight: 400 }}>{line.slice(4)}</h4>);
      } else if (line.startsWith('---')) {
        elements.push(<hr key={key++} style={{ border: 'none', borderTop: '1px solid rgba(15,23,42,0.6)', margin: '20px 0' }} />);
      } else if (line.startsWith('**') && line.endsWith('**')) {
        elements.push(<p key={key++} style={{ fontWeight: 600, color: '#0f172a', margin: '8px 0' }}>{line.slice(2, -2)}</p>);
      } else if (line.trim() === '') {
        elements.push(<br key={key++} />);
      } else {
        // Inline bold
        const parts = line.split(/(\*\*[^*]+\*\*)/g);
        const formatted = parts.map((part, i) => {
          if (part.startsWith('**') && part.endsWith('**')) {
            return <strong key={i} style={{ color: '#0f172a' }}>{part.slice(2, -2)}</strong>;
          }
          return part;
        });
        elements.push(<p key={key++} style={{ margin: '6px 0', lineHeight: 1.82 }}>{formatted}</p>);
      }
    }
    return <>{elements}</>;
  };

  // renderAI — removed (dead code)

  // renderStories — removed (dead code, replaced by Archive component)

  // ── MFA Handlers ──
  React.useEffect(() => {
    const checkMfa = async () => {
      try {
        const pref = await fetchMFAPreference();
        setMfaEnabled(pref?.preferred === 'TOTP' || (pref?.enabled && pref.enabled.includes('TOTP')));
      } catch (e) { console.log('MFA check:', e); }
      setMfaLoading(false);
    };
    checkMfa();
  }, []);

  const handleMfaSetup = async () => {
    setMfaError('');
    try {
      const totpSetup = await setUpTOTP();
      const uri = totpSetup.getSetupUri('Talebound');
      setMfaSetupUri(uri.toString());
      setMfaStep('setup');
    } catch (e) { setMfaError('Something went wrong setting up two-factor authentication. Please try again.'); }
  };

  const handleMfaVerify = async () => {
    setMfaError('');
    try {
      await verifyTOTPSetup({ code: mfaVerifyCode });
      await updateMFAPreference({ totp: 'PREFERRED' });
      setMfaEnabled(true);
      setMfaStep('done');
    } catch (e) { setMfaError('Verification failed — make sure you entered the latest 6-digit code from your app.'); }
  };

  const handleMfaDisable = async () => {
    setMfaError('');
    try {
      await updateMFAPreference({ totp: 'DISABLED' });
      setMfaEnabled(false);
      setMfaStep('idle');
    } catch (e) { setMfaError('Something went wrong disabling two-factor authentication. Please try again.'); }
  };

  // renderAccount — extracted to pages/AccountPage.js

  // renderLegacyVault — removed (dead code, replaced by Archive component)
  // renderVault — removed (dead code, replaced by Archive component)
  // ═══════════════════════════════════════════════════════════
  //  STORYTELLER MODE — Book Builder focused experience
  // ═══════════════════════════════════════════════════════════
  // Storyteller prompts (ST_PROMPTS, AI_Q_CATEGORIES imported from constants/prompts)
  const [stShuffleIdx, setStShuffleIdx] = React.useState(0);
  const [stAiQuestion, setStAiQuestion] = React.useState(null);
  const [stAiQuestionLoading, setStAiQuestionLoading] = React.useState(false);
  const [stAiQuestionHistory, setStAiQuestionHistory] = React.useState([]);
  const [mothersAudience, setMothersAudience] = React.useState(null); // 'mom' | 'partner' | null
  const stStoryList = stories.filter(s => s.type === 'story' || !s.type);

  // Generate a fresh AI question
  const generateAiQuestion = async () => {
    setStAiQuestionLoading(true);
    try {
      const occasion = isGiftBook && activeBook?.occasion;
      const occasionType = occasion && OCCASION_TYPES.find(o => o.id === occasion);

      // Build the full list of asked questions (seed prompts + AI-generated)
      const isPartner = occasion === 'MOTHERS_DAY' && mothersAudience === 'partner';
      const promptKey = isPartner ? 'MOTHERS_DAY_PARTNER' : occasion;
      const seedPrompts = occasion
        ? (CELEBRATION_PROMPTS[promptKey] || CELEBRATION_PROMPTS[occasion] || CELEBRATION_PROMPTS.OTHER).map(p => p.text)
        : ST_PROMPTS.map(p => p.text);
      const askedQuestions = [...seedPrompts, ...stAiQuestionHistory];

      let userContent, systemPrompt;

      if (occasionType) {
        // Gift book — occasion-aware prompt
        const recipientName = activeBook.recipientName || 'them';
        const isMotherPartner = isPartner;
        const relationship = isMotherPartner ? 'wife as a mother' : occasionType.label === "Mother's Day" ? 'mom' : occasionType.label === "Father's Day" ? 'dad' : occasionType.label === 'Grandparents Day' ? 'grandparent' : 'loved one';
        const partnerContext = isMotherPartner ? '\nThis is a Mother\'s Day gift book written by a husband/partner about his wife as a mother — not by an adult child about their own mom. Generate a question from that perspective.' : '';

        userContent = `You are helping someone write a ${occasionType.label} keepsake book about their ${relationship}${recipientName !== 'them' ? ` (${recipientName})` : ''}.

They have already been asked these questions:
${askedQuestions.map(q => `- ${q}`).join('\n')}

Generate ONE new interview question they haven't been asked yet.
${partnerContext}
Rules:
- Fit one of these categories: portrait (who they are), story (specific memory), or legacy (what they mean)
- Sensory and specific — not vague or generic
- Warm but not cheesy
- Single question only, no explanation
- Return just the question, nothing else`;
        systemPrompt = `You generate warm, specific interview questions for a ${occasionType.label} keepsake book. Each question should evoke a vivid memory or feeling. Respond with only the question.`;
      } else {
        // Standard storyteller book — generate in the spirit of the active prompt
        // pack so "Surprise me" stays on-theme with the vibe the writer picked.
        const activePack = PROMPT_PACKS.find(p => p.id === activePackId) || PROMPT_PACKS[0];
        const usedCount = stAiQuestionHistory.length;
        const category = AI_Q_CATEGORIES[usedCount % AI_Q_CATEGORIES.length];
        const themeLine = activePack
          ? `PACK THEME: ${activePack.name} — ${activePack.blurb}\nGenerate a question that fits this vibe.`
          : `CATEGORY: ${category}`;

        const existingStories = stStoryList.map(s => s.title).filter(Boolean).join(', ');
        const storyContext = existingStories
          ? `They have already written stories about: ${existingStories}. Ask about something DIFFERENT.`
          : '';

        userContent = `Generate ONE question for someone writing a keepsake book of life stories.

${themeLine}

Rules:
- Ask about a SPECIFIC moment, scene, or person, not a general reflection
- The question should make them picture a time and place, not philosophize
- Keep it under 25 words, conversational, like a curious friend leaning in
- Make it easy for anyone aged 40-80 to answer out loud
- Do NOT use em-dashes, and do NOT use the words legacy, preserve, cherish, or treasure
- Do NOT ask about "most meaningful" or "most important" anything, too abstract
${storyContext}
- Do NOT repeat or rephrase any of these: ${askedQuestions.join(' | ')}

Reply with ONLY the question text. No quotes, no preamble.`;
        systemPrompt = 'You generate vivid, specific, warm interview questions for a keepsake book. Each question targets one concrete memory and sounds like a curious friend. Respond with only the question.';
      }

      const data = await callAi({
        action: 'follow_up',
        messages: [{ role: 'user', content: userContent }],
        roughDraft: '',
        systemPrompt,
      });
      const question = (data.text || '').replace(/^["']|["']$/g, '').trim();
      if (question) {
        setStAiQuestion({ text: question, emoji: '' });
        setStAiQuestionHistory(prev => [...prev, question]);
      }
    } catch (err) {
      console.error('AI question generation error:', err);
      showToast('Could not generate a question. Try again.', 'error');
    }
    setStAiQuestionLoading(false);
  };


  // Photo quality warning state
  const [photoQualityWarning, setPhotoQualityWarning] = React.useState(null);

  // ── Storyteller: Write Story state ──
  const [stWriteTitle, setStWriteTitle] = React.useState('');
  const [stWriteBody, setStWriteBody] = React.useState('');
  const [stWritePolishing, setStWritePolishing] = React.useState(false);
  const [stWriteSaving, setStWriteSaving] = React.useState(false);
  const [stWriteSavingPhase, setStWriteSavingPhase] = React.useState(0); // 0=initial, 1=uploading, 2=almost-there, 3=slow
  const [stWritePhotoFile, setStWritePhotoFile] = React.useState(null);
  const [stWritePhotoPreview, setStWritePhotoPreview] = React.useState(null);
  const [stPhotoEnhanceSkipped, setStPhotoEnhanceSkipped] = React.useState(false);
  const [stEnhanceAfterSave, setStEnhanceAfterSave] = React.useState(false);

  // ── Storyteller: AI Interview state ──
  const [stShowGateway, setStShowGateway] = React.useState(false); // show "how to capture" gateway
  const [stInterviewMode, setStInterviewMode] = React.useState(false); // false=simple record, true=AI interview
  const [stInterviewMessages, setStInterviewMessages] = React.useState([]);
  const [stInterviewInput, setStInterviewInput] = React.useState('');
  const [stInterviewLoading, setStInterviewLoading] = React.useState(false);
  const [stInterviewStep, setStInterviewStep] = React.useState('pick'); // 'pick' | 'conversation' | 'rapid-fire' | 'generating' | 'review' — TB-104 Stage B: 'answer'+'chat' collapsed into 'conversation'
  // TB-104 Stage B: explicit boolean replacing the fragile isWrappedUp string-scan (red-team P2-2)
  const [interviewIsWrapped, setInterviewIsWrapped] = React.useState(false);
  // TB-104 Stage 2b: rapid-fire session state (Hot Seat only)
  // rapidAnswers: { [promptIndex: number]: string } — keyed by prompt index within the active pack
  const [rapidAnswers, setRapidAnswers] = React.useState({}); // TB-104 Stage 2b
  const [rapidCurrentIdx, setRapidCurrentIdx] = React.useState(0); // TB-104 Stage 2b
  const stInterviewEndRef = React.useRef(null);
  // REVEAL-001 C5 (Tell Pip more): the savedStoryId of a reopened chapter while its reopened interview
  // is in flight. STATE (not a ref) because the UI reacts to it — the Finish guard (Guard 1) and the
  // abandon branch (Guard 2) both need it to be reactive. Set by reopenStoryForMore; cleared when the
  // session COMPLETES (the generateStInterviewStory tail, which routes on a value CAPTURED at generation
  // start — Guard 2 — so a mid-flight state change can't misroute) or is ABANDONED (nav away while still
  // at step 'conversation'). A truthy captured value routes regen to updateSavedInterviewStory (same
  // entry, no duplicate) instead of the create path.
  const [stReopenStoryId, setStReopenStoryId] = React.useState(null);

  // REVEAL-001 C2 — DEV-ONLY reveal shortcut. In development builds ONLY,
  // ?revealStory=<entryId> opens the reveal (State 1) directly for an existing
  // saved story, so the reveal can be reviewed / e2e-driven without re-saving it
  // each iteration. Hard-gated to NODE_ENV==='development' — the whole effect is a
  // no-op in a production build (CRA sets NODE_ENV=production), so it can never
  // ship. Fires once, after stories load.
  const revealShortcutRef = React.useRef(false);
  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    if (revealShortcutRef.current || !stories || stories.length === 0) return;
    let id = null;
    try { id = new URLSearchParams(window.location.search).get('revealStory'); } catch { return; }
    if (!id) return;
    const entry = stories.find(s => s.id === id);
    if (!entry) return;
    revealShortcutRef.current = true;
    setStProcessed({
      title: entry.title || 'Your story',
      content: entry.content || '',
      saved: true,
      savedStoryId: entry.id,
      // SAVE-IDENTITY-1: this DEV shortcut opens a chapter that genuinely IS in the book, so stamp a
      // matched pair rather than leaning on the legacy fallback (keeps the reveal green here).
      generationId: `dev-reveal-${entry.id}`,
      savedGenerationId: `dev-reveal-${entry.id}`,
      activeVersion: 'whole',
      fromInterview: true,
    });
    setStInterviewMode(true);
    setStInterviewStep('review');
    setActiveTab('st-record');
    console.info('[REVEAL dev] opened reveal for story', id);
  }, [stories]); // eslint-disable-line react-hooks/exhaustive-deps

  // REVEAL-001 F-39 — DEV-ONLY reveal FIXTURE hook. A test can inject a full stProcessed (with a
  // handcrafted, verifiably-distinct Flick/Whole pair) via window.__REVEAL_FIXTURE__ before the app
  // loads, so the version-switch design contract (body text DIFFERS across the switch) is proven
  // DETERMINISTICALLY — no live generation, no data-model change. Hard-gated to development.
  const revealFixtureRef = React.useRef(false);
  React.useEffect(() => {
    if (process.env.NODE_ENV !== 'development') return;
    const f = typeof window !== 'undefined' ? window.__REVEAL_FIXTURE__ : null;
    if (!f || revealFixtureRef.current) return;
    revealFixtureRef.current = true;
    // F-68: optionally inject a synthetic active book so CONTEXT-dependent reveal behavior (e.g. the
    // celebration-book discard landing) is testable deterministically. Dev-only, same gate as the fixture.
    if (f.book && f.book.id) {
      setBooks(prev => (prev.some(b => b.id === f.book.id) ? prev : [...(prev || []), f.book]));
      setActiveBookId(f.book.id);
    }
    // SAVE-IDENTITY-1: matched stamp so an injected fixture renders green without the legacy fallback.
    // Spread LAST stays authoritative — a fixture may override either field to exercise the guard.
    setStProcessed({ saved: true, activeVersion: 'whole', fromInterview: true, generationId: 'dev-fixture', savedGenerationId: 'dev-fixture', ...f });
    setStInterviewMode(true);
    setStInterviewStep('review');
    setActiveTab('st-record');
    console.info('[REVEAL dev] opened reveal FIXTURE');
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // TB-104 Stage 2a: adaptive follow-up engine state
  const [interviewFollowUpCount, setInterviewFollowUpCount] = React.useState(0);
  const [interviewAskedMoves, setInterviewAskedMoves] = React.useState([]);
  // interviewConversation: [{ role: 'user'|'assistant', content: string }]
  // normalized shape that the engine's buildFollowUpPrompt and buildAssemblerPrompt expect
  const [interviewConversation, setInterviewConversation] = React.useState([]);
  // lastClassification stored so generateStInterviewStory can pass it to routeToTier
  const [interviewLastClassification, setInterviewLastClassification] = React.useState(null);
  // emptyCount tracks consecutive EMPTY answers for re-invitation logic (spec 8.1)
  const [interviewEmptyCount, setInterviewEmptyCount] = React.useState(0);
  // TB-104 keep-going: extra follow-up budget granted when storyteller taps "Keep going" past a soft wrap
  const [interviewBudgetBonus, setInterviewBudgetBonus] = React.useState(0); // TB-104 keep-going
  // TB-104 keep-going: last stop reason — used to decide whether to show [Keep going] on wrap card
  // 'DONE-SIGNAL' wraps must NOT offer Keep going; CAP_REACHED and DIMINISHING_RETURNS may.
  const [interviewWrapReason, setInterviewWrapReason] = React.useState(null); // TB-104 keep-going
  // TB-134 fix 1: real, incrementing count of how many times a wrap has actually been shown to
  // the user this interview (every setInterviewIsWrapped(true) call). Confirmed by direct code
  // read on 2026-07-04 that neither call site into selectMove() ever passed a wrapOfferCount —
  // it silently defaulted to 0, so the engine's own wrap-offer cap (wrapAlreadyOffered) never
  // engaged in production even though it shipped in interviewEngine.js on 2026-07-02. This state
  // closes that gap; see selectMove's wrapOfferCount doc comment in src/utils/interviewEngine.js.
  const [interviewWrapOfferCount, setInterviewWrapOfferCount] = React.useState(0); // TB-134 fix 1
  // F-24 wrap engine (spoken offers + crisis gate)
  const [interviewPendingOffer, setInterviewPendingOffer] = React.useState(null); // 'normal' | 'dignity' when Pip has just spoken an offer
  const [interviewOffersMade, setInterviewOffersMade] = React.useState(0); // spoken-offer count (cap 2)
  const [interviewCrisisStopped, setInterviewCrisisStopped] = React.useState(false); // crisis gate fired: Pip asks no more questions this session
  // F-334: the persistent generation-failure banner on the conversation screen.
  // { kind: 'failure'|'too_thin', cause?: string, message: string } | null. NEVER cleared by a timer:
  // only the teller's dismiss, or their next Finish attempt, takes it down.
  const [stGenerationFailure, setStGenerationFailure] = React.useState(null);
  // F-415-STREAM: the Whole Tale's text streamed so far, rendered live on the generating screen.
  // Fed by callAiResumable's onPartial (monotonic, real model output only — see
  // STREAM-PREVIEW-NEVER-A-STORY-1). Cleared at every generation entry and on failure: a failed
  // generation takes its preview with it (F-334 — the partial is not a story and is never kept).
  const [stStreamPreview, setStStreamPreview] = React.useState('');
  // Founder walk feedback 2026-08-10 round 2: the assembler phase sat silent for 18s. The ASSEMBLER
  // is also a streamed call now, so its REAL progress — the draft's word count growing — feeds the
  // strip. Count only, never the draft text: the draft is raw material the polish rewrites, and
  // showing words the teller will never keep is the substitute shape (F-334).
  const [stDraftWords, setStDraftWords] = React.useState(0);
  const [interviewSoftCrisisAcked, setInterviewSoftCrisisAcked] = React.useState(false); // F-111 item 0: soft crisis ack shown once this session (does NOT halt)
  const [interviewViolenceGrounded, setInterviewViolenceGrounded] = React.useState(false); // F-174: third-party violence grounding shown once this session (does NOT halt)
  // TB-104 thread-salience: tracks named persons / vivid objects already deepened so the same
  // detail is not deepened twice within a single interview thread.
  const [interviewDeepenedThreads, setInterviewDeepenedThreads] = React.useState([]); // TB-104 thread-salience
  // TB-104 breadth-fix: counts consecutive zoom-in follow-ups (DEEPEN/SENSORY/SEQUENCE/FEELING)
  // without a breadth pivot. Resets to 0 on any pivot or non-zoom-in move.
  const [interviewConsecutiveZoomCount, setInterviewConsecutiveZoomCount] = React.useState(0); // TB-104 breadth-fix

  // ── Storyteller: Book AI state ──
  const [stBookAiOpen, setStBookAiOpen] = React.useState(false);
  const [stBookSaveStatus, setStBookSaveStatus] = React.useState(''); // 'saving' | 'saved' | ''
  const [stBookAiLoading, setStBookAiLoading] = React.useState(false);
  const [stBookAiResult, setStBookAiResult] = React.useState(null); // { foreword, suggestions, ordering }

  // ── Storyteller: Photo Story state ──
  const [stPhotoStoryLoading, setStPhotoStoryLoading] = React.useState(false);

  // AI Interview: start — set the prompt and go to the correct entry step.
  // TB-104 Stage B: default step changed from 'answer' to 'conversation'.
  // For Hot Seat the caller passes step='rapid-fire'; all other packs use step='conversation'.
  // TB-104 Stage 2b: step parameter added; rapid-fire state reset on every new interview.
  const startStInterview = (prompt, step = 'conversation') => { // TB-104 Stage B
    // Activation-funnel beacon: this is the single chokepoint every text interview
    // funnels through (standard conversation + rapid-fire), regardless of which
    // button or entry path called it — so logging here can't be bypassed the way
    // per-button beacons were. Fire-and-forget; voice is logged at voice_token.
    logInterviewStart();
    // REVEAL-001 C5: a fresh interview is definitionally NOT a reopened "Tell Pip more" session.
    // Defensively clear the reopen marker so a stale id (e.g. left set by a failed reopen generation
    // the user never navigated away from) can never route THIS new story's save into an existing
    // entry's update seam.
    setStReopenStoryId(null);
    setStSelectedPrompt(prompt);
    setStInterviewMode(true);
    setStInterviewStep(step);
    setStInterviewInput('');
    setStoryAudioBlob(null);
    setStoryAudioUrl('');
    setStProcessed({ title: '', content: '' });
    // TB-104 Stage 2a: reset adaptive engine state on every new interview
    setInterviewFollowUpCount(0);
    setInterviewAskedMoves([]);
    setInterviewConversation([]);
    setInterviewLastClassification(null);
    setInterviewEmptyCount(0);
    setStInterviewMessages([]);
    setInterviewIsWrapped(false); // TB-104 Stage B
    setInterviewBudgetBonus(0); // TB-104 keep-going
    setInterviewWrapReason(null); // TB-104 keep-going
    setInterviewWrapOfferCount(0); // TB-134 fix 1: fresh interview, no wraps offered yet
    setInterviewPendingOffer(null); setInterviewOffersMade(0); setInterviewCrisisStopped(false); setInterviewSoftCrisisAcked(false); setInterviewViolenceGrounded(false); // F-24 wrap engine + F-111 item 0 soft crisis + F-174 violence grounding
    setInterviewDeepenedThreads([]); // TB-104 thread-salience
    setInterviewConsecutiveZoomCount(0); // TB-104 breadth-fix
    // TB-104 Stage 2b: reset rapid-fire session state
    setRapidAnswers({});
    setRapidCurrentIdx(0);
  };

  // REVEAL-001 C5 — "Tell Pip more": reopen the interview for an ALREADY-SAVED chapter, WITHOUT
  // wiping context. Deliberately NOT startStInterview (that resets stProcessed → drops savedStoryId,
  // and empties interviewConversation → loses the chapter). Instead we SEED the engine thread with the
  // polished chapter as the already-given first answer + a warm invite, re-prime the prompt, reset only
  // the per-session engine counters, and PRESERVE savedStoryId so the regeneration persists forward-only
  // to the SAME entry (updateSavedInterviewStory) — never a duplicate. Caller-layer only; no pipeline/
  // voice file is touched. Q3: polished-chapter CONTEXT only — no raw transcript is persisted.
  const reopenStoryForMore = () => {
    const p = stProcessed;
    const id = p?.savedStoryId;
    if (!id) return;
    const activeVersion = p.activeVersion || 'whole';
    const chapterText = (p.versions?.[activeVersion]?.content) || p.content || '';
    const chapterTitle = p.title || 'Your story';
    if (!chapterText.trim()) return;

    setStReopenStoryId(id);

    // Re-prime the prompt: its text is the assembler's "question" for the seeded chapter pair, and the
    // opener bubble shows it. The chapter title anchors the reopened topic.
    setStSelectedPrompt(prev => ({ text: chapterTitle, emoji: (prev && prev.emoji) || '' }));

    // Seed the engine thread as [firstAnswer, assistantQ1] — the chapter is the already-given first
    // answer; Pip's invite is the first assistant turn. The user's first addition then flows through
    // the normal follow-up path (ivHasUserAnswer is already true), so additions are stitched by the
    // assembler with the chapter as context. The DISPLAY thread shows only Pip's invite (the chapter is
    // not echoed back at the storyteller).
    const pipInvite = "We've already got this chapter safe in your book. What would you like to add to it?";
    setInterviewConversation([
      { role: 'user', content: chapterText },
      { role: 'assistant', content: pipInvite },
    ]);
    setStInterviewMessages([{ role: 'assistant', content: pipInvite }]);

    // Fresh per-session engine counters (mirror startStInterview) — but conversation/prompt/savedStoryId
    // are preserved above, NOT reset.
    setInterviewFollowUpCount(0);
    setInterviewAskedMoves([]);
    setInterviewLastClassification(null);
    setInterviewEmptyCount(0);
    setInterviewIsWrapped(false);
    setInterviewBudgetBonus(0);
    setInterviewWrapReason(null);
    setInterviewWrapOfferCount(0);
    setInterviewDeepenedThreads([]);
    setInterviewConsecutiveZoomCount(0);
    setRapidAnswers({});
    setRapidCurrentIdx(0);

    // Keep savedStoryId on stProcessed (belt-and-suspenders with stReopenStoryId); clear the prior
    // generation caches so no stale Flick/Whole/tierCache bleeds into the regenerated chapter.
    setStProcessed({ title: chapterTitle, content: chapterText, savedStoryId: id });
    setStInterviewInput('');
    setStoryAudioBlob(null);
    setStoryAudioUrl('');
    setStInterviewMode(true);
    setStInterviewStep('conversation');
    try { trackEvent('tell_pip_more_opened', { story_id: id }); } catch (e) { /* never break flow */ }
    if (typeof window !== 'undefined') window.scrollTo(0, 0);
  };

  // TB-104 Stage 2a: adaptive follow-up loop
  // Called when the user submits a follow-up answer in the 'chat' step.
  // Also the entry point for the voice-reply path in the chat step.
  //
  // State reads: interviewFollowUpCount, interviewAskedMoves, interviewConversation,
  //              interviewEmptyCount, activePackId, isGiftBook, activeBook
  // State writes: stInterviewMessages (display thread), interviewConversation (engine shape),
  //               interviewFollowUpCount, interviewAskedMoves, interviewLastClassification,
  //               interviewEmptyCount, stInterviewInput, stInterviewLoading, stInterviewStep
  const sendStInterviewMsg = async (text, {
    // Callers may pass current engine state snapshot to avoid stale-closure reads.
    // When omitted the React state values are used (safe for synchronous callers only).
    followUpCountOverride = null,
    askedMovesOverride = null,
    conversationOverride = null,
    emptyCountOverride = null,
    deepenedThreadsOverride = null, // TB-104 thread-salience
    consecutiveZoomCountOverride = null, // TB-104 breadth-fix
    wrapOfferCountOverride = null, // TB-134 fix 1
    budgetBonusOverride = null, // TB-134 fix 2: caller (StRecordPage) may extend the budget synchronously
    // right before calling this function; interviewBudgetBonus state would not have re-rendered
    // yet at call time (same stale-closure hazard the other *Override params above already guard).
  } = {}) => {
    if (!text?.trim() || stInterviewLoading) return;

    const answerText = text.trim();
    const pack = activePackId || '';
    // Relational prompt: how-we-fell-in-love, people-who-marked-me, becoming-a-family
    const RELATIONAL_PACKS = ['how-we-fell-in-love', 'people-who-marked-me', 'becoming-a-family'];
    const isRelationalPrompt = RELATIONAL_PACKS.includes(pack);
    // Celebration / third-person framing (spec 8.5).
    // bookType:'GIFT' denotes the Celebration book (stories about a recipient), NOT the gift coupon.
    const isCelebration = !!isGiftBook;
    const recipientName = activeBook?.recipientName || '';

    // Snapshot current engine state (callers can pass overrides to avoid stale closures)
    const currentFollowUpCount = followUpCountOverride !== null ? followUpCountOverride : interviewFollowUpCount;
    const currentAskedMoves = askedMovesOverride !== null ? askedMovesOverride : interviewAskedMoves;
    const currentConversation = conversationOverride !== null ? conversationOverride : interviewConversation;
    const currentEmptyCount = emptyCountOverride !== null ? emptyCountOverride : interviewEmptyCount;
    const currentDeepenedThreads = deepenedThreadsOverride !== null ? deepenedThreadsOverride : interviewDeepenedThreads; // TB-104 thread-salience
    const currentConsecutiveZoomCount = consecutiveZoomCountOverride !== null ? consecutiveZoomCountOverride : interviewConsecutiveZoomCount; // TB-104 breadth-fix
    const currentWrapOfferCount = wrapOfferCountOverride !== null ? wrapOfferCountOverride : interviewWrapOfferCount; // TB-134 fix 1
    const currentBudgetBonus = budgetBonusOverride !== null ? budgetBonusOverride : interviewBudgetBonus; // TB-134 fix 2
    // F-62: coldStart is CALLER state — a seedless entry has no prompt text (the doorway's only job).
    // Topic-picked, AI-question, and reopen flows all set prompt text, so this stays false everywhere today.
    const coldStart = !((stSelectedPrompt?.text || '').trim());
    const currentWarmupCount = countWarmupMoves(currentAskedMoves); // F-62: the ONE shared derivation

    // Append user answer to display thread and engine conversation
    const userDisplayMsg = { role: 'user', content: answerText };
    const updatedDisplayMsgs = [...stInterviewMessages, userDisplayMsg];
    const updatedConversation = [...currentConversation, { role: 'user', content: answerText }];
    setStInterviewMessages(updatedDisplayMsgs);
    setInterviewConversation(updatedConversation);
    setStInterviewInput('');
    setStInterviewLoading(true);

    try {
      // --- F-24 CRISIS GATE (present-tense self-harm only; NO writes — the Quiet Shelf is dead per founder
      // ruling). Pip speaks the crisis line once, asks NO further questions this session, persists NOTHING.
      // Past-trauma narration never fires this (shouldFireCrisisGate = present-tense phrasing only). ---
      if (!interviewCrisisStopped && shouldFireCrisisGate(answerText)) {
        try { trackEvent('interview_crisis_gate', {}); } catch (e) { /* never break flow */ }
        setStInterviewMessages([...updatedDisplayMsgs, { role: 'assistant', content: CRISIS_GATE_LINE }]);
        setInterviewCrisisStopped(true);
        setInterviewPendingOffer(null);
        setStInterviewLoading(false);
        /* TYPED-CHAT-SHELL-1: thread pane owns scroll (applyThreadScroll). */
        return;
      }
      // --- F-111 ITEM 0 (F-130): SOFT crisis class — a THIRD-PERSON present-tense self-harm disclosure the
      // first-person hard gate misses ("she really wants to kill herself"). Pip acknowledges + offers the door
      // ONCE, WITHOUT halting the (possibly fictional) story: no interviewCrisisStopped, the telling continues on
      // the next turn. Provisional copy (founder-gated at the census); DOES NOT SHIP before the professional
      // consult ruling. Never fires when the hard gate does (shouldFireSoftCrisisAck excludes hasCrisisSignal). ---
      if (!interviewSoftCrisisAcked && !interviewCrisisStopped && shouldFireSoftCrisisAck(answerText)) {
        try { trackEvent('interview_soft_crisis_ack', {}); } catch (e) { /* never break flow */ }
        setStInterviewMessages([...updatedDisplayMsgs, { role: 'assistant', content: THIRD_PERSON_CRISIS_SOFT_LINE }]);
        setInterviewSoftCrisisAcked(true);
        setStInterviewLoading(false);
        /* TYPED-CHAT-SHELL-1: thread pane owns scroll (applyThreadScroll). */
        return;
      }
      // --- F-174: THIRD-PARTY / PAST VIOLENCE disclosure (a killing; the storyteller is NOT the one at risk).
      // GROUND the weight and let the teller lead — Pip does NOT invoke 988 (no self-harm risk to the teller) and
      // does NOT push the write-up as the next move. A soft safety line is appended ONLY when present/ongoing
      // danger is implied (impliesOngoingDanger). Fires at most ONCE; the telling continues on the next turn.
      // Runs AFTER both self-harm gates (shouldFireViolenceGrounding stands down whenever either applies), so a
      // self-harm disclosure never routes here. Provisional copy (founder base line ratified F-174-GO); DOES NOT
      // SHIP before Fable review + founder sign-off on the ongoing-danger line. ---
      if (!interviewViolenceGrounded && !interviewCrisisStopped && shouldFireViolenceGrounding(answerText)) {
        try { trackEvent('interview_violence_grounding', { ongoing: impliesOngoingDanger(answerText) }); } catch (e) { /* never break flow */ }
        const groundLine = THIRD_PARTY_VIOLENCE_GROUND_LINE + (impliesOngoingDanger(answerText) ? ' ' + VIOLENCE_ONGOING_SAFETY_LINE : '');
        setStInterviewMessages([...updatedDisplayMsgs, { role: 'assistant', content: groundLine }]);
        setInterviewViolenceGrounded(true);
        setStInterviewLoading(false);
        /* TYPED-CHAT-SHELL-1: thread pane owns scroll (applyThreadScroll). */
        return;
      }
      // After the gate fired: Pip asks NO more questions. Only an unmistakable redirect resumes (gentle path).
      if (interviewCrisisStopped) {
        if (isCrisisRedirect(answerText)) {
          setInterviewCrisisStopped(false); // resume; heavy content is present so the gentle path governs
        } else {
          setStInterviewMessages([...updatedDisplayMsgs, { role: 'assistant', content: "I'm right here with you." }]);
          setStInterviewLoading(false);
          /* TYPED-CHAT-SHELL-1: thread pane owns scroll (applyThreadScroll). */
          return;
        }
      }

      // --- F-24/F-40 ACCEPTANCE: an affirmative reply to a spoken offer ends the interview -> generation.
      // Gate on the SHARED reconstructOfferState (awaitingAcceptance) so voice and text agree; generation
      // lands the UNSAVED Reveal (State 0) — never an auto-save (CONSENT LAW). ---
      if (reconstructOfferState(currentConversation).awaitingAcceptance && isWrapAcceptance(answerText)) {
        try { trackEvent('wrap_offer_accepted', { variant: interviewPendingOffer || 'dignity' }); } catch (e) { /* never break flow */ }
        setInterviewPendingOffer(null);
        setStInterviewLoading(false);
        // Generate from everything shared BEFORE this acceptance turn (so "yes please" never enters the story).
        const allAnswers = currentConversation.filter(t => t.role === 'user').map(t => t.content).filter(Boolean).join('\n\n');
        await generateStInterviewStory(allAnswers);
        return;
      }
      // A non-acceptance answer after an offer = the storyteller kept going; clear the flag and continue.
      if (interviewPendingOffer) setInterviewPendingOffer(null);

      // --- STEP 1: Classify the answer (heuristic-first, AI tie-break on ambiguous) ---
      // TYPED channel: wideDoneFloor -> the gold "Write it up" card is an OFFER, not an auto-save, so the
      // wide floor's recall matters and its over-detection is harmless (save_story F-14). Voice auto-acts
      // and stays trimmed + model-tool.
      let classification = classifyAnswer(answerText, { followUpCount: currentFollowUpCount, pack, isRelationalPrompt, wideDoneFloor: true });
      setInterviewLastClassification(classification);

      // Hybrid tie-break: one Haiku call only when heuristic is ambiguous (spec Section 7.2, locked decision)
      if (classification.isAmbiguous) {
        try {
          const { systemPrompt: clsSystem, userContent: clsUser } = buildClassificationPrompt(answerText, {
            promptText: stSelectedPrompt?.text || '',
            pack,
            isRelationalPrompt,
          });
          const clsData = await callAi({
            action: 'follow_up',
            messages: [{ role: 'user', content: clsUser }],
            roughDraft: '',
            systemPrompt: clsSystem,
          });
          const aiLabel = parseClassification(clsData.text || '');
          // Override heuristic label with AI label; keep isAmbiguous false now that it's resolved
          classification = { label: aiLabel, confidence: 'high', isAmbiguous: false };
          setInterviewLastClassification(classification);
        } catch (clsErr) {
          // AI tie-break failed — fall back to heuristic (never block the user, spec 8.7)
          console.warn('[TB-104] Classification AI call failed, using heuristic label:', clsErr);
        }
      }

      // --- STEP 1b: CLARIFICATION (F-148) — the teller asked Pip to repeat, not to move on ---
      // "What?" / "huh?" / "say again?" / "what do you mean?" is a request to hear the SAME question again,
      // never a non-answer. Re-emit Pip's pending question VERBATIM (zero new copy, no model call, no empty
      // count): the last assistant turn in the thread is exactly what Pip just asked. Never switch-gears,
      // never a patience/warmup line. If somehow there is no prior question to repeat, fall through.
      if (classification.label === 'CLARIFICATION') {
        // F-156 A1: repeat ONLY the pending question — strip any stale reaction sentence so it never re-fires.
        const lastPipQuestion = stripToPendingQuestion([...updatedDisplayMsgs].reverse().find(m => m.role === 'assistant')?.content);
        if (lastPipQuestion) {
          setStInterviewMessages([...updatedDisplayMsgs, { role: 'assistant', content: lastPipQuestion }]);
          setStInterviewLoading(false);
          /* TYPED-CHAT-SHELL-1: thread pane owns scroll (applyThreadScroll). */
          return;
        }
      }

      // --- STEP 2: EMPTY path — soft re-invitation, not a follow-up move (spec 8.1) ---
      // TB-104 breadth-fix: context-aware EMPTY — mid-interview "idk" means "tapped out on
      // this thread, move on", not "I haven't started yet". Pass followUpCount and whether
      // there has been any substantive content so getEmptyReInvitation can branch correctly.
      // F-62 risk-guard (f): while the cold-start ramp is OPEN (followUpCount === warmupCount — every
      // follow-up so far was a warmup), an EMPTY answer must reach selectMove so WARMUP/WARMUP-REST can
      // fire — the generic re-invitation would smother the ramp. After the ramp closes (a real move or a
      // REST advanced followUpCount past warmupCount), EMPTY resumes this normal soft handling.
      const coldStartRampOpen = isColdStartRampOpen({
        coldStart,
        followUpCount: currentFollowUpCount,
        warmupCount: currentWarmupCount,
        askedMoves: currentAskedMoves,
      });
      if (classification.label === 'EMPTY' && !coldStartRampOpen) {
        const newEmptyCount = currentEmptyCount + 1;
        setInterviewEmptyCount(newEmptyCount);
        // F-111 DEFECT 3 — SILENCE POLICY (founder-approved F-137): after 2 consecutive empty/greeting answers,
        // Pip offers ONE gentle rest, then STOPS generating (cost cap: no model call). This REPLACES the old
        // 2-empty wrap-nudge ("Want me to write up...") with the no-pressure rest line for a quiet/overwhelmed
        // teller; the always-visible Finish control stays for when they're ready. Grief/crisis is handled upstream
        // (companion fork + crisis gate), so this never hurries a grieving teller.
        if (newEmptyCount >= 2) {
          setStInterviewMessages([...updatedDisplayMsgs, { role: 'assistant', content: SILENCE_STOP_LINE }]);
          setStInterviewLoading(false);
          /* TYPED-CHAT-SHELL-1: thread pane owns scroll (applyThreadScroll). */
          return;
        }
        const hasSubstantiveContent = currentConversation.some( // TB-104 breadth-fix
          t => t.role === 'user' && (t.content || '').trim().split(/\s+/).filter(Boolean).length >= 5
        );
        const { text: reinviteText, skip, isWrapOffer } = getEmptyReInvitation(pack, newEmptyCount, { // TB-104 breadth-fix
          followUpCount: currentFollowUpCount,
          hasSubstantiveContent,
        });
        if (skip || !reinviteText) {
          // Second EMPTY at start of interview: stop the loop, let the user trigger write-up or go back
          setStInterviewMessages([...updatedDisplayMsgs, {
            role: 'assistant',
            content: 'That\'s okay. Want me to write up what you have so far?',
          }]);
        } else if (isWrapOffer) {
          // Two consecutive empties mid-interview: graceful wrap offer (TB-104 breadth-fix)
          setInterviewIsWrapped(true);
          setInterviewWrapReason('EMPTY_WRAP');
          setInterviewWrapOfferCount(currentWrapOfferCount + 1); // TB-134 fix 1: this counts as a wrap shown to the user
          setStInterviewMessages([...updatedDisplayMsgs, { role: 'assistant', content: reinviteText }]);
        } else {
          setStInterviewMessages([...updatedDisplayMsgs, { role: 'assistant', content: reinviteText }]);
        }
        setStInterviewLoading(false);
        /* TYPED-CHAT-SHELL-1: thread pane owns scroll (applyThreadScroll). */
        return;
      }

      // Reset empty count since this answer was non-empty
      setInterviewEmptyCount(0);

      // --- STEP 3: Select move (or stop) ---
      const prevAnswerWordCount = currentConversation.length > 0
        ? (currentConversation[currentConversation.length - 1]?.content || '').trim().split(/\s+/).filter(Boolean).length
        : null;

      const { move, stop, stopReason, isBreadthPivot, silenceStop } = selectMove({
        classification,
        answerText,
        pack,
        followUpCount: currentFollowUpCount,
        askedMoves: currentAskedMoves,
        prevAnswerWordCount,
        conversationalPath: true, // TB-104 rollout Stage A: belt-and-suspenders (default is already true)
        budgetBonus: currentBudgetBonus, // TB-104 keep-going / TB-134 fix 2: extend cap when storyteller chose to continue
        deepenedThreads: currentDeepenedThreads, // TB-104 thread-salience
        consecutiveZoomCount: currentConsecutiveZoomCount, // TB-104 breadth-fix
        isCelebration, // TB-IQ-R2: suppress FEELING-IN-THE-MOMENT for third-person celebration observers
        wrapOfferCount: currentWrapOfferCount, // TB-134 fix 1: real count now reaches the engine's wrap-offer cap
        coldStart, // F-62: caller-authoritative seedless-entry state
        warmupCount: currentWarmupCount, // F-62: derived via countWarmupMoves(askedMoves)
        consecutiveEmptyCount: currentEmptyCount, // F-111 DEFECT 3: silence policy
        // Sitting through this answer, so a later bare "That's all I remember." can see an earlier home gist.
        conversation: updatedConversation,
      });

      // F-111 DEFECT 3 — SILENCE-STOP (belt-and-suspenders for any empty that reaches selectMove, e.g. the
      // cold-start ramp): emit the founder-approved rest line once, stop generating. Same contract as WARMUP-REST.
      if (silenceStop) {
        setStInterviewMessages([...updatedDisplayMsgs, { role: 'assistant', content: SILENCE_STOP_LINE }]);
        setStInterviewLoading(false);
        /* TYPED-CHAT-SHELL-1: thread pane owns scroll (applyThreadScroll). */
        return;
      }

      // F-62 REST branch (the WARMUP_REST_LINE caller contract — identical on voice): emit the fixed
      // line (no model call), advance followUpCount, append NOTHING to askedMoves. followUpCount moving
      // past warmupCount closes the ramp gate terminally — REST fires at most once per interview.
      if (stopReason === 'WARMUP-REST') {
        setStInterviewMessages([...updatedDisplayMsgs, { role: 'assistant', content: WARMUP_REST_LINE }]);
        setInterviewConversation([...updatedConversation, { role: 'assistant', content: WARMUP_REST_LINE }]);
        setInterviewFollowUpCount(currentFollowUpCount + 1);
        setStInterviewLoading(false);
        /* TYPED-CHAT-SHELL-1: thread pane owns scroll (applyThreadScroll). */
        return;
      }

      if (stop) {
        // Engine says stand down — set the wrap boolean so the gold card surfaces.
        // TB-104 generous pursuit: do NOT emit a redundant in-thread "write it up" bubble here.
        // The gold card (iv-writeup-nudge) is the single wrap affordance (de-dup fix).
        // For DONE-SIGNAL only, emit a brief acknowledgment (no offer to write — card handles that).
        if (stopReason === 'DONE-SIGNAL') {
          setStInterviewMessages([...updatedDisplayMsgs, { role: 'assistant', content: 'Got it.' }]);
        }
        // For all other stop reasons (CAP_REACHED, DIMINISHING_RETURNS, etc.) the gold card
        // appears immediately — no in-thread bubble needed, avoiding the double-wrap.
        setInterviewIsWrapped(true); // TB-104 Stage B: explicit boolean
        setInterviewWrapReason(stopReason); // TB-104 keep-going: preserve reason for card UI
        setInterviewWrapOfferCount(currentWrapOfferCount + 1); // TB-134 fix 1: this counts as a wrap shown to the user
        setStInterviewLoading(false);
        /* TYPED-CHAT-SHELL-1: thread pane owns scroll (applyThreadScroll). */
        return;
      }

      // --- F-24 OFFER: after the 7-turn gate PERMITS, the first done-signal/resolution-beat makes Pip
      // SPEAK the ratified offer as an additive turn (the storyteller can still keep going). Heavy content
      // waives the gate and uses the dignity variant. Capped at 2; re-offer only on a STRONG signal. ---
      {
        const recentUserAnswers = currentConversation.filter(t => t.role === 'user').map(t => t.content);
        const heavy = conversationHasHeavyContent(updatedConversation);
        const off = shouldOfferWrap({
          substantiveTurns: countSubstantiveTurns(updatedConversation),
          offersMade: reconstructOfferState(updatedConversation).offersMade, // F-40 (C): shared count (was interviewOffersMade)
          lastAnswer: answerText,
          recentUserAnswers,
          hardMemory: heavy,
          conversation: updatedConversation,
          askedMoves: currentAskedMoves,
        });
        if (off) {
          try { trackEvent('wrap_offer_shown', { variant: off.variant, signal: off.signal }); } catch (e) { /* never break flow */ }
          const convoWithOffer = [...updatedConversation, { role: 'assistant', content: off.line }];
          setStInterviewMessages([...updatedDisplayMsgs, { role: 'assistant', content: off.line }]);
          setInterviewConversation(convoWithOffer);
          setInterviewPendingOffer(off.variant);
          setInterviewOffersMade(interviewOffersMade + 1);
          setStInterviewLoading(false);
          /* TYPED-CHAT-SHELL-1: thread pane owns scroll (applyThreadScroll). */
          return;
        }
      }

      // --- F-156 A2: WARMUP interim DOOR patch — emit the FIXED written line, no model call. ---
      // The cold-start WARMUP invitation was model-generated and ballooned to a ~75-word re-onboarding
      // (F-152). Replace it with the founder-ratified fixed line. Heavy content NEVER reaches here (heavy/
      // substantive first answers anchor or route to a real move + the companion fork; crisis fires upstream)
      // — the heavy guard is belt-and-suspenders so a heavy answer can never get the scripted line. Ramp
      // accounting stays identical to the model path (append 'WARMUP', advance followUpCount, reset zoom).
      // Superseded by the full RAMP-1 build.
      if (move === 'WARMUP' && !conversationHasHeavyContent(updatedConversation)) {
        // F-156-A-PUBLISH: door line by ramp position — warmupCount 0 → topic, 1 → place.
        // COLD-START-COURTESY-1: engine prepends the fixed ack iff the last assistant was the
        // cold check-in and this answer carries a reciprocal ask. Still WARMUP. No model call.
        const doorLine = getWarmupDoorLine(currentWarmupCount, updatedConversation);
        const convoWithWarmup = [...updatedConversation, { role: 'assistant', content: doorLine }];
        setStInterviewMessages([...updatedDisplayMsgs, { role: 'assistant', content: doorLine }]);
        setInterviewConversation(convoWithWarmup);
        setInterviewFollowUpCount(currentFollowUpCount + 1);
        setInterviewAskedMoves([...currentAskedMoves, 'WARMUP']);
        setInterviewConsecutiveZoomCount(0); // WARMUP is not a zoom-in move
        setStInterviewLoading(false);
        /* TYPED-CHAT-SHELL-1: thread pane owns scroll (applyThreadScroll). */
        return;
      }

      // --- STEP 4: Build and fire the follow-up question ---
      const { systemPrompt: fuSystem, userContent: fuUser } = buildFollowUpPrompt({
        pack,
        move,
        conversation: updatedConversation,
        recipientName,
        isCelebration,
        followUpCount: currentFollowUpCount,
        warmupCount: currentWarmupCount, // F-62: rotated WARMUP invitation index (derived, shared helper)
      });

      // TB-IQ Part 3: guarded generation. validateFollowUpStructure (compound/reaction
      // hygiene) still runs inside guardedFollowUp; the guard adds fragment/leading-template
      // detection + one regeneration on a hard violation.
      const questionText = await guardedFollowUp(callAi, { systemPrompt: fuSystem, userContent: fuUser, conversation: updatedConversation, move });

      // Append follow-up question to both display thread and engine conversation
      const updatedConversationWithQ = [...updatedConversation, { role: 'assistant', content: questionText }];
      setStInterviewMessages([...updatedDisplayMsgs, { role: 'assistant', content: questionText }]);
      setInterviewConversation(updatedConversationWithQ);

      // Advance engine state. F-177 THREAD-COVERAGE-2: the cap counter advances on SUBSTANTIVE answers only,
      // via the ONE shared counter-only predicate (same as the voice replay). Near-no-op on text (typed answers
      // are already substantive); the only delta is a rare short filler like "ok" no longer advancing the cap
      // — flagged + quantified by the LOCK-B text-distribution replay, never assumed.
      const newFollowUpCount = isSubstantiveForCounter(answerText) ? currentFollowUpCount + 1 : currentFollowUpCount;
      const newAskedMoves = [...currentAskedMoves, move];
      setInterviewFollowUpCount(newFollowUpCount);
      setInterviewAskedMoves(newAskedMoves);

      // TB-104 breadth-fix: update consecutive zoom counter.
      // Zoom-in moves increment the counter; any breadth pivot (OPEN, POPULATE-THE-SCENE, SCENE-SET,
      // MEANING-REFLECTION, or the forced-breadth-pivot path) resets it to 0.
      const ZOOM_IN_MOVES_APP = new Set(['DEEPEN', 'SENSORY', 'SEQUENCE', 'FEELING-IN-THE-MOMENT']); // TB-104 breadth-fix
      if (isBreadthPivot || !ZOOM_IN_MOVES_APP.has(move)) {
        setInterviewConsecutiveZoomCount(0); // TB-104 breadth-fix: pivot happened, reset
      } else {
        setInterviewConsecutiveZoomCount(currentConsecutiveZoomCount + 1); // TB-104 breadth-fix: zoom-in happened, increment
      }

      // TB-104 thread-salience: when DEEPEN fires, record what was deepened for de-dup
      if (move === 'DEEPEN') {
        const deepened = findSalientThread(answerText, { deepenedThreads: currentDeepenedThreads, askedMoves: currentAskedMoves });
        if (deepened) setInterviewDeepenedThreads([...currentDeepenedThreads, deepened]);
      }

    } catch (err) {
      // AI call failed: show fallback message so user can still proceed (spec 8.7)
      console.error('[TB-104] Interview follow-up error:', err);
      setStInterviewMessages([...updatedDisplayMsgs, {
        role: 'assistant',
        content: 'I couldn\'t come up with a follow-up right now. Want to keep going or write up what you have?',
      }]);
    }

    setStInterviewLoading(false);
    /* TYPED-CHAT-SHELL-1: thread pane owns scroll (applyThreadScroll). */
  };

  // TB-104 keep-going: handler for the "Keep going" button on the soft-wrap card.
  // Clears the wrapped state, extends the budget by 4, then fires the next depth follow-up
  // immediately so the interview continues in the same thread without the user needing to type first.
  const handleInterviewKeepGoing = async () => { // TB-104 keep-going
    if (stInterviewLoading) return;
    const newBonus = interviewBudgetBonus + 4;
    setInterviewBudgetBonus(newBonus);
    setInterviewIsWrapped(false);
    setInterviewWrapReason(null);
    setStInterviewLoading(true);

    const pack = activePackId || '';
    const isCelebration = !!isGiftBook;
    const recipientName = activeBook?.recipientName || '';
    const currentConversation = interviewConversation;
    const currentAskedMoves = interviewAskedMoves;
    const currentDeepenedThreads = interviewDeepenedThreads; // TB-104 thread-salience
    const hardMemory = currentConversation.some(t => t.role === 'user' && hasHardMemorySignal(t.content));
    // Derive answer text from the last user turn for salience detection
    const lastUserTurnForKeepGoing = [...currentConversation].reverse().find(t => t.role === 'user');
    const keepGoingAnswerText = lastUserTurnForKeepGoing ? lastUserTurnForKeepGoing.content : '';

    // Pick next un-fired depth move; fall back to OPEN with an explicit open invitation
    let nextMove = selectDepthMove({ pack, answerText: keepGoingAnswerText, askedMoves: currentAskedMoves, hardMemory, deepenedThreads: currentDeepenedThreads }); // TB-104 thread-salience

    try {
      const { systemPrompt: fuSystem, userContent: fuUser } = buildFollowUpPrompt({
        pack,
        move: nextMove,
        conversation: currentConversation,
        recipientName,
        isCelebration,
        followUpCount: interviewFollowUpCount,
        warmupCount: countWarmupMoves(currentAskedMoves), // F-62: derived, shared helper (uniform across all 4 sites)
      });

      // TB-IQ Part 3: guarded generation (regenerates once on a hard structural violation).
      const questionText = await guardedFollowUp(callAi, { systemPrompt: fuSystem, userContent: fuUser, conversation: currentConversation, move: nextMove });
      const updatedConversationWithQ = [...currentConversation, { role: 'assistant', content: questionText }];
      setStInterviewMessages(prev => [...prev, { role: 'assistant', content: questionText }]);
      setInterviewConversation(updatedConversationWithQ);
      const newFollowUpCount = interviewFollowUpCount + 1;
      const newAskedMoves = [...currentAskedMoves, nextMove];
      setInterviewFollowUpCount(newFollowUpCount);
      setInterviewAskedMoves(newAskedMoves);
      // TB-104 thread-salience: track deepened thread when keep-going fires DEEPEN
      if (nextMove === 'DEEPEN') {
        const deepened = findSalientThread(keepGoingAnswerText, { deepenedThreads: currentDeepenedThreads, askedMoves: currentAskedMoves });
        if (deepened) setInterviewDeepenedThreads([...currentDeepenedThreads, deepened]);
      }
    } catch (err) {
      console.error('[TB-104] Keep-going follow-up error:', err);
      // Fallback open question so the conversation does not just stop
      const fallbackQ = 'What else about it comes back to you?'; // TB-104 keep-going
      const updatedConversationWithQ = [...currentConversation, { role: 'assistant', content: fallbackQ }];
      setStInterviewMessages(prev => [...prev, { role: 'assistant', content: fallbackQ }]);
      setInterviewConversation(updatedConversationWithQ);
      setInterviewFollowUpCount(prev => prev + 1);
      setInterviewAskedMoves(prev => [...prev, 'OPEN']);
    }

    setStInterviewLoading(false);
    /* TYPED-CHAT-SHELL-1: thread pane owns scroll (applyThreadScroll). */
  };

  // ── TB-104 steering controls ──────────────────────────────────────────────
  // Lightweight, always-available "you're in charge" controls so the storyteller
  // never feels trapped in a thread. Three actions: Skip (breadth pivot),
  // Different question (preserve-then-swap or swap), Rephrase (re-ask current Q).
  // These are additive; typed steering (isThreadRedirect) continues to work.

  // FOUNDER RULING 2026-08-10 — the two typed steer controls are GONE, and with them these four
  // functions, which existed only to serve them. Verbatim: *"I would vote that we just remove
  // 'reword the question' all together, I dont see the need for it"* and *"I would also vote to
  // remove this as well."*
  //
  // DELETED, NOT ORPHANED, and that is a deliberate departure from how the dictation-mic removal was
  // handled earlier the same day (IV-DICTATION-DEAD-CODE left its machinery in place and filed the
  // sweep). The difference is cascade: the mic's removal orphaned a keepsake chain that reached into
  // recording, video and blob state, so cutting it was a bigger change than the ruling. These four had
  // exactly ONE entry point each, verified by grep across src/ and e2e/, and reference no state that
  // outlives them. Leaving them would have meant four unreachable functions and four new build
  // warnings for no protection.
  //
  //   handleInterviewSkip        — breadth-pivot for "Ask me something else" once a turn existed
  //   swapToNextInterviewPrompt  — pre-answer question swap for the same control
  //   handleAskSomethingElse     — the control's entry point
  //   handleInterviewRephrase    — "Reword the question". It DID sanitize + run
  //                                validateFollowUpStructure; what it never had was the
  //                                detect-and-regenerate step, so a model meta-reply
  //                                ("I notice the question you've provided appears to be
  //                                incomplete...") sailed through to the founder.
  //
  // 🔴 CORRECTED after an evaluator checked the claim: an earlier version of this block said both
  // controls fired model calls "with NO structural guard". FALSE for handleInterviewSkip, which went
  // through guardedFollowUp and was fully guarded — its "Line 2:" leak was a shape no detector was
  // looking for (now SCAFFOLD-LEAK-1), not an unguarded path. The founder deleted both regardless,
  // so the outcome stands; only the recorded reason needed fixing.
  //
  // The teller can still steer by typing it; that path is guarded and this one never was.

  // KEPT — `writeUpInterviewSoFar` sat between those functions in the file but belongs to a different
  // control: the Back arrow's "save first" path. Nothing about it is steering.
  // Write up the in-progress interview. Preserves everything shared so far by routing to the normal
  // review/save screen.
  const writeUpInterviewSoFar = async () => {
    const allAnswers = interviewConversation
      .filter(t => t.role === 'user')
      .map(t => t.content)
      .filter(Boolean)
      .join('\n\n');
    await generateStInterviewStory(allAnswers);
  };

  // TB-104 Stage 2a: entry point when user submits their FIRST answer from the 'answer' step.
  // TB-104 Stage 4: skipConversation param added.
  //   skipConversation=false (default, "Continue" button): always transitions to 'chat' step.
  //     - If engine returns a follow-up move: show the follow-up question (existing behavior).
  //     - If engine returns stop: show a warm templated acknowledgment in chat with a "Write it up"
  //       choice; no extra AI call, tone derived from getPackConfig.
  //   skipConversation=true ("just write it up now" secondary path): bypasses chat and calls
  //     generateStInterviewStory directly, honoring the explicit tier. Preserves today's fast path.
  const submitFirstAnswer = async (answerText, explicitTier = null, skipConversation = false) => { // TB-104 Stage 4
    if (!answerText?.trim()) return;

    const pack = activePackId || '';
    const RELATIONAL_PACKS = ['how-we-fell-in-love', 'people-who-marked-me', 'becoming-a-family'];
    const isRelationalPrompt = RELATIONAL_PACKS.includes(pack);
    const isCelebration = !!isGiftBook;
    const recipientName = activeBook?.recipientName || '';

    // Build initial conversation: the original prompt as 'assistant', first answer as 'user'.
    // F-62: on a seedless (cold-start) entry there is no prompt — seed the engine thread with the
    // COLD_START_OPENER Pip actually spoke, so the conversation both channels produce is identical
    // (grief fork + follow-up prompts read this context). The display bubble already renders the
    // same constant via buildOpenerLine('') in StRecordPage.
    const coldStart = !((stSelectedPrompt?.text || '').trim());
    const initialConversation = [
      ...(stSelectedPrompt?.text
        ? [{ role: 'assistant', content: stSelectedPrompt.text }]
        : [{ role: 'assistant', content: COLD_START_OPENER }]),
      { role: 'user', content: answerText.trim() },
    ];

    // Classify the first answer. TYPED channel: wideDoneFloor (the gold card is an offer; save_story F-14).
    let classification = classifyAnswer(answerText, { followUpCount: 0, pack, isRelationalPrompt, wideDoneFloor: true });

    // Hybrid tie-break if ambiguous
    if (classification.isAmbiguous) {
      try {
        const { systemPrompt: clsSystem, userContent: clsUser } = buildClassificationPrompt(answerText, {
          promptText: stSelectedPrompt?.text || '',
          pack,
          isRelationalPrompt,
        });
        const clsData = await callAi({
          action: 'follow_up',
          messages: [{ role: 'user', content: clsUser }],
          roughDraft: '',
          systemPrompt: clsSystem,
        });
        const aiLabel = parseClassification(clsData.text || '');
        classification = { label: aiLabel, confidence: 'high', isAmbiguous: false };
      } catch (clsErr) {
        console.warn('[TB-104] Classification AI call failed on first answer, using heuristic:', clsErr);
      }
    }

    setInterviewLastClassification(classification);

    const { move, stop, stopReason } = selectMove({
      classification,
      answerText,
      pack,
      followUpCount: 0,
      askedMoves: [],
      prevAnswerWordCount: null,
      conversationalPath: true, // TB-104 rollout Stage A
      deepenedThreads: [], // TB-104 thread-salience: fresh interview, no prior deepened threads
      isCelebration, // TB-IQ-R2: suppress FEELING-IN-THE-MOMENT for third-person celebration observers
      coldStart, // F-62: caller-authoritative seedless-entry state (turn 0: warmupCount is 0 by definition)
      warmupCount: countWarmupMoves([]), // F-62: derived — no moves asked yet on the first answer
      // TB-134 fix 1: pass the real count here too for consistency/traceability, even though this
      // is always the interview's first turn (interviewWrapOfferCount is guaranteed 0 here —
      // startStInterview resets it on every new interview and this function only ever runs once
      // per interview, before any wrap could have been offered).
      wrapOfferCount: interviewWrapOfferCount,
    });

    // TB-104 Stage 4: skipConversation=true means the caller explicitly chose "just write it up now".
    // Go straight to generation, honoring the explicit tier. This is the secondary/fast path.
    if (skipConversation) {
      await generateStInterviewStory(answerText, explicitTier);
      return;
    }

    // TB-104 Stage 4: "Continue" path — ALWAYS enter the chat step so the interview is visible.
    // TB-104 depth retune (duplicate-question fix): the opener bubble is already rendered by
    // StRecordPage directly from stSelectedPrompt -- do NOT include the prompt as an assistant
    // message in the display thread or it renders twice. Display thread starts with the user answer only.
    // interviewConversation (engine state) still includes the prompt so the AI has full context.
    setStInterviewLoading(true);
    const initialDisplayMsgs = [
      { role: 'user', content: answerText.trim() },
    ];
    setStInterviewMessages(initialDisplayMsgs);
    setInterviewConversation(initialConversation);

    // --- CLARIFICATION on the very first answer (F-148) — the teller answered the opener with "What?" ---
    // Repeat the pending question, never a generic follow-up or a patience line. Re-emit the actual question
    // the teller is answering (the prompt text, or the cold-start opener) — a subset of what's already on
    // screen, so zero new copy and no model call. Handles both the seeded and seedless first turn.
    if (classification.label === 'CLARIFICATION') {
      // F-156 A1: repeat the pending question only (strip any greeting/reaction preamble from the opener).
      const repeatQ = stripToPendingQuestion((stSelectedPrompt?.text || '').trim() || COLD_START_OPENER);
      setStInterviewMessages([...initialDisplayMsgs, { role: 'assistant', content: repeatQ }]);
      setStInterviewLoading(false);
      setStInterviewStep('conversation');
      /* TYPED-CHAT-SHELL-1: thread pane owns scroll (applyThreadScroll). */
      return;
    }

    // --- EMPTY on the very first answer — soft re-invitation, never a generic follow-up. ---
    // TB-133 first-turn-empty fix: the follow-up handler (sendStInterviewMsg) has an explicit
    // EMPTY branch, but this first-turn path had none. An EMPTY first answer fell through
    // selectMove's { move:null, stop:false } into buildFollowUpPrompt, where MOVE_BY_ID[null]
    // silently became OPEN — so a genuine non-answer got a generic AI question instead of the
    // "even a word is fine" re-invite. Mirror the follow-up handler's EMPTY branch here (display
    // only; the empty answer stays in engine state, the re-invite is not added to it).
    // F-62 risk-guard (f): on a cold-start first answer, WARMUP must WIN — an EMPTY "hi" is exactly
    // what the ramp exists for, so it falls through to selectMove (which returns WARMUP at turn 0)
    // instead of the generic re-invite.
    if (classification.label === 'EMPTY' && !coldStart) {
      setInterviewEmptyCount(1); // first answer of the interview
      const { text: reinviteText } = getEmptyReInvitation(pack, 1, {
        followUpCount: 0,
        hasSubstantiveContent: false,
      });
      const reinvite = reinviteText || 'Take your time with this one. Even a word or a name to start is fine.';
      setStInterviewMessages([...initialDisplayMsgs, { role: 'assistant', content: reinvite }]);
      setStInterviewLoading(false);
      setStInterviewStep('conversation'); // TB-104 Stage B: was 'chat'
      /* TYPED-CHAT-SHELL-1: thread pane owns scroll (applyThreadScroll). */
      return;
    }

    if (stop) {
      // TB-104 rollout Stage A: replace the flat stopAcknowledgment template.
      // DONE-SIGNAL: static confirmation -- the person said "write it up", so we stop cleanly.
      // RICH: instant fixed line -- no AI call (red-team P1-5: RICH stops are instant).
      // ADEQUATE / other: content-aware wrap turn via buildWrapTurnPrompt (Haiku-class call).
      let wrapText;
      if (stopReason === 'DONE-SIGNAL') {
        wrapText = 'Got it. Use the button above whenever you\'re ready.';
      } else if (stopReason === 'RICH') {
        // Instant, no AI call. Content-neutral by design -- RICH answers are already complete.
        wrapText = 'You gave me a lot to work with there. Ready to write it up?';
      } else {
        // ADEQUATE or other first-answer stop: generate a content-aware two-sentence wrap turn.
        try {
          const { systemPrompt: wrapSystem, userContent: wrapUser } = buildWrapTurnPrompt({
            answerText,
            pack,
            isCelebration,
            recipientName,
          });
          const wrapData = await callAi({
            action: 'follow_up',
            messages: [{ role: 'user', content: wrapUser }],
            roughDraft: '',
            systemPrompt: wrapSystem,
          });
          wrapText = sanitizeDashes((wrapData.text || '').trim()) || 'Is there anything else you want to add, or should I write this up?'; // TB-104 generous pursuit
        } catch (wrapErr) {
          console.warn('[TB-104] Wrap turn AI call failed, using fallback:', wrapErr);
          wrapText = 'Is there anything else you want to add, or should I write this up?';
        }
      }
      setStInterviewMessages([...initialDisplayMsgs, { role: 'assistant', content: wrapText }]);
      setInterviewConversation([...initialConversation, { role: 'assistant', content: wrapText }]);
      // Keep followUpCount at 0 -- no real follow-up answers were collected, so needsAssembly()
      // must stay false (a single rich answer skips the assembler, per locked decision).
      setInterviewFollowUpCount(0);
      setInterviewAskedMoves([]);
      setInterviewEmptyCount(0);
      setInterviewDeepenedThreads([]); // TB-104 thread-salience: reset on stop path
      setInterviewConsecutiveZoomCount(0); // TB-104 breadth-fix: reset on stop path
      setInterviewIsWrapped(true); // TB-104 Stage B: explicit boolean — replaces isWrappedUp string-scan
      setInterviewWrapReason(stopReason); // TB-104 keep-going: preserve reason for card UI
      setInterviewWrapOfferCount(interviewWrapOfferCount + 1); // TB-134 fix 1: this counts as a wrap shown to the user
      setStInterviewLoading(false);
      setStInterviewStep('conversation'); // TB-104 Stage B: was 'chat'
      /* TYPED-CHAT-SHELL-1: thread pane owns scroll (applyThreadScroll). */
      return;
    }

    // F-156 A2: WARMUP interim DOOR patch on the FIRST answer (cold-start turn 0) — fixed line, no model
    // call. Heavy content never reaches here (anchors / routes to companion fork); heavy guard is
    // belt-and-suspenders. Mirrors the follow-up path's ramp accounting (followUpCount 1, askedMoves WARMUP).
    if (move === 'WARMUP' && !conversationHasHeavyContent(initialConversation)) {
      // F-156-A-PUBLISH: first answer is ramp position 0 → the topic door line.
      // COLD-START-COURTESY-1: engine prepends the fixed ack iff this answer carries a reciprocal ask.
      const doorLine = getWarmupDoorLine(0, initialConversation);
      const convoWithWarmup = [...initialConversation, { role: 'assistant', content: doorLine }];
      setStInterviewMessages([...initialDisplayMsgs, { role: 'assistant', content: doorLine }]);
      setInterviewConversation(convoWithWarmup);
      setInterviewFollowUpCount(1);
      setInterviewAskedMoves(['WARMUP']);
      setInterviewEmptyCount(0);
      setInterviewConsecutiveZoomCount(0);
      setStInterviewLoading(false);
      setStInterviewStep('conversation');
      /* TYPED-CHAT-SHELL-1: thread pane owns scroll (applyThreadScroll). */
      return;
    }

    // A follow-up is warranted: fetch and show the follow-up question.
    try {
      const { systemPrompt: fuSystem, userContent: fuUser } = buildFollowUpPrompt({
        pack,
        move,
        conversation: initialConversation,
        recipientName,
        isCelebration,
        followUpCount: 0,
        warmupCount: 0, // F-62: first answer — no invitations offered yet (uniform across all 4 sites)
      });

      // TB-IQ Part 3: guarded generation (regenerates once on a hard structural violation).
      const questionText = await guardedFollowUp(callAi, { systemPrompt: fuSystem, userContent: fuUser, conversation: initialConversation, move });
      const updatedConversationWithQ = [...initialConversation, { role: 'assistant', content: questionText }];
      setStInterviewMessages([...initialDisplayMsgs, { role: 'assistant', content: questionText }]);
      setInterviewConversation(updatedConversationWithQ);
      setInterviewFollowUpCount(1);
      setInterviewAskedMoves([move]);
      setInterviewEmptyCount(0);
      // TB-104 thread-salience: record deepened thread if DEEPEN fired as first follow-up
      if (move === 'DEEPEN') {
        const deepened = findSalientThread(answerText, { deepenedThreads: [], askedMoves: [] });
        if (deepened) setInterviewDeepenedThreads([deepened]);
      }
      setInterviewIsWrapped(false); // TB-104 Stage B: not wrapped, follow-up is in flight
      setStInterviewStep('conversation'); // TB-104 Stage B: was 'chat'
    } catch (err) {
      console.error('[TB-104] First follow-up failed:', err);
      // Fallback: go straight to generation rather than blocking the user (spec 8.7)
      await generateStInterviewStory(answerText, explicitTier);
    }

    setStInterviewLoading(false);
    /* TYPED-CHAT-SHELL-1: thread pane owns scroll (applyThreadScroll). */
  };

  // AI Interview: record a voice reply in the chat step
  // TB-104 Stage 3 (FIX 7): snapshot engine state BEFORE the async transcription call to avoid
  // stale-closure reads on interviewFollowUpCount/interviewAskedMoves/interviewConversation/interviewEmptyCount.
  const stInterviewVoiceReply = async () => {
    if (!storyAudioBlob) return;
    setStInterviewLoading(true);
    // Snapshot before await — guards against stale closures // TB-104 Stage 3
    const snapFollowUpCount = interviewFollowUpCount;
    const snapAskedMoves = interviewAskedMoves;
    const snapConversation = interviewConversation;
    const snapEmptyCount = interviewEmptyCount;
    const snapDeepenedThreads = interviewDeepenedThreads; // TB-104 thread-salience
    const snapConsecutiveZoomCount = interviewConsecutiveZoomCount; // TB-104 breadth-fix
    const snapWrapOfferCount = interviewWrapOfferCount; // TB-134 fix 1
    const snapBudgetBonus = interviewBudgetBonus; // TB-134 fix 2
    try {
      const text = await transcribeAudio(storyAudioBlob, storyAudioChunksRef.current);
      setStoryAudioBlob(null); setStoryAudioUrl('');
      if (text?.trim()) await sendStInterviewMsg(text, {
        followUpCountOverride: snapFollowUpCount,
        askedMovesOverride: snapAskedMoves,
        conversationOverride: snapConversation,
        emptyCountOverride: snapEmptyCount,
        deepenedThreadsOverride: snapDeepenedThreads, // TB-104 thread-salience
        consecutiveZoomCountOverride: snapConsecutiveZoomCount, // TB-104 breadth-fix
        wrapOfferCountOverride: snapWrapOfferCount, // TB-134 fix 1
        budgetBonusOverride: snapBudgetBonus, // TB-134 fix 2
      });
    } catch (err) { showToast('Could not transcribe.', 'error'); }
    setStInterviewLoading(false);
  };

  // AI Interview: generate final story from collected answers.
  // TB-104 Stage 2a: routing-aware.
  //   - If follow-ups were collected, runs the assembler first then routes to the correct tier.
  //   - If no follow-ups (RICH single answer), skips assembler and goes straight to cleanup/chapter.
  //   - An explicit `tier` argument (from the review buttons) overrides the engine's routing.
  // F-182/F-185: the storyteller-label scrub, extracted from the save path to a shared component helper so the
  // save flow AND all three re-polish handlers scrub identically. Pure fn of (text, {isCelebration, recipientName}).
  const scrubStorytellerLabel = (s, { isCelebration = false, recipientName = '' } = {}) => {
    if (!s || !/\bstoryteller\b/i.test(s)) return s;
    const name = (recipientName || '').trim();
    const subject = isCelebration ? (name || 'they') : 'I';
    const possessive = isCelebration ? (name ? `${name}'s` : 'their') : 'my';
    let out = s
      .replace(/\bthe storyteller'?s\b/gi, possessive)
      .replace(/\bthe storyteller\b/gi, subject)
      .replace(/\bstoryteller\b/gi, subject);
    if (subject === 'they') {
      out = out
        .replace(/\bthey was\b/gi, 'they were')
        .replace(/\bthey is\b/gi, 'they are')
        .replace(/\bthey has\b/gi, 'they have')
        .replace(/\bthey doesn'?t\b/gi, "they don't");
    }
    return out;
  };

  // F-182/F-185: the ONE shared polish generator for write-your-own (#1) + review re-polish (#2/#3). Routes
  // exactly like the save path: cleanup -> getFlickPolishPrompt (Haiku 0.3); chapter -> getWholePolishPrompt
  // (Sonnet-5, thinking OFF) + the SHAPE-1 plan pass when own-story (runShapePlan). A CELEBRATION story routes
  // to the third-person celebration branch (isCelebration) and never runs SHAPE-1, so gift books stay third
  // person. Post-processes with sanitizeDashes + scrubStorytellerLabel. Returns extracted {title, content} +
  // the post-processed full text.
  const polishStoryText = async (sourceText, tier, { question = null, isCelebration = false, recipientName = '', runShapePlan = true } = {}) => {
    const polishContext = { question, storyType: 'memoir', isCelebration };
    let promptText, model, extra;
    if (tier === 'chapter') {
      let shapePlan = null;
      // F-364: shouldRunShapePlan() is false on every path today (the V4 own-story body has no plan
      // slot). The branch stays so re-enabling is one constant flip, not a re-implementation.
      if (runShapePlan && shouldRunShapePlan({ isCelebration })) {
        try {
          const planData = await Promise.race([
            callAi({ action: 'follow_up', messages: [{ role: 'user', content: getWholeShapePlanPrompt(sourceText) }], roughDraft: '', systemPrompt: POLISH_SYSTEM_PROMPT, model: 'claude-sonnet-5', thinking: 'disabled' }),
            new Promise((_, reject) => setTimeout(() => reject(new Error('plan timeout')), 8000)),
          ]);
          const pt = (planData?.text || '').trim();
          if (pt && /TURN:/i.test(pt) && /THROUGH-LINE:/i.test(pt) && /LANDING:/i.test(pt)) shapePlan = pt;
        } catch (planErr) { console.warn('[SHAPE-1] re-polish plan skipped (v7 baseline):', planErr?.message); }
      }
      promptText = getWholePolishPrompt(sourceText, { ...polishContext, plan: shapePlan || undefined });
      model = 'claude-sonnet-5'; extra = { thinking: 'disabled' };
    } else {
      promptText = getFlickPolishPrompt(sourceText, polishContext);
      model = 'claude-haiku-4-5-20251001'; extra = { temperature: 0.3 };
    }
    const data = await callAi({ action: 'follow_up', messages: [{ role: 'user', content: promptText }], roughDraft: '', systemPrompt: POLISH_SYSTEM_PROMPT, model, ...extra });
    const polished = data.text || sourceText;
    let extractedTitle = null, extractedContent = polished;
    const lines = polished.split('\n').filter(l => l.trim());
    if (lines.length > 1 && lines[0].length < 80) { extractedTitle = lines[0].replace(/^#+\s*/, '').replace(/\*+/g, '').trim(); extractedContent = lines.slice(1).join('\n').trim(); }
    const scrub = (x) => scrubStorytellerLabel(sanitizeDashes(stripInterviewFrame(x).text), { isCelebration, recipientName });
    return {
      title: extractedTitle != null ? scrub(extractedTitle) : null,
      content: scrub(extractedContent),
      text: scrub(polished),
    };
  };

  // Resolve the polish routing for a re-polish handler the SAME way the save path does (F-182 shared decision).
  const resolveRepolishRouting = (sourceText) => resolvePolishRouting({
    isGiftBook,
    bookAuthorVantage: isGiftBook ? getAuthorVantage(activeBook?.authorRelationship || '') : 'self',
    forceFirstPerson: false,
    recipientName: activeBook?.recipientName || '',
    isHard: hasHardMemorySignal(sourceText || ''),
  });

  /**
   * F-334/F-335 — the one way a generation is allowed to end badly.
   *
   * Founder ruling: generation COMPLETES or FAILS, and a failure is told plainly. Every caller that
   * used to substitute something (a Flick alone, a raw concatenation, the teller's own unpolished
   * answers) now routes here instead. Three things happen, in this order, and none of them is a toast:
   *   1. a PERSISTENT banner is raised on the conversation screen — no 4s timer, dismissed only by the
   *      teller or by their next attempt ([V-333] obs 2: the old toast erased its own explanation);
   *   2. the cause is logged and an immediate founder alert fires (fire-and-forget, never blocks);
   *   3. the teller is put back in the conversation with every answer intact.
   */
  const failGeneration = (cause) => {
    try { trackEvent('generation_failed', { cause }); } catch (e) { /* never break flow */ }
    logGenerationFailed(cause); // structured log + founder alert, fire-and-forget
    setStStreamPreview(''); // F-415-STREAM: a failed generation takes its preview with it (F-334)
    setStDraftWords(0);
    setStGenerationFailure({ kind: 'failure', cause, message: GENERATION_FAILED_MESSAGE });
    setStInterviewStep('conversation');
  };

  // forceFirstPerson: when true, overrides the isGiftBook-derived isCelebration flag so the
  // assembler and cleanup both write in first person. Used by the voice path when the
  // storyteller is speaking as themselves (even if a gift book is currently active).
  const generateStInterviewStory = async (answerText, tier = null, conversationOverride = null, followUpCountOverride = null, forceFirstPerson = false, forceRegenerate = false, opts = {}) => {
    // REVEAL-002 R2: opts.autoSave RETIRED. A finish (typed OR voice, clicked/spoken/timer) saves
    // NOTHING — it creates a server-side draft and lands State 0. opts.voiceConversationId marks a voice
    // finish (persisted onto the draft, and onto the LegacyEntry at the later deliberate Save).
    // opts.trigger ('button'|'spoken'|'timer') is recorded on story_ready_viewed (the timer value counts
    // stories that USED to be silently lost at the 28-min cap).
    const { voiceConversationId: optsVoiceConvId = null, trigger: optsTrigger = null } = opts;
    if (!answerText?.trim()) return;

    // REVEAL-001 C5 Guard 2 (Fable F-41): CAPTURE the reopen id at generation START, then route the
    // tail on this local const — never a live state/ref read at completion. If the user navigates away
    // during 'generating', the abandon branch (setActiveTab) deliberately does NOT fire (they requested
    // this write-up), but even if the marker were cleared, this captured value still routes the in-flight
    // generation to the update seam (same entry), so the abandon/complete race can never create a duplicate.
    const reopenId = stReopenStoryId;

    // Thin-input guard (voice + text paths): if the total user content is fewer than
    // 8 words we do not have enough raw material to write a chapter. Calling the model
    // on near-empty input causes it to break character and ask for the answer instead
    // of producing a story — which is exactly the bug this guard exists to prevent.
    // 8 words is deliberately low: a legitimate short-answer pack answer ("My first car
    // was a Civic.") has 7 words and is fine to send. We only reject genuinely empty
    // transcripts (greetings, orphan opener turns, sub-utterance noise).
    const _thinInputWordCount = answerText.trim().split(/\s+/).filter(Boolean).length;
    if (_thinInputWordCount < 8) {
      showToast(
        "It looks like we didn't capture enough of your answer yet. Let's add a little more.",
        'info'
      );
      // Do NOT advance to 'generating' or save anything.
      return;
    }

    // Activation funnel: enough content, so the storyteller is now reaching story
    // generation (Pip is writing their story). Fire-and-forget beacon — the midpoint
    // between "started an interview" and "saved a story." See logReachedGeneration.
    logReachedGeneration(conversationHasHeavyContent(conversationOverride || interviewConversation)); // F-24 durable heavy flag

    // TB-104 Phase 2 voice: the voice path passes the conversation + follow-up count
    // explicitly (React state is not updated yet at call time). The text path uses live state.
    const effectiveConversation = conversationOverride || interviewConversation;
    const effectiveFollowUpCount = followUpCountOverride != null ? followUpCountOverride : interviewFollowUpCount;

    // ── F-383 PRE-GENERATION DRAFT ─────────────────────────────────────────────────────────────────
    // Draft the teller's OWN WORDS server-side BEFORE the first generation leg is dispatched. Until
    // now no server draft existed until a generation SUCCEEDED, so the conversation lived only in React
    // state and any failure took it with them — and the assembler runs against a 29s API Gateway
    // ceiling, so that is not hypothetical.
    //
    // EVERY conversation, INCLUDING crisis and grief (constitution §11, founder ruling 2026-08-06).
    // The older "crisis and grief are never auto-persisted" text existed to stop a disclosure being
    // KEPT without consent; applied here it produced the opposite harm, since the tellers having the
    // hardest conversations were the only ones losing an hour of words. A draft is the teller's words
    // held FOR THEM. The crisis GATE is untouched, and so is the F-75 auto-finish exemption: drafting
    // is not finishing, and nothing here saves anything to their book.
    //
    // IT MUST NEVER BLOCK GENERATION. Awaited only far enough to learn the id (needed so the completed
    // story UPDATES this row instead of creating a second one), and every failure is swallowed after a
    // log. A draft that breaks generation is worse than no draft.
    let preGenDraftId = null;
    try {
      const preDraft = await persistPreGenerationDraft(client, effectiveConversation, {
        userId: user?.userId || user?.username || '',
        promptId: stSelectedPrompt?.id || null,
        promptText: stSelectedPrompt?.text || null,
        packId: activePackId || null,
        bookId: activeBook?.id || null,
        occasion: activeBook?.occasion || null,
        voiceConversationId: optsVoiceConvId || null,
      });
      preGenDraftId = preDraft?.id || null;
    } catch (e) {
      console.error('[F-383] pre-generation draft failed (generation proceeds):', e?.message);
      try { trackEvent('pregen_draft_failed', { channel: optsVoiceConvId ? 'voice' : 'text' }); } catch (e2) { /* telemetry */ }
    }

    // Build the assembled text from all collected answers when follow-ups were used
    // TB-104 locked decision: skip assembler when there are no follow-ups to stitch
    const hasFollowUps = needsAssembly(effectiveFollowUpCount);

    // Q&A pairing for the assembler lives in the ENGINE now (buildQAPairs + isGreetingTurn +
    // PLEASANTRY-NOT-MATERIAL-1). F-216 moved it verbatim out of this closure so the pairing seam
    // is testable at its real entry point (CLAUDE.md change protocol #4) and so the pleasantry rule
    // rides the byte-identical voice twin. Behaviour on every pre-existing shape is unchanged --
    // see the NO-OP PROOF block in src/utils/interviewEngine.pleasantry.test.js.

    // Determine effective text to polish (assembled or raw)
    let textToPolish = answerText;

    // Explicit tier is used for cache key; if null we will auto-route below
    const cacheKey = tier || '__auto__';

    // Check cache first — if already generated this tier for this text, use it (no API call, no count)
    // TB-134 QA fix: forceRegenerate (the "try again" retry from a degraded result) always skips
    // this cache hit -- otherwise a retry after a timeout-degrade would just replay the same
    // Flick-only content that was cached under this tier, silently "succeeding" with nothing new.
    if (!forceRegenerate && stProcessed.tierCache?.[cacheKey] && stProcessed.tierCache._sourceText === answerText) {
      const cached = stProcessed.tierCache[cacheKey];
      setStProcessed(p => ({ ...p, title: cached.title, content: cached.content, raw: false, polishTier: cacheKey }));
      setStInterviewStep('review');
      return;
    }

    // Gate: check AI polish limit
    if (!canUseAiPolish) {
      showToast("You've done a lot of editing today! Come back tomorrow to keep going.", "info");
      return;
    }

    setStOriginalContent(prev => prev === null ? answerText : prev);
    setStGenerationFailure(null); // F-334: a fresh attempt clears the previous failure banner
    setStStreamPreview(''); // F-415-STREAM: never show a previous attempt's text for even a frame
    setStDraftWords(0);
    setStInterviewStep('generating');

    // Relationship-aware vantage: only 'self' vantage (recipient writing their own book) and
    // forceFirstPerson (voice path, where the storyteller is always speaking as themselves) assemble
    // in first person. Partner contributors on a Celebration book write ABOUT the recipient, so
    // they assemble in third person exactly like any other contributor. TB-108.
    const bookAuthorVantage = isGiftBook ? getAuthorVantage(activeBook?.authorRelationship || '') : 'self';
    const isFirstPersonVantage = forceFirstPerson || bookAuthorVantage === 'self';

    try {
      // --- STEP 1: Assembly (only when follow-ups were collected) ---
      if (hasFollowUps) {
        // isFirstPersonVantage overrides the gift-book flag so the assembler writes in first
        // person when the author is 'self' (recipient writing their own book) or on the voice path.
        // Partner contributors on a Celebration book assemble in third person (TB-108 corrected model).
        const isCelebration = isFirstPersonVantage ? false : !!isGiftBook; // relationship-aware
        const recipientName = activeBook?.recipientName || '';
        const pack = activePackId || '';
        const asmQaPairs = buildQAPairs({
          conversation: effectiveConversation,
          promptText: stSelectedPrompt?.text || '',
          fallbackAnswer: answerText,
        });
        // TB-104 Stage 3: compute hard-memory signal over all user answers before assembler call
        const allAnswerText = asmQaPairs.map(p => p.answer).join(' ');
        const isHard = hasHardMemorySignal(allAnswerText); // TB-104 Stage 3
        const { systemPrompt: asmSystem, userContent: asmUser } = buildAssemblerPrompt({
          pack,
          conversation: asmQaPairs,
          recipientName,
          isCelebration,
          isHotSeat: false,
          isHardMemory: isHard, // TB-104 Stage 3
        });
        try {
          // F-415: RESUMABLE. API Gateway cuts this connection at 29s and the Lambda keeps running,
          // so on 2026-08-07 (32.7s) and 2026-08-08 (44.3s) a finished story was thrown away by the
          // catch below and the teller was told it had not been written. The jobKey lets the Lambda
          // record the story and lets us collect it after the cut instead of failing on a 504.
          // Long voice transcripts are what push past 29s, so this grows as voice does.
          const asmData = await callAiResumable({
            action: 'follow_up',
            messages: [{ role: 'user', content: asmUser }],
            roughDraft: '',
            systemPrompt: asmSystem,
          }, { jobKey: newJobKey('asm'), onPartial: (text) => setStDraftWords(text.trim().split(/\s+/).filter(Boolean).length) });
          textToPolish = (asmData.text || '').trim() || answerText;
        } catch (asmErr) {
          // F-334 2b: the assembler no longer degrades to a raw concatenation of the teller's answers.
          // That concatenation reached the reveal dressed as "The Whole Tale · beautifully written"
          // with nothing on screen saying assembly had failed ([V-334] A7 red, photographed). Founder
          // ruling: COMPLETE OR FAIL. Fail honestly, keep every answer on screen, let them retry.
          console.warn('[TB-104] Assembler failed — failing honestly (no concatenation substitute):', asmErr);
          failGeneration('assembler_fail');
          return;
        }
      }

      // --- STEP 2: Determine tier (engine routing, unless caller passed explicit tier) ---
      // TB-104 Stage 3: compute hard-memory over combined text; pass isHardMemory + isCelebration to router
      // F-182: route via the ONE shared decision. PROVEN a no-op vs this inline logic across own-story /
      // gift-book contributor / first-person-vantage (TB-108) / forceFirstPerson (aiPrompts.polishRouting.test, 9/9).
      const { isCelebration: routeIsCelebration, isHard: routeIsHard } = resolvePolishRouting({
        isGiftBook, bookAuthorVantage, forceFirstPerson, isHard: hasHardMemorySignal(textToPolish),
      });
      let effectiveTier = tier;
      if (!effectiveTier) {
        const lastClassification = interviewLastClassification || { label: 'ADEQUATE' };
        effectiveTier = routeToTier({
          classification: lastClassification,
          assembledText: textToPolish,
          followUpCount: effectiveFollowUpCount,
          storyType: 'memoir',
          isHardMemory: routeIsHard, // TB-104 Stage 3
          isCelebration: routeIsCelebration, // TB-104 Stage 3
        });
      }

      // --- STEP 3: Dual-version polish — "A Flick of the Tail" (light) + "The Whole Tale" (full magic) ---
      // Both versions are produced IN PARALLEL from the same assembled text so total latency
      // is approximately one generation, not two. One incrementPolishCount() at the end so
      // generating both versions costs the user exactly ONE daily AI-polish allowance.
      //
      // Prompt routing:
      //   - "The Whole Tale" always uses getWholePolishPrompt (warm, crafted memoir prose).
      //   - "A Flick of the Tail" always uses getFlickPolishPrompt (lightest hand, their words).
      //   The legacy single-tier route (effectiveTier) is preserved in tierCache for any callers
      //   (review-screen re-polish buttons) that still expect it.
      //
      // Data shape stored in stProcessed:
      //   title / content     -> The Whole Tale version (the default displayed version)
      //   versions.flick      -> { title, content } for "A Flick of the Tail"
      //   versions.whole      -> { title, content } for "The Whole Tale" (mirrors title/content)
      //   polishTier          -> cacheKey (preserved for review-screen compatibility)
      //   tierCache           -> preserved (both versions also stored under their own keys)

      const polishContext = {
        question: stSelectedPrompt?.text,
        storyType: 'memoir',
        isCelebration: routeIsCelebration,
        isHardMemory: routeIsHard, // grief: strip self-blame/ideation from the saved keepsake
      };

      const flickPrompt = getFlickPolishPrompt(textToPolish, polishContext);

      // F-169 SHAPE-1 (plan-then-write): for own-story, run a PLAN pass first (getWholeShapePlanPrompt names
      // the TURN / THROUGH-LINE / LANDING from ONLY their real material — invents nothing), then give that plan
      // to the Whole writer so it arranges their material INTO a decided arc. Corpus-validated (F-169.1): mode
      // shape/voice/ending shift UP, inv-fact 14→9, inv-tex 74→58. Fully GRACEFUL: own-story only (celebration
      // twin unchanged, DZ-5); an 8s timeout + a well-formed-plan check; on any failure shapePlan stays null and
      // getWholePolishPrompt falls back to the byte-identical v7 baseline. Never breaks or unduly delays the save.
      let shapePlan = null;
      // F-364: shouldRunShapePlan() is false today (the V4 own-story body has no plan slot), so this
      // pass no longer runs on the save path. The branch stays so re-enabling is one constant flip.
      if (shouldRunShapePlan({ isCelebration: routeIsCelebration })) {
        try {
          const PLAN_TIMEOUT_MS = 8000;
          const planData = await Promise.race([
            callAi({ action: 'follow_up', messages: [{ role: 'user', content: getWholeShapePlanPrompt(textToPolish) }], roughDraft: '', systemPrompt: POLISH_SYSTEM_PROMPT, model: 'claude-sonnet-5', thinking: 'disabled' }),
            new Promise((_, reject) => setTimeout(() => reject(new Error('plan timeout')), PLAN_TIMEOUT_MS)),
          ]);
          const pt = (planData?.text || '').trim();
          // Only use a WELL-FORMED plan (all three labels present); otherwise v7 baseline.
          if (pt && /TURN:/i.test(pt) && /THROUGH-LINE:/i.test(pt) && /LANDING:/i.test(pt)) shapePlan = pt;
        } catch (planErr) { console.warn('[SHAPE-1] plan pass skipped (v7 baseline):', planErr?.message); }
        try { trackEvent('shape_plan', { used: !!shapePlan }); } catch (e) { /* telemetry best-effort */ }
      }
      const wholePrompt = getWholePolishPrompt(textToPolish, { ...polishContext, plan: shapePlan || undefined });

      // Helper: extract { title, content } from a model response using the existing logic.
      const fallbackTitle = promptToChapterTitle(stSelectedPrompt?.text || '') || 'My Story';
      const extractTitleContent = (polished) => {
        let title = fallbackTitle;
        let content = polished || textToPolish;
        const lines = (polished || '').split('\n').filter(l => l.trim());
        if (lines.length > 1) {
          const candidateTitle = lines[0].replace(/^#+\s*/, '').replace(/\*+/g, '').trim();
          const looksLikeTitle =
            candidateTitle.length < 60 &&
            !candidateTitle.includes('?') &&
            !/^(i don'?t|please|once you|you'?ve|based on|the storyteller|i('?ll| will| can))\b/i.test(candidateTitle) &&
            !/^(tell me|what (did|do|was|were|is|are)|how did|when did|where did)\b/i.test(candidateTitle);
          if (looksLikeTitle) {
            title = candidateTitle;
            content = lines.slice(1).join('\n').trim();
          }
        }
        // Belt-and-suspenders: strip any em/en-dashes the model emitted despite the prompt ban,
        // so they never reach a printed page (brand rule). Encoding fix in remnantAI prevents the
        // upstream "?" corruption; this enforces the no-em-dash rule on the final text.
        //
        // Same principle for the "the storyteller" leak (real celebration-book user hit this
        // 2026-07-01: assembler primed by [STORYTELLER] labels + no recipient name). The prompts
        // now forbid it; this guarantees it. Replace with the recipient's name when we have one,
        // otherwise neutral pronouns; first-person books get "I".
        // F-182/F-185: the storyteller-label scrub is now the shared component helper (same logic), called with
        // this generation's routing. Identical output to the prior local closure (isCel == routeIsCelebration).
        return {
          title: scrubStorytellerLabel(sanitizeDashes(stripInterviewFrame(title).text), { isCelebration: routeIsCelebration, recipientName: activeBook?.recipientName || '' }),
          content: scrubStorytellerLabel(sanitizeDashes(stripInterviewFrame(content).text), { isCelebration: routeIsCelebration, recipientName: activeBook?.recipientName || '' }),
        };
      };

      // Fire both polish calls in parallel — total latency ~= one generation.
      // Per-tier model + temperature routing (validated offline):
      //   - Flick: Haiku, temperature 0.3 (grounded/faithful; low temp reduces invented detail).
      //   - Whole: Sonnet-5, no temperature sent (Sonnet-5 rejects a non-default temperature;
      //     only Sonnet can hold "write beautifully, never invent a fact" simultaneously).
      //     Extended thinking explicitly OFF (thinking: 'disabled') -- the validated/signed-off
      //     stories were all generated with Sonnet thinking off; thinking-on measurably increases
      //     fabrication on this constrained-writing task (4-5/6 fidelity fails on vs ~10/11 clean off).
      //
      // TB-134 fix 3: client-side timeout guard. This dual call sits behind an immutable 29s
      // API Gateway hard limit with no prior client-side timeout -- latent risk before this pass,
      // and MORE likely to trigger now that fixes 1+2 above let interviews run longer before
      // generation (the wrap-offer cap no longer lets the engine bail the storyteller out early).
      // That means the risk lands on exactly the most-invested, most-engaged storytellers -- the
      // ones who stuck out a long interview. Race the pair against a timeout comfortably under
      // the 29s ceiling; on timeout, fall back to the faithful Flick draft if it made it back in
      // time, or surface a clear "still working" message and preserve the interview rather than
      // silently bouncing the storyteller with no explanation.
      // F-415-POLISH-RESUME: the WHOLE polish is now RESUMABLE, so the 29s API Gateway cut no longer
      // costs a story the Lambda actually finished writing. The old 25s client timer was deliberately
      // "~4s margin under the 29s ceiling" — it never reached a 504 because it killed the call first,
      // and once the coverage fix is on (Whole polish measured at 31.3s-35.2s) that timer would have
      // fired on EVERY long telling. The race, the budget and the F-334 verdict now live in
      // runPolishPair (extracted so it can be tested; App.js cannot be loaded in Jest).
      let isDegradedGeneration = false; // TB-134 fix 3: true when we fell back to Flick-only after a timeout
      // F-415-STREAM: local, not the state — the async closure would read the stale entry value.
      let sawStreamPreview = false;
      const pairOutcome = await runPolishPair({
        // Resumable: its own jobKey lets the Lambda record this story and lets us collect it after a cut.
        // F-415-STREAM: onPartial turns the resume's poll loop into a live channel — the Lambda
        // streams the Whole onto its job row and each poll renders what has arrived on the
        // generating screen. Real accumulated model text only, monotonic, and a failed generation
        // clears it (STREAM-PREVIEW-NEVER-A-STORY-1). The Flick below stays dark: it is fast,
        // hidden, and discarded when the pair is incomplete.
        callWhole: () => callAiResumable({
          action: 'follow_up',
          messages: [{ role: 'user', content: wholePrompt }],
          roughDraft: '',
          systemPrompt: POLISH_SYSTEM_PROMPT,
          model: 'claude-sonnet-5',
          thinking: 'disabled',
        }, { jobKey: newJobKey('whole'), onPartial: (text) => { sawStreamPreview = true; setStStreamPreview(text); } }),
        // Flick stays a plain call: it is Haiku, it is fast, and it is the version that is dropped
        // anyway when the pair is incomplete. Its failures are absorbed inside runPolishPair.
        callFlick: () => callAi({
          action: 'follow_up',
          messages: [{ role: 'user', content: flickPrompt }],
          roughDraft: '',
          systemPrompt: POLISH_SYSTEM_PROMPT,
          model: 'claude-haiku-4-5-20251001',
          temperature: 0.3,
        }),
      }); // F-416: never throws. A failed Whole comes back as status:'failed' naming the POLISH leg.
      if (pairOutcome.status !== 'complete') {
        // F-416: the cause arrives already classified by leg (polish_timeout / polish_fail /
        // pair_timeout / empty_output). It used to reach the outer catch and be renamed
        // `assembler_fail`, after the one leg that had already succeeded. Keep the real error visible
        // in the console: the cause enum says WHICH leg, this says why.
        if (pairOutcome.error) console.error('[F-416] polish leg failed:', pairOutcome.error);
        // F-334 (founder ruling): COMPLETE OR FAIL — no substitutes, ever. A Flick alone is a
        // SUBSTITUTE (a different, lesser artifact the teller cannot identify), so it is refused
        // and the generation fails honestly instead.
        failGeneration(pairOutcome.cause || 'pair_timeout');
        return;
      }
      // F-334 Fix 2: a finished Whole is the full version and is never thrown away because its
      // partner was still out ([V-334] A2 red). flickData is null on that landing and every
      // flick-side gate below is guarded on it.
      const wholeData = pairOutcome.wholeData;
      const flickData = pairOutcome.flickData;

      // PIPE-OUT-1 output gate, EXTENDED TO THE REVEAL by F-334 2c. It used to degrade Whole -> Flick ->
      // assembled draft "with disclosure", and the disclosure was a toast that faded in four seconds, so
      // what the teller kept was a substitute they had no way to notice. `chooseGeneration` now returns
      // the Whole Tale or nothing, and nothing means we fail honestly and preserve the interview.
      const outputChoice = chooseGeneration({ whole: wholeData && wholeData.text });
      if (!outputChoice.text) {
        failGeneration('empty_output');
        return;
      }

      let flickVersion = flickData && flickData.text ? extractTitleContent(flickData.text) : null;
      let wholeVersion = extractTitleContent(wholeData.text);

      // F-116 OUTPUT GATE (extends PIPE-LANG-1): validate the story the teller actually sees/saves BEFORE
      // it is shown. Three checks on the CHOSEN output: (1) language on the TITLE *and* the BODY vs the
      // SOURCE — the old PIPE-LANG-1 checked the body only, so a Spanish title on an English body slipped
      // (the live "El Sabor del Chocolate" case); (2) model SELF-TALK ("Wait, I need to write in English.",
      // "as an AI"), tightly scoped so legit first-person memoir never trips; (3) a DUPLICATED inline title,
      // deterministically cleaned. Prompt-side guards never held (this is recurrence #6), so it is enforced
      // OUTPUT-SIDE. On a CONFIDENT trip: regen once with a corrective directive that FORCES the SOURCE
      // language (never hardcoded English — TB-097 Spanish speakers must not be flipped) and forbids
      // meta/self-talk; then fail CLOSED to a safe faithful version (Flick if it passes, else the teller's
      // own raw answers, which are their own words by construction — correct language, no self-talk). The
      // deterministic dup-title clean is applied even when the gate passes. Every trip is logged (drift
      // early-warning telemetry). The newly-common post-F-103 thin-input finish is exactly the flailing case.
      {
        // deterministic dup-title clean applies to BOTH versions up front (belt-and-suspenders)
        wholeVersion = { ...wholeVersion, content: stripDuplicateTitle(wholeVersion.title, wholeVersion.content) };
        // F-334: flickVersion is null on the Whole-only landing (Flick never settled). The dup-title
        // clean and every flick-side gate below are guarded on it rather than assuming two versions.
        if (flickVersion) flickVersion = { ...flickVersion, content: stripDuplicateTitle(flickVersion.title, flickVersion.content) };
        const gate = gateStoryOutput({ sourceText: answerText, title: wholeVersion.title, content: wholeVersion.content });
        wholeVersion = { title: gate.cleanTitle, content: gate.cleanContent };
        if (!gate.ok) {
          const sourceLang = gate.detail.lang.sourceLang;
          let regenOk = false, fallbackUsed = null;
          try {
            const regen = await callAi({
              action: 'follow_up',
              messages: [{ role: 'user', content: wholePrompt + correctiveRegenDirective({ sourceLang, reasons: gate.reasons }) }],
              roughDraft: '', systemPrompt: POLISH_SYSTEM_PROMPT, model: 'claude-sonnet-5', thinking: 'disabled',
            });
            const regenVersion = extractTitleContent((regen && regen.text) || '');
            const regenGate = gateStoryOutput({ sourceText: answerText, title: regenVersion.title, content: regenVersion.content });
            if (regenGate.cleanContent && regenGate.ok) {
              wholeVersion = { title: regenGate.cleanTitle, content: regenGate.cleanContent };
              if (flickVersion && !gateStoryOutput({ sourceText: answerText, title: flickVersion.title, content: flickVersion.content }).ok) {
                flickVersion = { title: regenGate.cleanTitle, content: regenGate.cleanContent };
              }
              regenOk = true;
            }
          } catch (e) { /* regen failed -> fall through to the fail-closed fallback below */ }
          if (!regenOk) {
            const flickGate = flickVersion
              ? gateStoryOutput({ sourceText: answerText, title: flickVersion.title, content: flickVersion.content })
              : { ok: false };
            if (flickGate.ok) {
              wholeVersion = { title: flickGate.cleanTitle, content: flickGate.cleanContent }; fallbackUsed = 'flick';
            } else {
              const rawSafe = extractTitleContent(answerText); // raw answers = the teller's own words: correct language, no self-talk
              wholeVersion = rawSafe; flickVersion = rawSafe; fallbackUsed = 'raw-answers';
            }
            isDegradedGeneration = true; // disclose: this is the faithful/raw version, not the full polish
          }
          try {
            trackEvent('pipe_output_gate_trip', { reasons: gate.reasons.join('+'), source_lang: sourceLang, title_lang: gate.detail.lang.titleLang, body_lang: gate.detail.lang.bodyLang, tier: 'whole', regen_ok: regenOk, fallback: fallbackUsed || 'none' });
          } catch (e) { /* telemetry best-effort */ }
          console.warn('[PIPE-OUTPUT-GATE] trip', { reasons: gate.reasons, sourceLang, regenOk, fallbackUsed });
        }
      }

      // BORROWED-QUESTION GATE (sibling of F-116, different fail path): a Whole sentence that restates a
      // Pip/interviewer turn plus a yes/no staple (Absolutely / Yes / Yeah / I did) is interviewer residue,
      // not memoir. Detector is code, not a grader. ONE corrective regen naming those sentences; if still
      // dirty, fail honestly through failGeneration('empty_output') — no Flick, no raw, no isDegradedGeneration.
      // Interviewer turns: role !== 'user' covers typed Pip (assistant) and voice Pip (ai).
      {
        const interviewerTurns = (effectiveConversation || [])
          .filter((t) => t && t.role !== 'user')
          .map((t) => t.content || '');
        const borrowed = gateBorrowedQuestions(wholeVersion.content, interviewerTurns);
        if (!borrowed.ok) {
          let regenOk = false;
          try {
            const regen = await callAi({
              action: 'follow_up',
              messages: [{ role: 'user', content: wholePrompt + borrowedQuestionRegenDirective({ dirtySentences: borrowed.dirtySentences }) }],
              roughDraft: '', systemPrompt: POLISH_SYSTEM_PROMPT, model: 'claude-sonnet-5', thinking: 'disabled',
            });
            const regenVersion = extractTitleContent((regen && regen.text) || '');
            const rOut = gateStoryOutput({ sourceText: answerText, title: regenVersion.title, content: regenVersion.content });
            if (rOut.ok && rOut.cleanContent) {
              const borrowed2 = gateBorrowedQuestions(rOut.cleanContent, interviewerTurns);
              if (borrowed2.ok) {
                wholeVersion = { title: rOut.cleanTitle, content: rOut.cleanContent };
                regenOk = true;
              }
            }
          } catch (e) { /* regen failed -> fail honestly below */ }
          if (!regenOk) {
            failGeneration('empty_output');
            return;
          }
        }
      }

      // F-145 STORY-FIDELITY-1 — the fidelity gate (details-in vs details-out). `fidelityJudgeAndRegen` judges
      // the story against the SOURCE for dropped THREADS (H's class) + invented facts, and on a fail runs ONE
      // corrective regen with the findings injected (re-run through the F-116 output gate so a fix can't
      // reintroduce a language/self-talk leak), keeping the cleaner version. Used by BOTH the inline path
      // (below) and the async path (after the reveal). Reuses the tune-whole-tale judge core via fidelityJudge.js.
      let fidelityFlag = null, fidelityInlineHandled = false;
      const flagFrom = (d) => (d.fail ? {
        droppedThreads: (d.droppedThreads || []).map((t) => (t && t.thread) || '').filter(Boolean).slice(0, 8),
        inventions: (d.inventions || []).map((f) => (f && f.storyQuote) || '').filter(Boolean).slice(0, 8),
      } : null);
      // regen:false => JUDGE-ONLY (flag + metric), used on the below-threshold async path where there is no UI
      // to surface a regen and the shown/saved story stays as-is; regen:true => the inline (large-interview)
      // path that reveals a corrected version.
      const fidelityJudgeAndRegen = async (source, whole, { regen = true } = {}) => {
        const judgeOnce = async (storyContent) => {
          const judgeData = await callAi({ action: 'follow_up', messages: [{ role: 'user', content: buildFidelityJudgePrompt(source, storyContent) }], roughDraft: '', systemPrompt: FIDELITY_JUDGE_SYSTEM, model: 'claude-sonnet-5', thinking: 'disabled' });
          return fidelityGateDecision(parseFidelityVerdict((judgeData && judgeData.text) || ''));
        };
        const d1 = await judgeOnce(whole.content);
        const d1Info = { d1Threads: d1.droppedThreads.length, d1Inv: d1.inventions.length, d1Repairs: (d1.repairs || []).length };
        if (!d1.fail) return { version: whole, flag: null, upgraded: false, resolved: true, tripped: false, regenRan: false, ...d1Info };
        if (!regen) return { version: whole, flag: flagFrom(d1), upgraded: false, resolved: false, tripped: true, regenRan: false, ...d1Info }; // judge-only: flag the shown story's own findings
        let finalVersion = whole, finalDecision = d1;
        try {
          const regen = await callAi({ action: 'follow_up', messages: [{ role: 'user', content: wholePrompt + fidelityRegenDirective({ droppedThreads: d1.droppedThreads, inventions: d1.inventions }) }], roughDraft: '', systemPrompt: POLISH_SYSTEM_PROMPT, model: 'claude-sonnet-5', thinking: 'disabled' });
          let regenVersion = extractTitleContent((regen && regen.text) || '');
          regenVersion = { ...regenVersion, content: stripDuplicateTitle(regenVersion.title, regenVersion.content) };
          const rOut = gateStoryOutput({ sourceText: answerText, title: regenVersion.title, content: regenVersion.content });
          if (rOut.cleanContent) regenVersion = { title: rOut.cleanTitle, content: rOut.cleanContent };
          if (rOut.ok && regenVersion.content) {
            const d2 = await judgeOnce(regenVersion.content);
            const score = (d) => d.droppedThreads.length + d.inventions.length; // the regen was directed to restore threads
            if (!d2.fail || score(d2) <= score(d1)) { finalVersion = regenVersion; finalDecision = d2; }
          }
        } catch (e) { /* regen/re-judge failed -> keep the original */ }
        return { version: finalVersion, flag: flagFrom(finalDecision), upgraded: !!(finalVersion.content && finalVersion.content !== whole.content), resolved: !finalDecision.fail, tripped: true, regenRan: true, ...d1Info };
      };
      const emitFidelityMetric = (mode, res) => {
        try {
          if (res.tripped) trackEvent('pipe_fidelity_gate_trip', { mode, dropped_threads: res.d1Threads, inventions: res.d1Inv, regen_ran: !!res.regenRan, resolved: res.resolved, upgraded: res.upgraded, repairs: res.d1Repairs });
          else if (res.d1Repairs) trackEvent('pipe_fidelity_repairs', { mode, repairs: res.d1Repairs });
        } catch (e) { /* telemetry best-effort */ }
      };

      // F-145-SHIP (founder ruling): the fidelity gate ships as ASYNC, FLAG + METRIC ONLY, for EVERY generation
      // (all sizes) — no regen, no UI, no reveal latency, no silent swaps, Save untouched. Measurement is the
      // gate's whole job now (Morning Review reads the metrics; the daily judged-story fabrication/thread-drop
      // numbers are the before/after scoreboard for the [THREAD-COVERAGE-1] root fix + the anti-fabrication
      // prompt tighten, the next build). The INLINE judge->regen->reveal machinery below stays IN CODE but
      // DORMANT (threshold = Infinity => isLargeInterview is always false), ready to re-enable after the root
      // fix + a faster judge. F-145-FINAL verification showed inline was ~45-100s/gen and the gate trips often
      // on inventions — too heavy to ship enabled today. See .fable/forensics/f145-report.md.
      const FIDELITY_INLINE_THRESHOLD = Infinity; // F-145-SHIP: inline gate dormant; every generation runs async judge-only below
      const isLargeInterview = (textToPolish || '').length >= FIDELITY_INLINE_THRESHOLD;
      if (isLargeInterview && !isDegradedGeneration && !reopenId && wholeVersion && wholeVersion.content) {
        const inlineCacheKey = cacheKey, inlineOriginal = wholeVersion.content;
        const gatePromise = fidelityJudgeAndRegen(textToPolish, wholeVersion);
        let res = null;
        try {
          res = await Promise.race([
            gatePromise,
            new Promise((_, rej) => setTimeout(() => rej(new Error('FIDELITY_INLINE_TIMEOUT')), 50000)),
          ]);
        } catch (e) { res = null; /* timed out or errored -> reveal as-is; the background continuation flags it */ }
        fidelityInlineHandled = true; // the async block below never double-runs for a large interview
        if (res) {
          wholeVersion = res.version;    // completed in time -> reveal the passing (or best-available) version
          fidelityFlag = res.flag;       // flagged only if it still fails after the regen
          emitFidelityMetric('inline', res);
        } else {
          // reveal proceeds with the original; when the in-flight judge finishes, flag it in the background
          // (Morning Review picks it up). NEVER swaps the shown story. A real gate error just skips (fail-open).
          gatePromise.then((late) => {
            emitFidelityMetric('inline-late', late);
            if (late && late.tripped && late.flag) {
              setStProcessed((prev) => (prev && prev.polishTier === inlineCacheKey && prev.content === inlineOriginal) ? { ...prev, fidelityFlag: late.flag } : prev);
            }
          }).catch((err) => { console.warn('[PIPE-FIDELITY-GATE] inline judge failed:', err && err.message); try { trackEvent('pipe_fidelity_gate_skip', { mode: 'inline', reason: 'judge_unavailable' }); } catch (e2) { /* best-effort */ } });
        }
      }

      // Default displayed version is The Whole Tale (or the disclosed fallback, per PIPE-OUT-1 above).
      const { title, content } = wholeVersion;

      // F-268b SUBSTANCE FLOOR, RE-ASKED ON THE FINAL ARTIFACT (F-153 class, 3rd live instance).
      // PIPE-OUT-1's floor ran at :5299 on the RAW model text. Since then extractTitleContent, the
      // duplicate-title strip, and the F-116 output gate's regen/fallback have all REWRITTEN that
      // text. Checking the input to a transformation is not checking its output, and row 1b91ab99
      // (2026-07-30) went through exactly that gap: PIPE-OUT-1 recomputed on its SAVED content reads
      // usable=NO reason=truncated. Same predicate, asked about what actually becomes the page.
      // Fail closed and preserve the interview for retry.
      //
      // F-334: this branch STOPS sharing the stall string. A substance-floor block is a REFUSAL, not a
      // delay: Pip has too little to write with, and telling that teller "this is taking longer than
      // usual" described a wait that was not happening and hid the one thing they could act on
      // ([V-333] obs 1). It is not a generation FAILURE either, so it raises its own banner, says what
      // to do next, and fires no founder alert.
      const artifactFloor = checkSaveArtifact(content);
      if (!artifactFloor.savable) {
        try { trackEvent('substance_floor_block', { at: 'reveal', reason: artifactFloor.reason, words: String(content || '').trim().split(/\s+/).filter(Boolean).length }); } catch (e) { /* telemetry */ }
        setStGenerationFailure({ kind: 'too_thin', message: GENERATION_TOO_THIN_MESSAGE });
        setStInterviewStep('conversation');
        return;
      }

      // Cache the result. Use the ASSEMBLED multi-turn text (textToPolish) as the polish
      // source, NOT the first answer (answerText). Otherwise "Rewrite this beautifully" /
      // tier-switching on the review screen re-polishes only the first answer and silently
      // drops every follow-up detail (the "grabbed some but not all" bug).
      const tierCache = {
        ...(stProcessed.tierCache || {}),
        [cacheKey]: { title, content }, // legacy slot: The Whole Tale (default)
        'flick': flickVersion,
        'whole': wholeVersion,
        _sourceText: textToPolish,
      };

      // PIPE-SAVE-1: build the processed-story fields once so auto-save can pass the fresh object
      // to saveStInterviewStory (the same-tick setStProcessed has not flushed to `stProcessed` yet).
      const processedUpdate = {
        // SAVE-IDENTITY-1 (F-236): this generation's identity. It rides with the content through the
        // merges at :5488/:5563, so a story generated on top of a saved chapter's leftover state
        // carries a NEW id that cannot match that chapter's savedGenerationId — the reveal goes amber
        // and the Save button returns, instead of the toggle silently overwriting the old row (F-234).
        generationId: mintGenerationId(),
        title,
        content,
        originalContent: textToPolish,
        originalTitle: 'My Story',
        raw: false,
        fromInterview: true,
        polishTier: cacheKey,
        tierCache,
        // Dual-version payload — UI chooser reads from here.
        versions: {
          flick: flickVersion,
          whole: wholeVersion,
        },
        activeVersion: isDegradedGeneration ? 'flick' : 'whole', // TB-134 fix 3: label a degraded result honestly
        // F-145-FINAL: fidelityFlag = residual dropped-threads/inventions (null when clean/skipped/failed-open).
        // Set on the INLINE path above (before the reveal) or via setStProcessed on the async judge-only path
        // below. Flag + metric only — there is NO customer-facing fidelity UI (no banner, no swap, ever).
        fidelityFlag,
        // TB-134 QA fix: explicit, persisted signal that this is a degraded (Flick-only) result --
        // the review screen drives the "Choose your version" chooser off this flag instead of
        // inferring it from activeVersion + a toast that has already vanished. A successful
        // generation (first try or retry) always lands here with isDegradedGeneration:false,
        // which restores the real two-version chooser.
        isDegradedGeneration,
        // F-415-STREAM (founder walk feedback 2026-08-10): the teller WATCHED this story being
        // written, so the reveal's landing veil (whose ceremony is "your story is arriving") is
        // redundant and reads as a screen flash. The veil decision in StRecordPage suppresses on it.
        streamed: sawStreamPreview,
        // PIPE-SAVE-1: attribution for a voice-originated save (persisted onto the LegacyEntry).
        voiceConversationId: optsVoiceConvId || null,
        // BENCH-TAG-1 (F-171): which writer produced this story, so the bench splits grades BY TELEMETRY
        // (not the deploy timestamp). F-365: own-story now emits 'v4' -- the plan pass is disabled, so
        // shapePlan is permanently null and the old expression would have tagged every V4 story 'v7', the
        // name of the body it replaced. That would have filed the rollback's results under the writer it
        // was measured against and made the 7-day metric unreadable. 'shape1' stays reachable: it is what
        // this emits again the moment OWN_STORY_SHAPE_PLAN_ENABLED flips back on. Celebration is unchanged.
        writerVersion: routeIsCelebration ? 'v7-celebration' : (shapePlan ? 'shape1' : 'v4'),
        // Retry args for the "try again" affordance on the degraded review screen -- lets
        // retryPolishedGeneration() replay this exact call with forceRegenerate:true. Cleared
        // on a non-degraded result so no stale retry state lingers once the real Whole Tale lands.
        retryArgs: isDegradedGeneration
          ? { answerText, tier, conversationOverride, followUpCountOverride, forceFirstPerson }
          : null,
      };
      setStProcessed(p => ({ ...p, ...processedUpdate }));

      // ONE increment — two versions cost the user one daily allowance, not two.
      // TB-134 fix 3: a degraded (Flick-only, post-timeout) result does not consume the daily
      // allowance, so the storyteller can freely retry for the full "Whole Tale" version without
      // being penalized for a slow response that was not their fault.
      if (!isDegradedGeneration) {
        incrementPolishCount();
      }
      // F-415-STREAM (founder walk feedback 2026-08-10, "screen flashes/jumps"): when the teller
      // was watching the story stream, hold the finished text on screen for a beat before the step
      // change, so the reveal doesn't jump-cut mid-read. Presentation timing only — nothing here
      // fakes progress, and the non-streamed path is unchanged.
      if (sawStreamPreview) { await new Promise(r => setTimeout(r, 250)); }
      setStInterviewStep('review');

      // F-145-FINAL — the ASYNC JUDGE-ONLY path (below-threshold interviews; near-zero dropped-thread risk at
      // this size). Runs only when the inline path did NOT handle it. The reveal is ALREADY on screen; the
      // judge runs in the BACKGROUND and is FLAG + METRIC ONLY — no regen is surfaced, the shown/saved story
      // never changes, there is no UI. Both hard rules hold: nothing changes silently, Save is never blocked
      // (fire-and-forget), fail-open loses no story. Skipped on reopen/degraded.
      if (!fidelityInlineHandled && !reopenId && !isDegradedGeneration && wholeVersion && wholeVersion.content) {
        const fidCacheKey = cacheKey, originalContent = wholeVersion.content, originalTitle = wholeVersion.title;
        (async () => {
          try {
            const res = await Promise.race([
              fidelityJudgeAndRegen(textToPolish, { title: originalTitle, content: originalContent }, { regen: false }),
              new Promise((_, rej) => setTimeout(() => rej(new Error('FIDELITY_JUDGE_TIMEOUT')), 25000)),
            ]);
            emitFidelityMetric('async', res);
            if (!res.tripped) return; // clean — nothing changes on screen
            // Record the flag only (the shown story's own findings) — never swaps, never rewrites a saved artifact.
            setStProcessed((prev) => {
              if (!prev || prev.polishTier !== fidCacheKey || prev.content !== originalContent) return prev; // superseded / edited / tier-switched
              return { ...prev, fidelityFlag: res.flag };
            });
          } catch (e) {
            // fail OPEN: a slow/failing judge must NEVER block or lose a save. Leave the story as shown.
            console.warn('[PIPE-FIDELITY-GATE] async judge unavailable, proceeding unflagged:', e && e.message);
            try { trackEvent('pipe_fidelity_gate_skip', { reason: 'judge_unavailable', mode: 'async' }); } catch (e2) { /* best-effort */ }
          }
        })();
      }

      // PIPE-SAVE-1: auto-save on a spoken done-signal. The story that reaches here is always usable
      // (chooseGeneration returned early above if nothing was) so this never saves an empty keepsake.
      // On success the user lands on the "saved" celebration (with one-tap undo); on failure
      // saveStInterviewStory keeps them on the review screen with a loud, actionable retry.
      if (reopenId) {
        // REVEAL-001 C5 — "Tell Pip more" completion: this chapter was ALREADY saved. Persist the
        // regenerated (grown) version forward-only to the SAME entry via updateSavedInterviewStory —
        // never saveStInterviewStory (which would create a duplicate). Then land back on the State-1
        // reveal showing the appended chapter. interview_completed marks a real forward-only append.
        // Routed on the START-captured reopenId (Guard 2), so a mid-flight nav can't misroute it.
        // SAVE-IDENTITY-1: this regeneration IS what gets persisted to reopenId, so stamp it as saved.
        // (The grown chapter must stay GREEN — it really is in the book — which is why the stamp is
        // taken from processedUpdate's fresh generationId rather than the stale one on stProcessed.)
        const fresh = { ...stProcessed, ...processedUpdate, savedStoryId: reopenId, savedGenerationId: processedUpdate.generationId, saved: true, dirty: false };
        const ok = await updateSavedInterviewStory(fresh, { silent: true });
        setStProcessed(fresh); // saved:true + savedStoryId + new content → renders State 1 with the grown chapter
        if (ok) {
          showToast('Added to your book.');
          try { trackEvent('interview_completed', { context: 'tell_pip_more', story_id: reopenId }); } catch (e) { /* telemetry best-effort */ }
        } else {
          // Update failed: the additions are still in stProcessed on the reveal. Point the user at the
          // Edit → Save changes retry (same gated update seam) so nothing is silently lost.
          showToast("Couldn't save your additions. Open Edit, then tap Save changes to keep them.", 'error');
        }
        setStReopenStoryId(null);
      } else {
        // REVEAL-002 R1+R2 (UNIFIED-FINISH): a finish — TYPED or VOICE, clicked/spoken/timer-forced —
        // saves NOTHING. Persist the ready story as a SERVER-SIDE draft so it survives a crash AND a
        // device switch, then land on the amber State 0. The draft is removed ONLY after a confirmed Save
        // (saveStInterviewStory) or an explicit Discard. Retiring the old voice auto-save here also
        // eliminates the autoSave/abandon → duplicate-LegacyEntry race class (the save is now a later,
        // deliberate, stSaveIdRef-guarded tap from State 0, never an in-flight auto-write).
        const channel = optsVoiceConvId ? 'voice' : 'typed';
        const trigger = optsTrigger || (optsVoiceConvId ? 'spoken' : 'button'); // 'button'|'spoken'|'timer'
        // REVEAL-002 R3: the veil opens on the teller's OWN first line (verbatim, first sentence). Derived
        // from the raw conversation and carried on stProcessed (client) — only needed on this fresh arrival.
        const tellerFirstLine = deriveTellerFirstLine(effectiveConversation);
        // prefers-reduced-motion → no veil (landing:'skipped'); otherwise the veil plays once (landing:'played').
        const reducedMotion = typeof window !== 'undefined' && window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        const merged = { ...stProcessed, ...processedUpdate };
        try {
          const draftCtx = {
            userId: user?.userId || user?.username || '',
            promptId: stSelectedPrompt?.id || null,
            promptText: stSelectedPrompt?.text || null,
            packId: activePackId || null,
            bookId: activeBook?.id || null,
            occasion: activeBook?.occasion || null,
            voiceConversationId: optsVoiceConvId || null,
          };
          // F-383 D3 — ONE ROW PER INTERVIEW. When the pre-generation draft exists, the completed story
          // UPDATES it. Creating a second row here would double the row rate against
          // fetchActiveDraft's 100-item page, make DRAFT-RESUME-CLOBBER more reachable, and leave a
          // stale words-only row that removeDraft never clears on save — so a teller who saved
          // successfully would still be offered a resume of the conversation they just finished.
          const draft = preGenDraftId
            ? await updateDraftWithStory(client, preGenDraftId, merged, draftCtx)
            : await persistDraft(client, merged, draftCtx);
          if (draft?.id) {
            // STOP-TAG (F-157.2): carry the pack/prompt from draftCtx onto the processed story so it survives
            // to the save seam (and a reload-then-save, since the draft persists them durably).
            setStProcessed(p => ({ ...p, draftId: draft.id, packId: draftCtx.packId, promptId: draftCtx.promptId, tellerFirstLine, finishTrigger: trigger, landingPlayedAt: draft.landingPlayedAt || null }));
            if (reducedMotion) {
              // No veil will render, so mark the landing NOW on the row — a later (non-reduced) device-switch
              // resume must still NEVER replay it (grief-replay guarantee, note 3). The veil marks it itself
              // (on mount) in the animated path.
              try { const played = await markLandingPlayed(client, draft.id, new Date().toISOString()); if (played) setStProcessed(p => (p.draftId === draft.id ? { ...p, landingPlayedAt: played.landingPlayedAt } : p)); } catch (e) { /* non-fatal */ }
            }
          }
          try { trackEvent('story_ready_viewed', { landing: reducedMotion ? 'skipped' : 'played', channel, trigger, book_type: isGiftBook ? 'celebration' : 'personal' }); } catch (e) { /* telemetry best-effort */ }
        } catch (e) {
          // Draft persist failed: the ready story is still on screen (the old form fallback renders off
          // stProcessed — State 0 is gated on draftId) and the localStorage belt covers dirty edits. Never
          // block the finish on a draft write. Fire draft_persist_failed so Tier-A can decide retry-vs-fallback
          // from real data; keep the fallback for now.
          console.error('[REVEAL-002] draft persist failed:', e);
          try { trackEvent('draft_persist_failed', { stage: 'finish', channel }); } catch (e2) { /* telemetry */ }
          try { trackEvent('story_ready_viewed', { landing: 'skipped', channel, trigger, draft: 'failed' }); } catch (e2) { /* telemetry */ }
        }
      }
    } catch (err) {
      // F-334: the catch-all now fails the same honest way as every named cause — persistent banner,
      // no story, alert fired. It was a 4s error toast, which is how an unknown failure stayed unknown.
      console.error('[TB-104] Story generation failed:', err);
      failGeneration('assembler_fail');
    }
  };

  // TB-134 QA fix: "try again" affordance for a degraded (Flick-only) review screen. Replays the
  // exact generateStInterviewStory call that produced the degraded result, with forceRegenerate:true
  // so it does not just re-read the stale Flick content back out of tierCache. On success this
  // lands a real two-version result (isDegradedGeneration:false), which restores the normal
  // "Choose your version" chooser. On a second timeout it degrades again and the persistent note
  // stays up -- no allowance is spent either way unless a full dual-version result comes back.
  const retryPolishedGeneration = async () => {
    const args = stProcessed.retryArgs;
    if (!args) return;
    await generateStInterviewStory(
      args.answerText,
      args.tier,
      args.conversationOverride,
      args.followUpCountOverride,
      args.forceFirstPerson,
      true // forceRegenerate
    );
  };

  // TB-104 Stage 2b: Hot Seat batch assembly.
  // Mirrors generateStInterviewStory but is dedicated to the rapid-fire path.
  // qaPairs: [{ question: string, answer: string }] — blanks already filtered by caller.
  // Calls buildAssemblerPrompt with isHotSeat:true, then routes through the same
  // tier-routing / polish / title-extraction / tierCache / incrementPolishCount path
  // so the result lands in the identical 'review' screen.
  const generateHotSeatStory = async (qaPairs) => {
    if (!qaPairs || qaPairs.length === 0) return;

    // Gate: check AI polish limit (same gate as generateStInterviewStory)
    if (!canUseAiPolish) {
      showToast("You've done a lot of editing today! Come back tomorrow to keep going.", 'info');
      return;
    }

    const isCelebration = !!isGiftBook; // TB-104 Stage 2b
    const recipientName = activeBook?.recipientName || ''; // TB-104 Stage 2b
    const pack = activePackId || 'hot-seat'; // TB-104 Stage 2b

    // Raw text of all answers concatenated (used for cache key + tier routing)
    const rawCombined = qaPairs.map(p => p.answer).filter(Boolean).join('\n\n');

    // TB-104 Stage 3: compute hard-memory signal over all Hot Seat answers
    const isHard = hasHardMemorySignal(rawCombined); // TB-104 Stage 3

    setStOriginalContent(prev => prev === null ? rawCombined : prev);
    setStStreamPreview(''); // F-415-STREAM: Hot Seat bypasses the polish pair — no stale preview
    setStDraftWords(0);
    setStInterviewStep('generating');

    try {
      // TB-104 Hot Seat Q&A: bypass the prose assembler entirely.
      // The Rapid Fire chapter is stored as a structured Q&A sentinel block,
      // not blended prose. Each answered pair becomes one Q:/A: entry.
      // Convention (RAPID-FIRE-QA): content begins with "[RAPID-FIRE-QA]\n" so
      // the PDF renderer (RemnantBookPDF.js) and screen preview (ChapterPagePreview.js)
      // can detect and render the Q&A layout. Prose chapters never start with this
      // prefix, so backward compatibility is guaranteed.
      //
      // Per-answer light cleanup: a single AI call strips filler/typos from ALL answers
      // in one pass. The Q&A structure is preserved — the AI is given strict instructions
      // to emit only the cleaned answer text for each item, never to merge or invent.
      // Voice firewall: never invent answers; only what the storyteller said is kept.

      // Build title
      const title = isCelebration
        ? `${recipientName || 'Their'} Rapid Fire`
        : 'Rapid Fire'; // TB-104 Hot Seat Q&A

      // Step 1: light per-answer cleanup (single AI call, structure-preserving)
      let cleanedPairs = qaPairs;
      if (canUseAiPolish && qaPairs.length > 0) {
        try {
          const qaBlock = qaPairs.map((p, i) => `[${i}] Q: ${p.question}\nA: ${p.answer}`).join('\n\n');
          const cleanupSystem = `You are lightly editing a list of rapid-fire interview answers for a printed keepsake book. ` +
            `Each answer is labeled [N] A: ...  Your job is to fix obvious typos, remove filler words (um, uh, like, you know), ` +
            `and trim trailing fragments — but PRESERVE the storyteller's exact voice and brevity. ` +
            `Short punchy answers must stay short. ` +
            `Do NOT change meaning, do NOT invent content, do NOT merge answers, do NOT add prose. ` +
            `Output each cleaned answer on its own line, in the same order, labeled exactly: [0] [1] [2] ... ` +
            `Output ONLY the labeled cleaned answers, nothing else.`;
          const cleanupUser = `Clean these rapid-fire answers:\n\n${qaBlock}`;
          const cleanData = await callAi({
            action: 'follow_up',
            messages: [{ role: 'user', content: cleanupUser }],
            roughDraft: '',
            systemPrompt: cleanupSystem,
          });
          // Parse [N] answer lines from response; fall back to original on any mismatch
          const raw = (cleanData.text || '').trim();
          const parsed = {};
          const matches = raw.matchAll(/\[(\d+)\]\s*([^\[]*)/gs);
          for (const m of matches) {
            const idx = parseInt(m[1], 10);
            const cleaned = m[2].trim();
            if (!isNaN(idx) && cleaned) parsed[idx] = cleaned;
          }
          if (Object.keys(parsed).length === qaPairs.length) {
            cleanedPairs = qaPairs.map((p, i) => ({ question: p.question, answer: parsed[i] || p.answer }));
          }
        } catch (cleanErr) {
          // Cleanup failed — use raw answers (voice firewall: no fabrication)
          console.warn('[TB-104 Hot Seat Q&A] Per-answer cleanup failed, using raw:', cleanErr);
        }
        incrementPolishCount();
      }

      // Step 2: Build the sentinel Q&A content block
      // Format: [RAPID-FIRE-QA]\nQ: ...\nA: ...\n\nQ: ...\nA: ...
      const qaContent = '[RAPID-FIRE-QA]\n' + // TB-104 Hot Seat Q&A sentinel
        cleanedPairs.map(p => `Q: ${p.question.trim()}\nA: ${p.answer.trim()}`).join('\n\n');

      // Cache as 'qa' tier (distinct from cleanup/chapter; never rerouted)
      const cacheKey = 'qa'; // TB-104 Hot Seat Q&A
      const tierCache = { [cacheKey]: { title, content: qaContent }, _sourceText: rawCombined };
      setStProcessed(p => ({
        ...p,
        generationId: mintGenerationId(), // SAVE-IDENTITY-1: Hot Seat output is a generation too
        title,
        content: qaContent,
        originalContent: qaContent,
        originalTitle: title,
        raw: false,
        polishTier: cacheKey,
        tierCache,
      }));
      setStInterviewStep('review');
    } catch (err) {
      console.error('[TB-104 Stage 2b] Hot Seat story generation failed:', err);
      showToast('Story generation failed.', 'error');
      setStInterviewStep('rapid-fire');
    }
  };

  // AI Interview: save the generated story
  const saveStInterviewStory = async (processedOverride = null, opts = {}) => {
    // PIPE-SAVE-1 F-3: revealAfterSave (auto-save path) stays on the review screen in a SAVED state
    // instead of flashing past the chapter to a generic celebration. Manual save keeps the existing
    // navigate + celebration behavior.
    const { revealAfterSave = false, fromReveal = false } = opts; // fromReveal: REVEAL-002 State-0 Save
    // PIPE-SAVE-1: auto-save (spoken done-signal) passes the freshly generated story object
    // directly, because the same-tick setStProcessed has not yet flushed to `stProcessed` in this
    // closure. Manual save passes nothing and reads current state as before.
    // HARDENING (2026-07-09 incident): only treat the arg as an override when it's actually a
    // processed story (isProcessedStoryOverride -- has string content). A Save button wired as
    // onClick={saveStInterviewStory} passes a click Event as the first arg -- that must NOT be
    // mistaken for the story object (it made p.title/p.content undefined and broke every manual
    // save in prod). Guarded by src/utils/saveGuards.test.js. Belt to the call-site arrow suspenders.
    const p = isProcessedStoryOverride(processedOverride) ? processedOverride : stProcessed;
    if (stSavingRef.current) return; // TB-118: ignore taps while a save is in flight
    // F-268b SUBSTANCE FLOOR — the CHOKE POINT. Every save arrives here (manual Save, the REVEAL-002
    // State-0 Save, the voice spoken-done auto-save, the F-7 update path), and this function writes
    // p.content straight to the entry below. The reveal-side gate stops a broken artifact being shown;
    // this one stops it being PERSISTED even if some future path reaches save without passing through
    // generation. Fail closed: refuse, keep the story on screen, and say so truthfully using the
    // existing save-failure copy — nothing is lost and the teller can retry.
    const saveFloor = checkSaveArtifact(p && p.content);
    if (!saveFloor.savable) {
      try { trackEvent('substance_floor_block', { at: 'save', reason: saveFloor.reason }); } catch (e) { /* telemetry */ }
      console.warn('[F-268b] save refused by the substance floor:', saveFloor.reason);
      showToast("Couldn't save your story. It's still here — tap Save to try again.", 'error');
      return;
    }
    stSavingRef.current = true;
    setStSaving(true);
    if (!stSaveIdRef.current) {
      stSaveIdRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    const targetBookId = ensureBookExists();
    try {
      // Upload audio to S3 if we have a recording
      // Save media — prefer video blob if present, fall back to audio
      const videoBlob = p.videoBlob;
      const audioOnlyBlob = p.audioBlob;
      const hasVideo = !!videoBlob;
      const recordingBlob = hasVideo ? videoBlob : audioOnlyBlob;
      let mediaPath = null;
      if (recordingBlob) {
        try {
          const safeName = (p.title || 'Voice_Story').replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '_');
          // TB-103: use preferred ext so Safari recordings land with the correct extension.
          const mediaExt = hasVideo ? preferredVideoExt : preferredAudioExt;
          const fileName = `${safeName}_${Date.now()}.${mediaExt}`;
          const { fetchAuthSession } = await import('aws-amplify/auth');
          const session = await fetchAuthSession();
          const identityId = session.identityId;
          const folder = hasVideo ? 'video' : 'audio';
          mediaPath = `private/${identityId}/${folder}/${fileName}`;
          await uploadData({ path: mediaPath, data: recordingBlob, options: { contentType: hasVideo ? preferredVideoContentType : preferredAudioContentType, metadata: { title: p.title } } });
        } catch (mediaErr) { console.warn('[Media Upload] Failed during save:', mediaErr.message); mediaPath = null; }
      }
      let contentToSave = p.content;
      if (mediaPath) { contentToSave += `\n\n[${hasVideo ? 'video' : 'audio'}:${mediaPath}]`; }
      // PIPE-SAVE-1 enforcement point: persist voiceConversationId on the entry so a voice-originated
      // save is attributable in production (the 2026-07-09 blast-radius had to proxy through QR links
      // because this was only ever transient React state). The field is only ADDED to the input when
      // it actually has a value, so a text/written save never sends it -- that decouples the existing
      // save paths from the optional schema field entirely (a text save cannot break if the field
      // isn't deployed yet). Only a voice save sends it, and voice ships together with the schema push.
      const entryInput = { id: stSaveIdRef.current, userId: user?.userId || user?.username, title: p.title, content: contentToSave, type: 'story', createdAt: new Date().toISOString() };
      if (p.voiceConversationId) entryInput.voiceConversationId = p.voiceConversationId;
      // STOP-TAG (F-157.2): persist the pack + prompt so a saved story is Journey-buckettable. Sourced from
      // the processed story (threaded from draftCtx at generation), with a live-state fallback for the
      // fresh-save path. Nullable + additive — omitted when unknown (direct-write / legacy rows stay null).
      const savePackId = p.packId || activePackId || null;
      const savePromptId = p.promptId || stSelectedPrompt?.id || null;
      if (savePackId) entryInput.packId = savePackId;
      if (savePromptId) entryInput.promptId = savePromptId;
      // BENCH-TAG-1 (F-171): persist which writer produced this story (threaded on the processed story from
      // generation, :5445). Additive/nullable — omitted when unknown (a reload-then-save that lost it, or a
      // non-whole-tale path) so the bench reads those as 'unknown' rather than a timestamp guess.
      const saveWriter = p.writerVersion || null;
      if (saveWriter) entryInput.writerVersion = saveWriter;
      // Idempotent retry: if page confirm failed earlier, stSaveIdRef was kept and the entry
      // may already exist. Treat ConditionalCheckFailed / already-exists as OK and continue
      // page attach + confirm only (no second orphan LegacyEntry).
      let newStoryId;
      try {
        const result = await client.graphql({ query: createLegacyEntry, variables: { input: entryInput } });
        const createdId = result?.data?.createLegacyEntry?.id;
        if (!createdId) {
          const graphErrors = result?.errors || [];
          const err = new Error(graphErrors.map(e => e?.message || '').join('; ') || 'createLegacyEntry returned no id');
          err.errors = graphErrors;
          throw err;
        }
        newStoryId = createdId;
      } catch (createErr) {
        let errBlob = '';
        try { errBlob = `${createErr?.message || ''} ${createErr?.name || ''} ${JSON.stringify(createErr?.errors || createErr)}`; } catch (e) { errBlob = String(createErr); }
        const alreadyExists = /ConditionalCheckFailed|already exists|duplicate/i.test(errBlob);
        if (alreadyExists && stSaveIdRef.current) {
          newStoryId = stSaveIdRef.current;
          console.warn('[saveStInterviewStory] createLegacyEntry id already exists — continuing page attach with', newStoryId);
        } else {
          throw createErr;
        }
      }
      // Auto-add to book AND immediately persist
      if (newStoryId) {
        // Activation funnel: saving a story is the activation event. book_type lets
        // us track own-story vs celebration as separate funnels.
        trackEvent('entry_saved', { source: 'interview', book_type: isGiftBook ? 'celebration' : 'personal' });
        // REVEAL-001 §6 (Correction 2): story_milestone + book_printable_reached fire at SAVE SUCCESS,
        // NOT at State-3 arrival — a user who saves then closes from State 1 still crossed the milestone.
        // Each guarded once (per-book/per-milestone flag) so a retry can't double-fire. (first_prompt_shown
        // is the only State-3 event; it fires when Pip's next question surfaces there.)
        try {
          const newChapterCount = (stBookPages.length || 0) + 1;
          const bookType = isGiftBook ? 'celebration' : 'personal';
          if ([2, 5, 10].includes(newChapterCount)) {
            const mKey = `story_milestone_${targetBookId}_${newChapterCount}`;
            if (!localStorage.getItem(mKey)) { localStorage.setItem(mKey, '1'); trackEvent('story_milestone', { count: newChapterCount, book_type: bookType }); }
          }
          const wasPrintable = !stBookEstimate(stBookPages.length || 0).needsMorePages;
          const nowPrintable = !stBookEstimate(newChapterCount).needsMorePages;
          const pKey = `book_printable_reached_${targetBookId}`;
          if (nowPrintable && !wasPrintable && !localStorage.getItem(pKey)) { localStorage.setItem(pKey, '1'); trackEvent('book_printable_reached', { chapters: newChapterCount, book_type: bookType }); }
        } catch (e) { /* analytics / localStorage must never break the save */ }
        let photoPath = null, photoUrl = null, photoBase64 = null;
        if (p.pendingPhotoFile) {
          try {
            const file = p.pendingPhotoFile;
            const ext = file.name.split('.').pop() || 'jpg';
            const fileName = `book_photo_${Date.now()}.${ext}`;
            const pathBuilder = ({ identityId }) => `private/${identityId}/photos/${fileName}`;
            const uploadResult = await (uploadData({ path: pathBuilder, data: file, options: { contentType: file.type, cacheControl: 'public, max-age=31536000, immutable' } })).result;
            photoPath = uploadResult?.path || null;
            if (photoPath) {
              const urlResult = await getUrl({ path: photoPath });
              photoUrl = urlResult?.url?.toString() || null;
              photoBase64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
            }
          } catch (photoErr) { console.warn('[Photo Upload] Failed during save:', photoErr.message); }
        }
        // Persist photoPath on the story itself
        if (photoPath) {
          try { await client.graphql({ query: updateLegacyEntry, variables: { input: { id: newStoryId, photoPath } } }); } catch (e) { console.warn('Failed to save photoPath on story:', e); }
        }
        const newPage = { storyId: newStoryId, photoUrl, photoPath, photoBase64, caption: '', photoSize: 'large', layout: photoPath ? 'photo-spread' : 'classic' };
        // Orphan-safe (BACKLOG orphan fix): base the page list on booksRef (which reflects any
        // save completed earlier this tick) rather than the possibly-stale stBookPages, so a rapid
        // second save can't drop the first save's page and leave its entry orphaned. buildUpdatedBookPages
        // keeps the ref's pages, overlays the rich in-session photo fields, and appends the new page.
        const refPages = (booksRef.current.find(b => b.id === targetBookId) || {}).pages || [];
        const updatedPages = buildUpdatedBookPages(refPages, stBookPages, newPage);
        setStBookPages(updatedPages);
        const bookData = { title: stBookTitle, subtitle: stBookSubtitle, author: stBookAuthor, dedication: stBookDedication, coverStyle: stBookCover, coverDesign: stBookCoverDesign, coverPhotoPath: stBookCoverPhotoPath, coverPhotoY: stBookCoverPhotoY, coverPhotoOrientation: stBookCoverPhotoOrientation, coverPhotoTone: stBookCoverPhotoTone, pages: cleanPagesForSave(updatedPages) };
        await saveBookData(bookData, targetBookId);
        // Auto-link QR code (opt-out: enabled by default when media exists)
        if (mediaPath && p.includeQr !== false) {
          await autoLinkRecordingToQr(newStoryId, mediaPath, p.title, updatedPages, hasVideo ? 'VIDEO' : 'AUDIO', targetBookId);
        } else if (p.voiceConversationId && p.includeQr !== false) {
          // Voice interview: there's no client-side blob, but ElevenLabs holds the full
          // conversation recording (interviewer + storyteller) server-side. Fetch it into
          // the user's private/ space, then QR-link it the same way. Best-effort — a
          // failure here must NOT block the save (the story is already created).
          try {
            const { fetchAuthSession } = await import('aws-amplify/auth');
            const session = await fetchAuthSession();
            const identityId = session.identityId;
            const convId = p.voiceConversationId;
            const voiceKey = `private/${identityId}/audio/voice_${convId}.mp3`;
            const audioResult = await callAi({ action: 'voice_audio', conversationId: convId, s3Key: voiceKey });
            if (audioResult && audioResult.s3Key) {
              await autoLinkRecordingToQr(newStoryId, audioResult.s3Key, p.title, updatedPages, 'AUDIO', targetBookId);
            }
          } catch (voiceAudioErr) {
            console.warn('[VoiceAudio] Could not attach the conversation recording:', voiceAudioErr.message);
          }
        }
      }
      await refreshStories();
      if (revealAfterSave || fromReveal) {
        // F-75 (production incident: "Chapter n, kept." but the story ORPHANED): the LegacyEntry is created,
        // but the book PAGE write above (saveBookData → persistBooks → persistUserData('legacyBook')) is
        // fire-and-forget and can be DROPPED by a last-write-wins race on the blob. CONFIRM the page actually
        // landed server-side BEFORE claiming "saved"/"kept" and BEFORE deleting the draft (the only recovery
        // copy). If it did not land, fail HONESTLY — keep the draft, stay on State 0, let the user retry —
        // never a silent orphan + a false "kept".
        const pageConfirmed = await confirmBookPagePersisted(targetBookId, newStoryId);
        if (!pageConfirmed) {
          showToast("Saved your words, but couldn't finish adding the page. Your story is safe. Tap Save to try again.", 'error');
          try { trackEvent('reveal_save_page_unconfirmed', { book_type: isGiftBook ? 'celebration' : 'personal' }); } catch (e) { /* telemetry */ }
          return false; // caller reverts revealKept; saved NOT set; draft NOT deleted → honest, retryable
        }
        // SAVE FIRST, stay on the reveal (PIPE-SAVE-1 F-3 + REVEAL-002 UNIFIED-FINISH): do NOT navigate
        // away or fire the generic celebration. Flip the review screen to a SAVED state — the chapter
        // stays the hero. savedStoryId powers Remove and the State-3 celebration the caller then shows.
        stSaveIdRef.current = null; // TB-118: page confirmed — next story gets a fresh id
        showToast('Saved to your book.');
        // SAVE-IDENTITY-1 (F-236): stamp WHICH generation reached the book. `p` is the story that was
        // just written, so its generationId is the one this savedStoryId belongs to. A later story
        // merged onto this object brings its own generationId and therefore reads as UNSAVED.
        setStProcessed({ ...p, saved: true, savedStoryId: newStoryId, savedGenerationId: p.generationId, dirty: false, draftId: null });
        // REVEAL-002 note 1: the draft is deleted ONLY here, AFTER a confirmed save (never delete-then-save).
        // The row that survived the crash/device-switch is now redundant with the real LegacyEntry.
        if (fromReveal && p.draftId) {
          try { await removeDraft(client, p.draftId); } catch (e) { console.warn('[REVEAL-002] draft cleanup after save failed (non-fatal):', e?.message); }
          try { trackEvent('story_saved_from_reveal', { book_type: isGiftBook ? 'celebration' : 'personal' }); } catch (e) { /* telemetry */ }
        }
        return newStoryId; // success signal so the State-0 caller can advance to the State-3 celebration
      } else {
        stSaveIdRef.current = null; // TB-118: saved — next story gets a fresh id
        showToast(`Story saved to ${stBookTitle || 'your book'}!`);
        // Capture for the completion celebration BEFORE we reset stProcessed.
        const savedTitle = p.title || 'Your story';
        const newChapterCount = (stBookPages.length || 0) + 1;
        const shouldEnhance = stEnhanceAfterSave && p.pendingPhotoFile;
        setStInterviewStep('pick'); setStInterviewMessages([]); setStProcessed({ title: '', content: '' }); setStSelectedPrompt(null);
        setStInterviewMode(false);
        setStPhotoEnhanceSkipped(false); setStEnhanceAfterSave(false);
        if (shouldEnhance) {
          setStBookStep(9); setStBookOpen(false); setActiveTabRaw('book'); window.scrollTo(0, 0);
        } else {
          // Completion loop: land on the book underneath, then celebrate on top.
          setStBookStep(0); setStBookOpen(false); setActiveTabRaw('book'); window.scrollTo(0, 0);
          setStStoryCelebration({ title: savedTitle, chapterCount: newChapterCount, storyId: newStoryId, canRemove: true });
        }
      }
    } catch (e) {
      // PIPE-SAVE-1 (rider 3): failure must be loud + actionable, never silent. The story is still
      // in stProcessed on the review screen; the Save button is re-enabled below, so tapping again
      // retries. Never imply the story was kept when it was not.
      showToast("Couldn't save your story. It's still here — tap Save to try again.", 'error');
      // TB-118: reset the id so a retry after a real failure isn't blocked by the
      // already-created guard (worst case on a mid-save partial failure: one duplicate,
      // same as before — never a blocked save).
      stSaveIdRef.current = null;
      return false; // REVEAL-002: failure signal — the State-0 caller must NOT advance to celebration
    } finally {
      stSavingRef.current = false;
      setStSaving(false);
    }
  };

  // REVEAL-002 R1 — draft lifecycle handlers exposed to the reveal (StRecordPage State 0).
  // Discard: the ONLY non-save way a draft leaves. Deletes the row server-side (nothing of the story
  // persists — the confirm copy is literally true), re-enables the orphan modal for the NEXT open
  // (note 2), then returns to New Story home with Pip. No residue.
  const discardUnsavedDraft = async () => {
    const id = stProcessed?.draftId;
    if (id) {
      try { await removeDraft(client, id); } catch (e) { console.warn('[REVEAL-002] discard delete failed (non-fatal):', e?.message); }
      try { trackEvent('story_discarded_unsaved', { book_type: isGiftBook ? 'celebration' : 'personal' }); } catch (e) { /* telemetry */ }
    }
    orphanSuppressedRef.current = false; // note 2: draft gone → the orphan modal is free next open
    // F-67: land on the CLEAN story-start, never a half-initialized answer screen. Discard used to set
    // interview mode TRUE with step 'pick' and a null prompt, which falls through to the answer/record
    // screen (StRecordPage ~2900) — for a celebration book that rendered "A question about Mom: \"\"" (an
    // empty-quotes question, the null prompt). Exiting interview mode (as the "← Back" clean-exit does)
    // lands on the book-aware topic picker: own-book → New Story home with Pip; celebration → that book's
    // clean story-start. setStSelectedPrompt(null) + empty stProcessed leave no residue either way.
    setStProcessed({ title: '', content: '' });
    setStSelectedPrompt(null);
    setStInterviewInput('');
    setStInterviewMessages([]);
    setStInterviewMode(false);
    setStInterviewStep('pick');
  };

  // REVEAL-002 R3: the veil marks the landing played ON THE ROW the instant it mounts (once per finish-
  // arrival). Setting it at veil-start (not veil-end) means a close mid-veil still leaves it marked, so a
  // resume / device switch NEVER replays the landing (grief-replay guarantee). Idempotent + best-effort.
  const markStoryLandingPlayed = async (draftId) => {
    if (!draftId) return;
    try {
      const played = await markLandingPlayed(client, draftId, new Date().toISOString());
      setStProcessed(p => (p && p.draftId === draftId ? { ...p, landingPlayedAt: (played && played.landingPlayedAt) || new Date().toISOString() } : p));
    } catch (e) { console.warn('[REVEAL-002] markLandingPlayed failed (non-fatal):', e?.message); }
  };

  // Edit-before-save (State 0): edits update the DRAFT row (there is no book entry yet). `fields` carry
  // the new title + version content; story_ready_edited fires on a real update.
  const updateStoryDraftEdits = async (fields) => {
    const id = stProcessed?.draftId;
    if (!id) return false;
    try {
      await saveDraftEdits(client, id, fields);
      try { trackEvent('story_ready_edited', { book_type: isGiftBook ? 'celebration' : 'personal' }); } catch (e) { /* telemetry */ }
      return true;
    } catch (e) { console.warn('[REVEAL-002] draft edit update failed:', e?.message); return false; }
  };

  // PIPE-SAVE-1 F-7: persist post-save edits on the reveal (title/story/version). Updates the EXISTING
  // saved entry (never creates a duplicate). Uses the SAME shape guard as saveStInterviewStory
  // (isProcessedStoryOverride) so a button wired onClick={updateSavedInterviewStory} passing a click
  // Event can never corrupt the update — the ef3f6ee outage class. Stopgap; superseded by the Reveal.
  // opts.silent: the REVEAL version switch persists which version prints with NO ceremony (founder
  // ruling: reading-level choice, reversible, both versions stay saved, self-labeling). A "Changes
  // saved." toast there answers a question nobody asked. State-2 "Save changes" still confirms normally.
  const updateSavedInterviewStory = async (processedOverride = null, opts = {}) => {
    const p = isProcessedStoryOverride(processedOverride) ? processedOverride : stProcessed;
    const id = p.savedStoryId;
    if (!id || stSavingRef.current) return;
    stSavingRef.current = true; setStSaving(true);
    try {
      await client.graphql({ query: updateLegacyEntry, variables: { input: { id, title: p.title, content: p.content } } });
      await refreshStories();
      setStProcessed(prev => ({ ...prev, dirty: false })); // back to the clean "Saved" chip
      if (!opts.silent) showToast('Changes saved.');
      return true;
    } catch (e) {
      if (!opts.silent) showToast("Couldn't save your changes. Tap Save changes to try again.", 'error');
      return false;
    } finally {
      stSavingRef.current = false; setStSaving(false);
    }
  };

  // Write Assist: polish rough text — uses shared 3-tier system
  const handleStWritePolish = async (tier = 'cleanup') => {
    if (!stWriteBody.trim()) return;
    if (!canUseAiPolish) {
      showToast("You've done a lot of editing today! Come back tomorrow to keep going.", "info");
      return;
    }
    setStOriginalContent(prev => prev === null ? stWriteBody : prev);
    setStWritePolishing(true);
    try {
      const sourceText = stOriginalContent !== null ? stOriginalContent : stWriteBody;
      const routing = resolveRepolishRouting(sourceText);
      // F-185: write-your-own now runs the SAME shared Whole/Flick as the save path (cleanup -> Flick; chapter ->
      // Whole + SHAPE-1 for own-story; a gift book routes to the third-person celebration branch). The drifted
      // getStoryCleanupPrompt is no longer used here.
      const { title, content } = await polishStoryText(sourceText, tier, { question: null, isCelebration: routing.isCelebration, recipientName: routing.recipientName, runShapePlan: routing.runShapePlan });
      if (content) {
        // F-187: never prepend the AI's title line to the body. Use the EXTRACTED body always; adopt the AI
        // title only when the user has no title yet, otherwise keep theirs. (The prior else set the full output
        // incl. the title line into the body when a title already existed.)
        if (title && !stWriteTitle.trim()) setStWriteTitle(title);
        setStWriteBody(content);
        incrementPolishCount();
        showToast('Done! Review and edit as you like.');
      }
    } catch (err) { showToast('Something went wrong.', 'error'); }
    setStWritePolishing(false);
  };


  // Save written story
  const handleStSaveWritten = async () => {
    if (!stWriteTitle.trim() || !stWriteBody.trim()) { showToast('Please add a title and your story.', 'error'); return; }
    if (stWriteSaving) return; // guard against double-submit
    const isFirstBook = booksRef.current.length === 0;
    // Capture the bookId synchronously — activeBookId state setter may not have
    // flushed yet when ensureBookExists creates a new book.
    const targetBookId = ensureBookExists();
    setStWriteSaving(true);
    setStWriteSavingPhase(0);
    // Time-based microcopy: bump phase at intervals so the wait feels intentional.
    const phaseTimers = [
      setTimeout(() => setStWriteSavingPhase(1), 1500),
      setTimeout(() => setStWriteSavingPhase(2), 4000),
      setTimeout(() => setStWriteSavingPhase(3), 8000),
    ];
    try {
      // Run createLegacyEntry and photo upload in parallel — they don't depend on each other.
      // Skip the post-upload getUrl: photoBase64 (createObjectURL) covers immediate display, and
      // the App.js photo-resolve effect re-derives photoUrl from photoPath in the background.
      const file = stWritePhotoFile;
      const photoPromise = file
        ? (async () => {
            try {
              const ext = file.name.split('.').pop() || 'jpg';
              const fileName = `book_photo_${Date.now()}.${ext}`;
              const pathBuilder = ({ identityId }) => `private/${identityId}/photos/${fileName}`;
              const uploadResult = await (uploadData({ path: pathBuilder, data: file, options: { contentType: file.type, cacheControl: 'public, max-age=31536000, immutable' } })).result;
              const photoPath = uploadResult?.path || null;
              const photoBase64 = URL.createObjectURL(file);
              return { photoPath, photoUrl: null, photoBase64 };
            } catch (photoErr) { console.warn('[Photo Upload] Failed during save:', photoErr.message); return { photoPath: null, photoUrl: null, photoBase64: null }; }
          })()
        : Promise.resolve({ photoPath: null, photoUrl: null, photoBase64: null });

      const entryPromise = client.graphql({ query: createLegacyEntry, variables: { input: { userId: user?.userId || user?.username, title: stWriteTitle.trim(), content: stWriteBody.trim(), type: 'story', createdAt: new Date().toISOString() } } });

      const [entryResult, photoResult] = await Promise.all([entryPromise, photoPromise]);
      const newStory = entryResult.data.createLegacyEntry;
      const newStoryId = newStory.id;
      const { photoPath, photoUrl, photoBase64 } = photoResult;

      // Optimistically add to in-memory stories so the chapter list and book
      // render find the new entry on next paint. Without this, navigation
      // happens before refreshStories completes and the page renders empty.
      // The trailing background refreshStories will replace this with the
      // canonical record (including any server-side fields like photoPath
      // from the fire-and-forget updateLegacyEntry).
      if (newStoryId) {
        const optimisticStory = { ...newStory, photoPath: photoPath || newStory.photoPath || null };
        setStories(prev => [optimisticStory, ...prev.filter(s => s.id !== newStoryId)]);
      }

      if (newStoryId) {
        if (photoPath) {
          client.graphql({ query: updateLegacyEntry, variables: { input: { id: newStoryId, photoPath } } }).catch(e => console.warn('Failed to save photoPath on story:', e));
        }
        const newPage = { storyId: newStoryId, photoUrl, photoPath, photoBase64, caption: '', photoSize: 'large', layout: photoPath ? 'photo-spread' : 'classic' };
        // Read the target book's existing pages from the ref (always current),
        // not stBookPages (state can be stale if activeBookId just changed).
        const targetBook = booksRef.current.find(b => b.id === targetBookId);
        const existingPages = targetBook?.pages || [];
        const updatedPages = [...existingPages, newPage];
        setStBookPages(updatedPages);
        const bookData = { title: stBookTitle, subtitle: stBookSubtitle, author: stBookAuthor, dedication: stBookDedication, coverStyle: stBookCover, coverDesign: stBookCoverDesign, coverPhotoPath: stBookCoverPhotoPath, coverPhotoY: stBookCoverPhotoY, coverPhotoOrientation: stBookCoverPhotoOrientation, coverPhotoTone: stBookCoverPhotoTone, pages: cleanPagesForSave(updatedPages) };
        saveBookData(bookData, targetBookId);
      }
      showToast(isFirstBook ? "We created your first book and added this chapter." : 'Story added to your book!');
      try { localStorage.removeItem(`remnant_draft_${user?.userId || user?.username}`); } catch(e) {}
      const shouldEnhance = stEnhanceAfterSave && stWritePhotoFile;
      setStWriteTitle(''); setStWriteBody(''); setStOriginalContent(null); setStWritePhotoFile(null); setStWritePhotoPreview(null);
      setStPhotoEnhanceSkipped(false); setStEnhanceAfterSave(false);
      // skipSave: we just persisted fresh pages via saveBookData. saveActiveBook
      // would re-save using stale stBookPages state (setState hasn't flushed),
      // overwriting the fresh save. Skip it.
      if (shouldEnhance) {
        setActiveTab('st-book', { skipSave: true });
        setTimeout(() => setStBookStep(9), 100);
      } else {
        // F-73 (founder ruling): after a written save, land on the SAME question picker the interview
        // tell-another lands on (the prompts that spark the next story), not the book view. Exit interview
        // mode so StRecordPage renders its book-aware picker, never a half-initialized answer screen.
        setStInterviewMode(false); setStInterviewStep('pick'); setStSelectedPrompt(null); setStInterviewMessages([]);
        setActiveTab('st-record', { skipSave: true });
      }
      refreshStories().catch(() => {});
    } catch (e) { showToast('Save failed.', 'error'); }
    finally {
      phaseTimers.forEach(clearTimeout);
      setStWriteSaving(false);
      setStWriteSavingPhase(0);
    }
  };

  // Handle post-recording processing (reuses existing startStoryRecording/stopStoryRecording from legacy)
  const handleStPostRecord = async () => {
    if (!storyAudioBlob) { showToast("No recording found.", 'error'); setStPostRecordStep(null); return; }
    setStPostRecordStep('processing');
    try {
      const audioBlob = storyAudioBlob; // preserve before any clearing
      const videoBlob = storyVideoBlob || null; // carry video blob if present
      const text = await transcribeAudio(audioBlob, storyAudioChunksRef.current);
      if (!text || !text.trim()) { showToast("Couldn't hear anything. Try again.", 'error'); setStPostRecordStep(null); return; }
      const title = stSelectedPrompt?.text || 'My Story';
      setStProcessed({ title, content: text.trim(), raw: true, audioBlob, videoBlob, includeQr: true });
      setStPostRecordStep('review');
    } catch (e) { console.error(e); showToast('We had trouble transcribing your recording. Please try again or try a shorter recording.', 'error'); setStPostRecordStep(null); }
  };

  // Polish story from simple recording — now uses shared 3-tier system
  const handleStPolishStory = async (tier = 'cleanup') => {
    const sourceText = stProcessed.originalContent || stProcessed.content;
    if (!sourceText?.trim()) return;
    // Check cache first (no API call, no count)
    if (stProcessed.tierCache?.[tier] && stProcessed.tierCache._sourceText === sourceText) {
      const cached = stProcessed.tierCache[tier];
      setStProcessed(p => ({ ...p, title: cached.title, content: cached.content, raw: false, polishTier: tier }));
      setStPostRecordStep('review');
      return;
    }
    // Gate: check AI polish limit
    if (!canUseAiPolish) {
      showToast("You've done a lot of editing today! Come back tomorrow to keep going.", "info");
      return;
    }
    const originalContent = stProcessed.originalContent || stProcessed.content;
    const originalTitle = stProcessed.originalTitle || stProcessed.title;
    setStOriginalContent(prev => prev === null ? sourceText : prev);
    setStPostRecordStep('processing');
    try {
      // F-185: shared Whole/Flick (gift-book stays third-person via the celebration branch). Preserves tierCache.
      const routing = resolveRepolishRouting(sourceText);
      const polished = await polishStoryText(sourceText, tier, { question: stSelectedPrompt?.text, isCelebration: routing.isCelebration, recipientName: routing.recipientName, runShapePlan: routing.runShapePlan });
      const title = polished.title || stProcessed.title || 'My Story';
      const content = polished.content;
      const tierCache = { ...(stProcessed.tierCache || {}), [tier]: { title, content }, _sourceText: sourceText };
      setStProcessed(p => ({ ...p, title, content, raw: false, originalContent, originalTitle, polishTier: tier, tierCache }));
      incrementPolishCount();
      setStPostRecordStep('review');
    } catch (e) {
      console.error('Polish error:', e);
      const errBody = e?.response?.body ? await e.response.body.json().catch(() => null) : null;
      showToast('Something went wrong. Please try again — if it keeps happening, contact us.', 'error');
      setStProcessed(p => ({ ...p, title: originalTitle, content: originalContent, raw: true }));
      setStPostRecordStep('review');
    }
  };

  // Save the polished story from recording
  const handleStSaveRecorded = async () => {
    if (stSavingRef.current) return; // TB-118: ignore taps while a save is in flight
    stSavingRef.current = true;
    setStSaving(true);
    if (!stSaveIdRef.current) {
      stSaveIdRef.current = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
    }
    const targetBookId = ensureBookExists();
    try {
      // Save media to S3 — prefer video blob if present, fall back to audio
      const videoBlob = stProcessed.videoBlob || storyVideoBlob;
      const audioOnlyBlob = stProcessed.audioBlob || storyAudioBlob;
      const hasVideo = !!videoBlob;
      const recordingBlob = hasVideo ? videoBlob : audioOnlyBlob;
      let mediaPath = null;
      if (recordingBlob) {
        try {
          const safeName = (stProcessed.title || 'Voice_Story').replace(/[^a-zA-Z0-9-_ ]/g, '').replace(/\s+/g, '_');
          // TB-103: use preferred ext so Safari recordings land with the correct extension.
          const mediaExt = hasVideo ? preferredVideoExt : preferredAudioExt;
          const fileName = `${safeName}_${Date.now()}.${mediaExt}`;
          const { fetchAuthSession } = await import('aws-amplify/auth');
          const session = await fetchAuthSession();
          const identityId = session.identityId;
          const folder = hasVideo ? 'video' : 'audio';
          mediaPath = `private/${identityId}/${folder}/${fileName}`;
          await uploadData({ path: mediaPath, data: recordingBlob, options: { contentType: hasVideo ? preferredVideoContentType : preferredAudioContentType, metadata: { title: stProcessed.title } } });
        } catch (mediaErr) { console.warn('[Media Upload] Failed during save:', mediaErr.message); mediaPath = null; }
      }
      let contentToSave = stProcessed.content;
      if (mediaPath) { contentToSave += `\n\n[${hasVideo ? 'video' : 'audio'}:${mediaPath}]`; }
      const result = await client.graphql({ query: createLegacyEntry, variables: { input: { id: stSaveIdRef.current, userId: user?.userId || user?.username, title: stProcessed.title, content: contentToSave, type: 'story', createdAt: new Date().toISOString() } } });
      const newStoryId = result.data.createLegacyEntry.id;
      // Auto-add to book AND immediately persist
      if (newStoryId) {
        // Activation funnel: saving a story is the activation event. book_type lets
        // us track own-story vs celebration as separate funnels.
        trackEvent('entry_saved', { source: 'interview', book_type: isGiftBook ? 'celebration' : 'personal' });
        // REVEAL-001 §6 (Correction 2): story_milestone + book_printable_reached fire at SAVE SUCCESS,
        // NOT at State-3 arrival — a user who saves then closes from State 1 still crossed the milestone.
        // Each guarded once (per-book/per-milestone flag) so a retry can't double-fire. (first_prompt_shown
        // is the only State-3 event; it fires when Pip's next question surfaces there.)
        try {
          const newChapterCount = (stBookPages.length || 0) + 1;
          const bookType = isGiftBook ? 'celebration' : 'personal';
          if ([2, 5, 10].includes(newChapterCount)) {
            const mKey = `story_milestone_${targetBookId}_${newChapterCount}`;
            if (!localStorage.getItem(mKey)) { localStorage.setItem(mKey, '1'); trackEvent('story_milestone', { count: newChapterCount, book_type: bookType }); }
          }
          const wasPrintable = !stBookEstimate(stBookPages.length || 0).needsMorePages;
          const nowPrintable = !stBookEstimate(newChapterCount).needsMorePages;
          const pKey = `book_printable_reached_${targetBookId}`;
          if (nowPrintable && !wasPrintable && !localStorage.getItem(pKey)) { localStorage.setItem(pKey, '1'); trackEvent('book_printable_reached', { chapters: newChapterCount, book_type: bookType }); }
        } catch (e) { /* analytics / localStorage must never break the save */ }
        let photoPath = null, photoUrl = null, photoBase64 = null;
        if (stProcessed.pendingPhotoFile) {
          try {
            const file = stProcessed.pendingPhotoFile;
            const ext = file.name.split('.').pop() || 'jpg';
            const fileName = `book_photo_${Date.now()}.${ext}`;
            const pathBuilder = ({ identityId }) => `private/${identityId}/photos/${fileName}`;
            const uploadResult = await (uploadData({ path: pathBuilder, data: file, options: { contentType: file.type, cacheControl: 'public, max-age=31536000, immutable' } })).result;
            photoPath = uploadResult?.path || null;
            if (photoPath) {
              const urlResult = await getUrl({ path: photoPath });
              photoUrl = urlResult?.url?.toString() || null;
              photoBase64 = await new Promise((res, rej) => { const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file); });
            }
          } catch (photoErr) { console.warn('[Photo Upload] Failed during save:', photoErr.message); }
        }
        // Persist photoPath on the story itself
        if (photoPath) {
          try { await client.graphql({ query: updateLegacyEntry, variables: { input: { id: newStoryId, photoPath } } }); } catch (e) { console.warn('Failed to save photoPath on story:', e); }
        }
        const newPage = { storyId: newStoryId, photoUrl, photoPath, photoBase64, caption: '', photoSize: 'large', layout: photoPath ? 'photo-spread' : 'classic' };
        // Orphan-safe (BACKLOG orphan fix): see buildUpdatedBookPages — base on booksRef, not the
        // possibly-stale stBookPages, so a rapid second save can't orphan the first save's page.
        const refPages = (booksRef.current.find(b => b.id === targetBookId) || {}).pages || [];
        const updatedPages = buildUpdatedBookPages(refPages, stBookPages, newPage);
        setStBookPages(updatedPages);
        const bookData = { title: stBookTitle, subtitle: stBookSubtitle, author: stBookAuthor, dedication: stBookDedication, coverStyle: stBookCover, coverDesign: stBookCoverDesign, coverPhotoPath: stBookCoverPhotoPath, coverPhotoY: stBookCoverPhotoY, coverPhotoOrientation: stBookCoverPhotoOrientation, coverPhotoTone: stBookCoverPhotoTone, pages: cleanPagesForSave(updatedPages) };
        saveBookData(bookData, targetBookId);
        // Auto-link QR code (opt-out: enabled by default when media exists)
        if (mediaPath && stProcessed.includeQr !== false) {
          await autoLinkRecordingToQr(newStoryId, mediaPath, stProcessed.title, updatedPages, hasVideo ? 'VIDEO' : 'AUDIO');
        }
      }
      stSaveIdRef.current = null; // TB-118: saved — next story gets a fresh id
      showToast('Story added to your book!');
      const shouldEnhanceRec = stEnhanceAfterSave && stProcessed.pendingPhotoFile;
      setStPostRecordStep(null); setStProcessed({ title: '', content: '' }); setStSelectedPrompt(null);
      setStoryAudioBlob(null); setStoryAudioUrl('');
      setStPhotoEnhanceSkipped(false); setStEnhanceAfterSave(false);
      setStoryVideoBlob(null); setStoryVideoUrl('');
      await refreshStories();
      if (shouldEnhanceRec) {
        setActiveTab('st-book');
        setTimeout(() => setStBookStep(9), 100);
      } else if (!mediaPath) {
        setActiveTab('st-book');
      }
    } catch (e) {
      showToast('Save failed.', 'error');
      stSaveIdRef.current = null; // TB-118: allow a clean retry after a real failure
    } finally {
      stSavingRef.current = false;
      setStSaving(false);
    }
  };

  const [stInterviewPolishing, setStInterviewPolishing] = React.useState(false);

  const handleInterviewPolish = async (tier = 'cleanup') => {
    if (!stProcessed.content?.trim() && !stProcessed.originalContent?.trim()) return;
    const sourceText = stProcessed.originalContent || stProcessed.content;
    // Check cache first (no API call, no count)
    if (stProcessed.tierCache?.[tier] && stProcessed.tierCache._sourceText === sourceText) {
      const cached = stProcessed.tierCache[tier];
      setStProcessed(p => ({ ...p, title: cached.title, content: cached.content, raw: false, polishTier: tier }));
      return;
    }
    // Gate: check AI polish limit
    if (!canUseAiPolish) {
      showToast("You've done a lot of editing today! Come back tomorrow to keep going.", "info");
      return;
    }
    const originalContent = stProcessed.originalContent || stProcessed.content;
    const originalTitle = stProcessed.originalTitle || stProcessed.title;
    setStOriginalContent(prev => prev === null ? sourceText : prev);
    setStInterviewPolishing(true);
    try {
      // F-185: shared Whole/Flick (gift-book stays third-person via the celebration branch). Preserves tierCache.
      const routing = resolveRepolishRouting(sourceText);
      const polished = await polishStoryText(sourceText, tier, { question: stSelectedPrompt?.text, isCelebration: routing.isCelebration, recipientName: routing.recipientName, runShapePlan: routing.runShapePlan });
      const title = polished.title || stProcessed.title || 'My Story';
      const content = polished.content;
      const tierCache = { ...(stProcessed.tierCache || {}), [tier]: { title, content }, _sourceText: sourceText };
      setStProcessed(p => ({ ...p, title, content, raw: false, originalContent, originalTitle, polishTier: tier, tierCache }));
      incrementPolishCount();
      showToast('Done! Review and edit as you like.');
    } catch (e) {
      console.error('Interview polish error:', e);
      showToast('Something went wrong.', 'error');
    }
    setStInterviewPolishing(false);
  };

  // TB-082: Storyteller mode removed. Story Manager sidebar is the single layout for all users.
  // renderStorytellerMode() and storytellerTabs were here — deleted for launch scope tightening.

  // Mobile nav — launch scope (session 74): Books / Write / Account.
  // Photo/Gift/Menu tabs removed — Gift Books fold into My Books grid; Photo Manager
  // is contextual to a book (not a top-level destination); Menu drawer held only
  // Account, which is now its own direct tab.
  const mobileNavItems = [
    { id: 'talk',     icon: <Feather size={18} weight="duotone" />,   label: 'New Story' },
    { id: 'book',     icon: <Book size={18} weight="duotone" />,         label: 'My Book' },
    { id: 'account',  icon: <Gear size={18} weight="duotone" />,         label: 'Account' },
  ];

  // ── Journal ──────────────────────────────────────────────
  // renderJournal — extracted to pages/JournalPage.js



  const feedbackTabLabels = {
    book: 'My Book', journal: 'Journal', stories: 'My Stories', capsule: 'Time Capsules',
    estate: 'Estate Planning', recipients: 'My Recipients',
    account: 'Account', upgrade: 'Upgrade', admin: 'Beta Feedback',
    ai: 'Write Story', memory: 'Interview',
  };

  // ── AppContext value — shared state for extracted page components ──
  const appContextValue = React.useMemo(() => ({
    // User & auth
    userId, displayName, beatName, isAdmin, isKeeper, subjectName, signOut,
    // Navigation & mode
    activeTab, setActiveTab, setActiveTabRaw,
    // Memory Care coming-soon gate (F-12)
    MEMORY_CARE_ENABLED,
    openMemoryCareComingSoon: () => setShowMemoryCareComingSoon(true),
    submitMemoryCareInterest,
    // F-13: in-app gift sheet opener (for the milestone whisper on the reveal screen)
    openGiftSheet: (source) => { try { trackEvent('gift_cta_click', { source: source || 'app', location: source || 'app' }); } catch (e) { /* telemetry */ } setShowGiftSheet(true); },
    // Stories
    stories, loadingStories, refreshStories, executeDeleteStory,
    // Multi-book system (from useUserData)
    books, setBooks, booksRef, activeBookId, setActiveBookId, activeBook, isGiftBook,
    persistBooks, saveBookData, removeStoryPageEverywhere, removeStoryPageFromBook,
    // Collections (from useUserData)
    collections, setCollections, saveCollections,
    // Recipients & sharing (from useUserData)
    recipients, setRecipients, saveRecipients,
    shares, setShares, saveShares,
    newRecipient, setNewRecipient, savingRecipient,
    handleAddRecipient, handleRemoveRecipient, handleSetAccessLevel,
    getSharedCount, getItemShares, openShareModal,
    // Account settings (from useUserData)
    accountName, setAccountName, accountSettings, setAccountSettings, saveAccountSettings,
    legacyMode, setLegacyMode, legacySubjectName, setLegacySubjectName,
    notifyCheckin, setNotifyCheckin, notifyRelease, setNotifyRelease,
    notifyCapsule, setNotifyCapsule, releaseMethod, setReleaseMethod,
    checkinFrequency, setCheckinFrequency,
    // Capsules & journal (from useUserData)
    capsules, setCapsules, saveCapsules,
    journalEntries, setJournalEntries, saveJournalEntries,
    // Journal page state
    journalText, setJournalText, journalMood, setJournalMood,
    journalAudioBlob, setJournalAudioBlob, journalAudioUrl, setJournalAudioUrl,
    journalAudioPlayUrl, journalRecording, journalRecordingTime, setJournalRecordingTime,
    startJournalRecording, stopJournalRecording,
    savingJournal, setSavingJournal,
    viewingJournalEntry, setViewingJournalEntry,
    editingJournalEntry, setEditingJournalEntry, editJournalText, setEditJournalText,
    transcribeAudio, saveJournalAudioToVault, groupJournalByDate,
    // Capsule page state
    capsuleType, setCapsuleType, capsuleAiStarted, setCapsuleAiStarted,
    capsuleAiChat, setCapsuleAiChat, capsuleDraft, setCapsuleDraft,
    capsuleRecipientId, setCapsuleRecipientId,
    capsuleDelivery, setCapsuleDelivery, capsuleDate, setCapsuleDate,
    capsuleAiLoading, capsuleReply, setCapsuleReply,
    handleCapsuleAIReply, handleCapsuleAIStart, handleCapsuleExpand,
    capsuleLiveVideoRef, capsuleVideoRecording, capsuleVideoBlob, capsuleVideoUrl,
    setCapsuleVideoBlob, setCapsuleVideoUrl,
    startCapsuleRecording, stopCapsuleRecording,
    capsuleTitle, setCapsuleTitle, handleSaveCapsule, savingCapsule,
    editingCapsuleId, editCapsuleTitle, setEditCapsuleTitle,
    editCapsuleContent, setEditCapsuleContent,
    editCapsuleRecipientId, setEditCapsuleRecipientId,
    editCapsuleDelivery, setEditCapsuleDelivery, editCapsuleDate, setEditCapsuleDate,
    handleCancelCapsuleEdit, handleSaveCapsuleEdit,
    handleEditCapsule, handleDeleteCapsule,
    accountSection, setAccountSection,
    // Account page state
    formatBytes, videoTotalBytes, videoFiles,
    mfaLoading, mfaEnabled, mfaStep, setMfaStep,
    mfaSetupUri, setMfaSetupUri, mfaVerifyCode, setMfaVerifyCode,
    mfaError, setMfaError,
    handleMfaSetup, handleMfaVerify, handleMfaDisable,
    userDataLoaded, persistUserData,
    // Estate planning state (DynamoDB-persisted via useUserData)
    estateChecks, estateContacts, setEstateContacts, estateNotes, setEstateNotes,
    saveEstateNotes, saveEstateContacts,
    executorLetter, setExecutorLetter, executorLetterAiDraft, setExecutorLetterAiDraft,
    estateTab, setEstateTab, toggleCheck,
    savingEstate, setSavingEstate, executorLetterLoading, handleExecutorLetterAI,
    // Book builder state
    stBookPages, setStBookPages, stBookTitle, setStBookTitle,
    stBookSubtitle, setStBookSubtitle, stBookAuthor, setStBookAuthor,
    stBookDedication, setStBookDedication, stBookCover, setStBookCover, stBookCoverDesign, setStBookCoverDesign,
    stBookCoverPhotoPath, setStBookCoverPhotoPath,
    stBookCoverPhotoUrl, setStBookCoverPhotoUrl,
    stBookCoverPhotoBase64, setStBookCoverPhotoBase64, resolveCoverPhotoBase64,
    stBookCoverPhotoY, setStBookCoverPhotoY,
    stBookCoverPhotoOrientation, setStBookCoverPhotoOrientation,
    stBookCoverPhotoTone, setStBookCoverPhotoTone,
    stBookStep, setStBookStep, stBookOpen, setStBookOpen,
    stBookPreviewIdx, setStBookPreviewIdx,
    stBookSaveStatus, setStBookSaveStatus,
    saveActiveBook, enterBook, exitToBookList, createPersonalBook, ensureBookExists, deleteBook,
    // Files & vault
    files, fileUrls, loadingFiles, refreshFileList,
    // Premium
    isPremium,
    // Toast
    showToast,
    // Helpers
    getBookStories, getCollectionStories,
    addStoryToBook, removeStoryFromBook, moveStoryInBook,
    addStoryToCollection, removeStoryFromCollection,
    createCollection, deleteCollection,
    syncGiftBookChapters,
    // Welcome
    showWelcome, setShowWelcome, dismissWelcome,
    // Book builder extras (for BookBuilderPage)
    showGiftCreate, setShowGiftCreate,
    hideGiftCta, setHideGiftCta,
    betaDismissed, setBetaDismissed,
    cbCreateStep, setCbCreateStep, cbOccasion, setCbOccasion,
    cbRecipientName, setCbRecipientName, cbDeliveryDate, setCbDeliveryDate,
    cbCustomOccasion, setCbCustomOccasion,
    cbAuthorRelationship, setCbAuthorRelationship,
    cbExtraCopies, setCbExtraCopies, cbExtraAddresses, setCbExtraAddresses,
    cbUseSameAddress, setCbUseSameAddress,
    expandedContributor, setExpandedContributor,
    editingBook, setEditingBook,
    stBookEditIdx, setStBookEditIdx,
    stBookPhotoUploading, setStBookPhotoUploading,
    stBookPhotoRef,
    stChapterSearch, setStChapterSearch,
    stMemoryMode, setStMemoryMode, stMemoryTitle, setStMemoryTitle,
    stMemoryContent, setStMemoryContent, stMemoryPhotoPath, setStMemoryPhotoPath,
    stMemoryPhotoPreview, setStMemoryPhotoPreview, stMemoryPhotoBase64, setStMemoryPhotoBase64,
    stOrderStep, setStOrderStep, stOrderAddress, setStOrderAddress,
    stOrderId, setStOrderId, stOrderError, setStOrderError,
    stOrderLoading, setStOrderLoading, stOrderStatus, setStOrderStatus,
    stCheckingStatus, setStCheckingStatus,
    stInviteQuestion, setStInviteQuestion, stInviteMessage, setStInviteMessage,
    stInviteLink, setStInviteLink, stInviteLoading, setStInviteLoading,
    stInviteCopied, setStInviteCopied, stInviteName, setStInviteName,
    stInviteRelationship, setStInviteRelationship, stInvites, setStInvites,
    stPreviewPhotos,
    photoQualityWarning, setPhotoQualityWarning,
    stPhotoStoryLoading, setStPhotoStoryLoading,
    stBookAiOpen, setStBookAiOpen, stBookAiLoading, setStBookAiLoading, stBookAiResult, setStBookAiResult,
    chapterQrCodes, setChapterQrCodes,
    chapterMediaModal, setChapterMediaModal,
    chapterMediaType, setChapterMediaType,
    chapterRecording, setChapterRecording,
    chapterMediaBlob, setChapterMediaBlob,
    chapterMediaUrl, setChapterMediaUrl,
    chapterMediaSaving, setChapterMediaSaving,
    chapterLiveVideoRef,
    startChapterRecording, stopChapterRecording,
    removeChapterMedia, linkInterviewRecording, resetChapterMedia,
    photosResolving,
    setupBlankQrForAllChapters, blankQrBusy, isBookToolsOwner,
    streetInputRef, autocompleteRef,
    stStoryList,
    canUseAiPolish, incrementPolishCount,
    setStories, client, user,
    giftSyncedRef,
    // Navigation helpers for cross-tab links
    setStShowGateway, setStInterviewMode,
    // Memory Care subjects (persisted)
    memoryCareSubjects, saveMemoryCareSubjects, saveMemoryCareSubject,
    // Memory Care guided flow
    mcStep, setMcStep, mcSubject, setMcSubject,
    mcMood, setMcMood, mcSessionTopic, setMcSessionTopic,
    mcSuggestion, mcSuggestionLoading, fetchSessionSuggestion,
    mcTargetBookId, setMcTargetBookId,
    mcShaping, handleShapeIntoChapter, mcOriginalTranscript, handleRestoreTranscript,
    // Memory interview state
    memoryStep, setMemoryStep, memoryTab, setMemoryTab,
    memoryDementiaMode, setMemoryDementiaMode,
    memoryDayQuality, setMemoryDayQuality,
    memoryTopic, setMemoryTopic,
    memorySessionLength, setMemorySessionLength,
    memorySubjectName, setMemorySubjectName,
    memoryCarerName, setMemoryCarerName,
    startMemoryInterview,
    memoryMessages, memoryLoading,
    memoryInput, setMemoryInput,
    sendMemoryMessage, generateMemoryStory, resetMemoryInterview,
    memoryTitle, setMemoryTitle,
    memoryStory, setMemoryStory,
    memoryPhotoPreview, setMemoryPhotoPreview,
    memoryPhotoFile, setMemoryPhotoFile,
    saveMemoryInterview, savingMemory,
    interviewAudioUrl, interviewAudioBlob,
    setInterviewAudioBlob, setInterviewAudioUrl,
    interviewRecording, startInterviewRecording, stopInterviewRecording,
    interviewVideoMode, setInterviewVideoMode,
    interviewVideoBlob, setInterviewVideoBlob, interviewVideoUrl, setInterviewVideoUrl,
    interviewLiveVideoRef,
    transcribing,
    // StRecord page state
    stShowGateway, stInterviewMode,
    storyRecording, stopStoryRecording,
    stPostRecordStep, stAutoTranscribeTriggered,
    stSelectedPrompt, stShowPromptPicker, setStShowPromptPicker,
    stStartMode, setStStartMode,
    activePackId, setActivePackId,
    forcePackPicker, setForcePackPicker,
    seedlessStart, setSeedlessStart, // F-80: doorway seedless "Start talking" handshake
    pathOpenPackId, setPathOpenPackId,
    stShuffleIdx, setStShuffleIdx,
    stAiQuestion, setStAiQuestion, stAiQuestionLoading,
    mothersAudience, setMothersAudience,
    stInterviewStep, setStInterviewStep,
    stGenerationFailure, setStGenerationFailure, // F-334 persistent failure banner
    stStreamPreview, // F-415-STREAM: the Whole's text so far, for the generating screen
    stDraftWords, // F-415-STREAM: the assembler draft's live word count (strip, phase 1)
    stInterviewInput, setStInterviewInput,
    stInterviewMessages, setStInterviewMessages,
    stInterviewLoading, stInterviewPolishing,
    stProcessed, setStProcessed,
    stOriginalContent, setStOriginalContent,
    handleStPostRecord, handleStPolishStory, handleStSaveRecorded,
    startStInterview, generateStInterviewStory,
    retryPolishedGeneration, // TB-134 QA fix: retry affordance for a degraded (Flick-only) result
    // TB-104 Stage 2a: adaptive engine state and entry points
    submitFirstAnswer, sendStInterviewMsg, stInterviewVoiceReply,
    stInterviewEndRef,
    interviewFollowUpCount, interviewConversation, interviewLastClassification,
    // TB-104 Stage 3 (FIX 6): expose engine reset setters so StRecordPage can clear state on Back
    setInterviewFollowUpCount, setInterviewAskedMoves, setInterviewConversation,
    setInterviewLastClassification, setInterviewEmptyCount,
    // TB-104 Stage B: explicit wrap boolean (replaces fragile isWrappedUp string-scan)
    interviewIsWrapped, setInterviewIsWrapped,
    // TB-104 keep-going: budget bonus, wrap reason, and keep-going handler
    interviewBudgetBonus, setInterviewBudgetBonus,
    interviewWrapReason, setInterviewWrapReason,
    interviewWrapOfferCount, setInterviewWrapOfferCount, // TB-134 fix 1
    handleInterviewKeepGoing,
    // TB-104 steering: one "ask me something else" control + rephrase + write-up-so-far (Back save path)
    writeUpInterviewSoFar,
    // TB-104 thread-salience: deepened threads list (for de-dup across turns)
    interviewDeepenedThreads, setInterviewDeepenedThreads,
    // TB-104 breadth-fix: consecutive zoom counter (for anti-tunnel-vision)
    interviewConsecutiveZoomCount, setInterviewConsecutiveZoomCount,
    handleInterviewPolish, saveStInterviewStory,
    removeRevealedStory, // PIPE-SAVE-1 F-3: remove from the saved review/reveal
    updateSavedInterviewStory, // PIPE-SAVE-1 F-7: persist post-save edits on the reveal
    discardUnsavedDraft, // REVEAL-002 R1: State-0 Discard — delete the draft, back to New Story home
    updateStoryDraftEdits, // REVEAL-002 R1: State-0 Edit — persist edits to the draft row
    markStoryLandingPlayed, // REVEAL-002 R3: veil marks landingPlayedAt on mount (once per finish-arrival)
    reopenStoryForMore, // REVEAL-001 C5: "Tell Pip more" — forward-only reopen of a saved chapter
    stReopenStoryId, // REVEAL-001 C5 Guard 1: truthy while a reopened session is active (Finish guard)
    stSaving, // TB-118: disable save buttons while a save is in flight
    // TB-104 Stage 2b: Hot Seat rapid-fire state and handler
    generateHotSeatStory,
    rapidAnswers, setRapidAnswers,
    rapidCurrentIdx, setRapidCurrentIdx,
    generateAiQuestion, autoLinkRecordingToQr,
    // StWrite page state
    stWriteTitle, setStWriteTitle, stWriteBody, setStWriteBody,
    stWritePhotoFile, setStWritePhotoFile, stWritePhotoPreview, setStWritePhotoPreview,
    stWritePolishing, stWriteSaving, stWriteSavingPhase, handleStWritePolish, handleStSaveWritten,
    stPhotoEnhanceSkipped, setStPhotoEnhanceSkipped,
    stEnhanceAfterSave, setStEnhanceAfterSave,
    // Shared recording state (StWrite + StRecord)
    setStSelectedPrompt, storyAudioBlob, setStoryAudioBlob, storyAudioUrl, setStoryAudioUrl,
    storyVideoMode, setStoryVideoMode, storyVideoBlob, setStoryVideoBlob, storyVideoUrl, setStoryVideoUrl,
    storyLiveVideoRef,
    setStPostRecordStep, setStAutoTranscribeTriggered, startStoryRecording,
    // Admin
    adminFeedback, adminLoading, adminFilter, setAdminFilter,
    loadAdminFeedback, markFeedbackReviewed,
    // Keepsake & Book Reader
    handleEditStory, audioPlayerRef,
    bookReaderOpen, setBookReaderOpen,
    bookReaderChapter, setBookReaderChapter,
    bookReaderSource, setBookReaderSource,
    // Home page
    viewingStory, setViewingStory, quickCapture, setQuickCapture,
    quickCaptureExpanded, setQuickCaptureExpanded, savingQuickCapture, handleQuickCapture,
    completedSections, renderStoryContent,
  })); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <AppContext.Provider value={appContextValue}>
      {toast && (
        <div className={`toast toast-${toast.type}`} onClick={() => setToast(null)}>
          {toast.type === 'success' && '✓ '}{toast.type === 'error' && '✕ '}{toast.message}
        </div>
      )}
      <ShareModal />

      {/* TB-082: Storyteller mode removed. Story Manager runs for all authenticated users. */}

      <>

      {/* Keepsake Story View */}
      {<KeepsakeView />}

      {/* Book Reader */}
      {<BookReader />}

      {/* Orphan recovery — re-link stories that lost their place in a book (post multi-device clobber) */}
      <OrphanRecoveryModal
        orphanStories={orphanStories}
        books={books}
        defaultBookId={activeBookId || (() => { try { return localStorage.getItem('remnant_lastBookId'); } catch { return null; } })() || (books[0] && books[0].id) || null}
        onRecover={recoverOrphanToBook}
        onDismiss={dismissOrphanPrompt}
      />

      {/* Feedback Modal */}
      {/* F-12: Memory Care coming-soon modal (opened by gated subject-card / switcher taps). */}
      <MemoryCareComingSoon
        variant="modal"
        open={showMemoryCareComingSoon}
        onClose={() => setShowMemoryCareComingSoon(false)}
        onInterest={submitMemoryCareInterest}
      />

      {/* F-13: in-app gift sheet (warm path) */}
      <GiftSheet open={showGiftSheet} onClose={() => setShowGiftSheet(false)} />

      {showContact && (
        <div className="feedback-overlay" data-journey-modal="contact" onClick={() => setShowContact(false)}>
          <div className="feedback-modal" onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
            <div className="feedback-modal-header">
              <h3>Message us</h3>
              <button className="feedback-modal-close" onClick={() => setShowContact(false)}>×</button>
            </div>
            <div className="feedback-modal-body">
              {contactSuccess ? (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <p style={{ fontFamily: "'Lora', serif", fontSize: '1rem', color: '#0f172a', letterSpacing: '2px' }}>MESSAGE SENT</p>
                  <p style={{ color: '#0f172a', fontSize: '1rem', marginTop: '8px', fontStyle: 'italic' }}>We'll reply to your email as soon as we can. Thank you for reaching out.</p>
                </div>
              ) : (
                <>
                  <p style={{ color: '#0f172a', fontSize: '1rem', marginBottom: '12px', textAlign: 'center' }}>
                    Ask us anything, or tell us what's on your mind. A real person reads every message and replies straight to your email.
                  </p>
                  <textarea className="inp" placeholder="How can we help?"
                    value={contactMessage} onChange={e => setContactMessage(e.target.value)}
                    style={{ minHeight: '120px', fontSize: '1.05rem' }} />
                  <button className="btn btn-primary" style={{ width: '100%', marginTop: '8px' }}
                    onClick={submitContact} disabled={contactSubmitting || contactMessage.trim().length < 2}>
                    {contactSubmitting ? 'Sending...' : 'Send message'}
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {showFeedback && (
        <div className="feedback-overlay" data-journey-modal="feedback" onClick={() => setShowFeedback(false)}>
          <div className="feedback-modal" onClick={e => e.stopPropagation()} onMouseDown={e => e.stopPropagation()}>
            <div className="feedback-modal-header">
              <h3>Send Feedback</h3>
              <button className="feedback-modal-close" onClick={() => setShowFeedback(false)}>×</button>
            </div>
            <div className="feedback-modal-body">
              {feedbackSuccess ? (
                <div style={{ textAlign: 'center', padding: '24px 0' }}>
                  <div style={{ fontSize: '2.5rem', marginBottom: '14px' }}></div>
                  <p style={{ fontFamily: "'Lora', serif", fontSize: '1rem', color: '#0f172a', letterSpacing: '2px' }}>THANK YOU</p>
                  <p style={{ color: '#0f172a', fontSize: '1rem', marginTop: '8px', fontStyle: 'italic' }}>Your feedback helps us build something meaningful.</p>
                </div>
              ) : (
                <>
                  <div className="feedback-context">
                    Currently viewing: <strong style={{ color: '#3c4653' }}>{feedbackTabLabels[activeTab] || activeTab}</strong>
                  </div>

                  <p style={{ color: '#0f172a', fontSize: '1rem', marginBottom: '12px', textAlign: 'center' }}>
                    How is your experience so far?
                  </p>
                  <div className="feedback-stars">
                    {[1, 2, 3, 4, 5].map(n => (
                      <button key={n} className="feedback-star" onClick={() => setFeedbackRating(n)}
                        style={{ color: n <= feedbackRating ? '#c9a84c' : '#d4c8b0' }}>
                        {n <= feedbackRating ? '★' : '☆'}
                      </button>
                    ))}
                  </div>

                  <textarea className="inp" placeholder="What were you doing? What could be better? Any bugs?"
                    value={feedbackComment} onChange={e => setFeedbackComment(e.target.value)}
                    style={{ minHeight: '100px', fontSize: '1.05rem' }} />

                  <button className="btn btn-primary" style={{ width: '100%', marginTop: '8px' }}
                    onClick={submitFeedback} disabled={feedbackSubmitting || feedbackRating === 0}>
                    {feedbackSubmitting ? 'Sending...' : 'Submit Feedback'}
                  </button>

                  <p style={{ fontSize: '0.9rem', color: '#7a6b5a', textAlign: 'center', marginTop: '14px', lineHeight: 1.5 }}>
                    Need help with something specific?{' '}
                    <a href="mailto:support@taleboundbook.com" style={{ color: '#7c2d12', textDecoration: 'underline' }}>
                      Email support@taleboundbook.com
                    </a>
                  </p>
                </>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Welcome modal removed — first-run users land on the book-type chooser instead
          (see the showWelcome effect above). Book type is chosen at creation, which
          frames Pip's questions; no separate onboarding modal needed. */}

      {/* ═══ COMPLETION LOOP — Pip celebrates a saved chapter, then points to "tell another" / "see my book" ═══ */}
      {stStoryCelebration && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2100, background: 'radial-gradient(120% 80% at 50% 20%, #FFF6E2 0%, #FBF1E0 55%, #F4E7D0 100%)', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', textAlign: 'center', padding: 24 }}>
          {/* Pip holds the finished book at the "ready to print" milestone; cheers you on otherwise. */}
          <img src={stBookEstimate(stBookPages.length).needsMorePages ? pipCelebrating : pipHoldingBook} alt="Pip" style={{ height: 200, maxHeight: '34vh', objectFit: 'contain', filter: 'drop-shadow(0 14px 20px rgba(120,70,20,0.22))' }} />
          <h2 style={{ fontFamily: "'Lora', serif", fontWeight: 600, fontSize: '1.7rem', color: '#1E293B', margin: '18px 16px 6px' }}>That's a beautiful one!</h2>
          <p style={{ fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: '1.1rem', color: '#7a6b54', margin: '0 24px 28px', maxWidth: 420, lineHeight: 1.5 }}>
            "{stStoryCelebration.title}" is saved. That's chapter {stStoryCelebration.chapterCount} in your book.
          </p>
          <div style={{ fontFamily: "'Lora', serif", fontSize: '0.95rem', fontWeight: 800, color: '#92400e', background: 'rgba(220,160,60,0.14)', borderRadius: 9999, padding: '8px 18px', marginBottom: 26 }}>
            {(() => {
              const est = stBookEstimate(stBookPages.length);
              if (!est.needsMorePages) return '🎉 Your book is ready to print';
              const s = Math.max(1, Math.ceil(est.pagesNeeded / 3));
              return `About ${s} more ${s === 1 ? 'story' : 'stories'} until you can print`;
            })()}
          </div>
          <div style={{ width: '100%', maxWidth: 360, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <button onClick={() => { setStStoryCelebration(null); setActiveTab('talk'); }}
              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 9, width: '100%', background: 'linear-gradient(135deg,#F0A24C,#D2761F)', color: '#fff', border: 'none', borderRadius: 24, padding: 18, fontFamily: "'Lora', serif", fontSize: '1.15rem', fontWeight: 800, cursor: 'pointer', boxShadow: '0 10px 24px rgba(210,118,31,0.4), inset 0 1px 0 rgba(255,255,255,0.3)' }}>
              <span style={{ fontSize: '1.3rem' }}>🎙</span> Tell me another
            </button>
            <button onClick={() => { setStStoryCelebration(null); setActiveTab('st-book'); }}
              style={{ width: '100%', background: 'transparent', color: '#7a5a10', border: 'none', fontFamily: "'Lora', serif", fontStyle: 'italic', fontSize: '1.05rem', cursor: 'pointer', padding: 8 }}>
              See my book
            </button>
            {/* PIPE-SAVE-1 (rider 2): one-tap undo. Shown for auto-saved stories so a save the user
                did not intend (incl. a false-positive done-signal) is trivially reversible. */}
            {stStoryCelebration.canRemove && stStoryCelebration.storyId && (
              <button onClick={() => handleRemoveSavedStory(stStoryCelebration.storyId)}
                style={{ width: '100%', background: 'transparent', color: '#9a8570', border: 'none', fontFamily: "'Lora', serif", fontSize: '0.9rem', cursor: 'pointer', padding: 6, textDecoration: 'underline' }}>
                Remove this story
              </button>
            )}
          </div>
        </div>
      )}

      {/* ═══ CHAPTER MEDIA RECORDING MODAL ═══ */}
      {chapterMediaModal && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(44,36,32,0.6)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          onClick={(e) => { if (e.target === e.currentTarget) { resetChapterMedia(); setChapterMediaModal(null); } }}>
          <div style={{ background: '#fbf8f1', border: '1px solid rgba(15,23,42,0.12)', borderRadius: 16, maxWidth: 500, width: '100%', maxHeight: '90vh', overflowY: 'auto', padding: '32px 28px' }}>
            <h3 style={{ fontFamily: "'Lora', serif", fontSize: '1.4rem', color: '#0f172a', marginBottom: 8 }}>
              {chapterMediaType === 'video' ? ' Record a Video Message' : ' Record a Voice Message'}
            </h3>
            <p style={{ fontSize: '1rem', color: '#3c4653', marginBottom: 20, lineHeight: 1.6, fontStyle: 'italic' }}>
              This will be accessible via a QR code in the printed book.
            </p>

            {/* Type toggle */}
            <div style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
              <button onClick={() => { if (!chapterRecording) setChapterMediaType('audio'); }}
                style={{ flex: 1, padding: '10px', borderRadius: 8, border: chapterMediaType === 'audio' ? '2px solid #0f172a' : '1px solid rgba(15,23,42,0.15)', background: chapterMediaType === 'audio' ? 'rgba(15,23,42,0.05)' : '#fff', cursor: 'pointer', fontSize: '1rem', fontFamily: "'Lora', serif" }}>
                 Audio
              </button>
              <button onClick={() => { if (!chapterRecording) setChapterMediaType('video'); }}
                style={{ flex: 1, padding: '10px', borderRadius: 8, border: chapterMediaType === 'video' ? '2px solid #0f172a' : '1px solid rgba(15,23,42,0.15)', background: chapterMediaType === 'video' ? 'rgba(15,23,42,0.05)' : '#fff', cursor: 'pointer', fontSize: '1rem', fontFamily: "'Lora', serif" }}>
                 Video
              </button>
            </div>

            {/* Live video preview */}
            {chapterMediaType === 'video' && chapterRecording && (
              <video ref={chapterLiveVideoRef} muted playsInline style={{ width: '100%', borderRadius: 12, marginBottom: 16, background: '#000' }} />
            )}

            {/* Playback preview */}
            {chapterMediaUrl && !chapterRecording && (
              <div style={{ marginBottom: 16 }}>
                {chapterMediaType === 'video' ? (
                  <video controls src={chapterMediaUrl} style={{ width: '100%', borderRadius: 12, background: '#000' }} />
                ) : (
                  <audio controls src={chapterMediaUrl} style={{ width: '100%' }} />
                )}
              </div>
            )}

            {/* Recording controls */}
            <div style={{ display: 'flex', gap: 10, marginBottom: 16 }}>
              {!chapterRecording && !chapterMediaUrl && (
                <button onClick={() => startChapterRecording(chapterMediaType)}
                  style={{ flex: 1, padding: '14px', borderRadius: 10, border: 'none', background: '#c53030', color: '#fff', cursor: 'pointer', fontSize: '1rem', fontFamily: "'Lora', serif", fontWeight: 600, letterSpacing: 1 }}>
                  ● Start Recording
                </button>
              )}
              {chapterRecording && (
                <button onClick={stopChapterRecording}
                  style={{ flex: 1, padding: '14px', borderRadius: 10, border: 'none', background: '#0f172a', color: '#fff', cursor: 'pointer', fontSize: '1rem', fontFamily: "'Lora', serif", fontWeight: 600, letterSpacing: 1, animation: 'pulse 1.5s ease infinite' }}>
                  ■ Stop Recording
                </button>
              )}
              {chapterMediaUrl && !chapterRecording && (
                <>
                  <button onClick={() => { resetChapterMedia(); }}
                    style={{ flex: 1, padding: '14px', borderRadius: 10, border: '1px solid rgba(15,23,42,0.15)', background: '#fff', cursor: 'pointer', fontSize: '1rem', color: '#0f172a' }}>
                    Re-record
                  </button>
                  <button onClick={saveChapterMedia} disabled={chapterMediaSaving}
                    style={{ flex: 1, padding: '14px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #7c2d12 0%, #8a6420 100%)', color: '#fff', cursor: 'pointer', fontSize: '1rem', fontFamily: "'Lora', serif", fontWeight: 600, letterSpacing: 1, opacity: chapterMediaSaving ? 0.6 : 1 }}>
                    {chapterMediaSaving ? 'Saving...' : '✓ Save'}
                  </button>
                </>
              )}
            </div>

            <button onClick={() => { resetChapterMedia(); setChapterMediaModal(null); }}
              style={{ width: '100%', padding: '12px', background: 'none', border: 'none', cursor: 'pointer', fontSize: '1rem', color: '#3c4653', fontStyle: 'italic' }}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* ═══ MEDIA PLAYER MODAL — for playing recordings from Archive ═══ */}
      {playingMedia && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 1100, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)' }} onClick={() => setPlayingMedia(null)} />
          <div style={{ position: 'relative', background: '#fbf8f1', borderRadius: 20, padding: '28px', maxWidth: 560, width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.25)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
              <h3 style={{ fontFamily: "'Lora', serif", fontSize: '1.15rem', color: '#0f172a', margin: 0 }}>
                {playingMedia.mediaType === 'VIDEO' ? 'Video' : 'Audio'} Recording
              </h3>
              <button onClick={() => setPlayingMedia(null)} style={{ background: 'none', border: 'none', fontSize: '1.4rem', cursor: 'pointer', color: '#3c4653', padding: 4 }}>×</button>
            </div>
            {playingMedia.mediaType === 'VIDEO' ? (
              <video controls autoPlay src={playingMedia.url} style={{ width: '100%', borderRadius: 12, background: '#000', maxHeight: '60vh' }} />
            ) : (
              <audio controls autoPlay src={playingMedia.url} style={{ width: '100%' }} />
            )}
          </div>
        </div>
      )}

      {/* ═══ QR CODE PROMPT MODAL — shown after saving a story with audio ═══ */}
      {qrPrompt && (
        <div style={{ position: 'fixed', inset: 0, zIndex: 2000, background: 'rgba(44,36,32,0.6)', backdropFilter: 'blur(6px)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}
          onClick={(e) => { if (e.target === e.currentTarget) setQrPrompt(null); }}>
          <div style={{ background: '#fbf8f1', border: '1px solid rgba(15,23,42,0.12)', borderRadius: 16, maxWidth: 440, width: '100%', padding: '40px 32px', textAlign: 'center' }}>
            <div style={{ fontSize: '2.5rem', marginBottom: 16 }}></div>
            <h3 style={{ fontFamily: "'Lora', serif", fontSize: '1.35rem', color: '#0f172a', marginBottom: 12 }}>
              Include your voice in the printed book?
            </h3>
            <p style={{ fontSize: '1.05rem', color: '#3c4653', lineHeight: 1.7, marginBottom: 28, fontStyle: 'italic' }}>
              A QR code will appear in the book so readers can scan and hear your original recording of this story.
            </p>
            <div style={{ display: 'flex', gap: 12, justifyContent: 'center' }}>
              <button
                style={{ padding: '14px 28px', borderRadius: 10, border: 'none', background: '#0f172a', color: '#fff', cursor: 'pointer', fontSize: '1rem', fontFamily: "'Lora', serif", fontWeight: 600, letterSpacing: 1 }}
                onClick={async () => {
                  const { storyId, s3Key, title, pages } = qrPrompt;
                  setQrPrompt(null);
                  await autoLinkRecordingToQr(storyId, s3Key, title, pages);
                }}>
                Yes, add QR code
              </button>
              <button
                style={{ padding: '14px 28px', borderRadius: 10, border: '1px solid rgba(15,23,42,0.15)', background: '#fff', cursor: 'pointer', fontSize: '1rem', color: '#3c4653', fontFamily: "'Lora', serif" }}
                onClick={() => setQrPrompt(null)}>
                Not now
              </button>
            </div>
            <p style={{ fontSize: '0.85rem', color: '#3c4653', marginTop: 16 }}>
              You can always add or change this later from the chapter editor.
            </p>
          </div>
        </div>
      )}

      <div className="remnant-shell">
        {/* Mobile top header */}
        <header className="mobile-header">
          <div className="mobile-header-brand">
            Talebound
          </div>
          {/* F-13b: gift pill → GHOST OUTLINE (transparent, amber text, thin warm border, smaller padding).
              Click/events unchanged from F-13. Desktop sidebar pill stays filled. */}
          <button data-testid="header-gift-pill"
            onClick={() => { try { trackEvent('gift_cta_click', { source: 'header', location: 'app_header' }); } catch (e) { /* telemetry */ } setShowGiftSheet(true); }}
            style={{ marginLeft: 'auto', display: 'inline-flex', alignItems: 'center', gap: 5, background: 'transparent', color: '#92400e', border: '1.5px solid rgba(146,64,14,0.4)', borderRadius: 9999, padding: '5px 12px', fontFamily: "'Lora', Georgia, serif", fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
            <Gift size={15} weight="fill" /> Gift
          </button>
        </header>

        {/* Desktop sidebar */}
        <aside className="sidebar">
          <div className="sidebar-brand">
            <h1>Talebound</h1>
          </div>
          <div className="sidebar-user" style={{ cursor: 'pointer' }} onClick={() => setActiveTab('account')}>
            <p>Signed in as</p>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <span>{displayName}</span>
              <span style={{ fontSize: '1rem', opacity: 0.5, marginLeft: '8px' }} title="Account Settings"></span>
            </div>
          </div>
          {/* F-13: gift pill on desktop (no desktop top bar, so it lives in the sidebar) */}
          <button data-testid="sidebar-gift-pill"
            onClick={() => { try { trackEvent('gift_cta_click', { source: 'header', location: 'app_sidebar' }); } catch (e) { /* telemetry */ } setShowGiftSheet(true); }}
            style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 6, background: '#92400e', color: '#fbf8f1', border: 'none', borderRadius: 9999, padding: '10px 18px', margin: '4px 16px 12px', fontFamily: "'Lora', Georgia, serif", fontSize: 15, fontWeight: 600, cursor: 'pointer', boxShadow: '0 1px 3px rgba(0,0,0,0.12)' }}>
            <Gift size={17} weight="fill" /> Gift
          </button>
          <nav className="sidebar-nav">
            {navItems.map(item => (
              <button key={item.id} data-testid={`nav-${item.id}`} className={`nav-item${(activeTab === item.id || (item.id === 'talk' && (activeTab === 'st-record' || activeTab === 'st-write'))) ? ' active' : ''}`} onClick={() => item.id === 'book' ? openMyBook() : setActiveTab(item.id)}>
                <span className="nav-icon">{item.icon}</span>
                {item.label}
              </button>
            ))}
            {/* TB-082: Plans & Pricing link removed from launch scope */}
          </nav>
          <div className="sidebar-footer">
            <button className="signout-btn" onClick={signOut}>Sign Out</button>
          </div>
        </aside>

        <main className="main-content">

          {activeTab === 'talk'       && <OwnStoryHome />}
          {activeTab === 'book'       && <BookBuilderPage />}
          {activeTab === 'giftbooks'  && <GiftBooksTab />}
          {activeTab === 'st-record'  && <StRecordPage />}
          {activeTab === 'st-write'   && <StWritePage />}
          {activeTab === 'journal'    && <JournalPage />}
          {activeTab === 'memory'     && <MemoryInterviewPage />}
          {activeTab === 'stories' && (
            <Archive
              stories={stories}
              loadingStories={loadingStories}
              bookStoryIds={stBookPages.map(p => p.storyId).filter(Boolean)}
              onViewStory={(story) => setViewingStory(story)}
              onUpdateStory={async (id, title, content) => {
                try {
                  await client.graphql({ query: updateLegacyEntry, variables: { input: { id, title, content } } });
                  const res = await client.graphql({ query: listLegacyEntries, variables: { filter: { userId: { eq: user.userId || user.username } }, limit: 500 } });
                  setStories(res.data.listLegacyEntries.items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
                  showToast('Updated!', 'success');
                } catch (err) { console.error('Update error:', err); showToast('Failed to update.', 'error'); }
              }}
              onDeleteStory={handleDeleteStory}
              onAddToBook={(storyId) => {
                if (stBookPages.some(p => p.storyId === storyId)) return;
                // Restore photo from story record (persisted in DynamoDB)
                const story = stories.find(s => s.id === storyId);
                const storyPhotoPath = story?.photoPath || null;
                // Also check in-session stash as fallback
                const saved = removedPagesRef.current[storyId];
                const photoPath = saved?.photoPath || storyPhotoPath;
                if (saved) delete removedPagesRef.current[storyId];
                setStBookPages([...stBookPages, {
                  storyId, photoUrl: null, photoPath,
                  caption: saved?.caption || '', photoSize: saved?.photoSize || 'large',
                  layout: saved?.layout || (photoPath ? 'photo-spread' : 'classic'),
                }]);
                showToast('Added to your book!', 'success');
              }}
              onRemoveFromBook={(storyId) => {
                // Stash page data so photos survive re-adding
                const page = stBookPages.find(p => p.storyId === storyId);
                if (page && page.photoPath) {
                  removedPagesRef.current[storyId] = { ...page };
                }
                setStBookPages(stBookPages.filter(p => p.storyId !== storyId));
                // F-252: tombstone this book, or the additive merge puts the page straight back on
                // the next persist. Non-destructive: the story stays in the library, and re-adding
                // clears the tombstone via the clearTombstones call in saveActiveBook.
                removeStoryPageFromBook(activeBookId, storyId);
                showToast('Removed from book.', 'success');
              }}
              onSaveNew={async ({ title, content, type }) => {
                setSavingStory(true);
                try {
                  const now = new Date();
                  await client.graphql({ query: createLegacyEntry, variables: { input: { userId: user.userId || user.username, title, content, type, createdAt: now.toISOString() } } });
                  const res = await client.graphql({ query: listLegacyEntries, variables: { filter: { userId: { eq: user.userId || user.username } }, limit: 500 } });
                  setStories(res.data.listLegacyEntries.items.sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt)));
                  showToast('Saved to your archive!', 'success');
                } catch (err) { console.error('Save error:', err); showToast('Failed to save.', 'error'); }
                setSavingStory(false);
              }}
              savingStory={savingStory}
              journalEntries={journalEntries}
              onNavigateToJournal={() => setActiveTab('journal')}
              recordings={recordings}
              loadingRecordings={loadingRecordings}
              onDeleteRecording={handleDeleteRecording}
              onUnlinkRecording={handleUnlinkRecording}
              onPlayRecording={handlePlayRecording}
            />
          )}
          {/* TB-082: cut routes gated off. Code kept for post-launch revival. */}
          {false && activeTab === 'estate'     && <EstatePlanningPage />}
          {false && activeTab === 'recipients' && <RecipientsPage />}
          {false && activeTab === 'capsule'    && <TimeCapsulePage />}
          {activeTab === 'account'    && (
            <>
              <div style={{ maxWidth: 720, margin: '0 auto', padding: '18px 20px', background: '#faf7f0', border: '1px solid #eadfc7', borderRadius: 14 }}>
                <div style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', color: '#3c2f1e', marginBottom: 4 }}>Need help? We're here.</div>
                <div style={{ color: '#7a6b5a', fontSize: '0.95rem', marginBottom: 14, lineHeight: 1.5 }}>Have a question, feeling stuck, or just want to share a thought? Reach us directly. A real person reads every message and replies to your email.</div>
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                  <button className="btn btn-primary" style={{ flex: '1 1 150px' }} onClick={() => setShowContact(true)}>Message us</button>
                  <button className="btn" style={{ flex: '1 1 150px', background: '#fff', border: '1px solid #cbb994', color: '#3c2f1e' }} onClick={() => setShowFeedback(true)}>Send feedback</button>
                </div>
              </div>
              <AccountPage />
            </>
          )}
          {activeTab === 'admin' && isAdmin && <AdminPage />}
          {false && activeTab === 'upgrade' && (
            <div>
              <div className="page-header">
                <h2 style={{ fontSize: '2.2rem' }}>Plans & Pricing</h2>
                <p>Start free. Order your book when you're ready. Keep going with Premium.</p>
              </div>

              {/* Beta banner */}
              {isPremium && (
                <div style={{ maxWidth: 880, marginBottom: 28, padding: '16px 24px', background: 'linear-gradient(135deg, rgba(184,148,72,0.08), rgba(184,148,72,0.03))', border: '1px solid rgba(184,148,72,0.25)', borderRadius: 12, display: 'flex', alignItems: 'center', gap: 12 }}>
                  <span style={{ fontSize: '1.3rem' }}>✦</span>
                  <p style={{ fontSize: '1rem', color: '#0f172a', lineHeight: 1.5 }}>
                    <strong>You're a beta tester.</strong> All features are unlocked for you during the beta — no charge. Thank you for being here early.
                  </p>
                </div>
              )}

              {/* 3 pricing cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: 20, maxWidth: 920, marginBottom: 40 }}>

                {/* ── FREE ── */}
                <div style={{ background: '#faf8f5', border: '2px solid rgba(15,23,42,0.1)', borderRadius: 16, padding: '32px 28px', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ marginBottom: 24 }}>
                    <p style={{ fontFamily: "'Lora', serif", fontSize: '0.85rem', letterSpacing: 3, textTransform: 'uppercase', color: '#3c4653', marginBottom: 8 }}>Start Here</p>
                    <div style={{ fontFamily: "'Lora', serif", fontSize: '2.4rem', color: '#0f172a', fontWeight: 400, marginBottom: 4 }}>Free</div>
                    <p style={{ fontSize: '0.95rem', color: '#3c4653' }}>No credit card needed</p>
                  </div>
                  <div style={{ flex: 1, marginBottom: 24 }}>
                    {[
                      'Write unlimited stories',
                      'Record your voice or type it out',
                      'Preview your book anytime',
                      'Professional story editing',
                      'Interview someone you love',
                      'Memory Care — guided interviews for dementia',
                    ].map(f => (
                      <div key={f} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
                        <span style={{ color: '#3c4653', fontSize: '1rem', lineHeight: '1.5', flexShrink: 0 }}>✓</span>
                        <span style={{ fontSize: '1rem', color: '#0f172a', lineHeight: 1.5 }}>{f}</span>
                      </div>
                    ))}
                  </div>
                  <div style={{ padding: '14px 24px', background: 'rgba(15,23,42,0.06)', borderRadius: 10, textAlign: 'center', fontFamily: "'Lora', serif", fontSize: '1rem', color: '#3c4653', letterSpacing: 1 }}>
                    Your current plan
                  </div>
                </div>

                {/* ── ORDER YOUR BOOK ── */}
                <div style={{ background: '#faf8f5', border: '2px solid rgba(15,23,42,0.35)', borderRadius: 16, padding: '32px 28px', display: 'flex', flexDirection: 'column', position: 'relative', boxShadow: '0 4px 24px rgba(15,23,42,0.08)' }}>
                  {/* Recommended badge */}
                  <div style={{ position: 'absolute', top: -13, left: '50%', transform: 'translateX(-50%)', background: '#0f172a', color: '#fbf8f1', fontSize: '0.75rem', letterSpacing: 2, textTransform: 'uppercase', padding: '5px 16px', borderRadius: 20, fontFamily: "'Lora', serif", whiteSpace: 'nowrap' }}>
                    Most Popular
                  </div>
                  <div style={{ marginBottom: 24 }}>
                    <p style={{ fontFamily: "'Lora', serif", fontSize: '0.85rem', letterSpacing: 3, textTransform: 'uppercase', color: '#3c4653', marginBottom: 8 }}>Your Keepsake Book</p>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                      <span style={{ fontFamily: "'Lora', serif", fontSize: '2.4rem', color: '#0f172a', fontWeight: 400 }}>$89</span>
                      <span style={{ fontFamily: "'Lora', serif", fontSize: '1.2rem', color: '#0f172a' }}>.99</span>
                    </div>
                    <p style={{ fontSize: '0.95rem', color: '#3c4653' }}>One-time · free shipping</p>
                  </div>
                  <div style={{ flex: 1, marginBottom: 24 }}>
                    {[
                      'Everything in Free',
                      'Beautiful hardcover printed book',
                      'Professional book layout',
                      'Free shipping to your door',
                    ].map(f => (
                      <div key={f} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
                        <span style={{ color: '#0f172a', fontSize: '1rem', lineHeight: '1.5', flexShrink: 0 }}>✓</span>
                        <span style={{ fontSize: '1rem', color: '#0f172a', lineHeight: 1.5, fontWeight: f === 'Beautiful hardcover printed book' ? 600 : 400 }}>{f}</span>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => setActiveTab('book')} style={{
                    padding: '14px 24px', width: '100%',
                    background: '#0f172a', color: '#fbf8f1',
                    border: 'none', borderRadius: 10, cursor: 'pointer',
                    fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: 1,
                    transition: 'all 0.2s',
                  }}
                  onMouseOver={e => { e.target.style.background = '#0f172a'; }}
                  onMouseOut={e => { e.target.style.background = '#0f172a'; }}
                  >
                    Go to My Book →
                  </button>
                </div>

                {/* ── PREMIUM ── */}
                <div style={{ background: 'linear-gradient(165deg, #faf8f5, rgba(184,148,72,0.04))', border: '2px solid rgba(184,148,72,0.2)', borderRadius: 16, padding: '32px 28px', display: 'flex', flexDirection: 'column' }}>
                  <div style={{ marginBottom: 24 }}>
                    <p style={{ fontFamily: "'Lora', serif", fontSize: '0.85rem', letterSpacing: 3, color: '#a08030', marginBottom: 8 }}>Talebound Premium</p>
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
                      <span style={{ fontFamily: "'Lora', serif", fontSize: '2.4rem', color: '#0f172a', fontWeight: 400 }}>$5</span>
                      <span style={{ fontFamily: "'Lora', serif", fontSize: '1.2rem', color: '#0f172a' }}>.99</span>
                      <span style={{ fontSize: '1rem', color: '#3c4653', marginLeft: 2 }}>/month</span>
                    </div>
                    <p style={{ fontSize: '0.95rem', color: '#3c4653' }}>Cancel anytime</p>
                  </div>
                  <div style={{ flex: 1, marginBottom: 24 }}>
                    {[
                      'Everything in Book',
                      'Digital Archive — recipes, letters, traditions, photos',
                      'Time Capsules — sealed letters delivered in the future',
                      'Estate Planning — wills, directives, digital assets',
                      'Recipients — choose who gets what, and when',
                      'Journal — private daily reflections',
                      'Discounted books ($69.99 each)',
                      'Early access to new features',
                    ].map((f, i) => (
                      <div key={f} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 10 }}>
                        <span style={{ color: '#a08030', fontSize: '1rem', lineHeight: '1.5', flexShrink: 0 }}>✓</span>
                        <span style={{ fontSize: '1rem', color: '#0f172a', lineHeight: 1.5 }}>{f}</span>
                      </div>
                    ))}
                  </div>
                  {isPremium ? (
                    <div style={{ padding: '14px 24px', background: 'rgba(184,148,72,0.1)', borderRadius: 10, textAlign: 'center', fontFamily: "'Lora', serif", fontSize: '1rem', color: '#a08030', letterSpacing: 1 }}>
                      ✦ Unlocked (Beta)
                    </div>
                  ) : (
                    <button onClick={unlockPremium} style={{
                      padding: '14px 24px', width: '100%',
                      background: 'linear-gradient(135deg, #7c2d12, #a08030)',
                      color: '#0a0a08', border: 'none', borderRadius: 10, cursor: 'pointer',
                      fontFamily: "'Lora', serif", fontSize: '1rem', letterSpacing: 1,
                      fontWeight: 600, transition: 'all 0.2s',
                      boxShadow: '0 2px 12px rgba(184,148,72,0.2)',
                    }}
                    onMouseOver={e => { e.target.style.transform = 'translateY(-1px)'; e.target.style.boxShadow = '0 4px 20px rgba(184,148,72,0.3)'; }}
                    onMouseOut={e => { e.target.style.transform = 'translateY(0)'; e.target.style.boxShadow = '0 2px 12px rgba(184,148,72,0.2)'; }}
                    >
                      Start Premium →
                    </button>
                  )}
                </div>
              </div>

              {/* Story editing explainer */}
              <div style={{ maxWidth: 880, padding: '24px 28px', background: 'rgba(15,23,42,0.03)', borderRadius: 12, border: '1px solid rgba(15,23,42,0.06)', marginBottom: 20 }}>
                <p style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', color: '#0f172a', marginBottom: 8 }}>About story editing</p>
                <p style={{ fontSize: '1rem', color: '#3c4653', lineHeight: 1.7 }}>
                  Every edit is purpose-built for a printed book — not a generic text cleanup. Choose from two levels: smooth your words into something readable, or let Pip rewrite your rough notes into a beautiful, flowing chapter. Your voice stays on every page. The rough edges don't. These aren't generic edits you'd get anywhere — they understand spoken stories, keep the way you actually talk, and format everything for a hardcover book.
                </p>
              </div>

              {/* How it works */}
              <div style={{ maxWidth: 880, padding: '24px 28px', background: 'rgba(15,23,42,0.03)', borderRadius: 12, border: '1px solid rgba(15,23,42,0.06)' }}>
                <p style={{ fontFamily: "'Lora', serif", fontSize: '1.05rem', color: '#0f172a', marginBottom: 8 }}>How it works</p>
                <p style={{ fontSize: '1rem', color: '#3c4653', lineHeight: 1.7 }}>
                  Start writing stories for free — no credit card, no trial period. When your book feels ready, order a printed copy. Every story you've edited, every photo you've added — it all becomes a real, hardcover book delivered to your door. If you want to keep building — more books, memory care for a parent, estate planning, time capsules — Premium keeps everything unlocked.
                </p>
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Mobile bottom nav */}
      <nav className="mobile-nav">
        {mobileNavItems.map(item => (
          <button
            key={item.id}
            data-testid={`mobile-nav-${item.id}`}
            className={`mobile-nav-item${
              (item.id === 'talk' && (activeTab === 'talk' || activeTab === 'st-record' || activeTab === 'st-write'))
              || (item.id === 'book' && activeTab === 'book')
              || (item.id === 'account' && activeTab === 'account')
              ? ' active' : ''
            }`}
            onClick={() => {
              if (item.id === 'write') {
                // Write tab: if user has a book open or any books exist, go straight
                // to the writing flow on the most recent book. If no books, route to
                // My Books so the user can start one.
                if (activeBookId) {
                  setStShowGateway(false);
                  setStInterviewMode(false);
                  setActiveTab('st-record');
                } else if ((books || []).length > 0) {
                  enterBook(books[0].id);
                  setStShowGateway(false);
                  setStInterviewMode(false);
                  setActiveTab('st-record');
                } else {
                  setActiveTab('book');
                  setShowGiftCreate(false);
                  setStBookStep(0);
                }
              } else if (item.id === 'book') {
                openMyBook();
              } else {
                setActiveTab(item.id);
              }
            }}
          >
            <span className="icon">{item.icon}</span>
            {item.label}
          </button>
        ))}
      </nav>

      {/* Floating feedback button */}
      <button className="feedback-fab" onClick={() => setShowFeedback(true)} title="Send Feedback" aria-label="Send Feedback">
        <ChatCircle size={22} weight="duotone" color="#fff" />
      </button>

      </>

      {/* ── Confirmation modals (replace window.confirm) ── */}
      {confirmRemoveRecipientId && (() => {
        const recipient = recipients.find(r => r.id === confirmRemoveRecipientId);
        return (
          <ConfirmModal
            title={recipient ? `Remove ${recipient.name}?` : 'Remove this recipient?'}
            message="They will no longer receive access to your content."
            confirmLabel="Remove"
            cancelLabel="Cancel"
            destructive
            onConfirm={() => {
              saveRecipients(recipients.filter(r => r.id !== confirmRemoveRecipientId));
              setConfirmRemoveRecipientId(null);
            }}
            onCancel={() => setConfirmRemoveRecipientId(null)}
          />
        );
      })()}

      {confirmDeleteStory && (
        <ConfirmModal
          title="Remove this story?"
          message={
            (confirmDeleteStory.msg
              ? confirmDeleteStory.msg + ' '
              : '') +
            "It will be removed from your book and archive. This can't be undone."
          }
          confirmLabel="Delete story"
          cancelLabel="Keep it"
          destructive
          onConfirm={async () => {
            const id = confirmDeleteStory.id;
            setConfirmDeleteStory(null);
            // F-252: this modal's own copy promises "removed from your book and archive", and until
            // now it did only the archive half — the book page survived, pointing at a story that no
            // longer existed. Ordering is delegated to runStoryDelete so it cannot drift from the
            // other delete paths: entry first, page only on a CONFIRMED delete.
            await runStoryDelete({
              storyId: id,
              deleteEntry: (sid) => executeDeleteStory(sid, { silent: true }),
              onFailure: () => showToast("Couldn't remove it. Nothing changed. Please try again.", 'error'),
              onDeleted: (sid) => {
                markStoryRemoved(sid);
                setStBookPages(prev => prev.filter(pg => pg.storyId !== sid));
                removeStoryPageEverywhere(sid); // every book, not just the active one
              },
            });
          }}
          onCancel={() => setConfirmDeleteStory(null)}
        />
      )}

      {confirmDeleteCapsuleId && (
        <ConfirmModal
          title="Delete this time capsule message?"
          message="Once deleted, this message cannot be recovered."
          confirmLabel="Delete"
          cancelLabel="Keep it"
          destructive
          onConfirm={() => {
            saveCapsules(capsules.filter(c => c.id !== confirmDeleteCapsuleId));
            setConfirmDeleteCapsuleId(null);
          }}
          onCancel={() => setConfirmDeleteCapsuleId(null)}
        />
      )}

    </AppContext.Provider>
  );
}

function App() {
  // Detect contribute path BEFORE any state/effects — this must win the race
  const _contributePath = window.location.pathname.match(/^\/contribute\/([a-zA-Z0-9_-]+)\/?$/);
  const [authState, setAuthState] = React.useState(_contributePath ? 'contribute' : 'loading');
  const [currentUser, setCurrentUser] = React.useState(null);
  const [contributeToken, setContributeToken] = React.useState(_contributePath ? _contributePath[1] : null);

  // GA4: associate the session with the signed-in user (opaque Cognito sub only,
  // never PII). Fires on every auth path since they all set currentUser.
  React.useEffect(() => {
    if (currentUser) {
      setAnalyticsUser(currentUser.userId || currentUser.username);
    }
  }, [currentUser]);

  // Check if user is already signed in on mount
  React.useEffect(() => {
    // Don't override contribute page routing
    const path = window.location.pathname;
    if (path.match(/^\/contribute\/[a-zA-Z0-9_-]+\/?$/)) return;

    // Capture a "start a celebration book" intent from the public /celebration
    // CTA (/?intent=celebration) before auth resolves, then strip it from the
    // URL. Everything downstream is gated on this flag, so visitors who don't
    // arrive via that CTA are entirely unaffected.
    try {
      if (new URLSearchParams(window.location.search).get('intent') === 'celebration') {
        localStorage.setItem('tb_intent', 'celebration');
        window.history.replaceState({}, '', window.location.pathname);
      }
    } catch (e) { /* private mode / no storage */ }

    const checkAuth = async () => {
      try {
        const user = await getCurrentUser();
        // TB-096: getCurrentUser() succeeds off cached/localStorage tokens WITHOUT a network
        // check, so a dead 30-day refresh token would otherwise slip through and mount the app
        // with no data (looks like total data loss). Validate the session is actually live
        // before entering. Note: getCurrentUser() THROWS for a genuine never-signed-in visitor
        // (handled in the outer catch → landing), so only a real expired session reaches here.
        try {
          const session = await fetchAuthSession();
          if (!session?.tokens?.accessToken) { setAuthState('session-expired'); return; }
        } catch (sessErr) {
          setAuthState('session-expired');
          return;
        }
        setCurrentUser(user);
        setAuthState('app');
      } catch (e) {
        // getCurrentUser() threw → genuinely not signed in (fresh visitor — never a
        // "session expired" case). If they came to start a celebration book, send them
        // straight to sign-up instead of the generic landing page.
        let pendingIntent = null;
        try { pendingIntent = localStorage.getItem('tb_intent'); } catch (e2) {}
        setAuthState(pendingIntent === 'celebration' ? 'auth-signup' : 'landing');
      }
    };
    checkAuth();
  }, []);

  // Listen for auth events (sign in / sign out from anywhere)
  React.useEffect(() => {
    const path = window.location.pathname;
    if (path.match(/^\/contribute\/[a-zA-Z0-9_-]+\/?$/)) return;

    const unsubscribe = Hub.listen('auth', ({ payload }) => {
      if (payload.event === 'signedIn') {
        sessionExpiredRef.current = false; // fresh session — re-arm expiry handling
        getCurrentUser().then(user => { setCurrentUser(user); setAuthState('app'); });
      }
      if (payload.event === 'signedOut') {
        // TB-096: when the expiry flow signs out, it owns the redirect (→ session-expired).
        // Don't let this generic handler override it with 'landing'.
        if (sessionExpiredRef.current) return;
        setCurrentUser(null); setAuthState('landing');
      }
      // TB-096: Amplify fires this when a background token refresh fails (session dead).
      if (payload.event === 'tokenRefresh_failure') {
        handleAuthExpiry('token_refresh_failure');
      }
    });
    return unsubscribe;
  }, []); // eslint-disable-line react-hooks/exhaustive-deps -- handleAuthExpiry is a stable useCallback

  const handleAuthSuccess = () => {
    sessionExpiredRef.current = false; // TB-096: re-arm expiry handling after a fresh sign-in
    const detect = (attempt = 0) => {
      getCurrentUser()
        .then(user => { setCurrentUser(user); setAuthState('app'); })
        .catch(() => {
          // The session can be briefly unreadable right after a fresh sign-in (especially a
          // signOut then signIn account switch). Retry a few times before concluding "not signed in".
          if (attempt < 5) setTimeout(() => detect(attempt + 1), 200);
          else setAuthState('landing');
        });
    };
    detect();
  };

  const sessionExpiredRef = React.useRef(false);

  // TB-096: central auth-expiry recovery. Try one silent refresh; if the session is truly
  // dead, sign out cleanly and route to a reassuring "your stories are safe" re-auth screen —
  // never leave the app mounted with empty data (the "missing chapters" trust failure).
  // Deduped so the 4 parallel mount fetches can't fire it 4×.
  const handleAuthExpiry = React.useCallback(async (source) => {
    if (sessionExpiredRef.current) return;
    sessionExpiredRef.current = true;
    console.warn('[TB-096] auth expiry detected at:', source);
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      if (session?.tokens?.accessToken) {
        // Refresh succeeded — reload to re-run mount data fetches with fresh tokens.
        window.location.reload();
        return;
      }
    } catch (refreshErr) { /* fall through to sign-out */ }
    try { await amplifySignOut(); } catch (_) { /* may already be gone */ }
    clearSignedUrlCache();
    clearAppScopedLocalStorage();
    setCurrentUser(null);
    setAuthState('session-expired');
  }, []);

  const handleSignOut = async (source) => {
    // TB-096: a data-fetch catch may call signOut('auth_expiry') — route that through the
    // expiry recovery (silent refresh → reassuring re-auth) rather than a plain sign-out.
    if (source === 'auth_expiry') return handleAuthExpiry(source);
    await amplifySignOut();
    // TB-075 QW-1: clear the in-memory signed URL cache so User A's photo
    // URLs (HMAC-signed to their identityId) don't leak into User B's
    // session on a shared browser tab. URLs would 403 harmlessly for User B
    // but the path structure itself reveals User A's private prefix.
    clearSignedUrlCache();
    clearAppScopedLocalStorage();
    setCurrentUser(null);
    setAuthState('landing');
  };

  if (authState === 'contribute') {
    return <ContributePage token={contributeToken} />;
  }

  if (authState === 'loading') {
    return (
      <div style={{ minHeight: '100svh', background: '#080f0c', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div style={{ fontFamily: "'Newsreader', serif", color: '#f0e6d4', letterSpacing: '4px', fontSize: '1.05rem' }}>
          Talebound
        </div>
      </div>
    );
  }

  if (authState === 'landing') {
    return (
      <LandingPage
        onGetStarted={() => setAuthState('auth-signup')}
        onSignIn={() => setAuthState('auth')}
      />
    );
  }

  if (authState === 'auth') {
    return (
      <AuthPage
        initialMode="signIn"
        onSuccess={handleAuthSuccess}
        onBack={() => setAuthState('landing')}
      />
    );
  }

  if (authState === 'auth-signup') {
    return (
      <AuthPage
        initialMode="signUp"
        onSuccess={handleAuthSuccess}
        onBack={() => setAuthState('landing')}
      />
    );
  }

  // TB-096: a dead/expired session lands here instead of an empty "data lost" shell.
  // No back button (Option A) — this is a forced re-auth; signing in returns them to the app.
  if (authState === 'session-expired') {
    return (
      <AuthPage
        initialMode="signIn"
        sessionExpiredMessage="Please sign in again to keep writing. Your stories are safe."
        onSuccess={handleAuthSuccess}
      />
    );
  }

  return <VaultApp signOut={handleSignOut} user={currentUser} />;
}

export default App;